import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Button, Collapse, Input, InputNumber, Modal, Space, Table, Tabs, Tag, Tooltip, message, Form, Row, Col, Select, Divider, Descriptions, Switch, Upload, Radio, Spin, Checkbox } from 'antd';
import { CopyOutlined, PrinterOutlined, WarningOutlined, DownOutlined, RightOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { PDFDocument } from 'pdf-lib';
import request from '../../../../utils/request';
import { getShippingPackagings } from '../../../../services/logistics';
import type { ShippingPackaging } from '../../../../services/logistics';
import { getInventoryUnitByBarcode } from '../../../../services/inventory';

type Props = {
  step?: any;
  data?: any;
  metrics?: any;
  instructionDetail?: any;
  onChange?: (k: string, v: any) => void;
  onSubmit?: () => Promise<void> | void;
  instructionCodeFromParent?: string;
  canOperate?: boolean;
  taskId?: number | null;
  executionSessionId?: string | null;
  stageNo?: number;
};

/**
 * 常规出库中间阶段（配货/装箱/贴单）专用占位组件。
 * 当前仅用于执行链路分流占位，不承载业务提交流程。
 */
const RegularOutboundPackRenderer: React.FC<Props> = ({ step, data, metrics, onChange, instructionCodeFromParent, instructionDetail, canOperate, executionSessionId, taskId, stageNo }) => {
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [previewTitle, setPreviewTitle] = useState<string>('附件预览');
  const [previewType, setPreviewType] = useState<'image' | 'file' | 'table'>('file');
  const [previewPrintText, setPreviewPrintText] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [previewTableCols, setPreviewTableCols] = useState<any[]>([]);
  const [previewTableData, setPreviewTableData] = useState<any[]>([]);
  const [previewDownloadName, setPreviewDownloadName] = useState<string>('');
  const [previewDownloadBlob, setPreviewDownloadBlob] = useState<Blob | null>(null);
  const [previewDownloadUrl, setPreviewDownloadUrl] = useState<string>('');
  const [printOtherAttachmentsModalOpen, setPrintOtherAttachmentsModalOpen] = useState<boolean>(false);
  const [buyWaybillModalOpen, setBuyWaybillModalOpen] = useState<boolean>(false);
  const [unableReasonModalOpen, setUnableReasonModalOpen] = useState<boolean>(false);
  const [unableReasonQuickValue, setUnableReasonQuickValue] = useState<string>('');
  const [unableReasonInput, setUnableReasonInput] = useState<string>('');
  const [labelingModalOpen, setLabelingModalOpen] = useState<boolean>(false);
  const [labelingTargetCode, setLabelingTargetCode] = useState<string>('');
  const [labelingLabelCode, setLabelingLabelCode] = useState<string>('');
  const [labelingStep, setLabelingStep] = useState<'target' | 'label'>('target');
  const [labelingAutoNext, setLabelingAutoNext] = useState<boolean>(true);
  const [labelingWarnBeep, setLabelingWarnBeep] = useState<boolean>(true);
  const [labelingConstraintLoading, setLabelingConstraintLoading] = useState<boolean>(false);
  const [labelingConstraintReady, setLabelingConstraintReady] = useState<boolean>(false);
  const [labelingConstraintName, setLabelingConstraintName] = useState<string>('');
  const [labelingConstraintMap, setLabelingConstraintMap] = useState<Map<string, Set<string>>>(new Map());
  const [labelingConstraintMatchableRowCount, setLabelingConstraintMatchableRowCount] = useState<number>(0);
  const [labelingConsumedMatchKeys, setLabelingConsumedMatchKeys] = useState<Set<string>>(new Set());
  const [labelingPendingOperationRecords, setLabelingPendingOperationRecords] = useState<any[]>([]);
  const [labelingLastResult, setLabelingLastResult] = useState<{ ok: boolean; text: string }>({ ok: true, text: '请先扫描出库对象编码' });
  const [labelingSessionRows, setLabelingSessionRows] = useState<Array<{ key: string; time: string; target: string; label: string; result: string; remark: string }>>([]);
  const [labelingSessionPage, setLabelingSessionPage] = useState<number>(1);
  const [labelingSessionPageSize, setLabelingSessionPageSize] = useState<number>(8);
  const [buyWaybillSubmitting, setBuyWaybillSubmitting] = useState<boolean>(false);
  const [buyWaybillCarrier, setBuyWaybillCarrier] = useState<string>('');
  const [buyWaybillTrackingNo, setBuyWaybillTrackingNo] = useState<string>('');
  const [buyWaybillFileList, setBuyWaybillFileList] = useState<any[]>([]);
  const [buyWaybillChargeAmount, setBuyWaybillChargeAmount] = useState<number | null>(null);
  const [buyWaybillChargeCurrency, setBuyWaybillChargeCurrency] = useState<string>('USD');
  const [buyWaybillChargeNote, setBuyWaybillChargeNote] = useState<string>('');
  const [buyWaybillProviderOptions, setBuyWaybillProviderOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [localProcessResultStatus, setLocalProcessResultStatus] = useState<string>('');
  const [localProcessUnableReason, setLocalProcessUnableReason] = useState<string>('');
  const [localWaybillCarrier, setLocalWaybillCarrier] = useState<string>('');
  const [localWaybillTrackingNo, setLocalWaybillTrackingNo] = useState<string>('');
  const [localWaybillChargeAmount, setLocalWaybillChargeAmount] = useState<string>('');
  const [localWaybillChargeCurrency, setLocalWaybillChargeCurrency] = useState<string>('');
  const [localWaybillChargeNote, setLocalWaybillChargeNote] = useState<string>('');
  const [localAttachmentsDetail, setLocalAttachmentsDetail] = useState<any[]>([]);
  const [baselineRows, setBaselineRows] = useState<any[]>([]);
  const [baselineLoading, setBaselineLoading] = useState<boolean>(false);
  const [readyBaselineInlineLoading, setReadyBaselineInlineLoading] = useState<boolean>(false);
  const [readyBaselineInlineError, setReadyBaselineInlineError] = useState<string>('');
  const [readyBaselineInlineCols, setReadyBaselineInlineCols] = useState<any[]>([]);
  const [readyBaselineInlineData, setReadyBaselineInlineData] = useState<any[]>([]);
  const [readyBaselineDoneRowKeys, setReadyBaselineDoneRowKeys] = useState<Set<number>>(new Set());
  const [readyBaselineOnlyUnmatched, setReadyBaselineOnlyUnmatched] = useState<boolean>(true);
  const [readyBaselineInlinePage, setReadyBaselineInlinePage] = useState<number>(1);
  const [readyBaselineInlinePageSize, setReadyBaselineInlinePageSize] = useState<number>(20);
  const [transportLabelPdfFileCount, setTransportLabelPdfFileCount] = useState<number>(0);
  const [transportLabelPdfPageTotal, setTransportLabelPdfPageTotal] = useState<number>(0);
  const [transportLabelImageFileCount, setTransportLabelImageFileCount] = useState<number>(0);
  const [printTransportLabelPdfRunning, setPrintTransportLabelPdfRunning] = useState<boolean>(false);
  const [printTransportLabelImageRunning, setPrintTransportLabelImageRunning] = useState<boolean>(false);
  const [transportLabelListExpanded, setTransportLabelListExpanded] = useState<boolean>(false);
  const [contentModalOpen, setContentModalOpen] = useState<boolean>(false);
  const [contentModalUnitCode, setContentModalUnitCode] = useState<string>('');
  const [contentModalLines, setContentModalLines] = useState<string[]>([]);
  const [iocDisplayRows, setIocDisplayRows] = useState<any[]>([]);
  const [iocDisplayLoading, setIocDisplayLoading] = useState<boolean>(false);
  const [boxes, setBoxes] = useState<Array<{ code: string; status?: 'open' | 'closed' | 'cancelled'; container_type?: string }>>([]);
  const [boxMeta, setBoxMeta] = useState<Record<string, any>>({});
  const [outputDraftByCode, setOutputDraftByCode] = useState<Record<string, any>>({});
  const [currentPackBoxCode, setCurrentPackBoxCode] = useState<string>('');
  const [addToExistingEnabled, setAddToExistingEnabled] = useState<boolean>(false);
  const [addToExistingCode, setAddToExistingCode] = useState<string>('');
  const [pickExistingOpen, setPickExistingOpen] = useState<boolean>(false);
  const [pickExistingCode, setPickExistingCode] = useState<string>('');
  const [pickExistingQuery, setPickExistingQuery] = useState<string>('');
  const [pickExistingPage, setPickExistingPage] = useState<number>(1);
  const [pickExistingPageSize, setPickExistingPageSize] = useState<number>(10);
  const [labelDirtyByCode, setLabelDirtyByCode] = useState<Record<string, boolean>>({});
  const [adjustLoading, setAdjustLoading] = useState<boolean>(false);
  const [adjustMode, setAdjustMode] = useState<'take_only' | 'take_put' | 'put_only'>('take_only');
  const [adjustFromBoxCode, setAdjustFromBoxCode] = useState<string>('');
  const [adjustToBoxCode, setAdjustToBoxCode] = useState<string>('');
  const [adjustTargetCode, setAdjustTargetCode] = useState<string>('');
  const [adjustQty, setAdjustQty] = useState<number>(1);
  const [adjustMoveAll, setAdjustMoveAll] = useState<boolean>(false);
  const [adjustMoveAllPreviewOpen, setAdjustMoveAllPreviewOpen] = useState<boolean>(false);
  const [adjustMoveAllPreviewRows, setAdjustMoveAllPreviewRows] = useState<any[]>([]);
  const [adjustMoveAllPreviewTotal, setAdjustMoveAllPreviewTotal] = useState<number>(0);
  const [rescanContainerCode, setRescanContainerCode] = useState<string>('');
  const [rescanFromBoxCode, setRescanFromBoxCode] = useState<string>('');
  const [rescanTargetCode, setRescanTargetCode] = useState<string>('');
  const [rescanQty, setRescanQty] = useState<number>(1);
  const [rescanLoading, setRescanLoading] = useState<boolean>(false);
  const [rescanStarted, setRescanStarted] = useState<boolean>(false);
  const [rescanCompareOpen, setRescanCompareOpen] = useState<boolean>(false);
  const [rescanBaselineRows, setRescanBaselineRows] = useState<Array<{ target_type: string; target: string; qty: number }>>([]);
  const [rescanBaselineLotsNetByKey, setRescanBaselineLotsNetByKey] = useState<Record<string, { target_type: string; target: string; qty: number }>>({});
  const [rescanPoolNetBaselineByKey, setRescanPoolNetBaselineByKey] = useState<Record<string, number>>({});
  const [packConsoleTab, setPackConsoleTab] = useState<'pack_start' | 'pack_adjust' | 'pack_rescan' | 'pack_hierarchy'>('pack_start');
  const [hierarchyParentCode, setHierarchyParentCode] = useState<string>('');
  const [hierarchyChildScanCode, setHierarchyChildScanCode] = useState<string>('');
  const [hierarchyLinks, setHierarchyLinks] = useState<Array<{ id: string; parent_code: string; child_code: string; source: string; time: string }>>([]);
  const [hierarchyBatchOpen, setHierarchyBatchOpen] = useState<boolean>(false);
  const [hierarchyBatchQuery, setHierarchyBatchQuery] = useState<string>('');
  const [hierarchyBatchPage, setHierarchyBatchPage] = useState<number>(1);
  const [hierarchyBatchPageSize, setHierarchyBatchPageSize] = useState<number>(10);
  const [hierarchyBatchLinkedQuery, setHierarchyBatchLinkedQuery] = useState<string>('');
  const [hierarchyBatchLinkedPage, setHierarchyBatchLinkedPage] = useState<number>(1);
  const [hierarchyBatchLinkedPageSize, setHierarchyBatchLinkedPageSize] = useState<number>(10);
  const [hierarchyBatchSelectedKeys, setHierarchyBatchSelectedKeys] = useState<React.Key[]>([]);
  const [localOperationRecords, setLocalOperationRecords] = useState<any[]>([]);
  const [packTargetQty, setPackTargetQty] = useState<number>(1);
  const [packTargetScan, setPackTargetScan] = useState<string>('');
  const [startContainerOpen, setStartContainerOpen] = useState<boolean>(false);
  const [startContainerLoading, setStartContainerLoading] = useState<boolean>(false);
  const [endContainerOpen, setEndContainerOpen] = useState<boolean>(false);
  const [endContainerSubmitting, setEndContainerSubmitting] = useState<boolean>(false);
  const [endContainerDraft, setEndContainerDraft] = useState<{
    code: string;
    container_type: 'box' | 'pallet' | 'other' | string;
    length_cm?: number;
    width_cm?: number;
    height_cm?: number;
    weight_g?: number;
  }>({ code: '', container_type: 'box' });
  const [endContainerLenInDraft, setEndContainerLenInDraft] = useState<number | undefined>(undefined);
  const [endContainerWidInDraft, setEndContainerWidInDraft] = useState<number | undefined>(undefined);
  const [endContainerHeiInDraft, setEndContainerHeiInDraft] = useState<number | undefined>(undefined);
  const [endContainerKgDraft, setEndContainerKgDraft] = useState<number | undefined>(undefined);
  const [endContainerLbDraft, setEndContainerLbDraft] = useState<number | undefined>(undefined);
  const [endContainerContentRows, setEndContainerContentRows] = useState<Array<{ key: string; target: string; qty: number }>>([]);
  const [endContainerContentPage, setEndContainerContentPage] = useState<number>(1);
  const [endContainerContentPageSize, setEndContainerContentPageSize] = useState<number>(10);
  const [endContainerLinkedPage, setEndContainerLinkedPage] = useState<number>(1);
  const [endContainerLinkedPageSize, setEndContainerLinkedPageSize] = useState<number>(5);
  const [targetPickerOpen, setTargetPickerOpen] = useState<boolean>(false);
  const [targetPickerSearch, setTargetPickerSearch] = useState<string>('');
  const [targetPickerPage, setTargetPickerPage] = useState<number>(1);
  const [targetPickerPageSize, setTargetPickerPageSize] = useState<number>(10);
  const [targetOverviewPage, setTargetOverviewPage] = useState<number>(1);
  const [targetOverviewPageSize, setTargetOverviewPageSize] = useState<number>(10);
  const [targetPickerSelectedKeys, setTargetPickerSelectedKeys] = useState<React.Key[]>([]);
  const [targetPickerAddQtyByKey, setTargetPickerAddQtyByKey] = useState<Record<string, number>>({});
  const [targetWeightGByKey, setTargetWeightGByKey] = useState<Record<string, number | null>>({});
  const [boxStatPage, setBoxStatPage] = useState<number>(1);
  const [boxStatPageSize, setBoxStatPageSize] = useState<number>(10);
  const [hierarchyDisplayPage, setHierarchyDisplayPage] = useState<number>(1);
  const [hierarchyDisplayPageSize, setHierarchyDisplayPageSize] = useState<number>(10);
  const [operationRecordsPage, setOperationRecordsPage] = useState<number>(1);
  const [operationRecordsPageSize, setOperationRecordsPageSize] = useState<number>(10);
  const [containerDetailOpen, setContainerDetailOpen] = useState<boolean>(false);
  const [containerDetailCode, setContainerDetailCode] = useState<string>('');
  const [targetDetailOpen, setTargetDetailOpen] = useState<boolean>(false);
  const [targetDetailTitle, setTargetDetailTitle] = useState<string>('');
  const [targetDetailCode, setTargetDetailCode] = useState<string>('');
  const [targetDetailPage, setTargetDetailPage] = useState<number>(1);
  const [targetDetailPageSize, setTargetDetailPageSize] = useState<number>(10);
  const [targetBoxOpen, setTargetBoxOpen] = useState<boolean>(false);
  const [targetBoxTitle, setTargetBoxTitle] = useState<string>('');
  const [targetBoxRows, setTargetBoxRows] = useState<any[]>([]);
  const [boxFinalOpen, setBoxFinalOpen] = useState<boolean>(false);
  const [boxFinalCode, setBoxFinalCode] = useState<string>('');
  const [voidingContainerCodes, setVoidingContainerCodes] = useState<Set<string>>(new Set());
  const [boxFinalDraft, setBoxFinalDraft] = useState<{ length_cm?: number; width_cm?: number; height_cm?: number; weight_g?: number }>({});
  const [boxFinalLenIn, setBoxFinalLenIn] = useState<number | undefined>(undefined);
  const [boxFinalWidIn, setBoxFinalWidIn] = useState<number | undefined>(undefined);
  const [boxFinalHeiIn, setBoxFinalHeiIn] = useState<number | undefined>(undefined);
  const [boxFinalLb, setBoxFinalLb] = useState<number | undefined>(undefined);
  const [boxFinalDirty, setBoxFinalDirty] = useState<boolean>(false);
  const [boxFinalContentPage, setBoxFinalContentPage] = useState<number>(1);
  const [boxFinalContentPageSize, setBoxFinalContentPageSize] = useState<number>(10);
  const [boxFinalLinkedPage, setBoxFinalLinkedPage] = useState<number>(1);
  const [boxFinalLinkedPageSize, setBoxFinalLinkedPageSize] = useState<number>(5);
  const [boxLabelOpen, setBoxLabelOpen] = useState<boolean>(false);
  const [boxLabelCode, setBoxLabelCode] = useState<string>('');
  const [boxLabelUrl, setBoxLabelUrl] = useState<string>('');
  const [boxLabelLoading, setBoxLabelLoading] = useState<boolean>(false);
  const [containerDetailPage, setContainerDetailPage] = useState<number>(1);
  const [containerDetailPageSize, setContainerDetailPageSize] = useState<number>(10);
  const [targetBoxPage, setTargetBoxPage] = useState<number>(1);
  const [targetBoxPageSize, setTargetBoxPageSize] = useState<number>(10);
  const [shippingPkgQuery, setShippingPkgQuery] = useState<string>('');
  const [shippingPkgLoading, setShippingPkgLoading] = useState<boolean>(false);
  const [shippingPkgErr, setShippingPkgErr] = useState<string>('');
  const [shippingPkgHit, setShippingPkgHit] = useState<ShippingPackaging | null>(null);
  const [shippingPkgOptions, setShippingPkgOptions] = useState<ShippingPackaging[]>([]);
  const shipQueryTimerRef = useRef<any>(null);
  const lastShipQueryRef = useRef<string>('');
  const shippingPkgSelectRef = useRef<any>(null);
  const targetPickerOpenPrevRef = useRef<boolean>(false);
  const draftSaveTimerRef = useRef<any>(null);
  const draftHydratedRef = useRef<boolean>(false);
  const draftSaveToServerTimerRef = useRef<any>(null);
  const lastServerDraftSigRef = useRef<string>('');
  const [startForm] = Form.useForm();
  const targetWeightLoadingRef = useRef<Set<string>>(new Set());
  const seededLedgerRef = useRef<boolean>(false);
  const previewFrameRef = useRef<HTMLIFrameElement | null>(null);
  const boxLabelFrameRef = useRef<HTMLIFrameElement | null>(null);
  const boxLabelPopupRef = useRef<Window | null>(null);
  const transportLabelPrintPopupRef = useRef<Window | null>(null);
  const transportLabelPrintDebug = true;
  const tlPrintLog = (...args: any[]) => {
    if (!transportLabelPrintDebug) return;
    try { console.debug('[RegularOutbound][TransportLabelPrint]', ...args); } catch {}
  };
  const scanTargetInputRef = useRef<any>(null);
  const labelingTargetInputRef = useRef<any>(null);
  const labelingLabelInputRef = useRef<any>(null);
  const hierarchyScanInputRef = useRef<any>(null);
  const beepAudioCtxRef = useRef<any>(null);
  const activePackBoxCode = String(currentPackBoxCode || '').trim() || (addToExistingEnabled ? String(addToExistingCode || '').trim() : '');
  const hasActivePackContainer = !!String(activePackBoxCode || '').trim();
  const adjustPoolCode = useMemo(() => {
    const i = Number((instructionDetail as any)?.id || 0);
    return Number.isFinite(i) && i > 0 ? `ADJ-POOL-${i}` : 'ADJ-POOL';
  }, [instructionDetail]);
  const normalizeAdjustTargetCode = useCallback((v: any) => String(v || '').trim().toUpperCase(), []);
  const resolveAdjustTargetType = useCallback(() => {
    const td = Array.isArray((instructionDetail as any)?.targets_detail) ? (instructionDetail as any).targets_detail : [];
    const hasIoc = td.some((t: any) => String(t?.target_type || '').trim() === 'instruction_output');
    return hasIoc ? 'instruction_output_container' : 'inventory_unit';
  }, [instructionDetail]);
  const makeAdjustTargetKey = useCallback((targetTypeRaw: any, targetCodeRaw: any) => {
    const t = String(targetTypeRaw || '').trim();
    const c = normalizeAdjustTargetCode(targetCodeRaw);
    if (!t || !c || c === '-') return '';
    return `${t}:${c}`;
  }, [normalizeAdjustTargetCode]);
  const adjustContainerOptions = useMemo(() => {
    const set = new Map<string, { code: string; st?: string }>();
    (boxes || []).forEach((b: any) => {
      const cc = String(b?.code || '').trim();
      if (!cc) return;
      set.set(cc, { code: cc, st: String(b?.status || '') });
    });
    Object.keys(outputDraftByCode || {}).forEach((k) => {
      const cc = String(k || '').trim();
      if (!cc || set.has(cc)) return;
      const d: any = (outputDraftByCode as any)?.[cc] || {};
      set.set(cc, { code: cc, st: String(d?.status || 'closed') });
    });
    return Array.from(set.values())
      .filter((x) => !!String(x?.code || '').trim() && String(x?.code || '').trim() !== String(adjustPoolCode || '').trim())
      .map((x) => {
        const st = String(x?.st || '').trim();
        const stTxt = st ? (st === 'closed' ? '已结束' : st === 'open' ? '进行中' : st === 'cancelled' ? '已作废' : st) : '';
        return { value: x.code, label: `${x.code}${stTxt ? `（${stTxt}）` : ''}` };
      });
  }, [boxes, outputDraftByCode, adjustPoolCode]);
  const hierarchyContainerRows = useMemo(() => {
    const m = new Map<string, { code: string; status: string; container_type?: string }>();
    (Array.isArray(boxes) ? boxes : []).forEach((b: any) => {
      const code = String(b?.code || '').trim();
      if (!code || code === String(adjustPoolCode || '').trim()) return;
      m.set(code, { code, status: String(b?.status || '').trim(), container_type: String(b?.container_type || '').trim() });
    });
    Object.keys(outputDraftByCode || {}).forEach((k) => {
      const code = String(k || '').trim();
      if (!code || code === String(adjustPoolCode || '').trim()) return;
      const d: any = (outputDraftByCode as any)?.[code] || {};
      if (!m.has(code)) {
        m.set(code, { code, status: String(d?.status || 'closed').trim(), container_type: String(d?.container_type || '').trim() });
      }
    });
    return Array.from(m.values())
      .sort((a, b) => String(a?.code || '').localeCompare(String(b?.code || '')));
  }, [boxes, outputDraftByCode, adjustPoolCode]);
  const hierarchyContainerMap = useMemo(() => {
    const m = new Map<string, { code: string; status: string; container_type?: string }>();
    (hierarchyContainerRows || []).forEach((x: any) => m.set(String(x?.code || '').trim(), x));
    return m;
  }, [hierarchyContainerRows]);
  const hierarchyParentOptions = useMemo(() => {
    const childSet = new Set<string>();
    const childCountByParent = new Map<string, number>();
    (Array.isArray(hierarchyLinks) ? hierarchyLinks : []).forEach((r: any) => {
      const p = String(r?.parent_code || '').trim();
      const c = String(r?.child_code || '').trim();
      if (c) childSet.add(c);
      if (p && c) childCountByParent.set(p, (childCountByParent.get(p) || 0) + 1);
    });
    const isPalletContainer = (row: any): boolean => {
      const ct = String(row?.container_type || '').trim().toLowerCase();
      if (ct === 'pallet' || ct === 'plt') return true;
      const code = String(row?.code || '').trim().toUpperCase();
      return code.startsWith('IOC-P-');
    };
    return (hierarchyContainerRows || [])
      .filter((x: any) => {
        const code = String(x?.code || '').trim();
        const st = String(x?.status || '').trim().toLowerCase();
        if (!code) return false;
        if (st === 'cancelled') return false;
        // 子容器不能作为父容器
        return !childSet.has(code);
      })
      .slice()
      .sort((a: any, b: any) => {
        const ap = isPalletContainer(a) ? 0 : 1;
        const bp = isPalletContainer(b) ? 0 : 1;
        if (ap !== bp) return ap - bp;
        return String(a?.code || '').localeCompare(String(b?.code || ''));
      })
      .map((x: any) => {
        const code = String(x?.code || '').trim();
        const st = String(x?.status || '').trim();
        const stTxt = st ? (st === 'closed' ? '已结束' : st === 'open' ? '进行中' : st === 'cancelled' ? '已作废' : st) : '';
        const childCount = childCountByParent.get(code) || 0;
        const extra = childCount > 0 ? `-已关联子容器${childCount}` : '';
        return { value: code, label: `${code}${stTxt ? `(${stTxt}${extra})` : ''}` };
      });
  }, [hierarchyContainerRows, hierarchyLinks]);
  useEffect(() => {
    const parent = String(hierarchyParentCode || '').trim();
    if (!parent) return;
    const ok = (hierarchyParentOptions || []).some((op: any) => String(op?.value || '').trim() === parent);
    if (!ok) setHierarchyParentCode('');
  }, [hierarchyParentCode, hierarchyParentOptions]);
  const hierarchyParentByChild = useMemo(() => {
    const m = new Map<string, string>();
    (Array.isArray(hierarchyLinks) ? hierarchyLinks : []).forEach((r: any) => {
      const p = String(r?.parent_code || '').trim();
      const c = String(r?.child_code || '').trim();
      if (!p || !c) return;
      m.set(c, p);
    });
    return m;
  }, [hierarchyLinks]);
  const hierarchyRowsByParent = useMemo(() => {
    const m = new Map<string, Array<{ id: string; child_code: string; source: string; time: string }>>();
    (Array.isArray(hierarchyLinks) ? hierarchyLinks : []).forEach((r: any) => {
      const p = String(r?.parent_code || '').trim();
      const c = String(r?.child_code || '').trim();
      if (!p || !c) return;
      const arr = m.get(p) || [];
      arr.push({ id: String(r?.id || ''), child_code: c, source: String(r?.source || '-'), time: String(r?.time || '') });
      m.set(p, arr);
    });
    m.forEach((arr, p) => {
      arr.sort((a, b) => String(a.child_code).localeCompare(String(b.child_code)));
      m.set(p, arr);
    });
    return m;
  }, [hierarchyLinks]);
  const hierarchyDisplayRows = useMemo(() => {
    const rows = (Array.isArray(hierarchyLinks) ? hierarchyLinks : [])
      .map((r: any) => ({
        ...r,
        parent_status: String((hierarchyContainerMap.get(String(r?.parent_code || '').trim()) as any)?.status || '').trim(),
        child_status: String((hierarchyContainerMap.get(String(r?.child_code || '').trim()) as any)?.status || '').trim(),
      }))
      .sort((a: any, b: any) => String(a?.parent_code || '').localeCompare(String(b?.parent_code || '')) || String(a?.child_code || '').localeCompare(String(b?.child_code || '')));
    let groupIdx = -1;
    let prevParent = '';
    return rows.map((r: any) => {
      const parent = String(r?.parent_code || '').trim();
      const isFirst = parent !== prevParent;
      if (isFirst) groupIdx += 1;
      prevParent = parent;
      return {
        ...r,
        __groupIdx: groupIdx,
        __isFirstInParentGroup: isFirst,
      };
    });
  }, [hierarchyLinks, hierarchyContainerMap]);
  const hierarchyCurrentChildren = useMemo(() => {
    const p = String(hierarchyParentCode || '').trim();
    if (!p) return [] as Array<{ id: string; child_code: string; source: string; time: string }>;
    return hierarchyRowsByParent.get(p) || [];
  }, [hierarchyRowsByParent, hierarchyParentCode]);
  const resolveLinkedChildrenRows = useCallback((parentCodeRaw: any) => {
    const p = String(parentCodeRaw || '').trim();
    if (!p) return [] as Array<{ id: string; child_code: string; child_status: string; source: string; time: string }>;
    const rows = hierarchyRowsByParent.get(p) || [];
    return rows.map((r: any) => {
      const childCode = String(r?.child_code || '').trim();
      const childMeta: any = hierarchyContainerMap.get(childCode) || {};
      const childBoxMeta: any = (boxMeta as any)?.[childCode] || {};
      const wg = Number(childBoxMeta?.weight_g);
      const child_weight_kg = Number.isFinite(wg) && wg > 0 ? Math.ceil(wg / 1000) : undefined;
      return {
        id: String(r?.id || ''),
        child_code: childCode,
        child_status: String(childMeta?.status || '').trim(),
        child_weight_kg,
        source: String(r?.source || '-').trim() || '-',
        time: String(r?.time || '').trim(),
      };
    });
  }, [hierarchyRowsByParent, hierarchyContainerMap, boxMeta]);
  const hierarchyBatchCandidates = useMemo(() => {
    const p = String(hierarchyParentCode || '').trim();
    if (!p) return [] as any[];
    const q = String(hierarchyBatchQuery || '').trim().toUpperCase();
    const childSet = new Set((hierarchyRowsByParent.get(p) || []).map((x) => String(x?.child_code || '').trim()));
    const parentSet = new Set((Array.isArray(hierarchyLinks) ? hierarchyLinks : []).map((x: any) => String(x?.parent_code || '').trim()).filter(Boolean));
    const isPalletContainer = (row: any): boolean => {
      const ct = String(row?.container_type || '').trim().toLowerCase();
      if (ct === 'pallet' || ct === 'plt') return true;
      const code = String(row?.code || '').trim().toUpperCase();
      return code.startsWith('IOC-P-');
    };
    return (hierarchyContainerRows || [])
      .filter((x: any) => {
        const code = String(x?.code || '').trim();
        const st = String(x?.status || '').trim().toLowerCase();
        if (!code || code === p) return false;
        if (st === 'cancelled') return false;
        if (isPalletContainer(x)) return false;
        if (childSet.has(code)) return false;
        if (parentSet.has(code)) return false;
        if (q && !code.toUpperCase().includes(q)) return false;
        return true;
      })
      .map((x: any) => {
        const code = String(x?.code || '').trim();
        const occupiedBy = String(hierarchyParentByChild.get(code) || '').trim();
        return { ...x, occupiedBy };
      });
  }, [hierarchyParentCode, hierarchyBatchQuery, hierarchyRowsByParent, hierarchyContainerRows, hierarchyParentByChild, hierarchyLinks]);
  const hierarchyBatchLinkedChildren = useMemo(() => {
    const p = String(hierarchyParentCode || '').trim();
    if (!p) return [] as any[];
    const q = String(hierarchyBatchLinkedQuery || '').trim().toUpperCase();
    return (hierarchyCurrentChildren || [])
      .map((r: any) => {
        const code = String(r?.child_code || '').trim();
        const meta: any = hierarchyContainerMap.get(code) || {};
        return {
          ...r,
          code,
          status: String(meta?.status || '').trim(),
          occupiedBy: p,
        };
      })
      .filter((x: any) => {
        const code = String(x?.code || '').trim().toUpperCase();
        if (q && !code.includes(q)) return false;
        return true;
      });
  }, [hierarchyParentCode, hierarchyBatchLinkedQuery, hierarchyCurrentChildren, hierarchyContainerMap]);
  const hierarchyHasPath = useCallback((startRaw: any, targetRaw: any, links: Array<{ parent_code: string; child_code: string }>) => {
    const start = String(startRaw || '').trim();
    const target = String(targetRaw || '').trim();
    if (!start || !target) return false;
    const graph = new Map<string, string[]>();
    (Array.isArray(links) ? links : []).forEach((r: any) => {
      const p = String(r?.parent_code || '').trim();
      const c = String(r?.child_code || '').trim();
      if (!p || !c) return;
      const arr = graph.get(p) || [];
      arr.push(c);
      graph.set(p, arr);
    });
    const seen = new Set<string>();
    const queue = [start];
    while (queue.length) {
      const cur = String(queue.shift() || '').trim();
      if (!cur || seen.has(cur)) continue;
      if (cur === target) return true;
      seen.add(cur);
      (graph.get(cur) || []).forEach((n) => { if (!seen.has(n)) queue.push(n); });
    }
    return false;
  }, []);
  const hierarchyWarn = useCallback((text: string) => {
    try {
      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (AudioCtx) {
        const ctx = beepAudioCtxRef.current || new AudioCtx();
        beepAudioCtxRef.current = ctx;
        try {
          if (ctx.state === 'suspended') void ctx.resume();
        } catch {}
        const beep = (at: number, freq: number) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'square';
          osc.frequency.setValueAtTime(freq, at);
          gain.gain.setValueAtTime(0.0001, at);
          gain.gain.exponentialRampToValueAtTime(0.22, at + 0.01);
          gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.2);
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start(at);
          osc.stop(at + 0.22);
        };
        const t0 = ctx.currentTime;
        beep(t0, 980);
        beep(t0 + 0.24, 820);
      }
    } catch {}
    message.warning(text);
  }, []);
  const playLabelingTone = useCallback((kind: 'ok' | 'warn') => {
    try {
      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = beepAudioCtxRef.current || new AudioCtx();
      beepAudioCtxRef.current = ctx;
      try {
        if (ctx.state === 'suspended') void ctx.resume();
      } catch {}
      const beep = (at: number, freq: number, dur = 0.12, gainMax = 0.18) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'square';
        osc.frequency.setValueAtTime(freq, at);
        gain.gain.setValueAtTime(0.0001, at);
        gain.gain.exponentialRampToValueAtTime(gainMax, at + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, at + dur);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(at);
        osc.stop(at + dur + 0.02);
      };
      const t0 = ctx.currentTime;
      if (kind === 'ok') {
        beep(t0, 980, 0.09, 0.14);
      } else {
        beep(t0, 900, 0.18, 0.22);
        beep(t0 + 0.2, 760, 0.2, 0.24);
      }
    } catch {}
  }, []);
  const addHierarchyChildrenToParent = useCallback((parentCodeRaw: any, childCodesRaw: any[], source: 'scan' | 'batch' = 'batch') => {
    const parent = String(parentCodeRaw || '').trim();
    const childCodes = Array.from(new Set((Array.isArray(childCodesRaw) ? childCodesRaw : []).map((x) => String(x || '').trim()).filter(Boolean)));
    if (!parent) { hierarchyWarn('请先选择父容器'); return; }
    if (!hierarchyContainerMap.has(parent)) { hierarchyWarn('父容器不存在'); return; }
    if (!childCodes.length) { hierarchyWarn('请先选择/输入子容器'); return; }
    const nextRows: Array<{ id: string; parent_code: string; child_code: string; source: string; time: string }> = [];
    const errs: string[] = [];
    const duplicates: string[] = [];
    const cycles: string[] = [];
    const rebindFromOtherParent: string[] = [];
    const linkList = Array.isArray(hierarchyLinks) ? hierarchyLinks : [];
    childCodes.forEach((code) => {
      if (code === parent) { errs.push(code); return; }
      if (!hierarchyContainerMap.has(code)) { errs.push(code); return; }
      const existingParent = String(hierarchyParentByChild.get(code) || '').trim();
      if (existingParent && existingParent !== parent) {
        rebindFromOtherParent.push(`${code}(${existingParent}→${parent})`);
      }
      if (existingParent && existingParent === parent) { duplicates.push(code); return; }
      if (hierarchyHasPath(code, parent, linkList)) { cycles.push(code); return; }
      const now = Date.now();
      nextRows.push({
        id: `local-hier-${now}-${Math.random()}`,
        parent_code: parent,
        child_code: code,
        source: source === 'scan' ? '扫描添加' : '批量添加',
        time: new Date(now).toISOString(),
      });
    });
    if (nextRows.length) {
      setHierarchyLinks((prev) => {
        const base = Array.isArray(prev) ? prev : [];
        // 扫描/批量添加均允许“改绑父容器”：同一子容器仅保留最新关联
        const changedChildren = new Set(nextRows.map((x) => String(x?.child_code || '').trim()).filter(Boolean));
        const kept = base.filter((x: any) => !changedChildren.has(String(x?.child_code || '').trim()));
        return [...nextRows, ...kept];
      });
      // 父子关联变化时，将受影响父容器标记为“箱标待更新”
      const dirtyParents = new Set<string>();
      dirtyParents.add(parent);
      nextRows.forEach((row: any) => {
        const childCode = String(row?.child_code || '').trim();
        if (!childCode) return;
        const oldParent = String(hierarchyParentByChild.get(childCode) || '').trim();
        if (oldParent && oldParent !== parent) dirtyParents.add(oldParent);
      });
      if (dirtyParents.size > 0) {
        const pool = String(adjustPoolCode || '').trim();
        setLabelDirtyByCode((prev) => {
          const next = { ...(prev || {}) } as Record<string, boolean>;
          dirtyParents.forEach((cc) => {
            const code = String(cc || '').trim();
            if (!code || code === pool) return;
            next[code] = true;
          });
          return next;
        });
      }
      message.success(`已关联 ${nextRows.length} 个子容器`);
    }
    if (rebindFromOtherParent.length) message.success(`已将以下子容器改绑到当前父容器：${rebindFromOtherParent.join(', ')}`);
    if (cycles.length) hierarchyWarn(`以下子容器关联会形成循环，已跳过：${cycles.join(', ')}`);
    if (errs.length) hierarchyWarn(`以下容器编码无效或不存在：${errs.join(', ')}`);
    if (duplicates.length && !nextRows.length) hierarchyWarn('所选子容器均已存在于当前父容器');
  }, [hierarchyContainerMap, hierarchyLinks, hierarchyParentByChild, hierarchyHasPath, hierarchyWarn, adjustPoolCode]);
  const removeHierarchyLinkById = useCallback((idRaw: any) => {
    const id = String(idRaw || '').trim();
    if (!id) return;
    const parentCode = String((Array.isArray(hierarchyLinks) ? hierarchyLinks : []).find((x: any) => String(x?.id || '').trim() === id)?.parent_code || '').trim();
    setHierarchyLinks((prev) => (Array.isArray(prev) ? prev.filter((x: any) => String(x?.id || '').trim() !== id) : prev));
    if (parentCode && parentCode !== String(adjustPoolCode || '').trim()) {
      setLabelDirtyByCode((prev) => ({ ...(prev || {}), [parentCode]: true }));
    }
  }, [hierarchyLinks, adjustPoolCode]);
  const adjustPoolSummary = useMemo(() => {
    try {
      const pool = String(adjustPoolCode || '').trim();
      const byTarget = new Map<string, { target_type: string; target: string; qty: number }>();
      (Array.isArray(localOperationRecords) ? localOperationRecords : []).forEach((r: any) => {
        if (String(r?.container_code || '').trim() !== pool) return;
        const ev = String(r?.event || r?.action || '').trim();
        if (!ev.startsWith('调整-')) return;
        const targetType = String(r?.target_type || '').trim();
        const target = normalizeAdjustTargetCode(r?.target || r?.target_code || r?.code);
        if (!targetType || !target || target === '-') return;
        const qty = Math.floor(Number(r?.qty ?? r?.quantity ?? r?.amount ?? 0) || 0);
        if (!Number.isFinite(qty) || qty === 0) return;
        const key = `${targetType}:${target}`;
        const cur = byTarget.get(key) || { target_type: targetType, target, qty: 0 };
        cur.qty += qty;
        byTarget.set(key, cur);
      });
      const rowsByTarget = Array.from(byTarget.values())
        .filter((x) => Math.floor(Number(x?.qty || 0) || 0) > 0)
        .sort((a, b) => (Number(b?.qty || 0) - Number(a?.qty || 0)) || String(a?.target || '').localeCompare(String(b?.target || '')));
      const totalPos = rowsByTarget.reduce((s, r) => s + Math.floor(Number(r?.qty || 0) || 0), 0);
      return { totalPos, targetKinds: rowsByTarget.length, rowsByTarget };
    } catch {
      return { totalPos: 0, targetKinds: 0, rowsByTarget: [] as any[] };
    }
  }, [adjustPoolCode, localOperationRecords, normalizeAdjustTargetCode]);
  const adjustPoolHeldByTargetKey = useMemo(() => {
    const m = new Map<string, number>();
    try {
      (adjustPoolSummary.rowsByTarget || []).forEach((r: any) => {
        const tp = String(r?.target_type || '').trim();
        const tg = normalizeAdjustTargetCode(r?.target);
        const qty = Math.max(0, Math.floor(Number(r?.qty || 0) || 0));
        if (!tp || !tg || tg === '-' || qty <= 0) return;
        m.set(`${tp}:${tg}`, qty);
      });
    } catch {}
    return m;
  }, [adjustPoolSummary, normalizeAdjustTargetCode]);
  const closedContainers = useMemo(() => {
    try {
      const map = new Map<string, { code: string; container_type?: string; status?: string }>();
      (Array.isArray(boxes) ? boxes : []).forEach((b: any) => {
        const code = String(b?.code || '').trim();
        if (!code) return;
        const st = String(b?.status || '').trim();
        if (st !== 'closed') return;
        map.set(code, { code, container_type: String(b?.container_type || '').trim(), status: st });
      });
      Object.keys(outputDraftByCode || {}).forEach((k) => {
        const code = String(k || '').trim();
        if (!code || map.has(code)) return;
        const d: any = (outputDraftByCode as any)?.[code] || {};
        const st = String(d?.status || '').trim();
        if (st && st !== 'closed' && st !== 'closed_pending_final') return;
        map.set(code, { code, container_type: String(d?.container_type || '').trim(), status: st || 'closed' });
      });
      return Array.from(map.values());
    } catch {
      return [] as Array<{ code: string; container_type?: string; status?: string }>;
    }
  }, [boxes, outputDraftByCode]);
  const closedContainersFiltered = useMemo(() => {
    try {
      const q = String(pickExistingQuery || '').trim().toUpperCase();
      if (!q) return closedContainers;
      return (closedContainers || []).filter((x: any) => String(x?.code || '').trim().toUpperCase().includes(q));
    } catch {
      return closedContainers;
    }
  }, [closedContainers, pickExistingQuery]);
  useEffect(() => {
    try {
      if (!pickExistingOpen) return;
      setPickExistingPage(1);
      const list = Array.isArray(closedContainersFiltered) ? closedContainersFiltered : [];
      if (list.length === 1) {
        const only = String(list?.[0]?.code || '').trim();
        if (only && only !== String(pickExistingCode || '').trim()) setPickExistingCode(only);
        return;
      }
      if (list.length === 0 && String(pickExistingCode || '').trim()) {
        setPickExistingCode('');
      }
    } catch {}
  }, [pickExistingOpen, closedContainersFiltered, pickExistingCode]);
  useEffect(() => {
    if (!hierarchyBatchOpen) return;
    setHierarchyBatchPage(1);
    setHierarchyBatchLinkedPage(1);
    setHierarchyBatchSelectedKeys([]);
  }, [hierarchyBatchOpen, hierarchyBatchQuery, hierarchyBatchLinkedQuery, hierarchyParentCode]);
  const canOperateExec = !!canOperate;
  const instrIdState = Number((instructionDetail as any)?.id || 0) || undefined;
  const stageNoResolved = Number(stageNo || (data as any)?.stage_no || (data as any)?.stage || 0) || 0;
  const draftIdentityId = Number(instrIdState || taskId || 0) || 0;
  const draftIdentityCode = String(instructionCodeFromParent || (instructionDetail as any)?.system_code || 'UNKNOWN').trim().replace(/[^\w-]+/g, '_');
  const draftStepId = useMemo(() => {
    const a = Number((step as any)?.id || 0);
    if (Number.isFinite(a) && a > 0) return a;
    const b = Number((data as any)?.step_id || (data as any)?.stepId || (data as any)?.step?.id || 0);
    if (Number.isFinite(b) && b > 0) return b;
    return undefined;
  }, [step, data]);
  const regularOutboundDraftKey = useMemo(
    () => `REGULAR_OUTBOUND_PACK_${draftIdentityId}_${stageNoResolved}_${draftIdentityCode || 'UNKNOWN'}`,
    [draftIdentityId, stageNoResolved, draftIdentityCode],
  );
  const instrWarehouseId = Number((instructionDetail as any)?.warehouse || (instructionDetail as any)?.warehouse_id || (instructionDetail as any)?.warehouse_detail?.id || 0) || undefined;
  const instrCustomerId = Number((instructionDetail as any)?.customer || (instructionDetail as any)?.customer_id || (instructionDetail as any)?.customer_detail?.id || 0) || undefined;
  const instrQuestionRows = useMemo(() => (
    (Array.isArray((instructionDetail as any)?.question_rows) && (instructionDetail as any).question_rows)
    || (Array.isArray((instructionDetail as any)?.questions_detail) && (instructionDetail as any).questions_detail)
    || (Array.isArray((instructionDetail as any)?.customer_questions) && (instructionDetail as any).customer_questions)
    || []
  ), [instructionDetail]);
  const instrAnswers = ((instructionDetail as any)?.customer_answers || {}) as any;
  const applyRegularOutboundDraftState = useCallback((d: any) => {
    try {
      if (!d || typeof d !== 'object') return;
      // 命中草稿时，阻止后续“仅后端种子”覆盖。
      seededLedgerRef.current = true;
      if (Array.isArray(d?.local_operation_records)) {
        setLocalOperationRecords(d.local_operation_records || []);
      }
      if (Array.isArray(d?.boxes)) {
        setBoxes(d.boxes || []);
      }
      if (d?.box_meta && typeof d.box_meta === 'object') {
        setBoxMeta(d.box_meta || {});
      }
      if (d?.output_draft_by_code && typeof d.output_draft_by_code === 'object') {
        setOutputDraftByCode(d.output_draft_by_code || {});
      }
      if (typeof d?.current_pack_box_code === 'string') {
        setCurrentPackBoxCode(String(d.current_pack_box_code || ''));
      }
      if (typeof d?.add_to_existing_enabled === 'boolean') {
        setAddToExistingEnabled(!!d.add_to_existing_enabled);
      }
      if (typeof d?.add_to_existing_code === 'string') {
        setAddToExistingCode(String(d.add_to_existing_code || ''));
      }
      if (Array.isArray(d?.hierarchy_links)) {
        setHierarchyLinks(d.hierarchy_links || []);
      }
      if (d?.label_dirty_by_code && typeof d.label_dirty_by_code === 'object') {
        setLabelDirtyByCode(d.label_dirty_by_code || {});
      }
      if (Array.isArray(d?.ready_baseline_done_row_keys)) {
        const nums = (d.ready_baseline_done_row_keys || [])
          .map((x: any) => Number(x))
          .filter((n: number) => Number.isFinite(n) && n > 0);
        setReadyBaselineDoneRowKeys(new Set(nums));
      }
      if (typeof d?.ready_baseline_only_unmatched === 'boolean') {
        setReadyBaselineOnlyUnmatched(!!d.ready_baseline_only_unmatched);
      }
      const draftProcessStatus = String(d?.process_result_status || '').trim().toLowerCase();
      const draftProcessReason = String(d?.process_unable_reason || '').trim();
      if (draftProcessStatus) {
        setLocalProcessResultStatus(draftProcessStatus);
        try { onChange?.('process_result_status', draftProcessStatus); } catch {}
      }
      if (draftProcessReason) {
        setLocalProcessUnableReason(draftProcessReason);
        try { onChange?.('process_unable_reason', draftProcessReason); } catch {}
      } else if (draftProcessStatus && draftProcessStatus !== 'unable') {
        setLocalProcessUnableReason('');
        try { onChange?.('process_unable_reason', ''); } catch {}
      }
    } catch {}
  }, [onChange]);
  useEffect(() => {
    draftHydratedRef.current = false;
    let cancelled = false;
    (async () => {
      try {
        if (!regularOutboundDraftKey) {
          draftHydratedRef.current = true;
          return;
        }
        let localDraft: any = null;
        try {
          const raw = localStorage.getItem(regularOutboundDraftKey);
          if (raw) localDraft = JSON.parse(raw || '{}');
        } catch { localDraft = null; }
        let serverDraftState: any = null;
        let serverUpdatedAt = 0;
        try {
          if (draftStepId) {
            const resp = await request.get(`/api/v1/instruction/exec/steps/${draftStepId}/draft/`, { params: { t: Date.now() } as any });
            const d = resp?.data?.draft;
            if (d && String(d?.kind || '').trim() === 'regular_outbound_pack' && d?.state && typeof d.state === 'object') {
              serverDraftState = d.state || {};
              const t = Number(new Date(d?.updated_at || 0).getTime());
              serverUpdatedAt = Number.isFinite(t) ? t : 0;
            }
          }
        } catch {}
        const localUpdatedAt = Number(localDraft?.updated_at || 0) || 0;
        const useServer = !!serverDraftState && (!localDraft || serverUpdatedAt >= localUpdatedAt);
        const picked = useServer ? serverDraftState : localDraft;
        if (picked && !cancelled) {
          applyRegularOutboundDraftState(picked);
          if (useServer) {
            try {
              localStorage.setItem(regularOutboundDraftKey, JSON.stringify({
                version: 1,
                updated_at: Date.now(),
                ...(picked || {}),
              }));
            } catch {}
          }
        }
      } catch {}
      if (!cancelled) draftHydratedRef.current = true;
    })();
    return () => { cancelled = true; };
  }, [regularOutboundDraftKey, draftStepId, applyRegularOutboundDraftState]);
  // 草稿不存在/已清空时，尝试从已提交 metrics/data 恢复关键展示数据（用于提交后本页回看与跨设备回看）
  useEffect(() => {
    if (!draftHydratedRef.current) return;
    const srcMetrics: any = (metrics && typeof metrics === 'object') ? metrics : {};
    const srcData: any = (data && typeof data === 'object') ? data : {};
    if ((Array.isArray(localOperationRecords) ? localOperationRecords : []).length <= 0) {
      const recs = (Array.isArray(srcMetrics?.operation_records) ? srcMetrics.operation_records : (
        Array.isArray(srcData?.operation_records) ? srcData.operation_records : (
          Array.isArray(srcData?.records) ? srcData.records : []
        )
      ));
      if (Array.isArray(recs) && recs.length > 0) {
        setLocalOperationRecords(recs);
      }
    }
    if ((Array.isArray(boxes) ? boxes : []).length <= 0) {
      const bs = (Array.isArray(srcMetrics?.boxes) ? srcMetrics.boxes : (Array.isArray(srcData?.boxes) ? srcData.boxes : []));
      if (Array.isArray(bs) && bs.length > 0) {
        setBoxes(bs);
      }
    }
    if ((Array.isArray(hierarchyLinks) ? hierarchyLinks : []).length <= 0) {
      const links = (Array.isArray(srcMetrics?.hierarchy_links) ? srcMetrics.hierarchy_links : (Array.isArray(srcData?.hierarchy_links) ? srcData.hierarchy_links : []));
      if (Array.isArray(links) && links.length > 0) {
        setHierarchyLinks(links);
      }
    }
    if (Object.keys(boxMeta || {}).length <= 0) {
      const bm = ((srcMetrics?.box_meta && typeof srcMetrics.box_meta === 'object') ? srcMetrics.box_meta : ((srcData?.box_meta && typeof srcData.box_meta === 'object') ? srcData.box_meta : null));
      if (bm && typeof bm === 'object' && Object.keys(bm).length > 0) {
        setBoxMeta(bm);
      }
    }
    if (Object.keys(outputDraftByCode || {}).length <= 0) {
      const od = ((srcMetrics?.output_draft_by_code && typeof srcMetrics.output_draft_by_code === 'object') ? srcMetrics.output_draft_by_code : ((srcData?.output_draft_by_code && typeof srcData.output_draft_by_code === 'object') ? srcData.output_draft_by_code : null));
      if (od && typeof od === 'object' && Object.keys(od).length > 0) {
        setOutputDraftByCode(od);
      }
    }
  }, [
    metrics,
    data,
    localOperationRecords,
    boxes,
    hierarchyLinks,
    boxMeta,
    outputDraftByCode,
  ]);
  useEffect(() => {
    if (!draftHydratedRef.current) return;
    if (!regularOutboundDraftKey) return;
    if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
    draftSaveTimerRef.current = setTimeout(() => {
      try {
        const payload = {
          version: 1,
          updated_at: Date.now(),
          local_operation_records: Array.isArray(localOperationRecords) ? localOperationRecords.slice(0, 4000) : [],
          boxes: Array.isArray(boxes) ? boxes : [],
          box_meta: boxMeta || {},
          output_draft_by_code: outputDraftByCode || {},
          current_pack_box_code: String(currentPackBoxCode || ''),
          add_to_existing_enabled: !!addToExistingEnabled,
          add_to_existing_code: String(addToExistingCode || ''),
          hierarchy_links: Array.isArray(hierarchyLinks) ? hierarchyLinks : [],
          label_dirty_by_code: labelDirtyByCode || {},
          ready_baseline_done_row_keys: Array.from(readyBaselineDoneRowKeys || []),
          ready_baseline_only_unmatched: !!readyBaselineOnlyUnmatched,
          process_result_status: String(localProcessResultStatus || '').trim(),
          process_unable_reason: String(localProcessUnableReason || '').trim(),
        };
        localStorage.setItem(regularOutboundDraftKey, JSON.stringify(payload));
      } catch {}
    }, 800);
    return () => {
      try { if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current); } catch {}
    };
  }, [
    regularOutboundDraftKey,
    localOperationRecords,
    boxes,
    boxMeta,
    outputDraftByCode,
    currentPackBoxCode,
    addToExistingEnabled,
    addToExistingCode,
    hierarchyLinks,
    labelDirtyByCode,
    readyBaselineDoneRowKeys,
    readyBaselineOnlyUnmatched,
    localProcessResultStatus,
    localProcessUnableReason,
  ]);
  useEffect(() => {
    if (!draftHydratedRef.current) return;
    if (!draftStepId) return;
    if (!canOperateExec) return;
    if (draftSaveToServerTimerRef.current) clearTimeout(draftSaveToServerTimerRef.current);
    draftSaveToServerTimerRef.current = setTimeout(() => {
      (async () => {
        try {
          const state = {
            local_operation_records: Array.isArray(localOperationRecords) ? localOperationRecords.slice(0, 4000) : [],
            boxes: Array.isArray(boxes) ? boxes : [],
            box_meta: boxMeta || {},
            output_draft_by_code: outputDraftByCode || {},
            current_pack_box_code: String(currentPackBoxCode || ''),
            add_to_existing_enabled: !!addToExistingEnabled,
            add_to_existing_code: String(addToExistingCode || ''),
            hierarchy_links: Array.isArray(hierarchyLinks) ? hierarchyLinks : [],
            label_dirty_by_code: labelDirtyByCode || {},
            ready_baseline_done_row_keys: Array.from(readyBaselineDoneRowKeys || []),
            ready_baseline_only_unmatched: !!readyBaselineOnlyUnmatched,
            process_result_status: String(localProcessResultStatus || '').trim(),
            process_unable_reason: String(localProcessUnableReason || '').trim(),
          };
          const draft = { kind: 'regular_outbound_pack', version: 1, updated_at: Date.now(), state };
          const sig = (() => { try { return JSON.stringify(draft); } catch { return String(Date.now()); } })();
          if (sig === lastServerDraftSigRef.current) return;
          lastServerDraftSigRef.current = sig;
          await request.post(`/api/v1/instruction/exec/steps/${draftStepId}/draft/`, { draft });
        } catch {}
      })();
    }, 1000);
    return () => {
      try { if (draftSaveToServerTimerRef.current) clearTimeout(draftSaveToServerTimerRef.current); } catch {}
    };
  }, [
    draftStepId,
    canOperateExec,
    localOperationRecords,
    boxes,
    boxMeta,
    outputDraftByCode,
    currentPackBoxCode,
    addToExistingEnabled,
    addToExistingCode,
    hierarchyLinks,
    labelDirtyByCode,
    readyBaselineDoneRowKeys,
    readyBaselineOnlyUnmatched,
    localProcessResultStatus,
    localProcessUnableReason,
  ]);
  const waybillOpsRef = useRef<HTMLDivElement | null>(null);
  const shippingLabelMappingBaselineCodes = useMemo(() => new Set([
    'SHIPPING_LABEL_TARGET_MAPPING',
    'SHIPPING_LABEL_TO_TARGET_MAPPING',
    'WAYBILL_TARGET_MAPPING',
    'WAYBILL_TO_TARGET_MAPPING',
  ]), []);
  const loadInstructionBaselines = useCallback(async () => {
    if (!instrIdState) { setBaselineRows([]); return; }
    try {
      setBaselineLoading(true);
      const resp = await request.get('/api/v1/instruction/baselines/', { params: { instruction: instrIdState, page_size: 1000 } });
      const rows = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
      setBaselineRows(Array.isArray(rows) ? rows : []);
    } catch {
      setBaselineRows([]);
    } finally {
      setBaselineLoading(false);
    }
  }, [instrIdState]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!instrIdState) {
        if (!cancelled) setBaselineRows([]);
        return;
      }
      try {
        setBaselineLoading(true);
        const resp = await request.get('/api/v1/instruction/baselines/', { params: { instruction: instrIdState, page_size: 1000 } });
        const rows = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
        if (!cancelled) setBaselineRows(Array.isArray(rows) ? rows : []);
      } catch {
        if (!cancelled) setBaselineRows([]);
      } finally {
        if (!cancelled) setBaselineLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [instrIdState]);
  const baselineSummary = useMemo(() => {
    const rows = Array.isArray(baselineRows) ? baselineRows : [];
    const total = rows.length;
    const ready = rows.filter((r: any) => String(r?.state || '').toLowerCase() === 'ready').length;
    const files = rows.reduce((acc: number, r: any) => {
      const list = Array.isArray(r?.files) ? r.files : [];
      const cnt = list.length > 0 ? list.length : Number(r?.files_count || 0);
      return acc + (Number.isFinite(cnt) ? cnt : 0);
    }, 0);
    const mappingReady = rows.some((r: any) => {
      const code = String(r?.code || '').trim().toUpperCase();
      return shippingLabelMappingBaselineCodes.has(code) && String(r?.state || '').toLowerCase() === 'ready';
    });
    return { total, ready, files, mappingReady };
  }, [baselineRows, shippingLabelMappingBaselineCodes]);
  const baselinePreviewCandidates = useMemo(() => {
    const rows = Array.isArray(baselineRows) ? baselineRows : [];
    return rows.flatMap((b: any) => {
      const files = Array.isArray(b?.files) ? b.files : [];
      return files.map((f: any, idx: number) => ({
        file: f,
        fileName: String(f?.file_name || f?.name || `基线文件${idx + 1}`),
      }));
    });
  }, [baselineRows]);
  const readyBaselinePreviewCandidates = useMemo(() => {
    const rows = Array.isArray(baselineRows) ? baselineRows : [];
    return rows.flatMap((b: any) => {
      const state = String(b?.state || '').toLowerCase();
      if (state !== 'ready') return [];
      const files = Array.isArray(b?.files) ? b.files : [];
      return files.map((f: any, idx: number) => ({
        baselineId: Number(b?.id || 0),
        fileId: Number(f?.id || 0),
        file: f,
        fileName: String(f?.file_name || f?.name || `基线文件${idx + 1}`),
      }));
    });
  }, [baselineRows]);
  const firstReadyBaselinePreviewCandidate = readyBaselinePreviewCandidates[0] || null;
  const replaceReadyBaselineFile = async (item: any, nextFile: File) => {
    const baselineId = Number(item?.baselineId || 0);
    const fileId = Number(item?.fileId || 0);
    if (!baselineId || !fileId) {
      message.warning('缺少基线文件信息，无法替换');
      return;
    }
    if (!nextFile) return;
    const ok = await new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: '确认上传并替换?',
        content: '将删除当前已就绪基线文件，并替换为新上传文件。',
        okText: '确认替换',
        cancelText: '取消',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!ok) return;
    try {
      await request.delete(`/api/v1/instruction/baseline-files/${fileId}/`);
      const fd = new FormData();
      fd.append('baseline', String(baselineId));
      fd.append('file', nextFile);
      await request.post('/api/v1/instruction/baseline-files/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      message.success('已上传并替换基线文件');
      await loadInstructionBaselines();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || '替换失败');
    }
  };
  const hasBaselineFiles = baselineSummary.files > 0;
  const baselineFilesTabLabel = (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        color: hasBaselineFiles ? undefined : '#bfbfbf',
      }}
    >
      <span>基线文件(贴单约束)</span>
      {hasBaselineFiles ? <WarningOutlined style={{ color: '#faad14' }} /> : null}
    </span>
  );
  const readyBaselineInlineDisplayData = useMemo(() => {
    const rows = Array.isArray(readyBaselineInlineData) ? readyBaselineInlineData : [];
    if (rows.length <= 1) return rows;
    const done = readyBaselineDoneRowKeys;
    return rows
      .map((r: any, idx: number) => ({ r, idx }))
      .sort((a: any, b: any) => {
        const ak = Number(a?.r?.__k);
        const bk = Number(b?.r?.__k);
        const ad = Number.isFinite(ak) ? done.has(ak) : false;
        const bd = Number.isFinite(bk) ? done.has(bk) : false;
        if (ad !== bd) return ad ? 1 : -1; // 未匹配在前，已匹配在后
        return a.idx - b.idx;
      })
      .map((x: any) => x.r);
  }, [readyBaselineInlineData, readyBaselineDoneRowKeys]);
  const readyBaselineInlineVisibleData = useMemo(() => {
    const rows = Array.isArray(readyBaselineInlineDisplayData) ? readyBaselineInlineDisplayData : [];
    if (!readyBaselineOnlyUnmatched) return rows;
    return rows.filter((r: any) => {
      const rk = Number(r?.__k);
      if (!Number.isFinite(rk)) return true;
      return !readyBaselineDoneRowKeys.has(rk);
    });
  }, [readyBaselineInlineDisplayData, readyBaselineOnlyUnmatched, readyBaselineDoneRowKeys]);
  const renderReadyBaselineInlinePreview = () => {
    if (baselineLoading) {
      return <div style={{ color: '#8c8c8c' }}>加载中...</div>;
    }
    if (!firstReadyBaselinePreviewCandidate) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: '#389e0d', whiteSpace: 'nowrap' }}>已就绪基线文件:</div>
            <span style={{ color: '#8c8c8c' }}>-</span>
          </div>
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: '#389e0d', whiteSpace: 'nowrap' }}>已就绪基线文件:</div>
            <span style={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{firstReadyBaselinePreviewCandidate.fileName}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 10, color: '#595959' }}>
              <Switch
                size="small"
                checked={readyBaselineOnlyUnmatched}
                onChange={(v) => {
                  setReadyBaselineOnlyUnmatched(v);
                  setReadyBaselineInlinePage(1);
                }}
              />
              <span>仅显示未匹配记录</span>
            </span>
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Button size="small" type="link" onClick={() => { void openAttachmentPreview(firstReadyBaselinePreviewCandidate.file, firstReadyBaselinePreviewCandidate.fileName, '基线文件'); }} style={{ padding: 0 }}>预览</Button>
            <Button size="small" type="link" onClick={() => { void downloadBaselineFile(firstReadyBaselinePreviewCandidate.file); }} style={{ padding: 0 }}>下载</Button>
            <Upload
              showUploadList={false}
              maxCount={1}
              disabled={!canOperateExec}
              beforeUpload={(file) => {
                if (!canOperateExec) return false;
                void replaceReadyBaselineFile(firstReadyBaselinePreviewCandidate, file as File);
                return false;
              }}
            >
              <Button size="small" type="link" disabled={!canOperateExec} style={{ padding: 0 }}>上传并替换</Button>
            </Upload>
          </div>
        </div>
        {readyBaselineInlineLoading ? (
          <div style={{ color: '#8c8c8c' }}>表格加载中...</div>
        ) : readyBaselineInlineError ? (
          <div style={{ color: '#cf1322' }}>{readyBaselineInlineError}</div>
        ) : (
          <Table
            size="small"
            rowKey="__k"
            dataSource={readyBaselineInlineVisibleData}
            columns={readyBaselineInlineCols as any}
            onRow={(record: any) => {
              const rk = Number(record?.__k);
              if (!Number.isFinite(rk)) return {};
              return readyBaselineDoneRowKeys.has(rk)
                ? { style: { background: '#b7eb8f' } }
                : {};
            }}
            pagination={{
              current: readyBaselineInlinePage,
              pageSize: readyBaselineInlinePageSize,
              total: readyBaselineInlineVisibleData.length,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total, range) => `共${total}条，显示${range[0]}-${range[1]}条`,
              onChange: (page, pageSize) => {
                setReadyBaselineInlinePage(page);
                if (pageSize !== readyBaselineInlinePageSize) {
                  setReadyBaselineInlinePageSize(pageSize);
                }
              },
            }}
            scroll={{ x: 'max-content', y: 420 }}
            tableLayout="auto"
            locale={{ emptyText: '当前基线文件无可展示数据' }}
          />
        )}
      </div>
    );
  };
  type MainSectionKey = 'overview' | 'pack_ops' | 'waybill_ops' | 'progress' | 'records';
  const [collapsedSections, setCollapsedSections] = useState<Record<MainSectionKey, boolean>>({
    overview: false,
    pack_ops: false,
    waybill_ops: false,
    progress: false,
    records: false,
  });
  const toggleSection = useCallback((key: MainSectionKey) => {
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);
  const renderSectionTitle = useCallback((label: React.ReactNode, key: MainSectionKey) => (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
      <span>{label}</span>
      <Button
        type="link"
        size="small"
        onClick={() => toggleSection(key)}
        style={{ padding: 0, height: 'auto', minWidth: 16 }}
        icon={collapsedSections[key] ? <RightOutlined /> : <DownOutlined />}
      />
    </div>
  ), [collapsedSections, toggleSection]);
  const renderSectionToggleIcon = useCallback((key: MainSectionKey) => (
    <Button
      type="link"
      size="small"
      onClick={() => toggleSection(key)}
      style={{ padding: 0, height: 'auto', minWidth: 16 }}
      icon={collapsedSections[key] ? <RightOutlined /> : <DownOutlined />}
    />
  ), [collapsedSections, toggleSection]);
  const currentPackTheory = useMemo(() => {
    try {
      const cc = String(activePackBoxCode || '').trim();
      if (!cc) return { ok: false as const };
      const meta: any = (boxMeta as any)?.[cc] || {};
      const tareG = Number(meta?.weight_g || 0);
      if (!Number.isFinite(tareG) || tareG <= 0) return { ok: false as const };
      const list = (Array.isArray(localOperationRecords) ? localOperationRecords : []).filter((r: any) =>
        String(r?.container_code || '').trim() === cc,
      );
      let contentG = 0;
      for (const r of list) {
        const ev = String(r?.event || r?.action || '').trim();
        const isOut = /取出|冲销|移除|移出/.test(ev);
        const isIn = /放入|加入|加回/.test(ev);
        if (!isIn && !isOut) continue;
        const tcode = String(r?.target || r?.target_code || r?.code || '').trim();
        if (!tcode) continue;
        const ttype = String(r?.target_type || '').trim();
        const weightKey = (ttype === 'instruction_output_container' ? `ioc:${tcode.toUpperCase()}` : `iu:${tcode.toUpperCase()}`);
        const directW = Number(r?.target_weight_g);
        const cacheW = (targetWeightGByKey as any)?.[weightKey];
        const w = Number.isFinite(directW) && directW > 0 ? directW : Number(cacheW);
        if (!Number.isFinite(w) || w <= 0) return { ok: false as const };
        const qtyRaw = Math.floor(Number(r?.qty || 0) || 0);
        const qty = ttype === 'instruction_output_container' ? 1 : qtyRaw;
        if (!Number.isFinite(qty) || qty <= 0) continue;
        const sign = isOut ? -1 : 1;
        contentG += w * qty * sign;
      }
      const totalKg = Number((Math.max(0, tareG + contentG) / 1000).toFixed(2));
      return { ok: true as const, totalKg };
    } catch {
      return { ok: false as const };
    }
  }, [activePackBoxCode, boxMeta, localOperationRecords, targetWeightGByKey]);
  const calcContainerTheory = useCallback((containerCodeRaw: any) => {
    try {
      const cc = String(containerCodeRaw || '').trim();
      if (!cc) return { ok: false as const };
      const tareRaw = Number(((boxMeta as any)?.[cc] || {})?.weight_g || 0);
      if (!Number.isFinite(tareRaw) || tareRaw <= 0) return { ok: false as const };
      const list = (Array.isArray(localOperationRecords) ? localOperationRecords : []).filter((r: any) =>
        String(r?.container_code || '').trim() === cc,
      );
      let contentG = 0;
      for (const r of list) {
        const ev = String(r?.event || r?.action || '').trim();
        const isOut = /取出|冲销|移除|移出/.test(ev);
        const isIn = /放入|加入|加回/.test(ev);
        if (!isIn && !isOut) continue;
        const tcode = String(r?.target || r?.target_code || r?.code || '').trim();
        if (!tcode) continue;
        const ttype = String(r?.target_type || '').trim();
        const weightKey = (ttype === 'instruction_output_container' ? `ioc:${tcode.toUpperCase()}` : `iu:${tcode.toUpperCase()}`);
        const directW = Number(r?.target_weight_g);
        const cacheW = (targetWeightGByKey as any)?.[weightKey];
        const w = Number.isFinite(directW) && directW > 0 ? directW : Number(cacheW);
        if (!Number.isFinite(w) || w <= 0) return { ok: false as const };
        const qtyRaw = Math.floor(Number(r?.qty || 0) || 0);
        const qty = ttype === 'instruction_output_container' ? 1 : qtyRaw;
        if (!Number.isFinite(qty) || qty <= 0) continue;
        const sign = isOut ? -1 : 1;
        contentG += w * qty * sign;
      }
      const totalKg = Number((Math.max(0, tareRaw + contentG) / 1000).toFixed(2));
      return { ok: true as const, totalKg };
    } catch {
      return { ok: false as const };
    }
  }, [boxMeta, localOperationRecords, targetWeightGByKey]);
  const resolveLinkedChildrenWeightKg = useCallback((parentCodeRaw: any): number => {
    try {
      const p = String(parentCodeRaw || '').trim();
      if (!p) return 0;
      const rows = hierarchyRowsByParent.get(p) || [];
      let sumG = 0;
      rows.forEach((r: any) => {
        const childCode = String(r?.child_code || '').trim();
        if (!childCode) return;
        const childMeta: any = (boxMeta as any)?.[childCode] || {};
        const wg = Number(childMeta?.weight_g);
        if (Number.isFinite(wg) && wg > 0) sumG += wg;
      });
      if (!Number.isFinite(sumG) || sumG <= 0) return 0;
      return Number((sumG / 1000).toFixed(2));
    } catch {
      return 0;
    }
  }, [hierarchyRowsByParent, boxMeta]);
  const endContainerHasLinkedChildren = useMemo(() => {
    const cc = String(endContainerDraft?.code || '').trim();
    if (!cc) return false;
    return (hierarchyRowsByParent.get(cc) || []).length > 0;
  }, [endContainerDraft?.code, hierarchyRowsByParent]);
  const endContainerTheory = useMemo(() => {
    const cc = String(endContainerDraft?.code || '').trim();
    if (!cc) return { ok: false as const };
    const base = calcContainerTheory(cc);
    if (!base.ok) return base;
    if (!endContainerHasLinkedChildren) return base;
    const linkedKg = resolveLinkedChildrenWeightKg(cc);
    return { ok: true as const, totalKg: Number((Number(base.totalKg || 0) + linkedKg).toFixed(2)) };
  }, [endContainerDraft?.code, calcContainerTheory, endContainerHasLinkedChildren, resolveLinkedChildrenWeightKg]);
  const boxFinalHasLinkedChildren = useMemo(() => {
    const cc = String(boxFinalCode || '').trim();
    if (!cc) return false;
    return (hierarchyRowsByParent.get(cc) || []).length > 0;
  }, [boxFinalCode, hierarchyRowsByParent]);
  const boxFinalTheory = useMemo(() => {
    const cc = String(boxFinalCode || '').trim();
    if (!cc) return { ok: false as const };
    const base = calcContainerTheory(cc);
    if (!base.ok) return base;
    if (!boxFinalHasLinkedChildren) return base;
    const linkedKg = resolveLinkedChildrenWeightKg(cc);
    return { ok: true as const, totalKg: Number((Number(base.totalKg || 0) + linkedKg).toFixed(2)) };
  }, [boxFinalCode, calcContainerTheory, boxFinalHasLinkedChildren, resolveLinkedChildrenWeightKg]);
  const targetPickerRowKey = useCallback((r: any, i?: number) => String(r?.key || r?.__key || r?.output_container_id || r?.output_code || r?.unit_code || i), []);
  const shippingPkgGroupedOptions = useMemo(() => {
    const rows = Array.isArray(shippingPkgOptions) ? shippingPkgOptions : [];
    const toLabel = (it: ShippingPackaging) => {
      const code = String(it?.system_code || '').trim();
      const name = String(it?.name || '').trim() || '-';
      const own = String(it?.ownership_type || '').trim();
      const ownText = own === 'warehouse' ? '仓库可用' : own === 'customer' ? '客户专属' : own || '-';
      return `${name}（${code || '-'}） - ${ownText}`;
    };
    const wh = rows.filter((x) => String(x?.ownership_type || '') === 'warehouse').map((it) => ({ label: toLabel(it), value: String(it?.system_code || '').trim() }));
    const cu = rows.filter((x) => String(x?.ownership_type || '') === 'customer').map((it) => ({ label: toLabel(it), value: String(it?.system_code || '').trim() }));
    const merged: any[] = [];
    if (wh.length > 0) merged.push({ label: '仓库可用', options: wh });
    if (cu.length > 0) merged.push({ label: '客户专属', options: cu });
    return merged.length > 0 ? merged : rows.map((it) => ({ label: toLabel(it), value: String(it?.system_code || '').trim() }));
  }, [shippingPkgOptions]);
  const toPosInt = (v: any): number | undefined => {
    const n = Number(v);
    if (!Number.isFinite(n)) return undefined;
    const i = Math.floor(n);
    if (i <= 0) return undefined;
    return i;
  };
  const mapShippingPackagingToContainerType = (packagingType?: string): 'box' | 'pallet' | 'other' => {
    const t = String(packagingType || '').toLowerCase();
    if (t === 'carton') return 'box';
    if (t === 'pallet') return 'pallet';
    return 'other';
  };
  const resetContainerInfoFields = useCallback(() => {
    try {
      startForm.resetFields(['container_type', 'length_cm', 'width_cm', 'height_cm', 'weight_g', 'condition']);
    } catch {}
  }, [startForm]);
  const fillFromShippingPkg = useCallback((hit: ShippingPackaging) => {
    try {
      setShippingPkgHit(hit);
      setShippingPkgErr('');
      try { setShippingPkgQuery(String(hit?.system_code || '').trim()); } catch {}
      // 先清空容器信息，避免沿用上一次手填/回填的残留值
      resetContainerInfoFields();
      const ct = mapShippingPackagingToContainerType((hit as any)?.packaging_type);
      const l = toPosInt((hit as any)?.length_cm);
      const w = toPosInt((hit as any)?.width_cm);
      const h = toPosInt((hit as any)?.height_cm);
      const wg = toPosInt((hit as any)?.weight_g);
      const cond = String((hit as any)?.condition || '').toLowerCase();
      startForm.setFieldsValue({
        container_type: ct,
        length_cm: (ct === 'pallet' ? 122 : l),
        width_cm: (ct === 'pallet' ? 102 : w),
        height_cm: (ct === 'pallet' ? undefined : h),
        weight_g: wg,
        condition: (cond === 'new' ? 'new' : 'used'),
      });
    } catch {}
  }, [startForm, resetContainerInfoFields]);
  const searchShippingPkg = useCallback(async (queryRaw: string) => {
    const qRaw = String(queryRaw || '');
    const q = qRaw.trim();
    if (!q) {
      setShippingPkgOptions([]);
      setShippingPkgErr('');
      setShippingPkgHit(null);
      return;
    }
    if (q.length < 2) {
      setShippingPkgOptions([]);
      setShippingPkgErr('');
      setShippingPkgHit(null);
      return;
    }
    const norm = q.toLowerCase();
    if (lastShipQueryRef.current === norm) return;
    lastShipQueryRef.current = norm;
    setShippingPkgLoading(true);
    setShippingPkgErr('');
    try {
      const reqs: any[] = [];
      reqs.push(getShippingPackagings({
        search: q,
        page_size: 50,
        ownership_type: 'warehouse',
        warehouse: (instrWarehouseId != null ? instrWarehouseId : undefined) as any,
      } as any));
      if (instrCustomerId != null) {
        reqs.push(getShippingPackagings({
          search: q,
          page_size: 50,
          ownership_type: 'customer',
          customer: instrCustomerId as any,
        } as any));
      }
      const resps = await Promise.all(reqs);
      const merged: any[] = [];
      for (const r of resps) {
        const part = Array.isArray((r as any)?.data?.results)
          ? (r as any).data.results
          : (Array.isArray((r as any)?.data) ? (r as any).data : []);
        merged.push(...(part || []));
      }
      const filtered = (merged || []).filter((x: any) => {
        const name = String(x?.name || '').toLowerCase();
        const code = String(x?.system_code || '').toLowerCase();
        return (name && name.includes(norm)) || (code && code.includes(norm));
      });
      const uniqMap = new Map<string, ShippingPackaging>();
      (filtered || []).forEach((it: any) => {
        const code = String(it?.system_code || '').trim();
        if (!code) return;
        if (!uniqMap.has(code)) uniqMap.set(code, it);
      });
      const finalList = Array.from(uniqMap.values()).slice(0, 50);
      setShippingPkgOptions(finalList);
      const looksLikeCode = /^sp-[a-z0-9]{1,6}-\d+$/i.test(q);
      if (looksLikeCode) {
        const hit = finalList.find((it) => String(it?.system_code || '').trim().toUpperCase() === q.toUpperCase());
        if (hit) fillFromShippingPkg(hit);
      }
    } catch (e: any) {
      setShippingPkgOptions([]);
      setShippingPkgHit(null);
      setShippingPkgErr(e?.response?.data?.detail || e?.message || '搜索失败');
    } finally {
      setShippingPkgLoading(false);
    }
  }, [fillFromShippingPkg, instrCustomerId, instrWarehouseId]);
  const loadDefaultShippingPkgOptions = useCallback(async () => {
    try {
      if (!startContainerOpen) return;
      if (shippingPkgLoading) return;
      if ((shippingPkgOptions || []).length > 0) return;
      setShippingPkgLoading(true);
      setShippingPkgErr('');
      const reqs: any[] = [];
      reqs.push(getShippingPackagings({
        page_size: 80,
        ownership_type: 'warehouse',
        warehouse: (instrWarehouseId != null ? instrWarehouseId : undefined) as any,
      } as any));
      if (instrCustomerId != null) {
        reqs.push(getShippingPackagings({
          page_size: 80,
          ownership_type: 'customer',
          customer: instrCustomerId as any,
        } as any));
      }
      const resps = await Promise.all(reqs);
      const merged: any[] = [];
      for (const r of resps) {
        const part = Array.isArray((r as any)?.data?.results)
          ? (r as any).data.results
          : (Array.isArray((r as any)?.data) ? (r as any).data : []);
        merged.push(...(part || []));
      }
      const uniqMap = new Map<string, ShippingPackaging>();
      (merged || []).forEach((it: any) => {
        const code = String(it?.system_code || '').trim();
        if (!code) return;
        if (!uniqMap.has(code)) uniqMap.set(code, it);
      });
      setShippingPkgOptions(Array.from(uniqMap.values()).slice(0, 120));
    } catch (e: any) {
      setShippingPkgOptions([]);
      setShippingPkgErr(e?.response?.data?.detail || e?.message || '加载发货包材失败');
    } finally {
      setShippingPkgLoading(false);
    }
  }, [startContainerOpen, shippingPkgLoading, shippingPkgOptions, instrWarehouseId, instrCustomerId]);
  useEffect(() => {
    if (!startContainerOpen) return;
    if (shipQueryTimerRef.current) clearTimeout(shipQueryTimerRef.current);
    shipQueryTimerRef.current = setTimeout(() => { void searchShippingPkg(shippingPkgQuery); }, 260);
    return () => { if (shipQueryTimerRef.current) clearTimeout(shipQueryTimerRef.current); };
  }, [shippingPkgQuery, startContainerOpen, searchShippingPkg]);
  useEffect(() => {
    if (!startContainerOpen) return;
    try {
      startForm.setFieldsValue({
        container_type: 'box',
        condition: 'used',
      });
    } catch {}
  }, [startContainerOpen, startForm]);
  const openStartNewContainerModal = () => {
    if (!canOperateExec) return;
    setStartContainerOpen(true);
    setShippingPkgQuery('');
    setShippingPkgErr('');
    setShippingPkgHit(null);
    setShippingPkgOptions([]);
    setShippingPkgLoading(false);
    lastShipQueryRef.current = '';
    try { startForm.resetFields(); } catch {}
  };
  const openPickExistingContainerModal = useCallback(() => {
    if (!canOperateExec) return;
    if (String(currentPackBoxCode || '').trim()) {
      message.warning('请先结束当前进行中的容器');
      return;
    }
    if ((Array.isArray(closedContainers) ? closedContainers.length : 0) <= 0) {
      message.warning('暂无可选的已结束容器');
      return;
    }
    setPickExistingCode('');
    setPickExistingQuery('');
    setPickExistingPage(1);
    setPickExistingPageSize(10);
    setPickExistingOpen(true);
  }, [canOperateExec, currentPackBoxCode, closedContainers]);
  const startAddToExistingContainer = useCallback(() => {
    const cc = String(pickExistingCode || '').trim();
    if (!cc) {
      message.warning('请先选择一个已结束容器');
      return;
    }
    setAddToExistingEnabled(true);
    setAddToExistingCode(cc);
    setPickExistingOpen(false);
    setLocalOperationRecords((prev) => ([
      {
        id: `local-add-existing-start-${Date.now()}`,
        time: new Date().toISOString(),
        event: '新增到已有容器',
        action: '新增到已有容器',
        container_code: cc,
        target: '',
        qty: 0,
        result: '成功',
        operator_name: '-',
        remark: `新增到已有容器 ${cc}`,
      },
      ...(Array.isArray(prev) ? prev : []),
    ]));
  }, [pickExistingCode]);
  async function remindContainerWeightAndHeight(
    payload: {
      code?: string;
      containerType?: string;
      heightCm?: number;
      weightG?: number;
      scene?: string;
    },
  ): Promise<boolean> {
    try {
      const cc = String(payload?.code || '').trim();
      const ct = String(payload?.containerType || '').trim().toLowerCase();
      const h = Number(payload?.heightCm);
      const wg = Number(payload?.weightG);
      const warnings: string[] = [];
      const wm = String(cartonMaxWeightText || '').trim().match(/-?\d+(\.\d+)?/);
      const weightLimit = wm ? Number(wm[0]) : NaN;
      if (Number.isFinite(weightLimit) && weightLimit > 0 && Number.isFinite(wg) && wg > 0) {
        const actualKg = Math.ceil(wg / 1000);
        if (actualKg > weightLimit) {
          warnings.push(`容器 ${cc || '-'} 实重 ${actualKg}kg 已超过限重 ${Number(weightLimit).toFixed(3)}kg。`);
        }
      }
      if (ct === 'pallet') {
        const hm = String(palletMaxHeightText || '').trim().match(/-?\d+(\.\d+)?/);
        const heightLimit = hm ? Number(hm[0]) : NaN;
        if (Number.isFinite(heightLimit) && heightLimit > 0 && Number.isFinite(h) && h > 0 && h > heightLimit) {
          warnings.push(`托盘容器 ${cc || '-'} 高度 ${h}cm 已超过托盘最大高度 ${Number(heightLimit).toFixed(3)}cm。`);
        }
      }
      if (!warnings.length) return true;
      return await new Promise<boolean>((resolve) => {
        Modal.confirm({
          title: '尺寸/重量提醒',
          centered: true,
          okText: '继续提交',
          cancelText: '返回修改',
          content: (
            <div style={{ lineHeight: 1.7 }}>
              {payload?.scene ? <div style={{ marginBottom: 8, color: '#595959' }}>当前操作：{payload.scene}</div> : null}
              {warnings.map((txt, idx) => (
                <div key={`box-remind-${idx}`} style={{ color: '#cf1322', marginBottom: idx === warnings.length - 1 ? 0 : 6 }}>
                  {idx + 1}. {txt}
                </div>
              ))}
            </div>
          ),
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
    } catch {
      return true;
    }
  }
  const endAddToExistingContainer = useCallback(async () => {
    const cc = String(addToExistingCode || '').trim();
    if (cc) {
      const hit: any = (Array.isArray(boxes) ? boxes : []).find((b: any) => String(b?.code || '').trim() === cc) || {};
      const draft: any = (outputDraftByCode as any)?.[cc] || {};
      const meta: any = (boxMeta as any)?.[cc] || {};
      const proceed = await remindContainerWeightAndHeight({
        code: cc,
        containerType: String(hit?.container_type || draft?.container_type || meta?.container_type || 'box').trim(),
        heightCm: Number(draft?.height_cm ?? meta?.height_cm),
        weightG: Number(draft?.weight_g ?? meta?.weight_g),
        scene: '结束新增',
      });
      if (!proceed) return;
    }
    setAddToExistingEnabled(false);
    setAddToExistingCode('');
    setPickExistingCode('');
    setPickExistingQuery('');
    setPickExistingOpen(false);
    if (cc) {
      setLocalOperationRecords((prev) => ([
        {
          id: `local-add-existing-end-${Date.now()}`,
          time: new Date().toISOString(),
          event: '结束新增',
          action: '结束新增',
          container_code: cc,
          target: '',
          qty: 0,
          result: '成功',
          operator_name: '-',
          remark: `结束新增 ${cc}`,
        },
        ...(Array.isArray(prev) ? prev : []),
      ]));
      setBoxFinalCode(cc);
      setBoxFinalOpen(true);
    }
  }, [addToExistingCode, boxes, outputDraftByCode, boxMeta, remindContainerWeightAndHeight]);
  const createNewContainerLocal = async () => {
    if (!canOperateExec) return;
    setStartContainerLoading(true);
    try {
      const vals = await startForm.validateFields();
      const ct = String(vals?.container_type || '').trim() as any;
      const hasAnyDim = (vals?.length_cm != null && vals?.length_cm !== '') || (vals?.width_cm != null && vals?.width_cm !== '') || (vals?.height_cm != null && vals?.height_cm !== '');
      const hasWeight = (vals?.weight_g != null && vals?.weight_g !== '');
      const l = (vals?.length_cm == null || vals?.length_cm === '') ? undefined : toPosInt(vals?.length_cm);
      const w = (vals?.width_cm == null || vals?.width_cm === '') ? undefined : toPosInt(vals?.width_cm);
      const h = (vals?.height_cm == null || vals?.height_cm === '') ? undefined : toPosInt(vals?.height_cm);
      const wg = (vals?.weight_g == null || vals?.weight_g === '') ? undefined : toPosInt(vals?.weight_g);
      const condVal = String(vals?.condition || '').trim();
      if (!ct) { message.warning('请选择容器类型'); return; }
      if (hasAnyDim) {
        if (ct === 'pallet') {
          if (!l || !w) { message.warning('托盘尺寸需填写完整（长/宽均为正整数，高度无需填写）'); return; }
        } else if (!l || !w || !h) {
          message.warning('尺寸需填写完整（长/宽/高均为正整数）');
          return;
        }
      }
      if (hasWeight && !wg) { message.warning('净重需为正整数'); return; }
      try {
        const validationWarnings: string[] = [];
        const inferCondFromText = (txt: any): 'new' | 'used' | undefined => {
          const s = String(txt ?? '').trim().toLowerCase();
          if (!s) return undefined;
          if (s === 'used' || s.includes('旧') || s.includes('二手')) return 'used';
          if (s === 'new' || s.includes('全新')) return 'new';
          return undefined;
        };
        const parseNumFromText = (v: any): number | undefined => {
          const s = String(v ?? '').trim();
          if (!s || s === '-') return undefined;
          const m = s.match(/-?\d+(\.\d+)?/);
          if (!m) return undefined;
          const n = Number(m[0]);
          return Number.isFinite(n) ? n : undefined;
        };
        const wantedCond = inferCondFromText(cartonSourceText);
        if (!condVal) {
          validationWarnings.push('未选择容器“新旧”，无法校验是否符合指令包装要求。');
        } else if (wantedCond && condVal !== wantedCond) {
          validationWarnings.push(`容器“新旧”与指令包装要求不一致：指令要求 ${wantedCond === 'new' ? '全新' : '旧'}，当前选择 ${condVal === 'new' ? '全新' : '旧'}。`);
        }
        const maxSideLimit = parseNumFromText(cartonMaxLengthText);
        if (ct === 'box' && maxSideLimit !== undefined) {
          if (!l || !w || !h) {
            validationWarnings.push('纸箱未填写完整尺寸（长/宽/高），无法校验装箱最长边。');
          } else {
            const maxSide = Math.max(l, w, h);
            if (maxSide > maxSideLimit) {
              validationWarnings.push(`纸箱最长边超出指令要求：容器最长边 ${maxSide}，指令上限 ${maxSideLimit}。`);
            }
          }
        }
        if (validationWarnings.length > 0) {
          const proceed = await new Promise<boolean>((resolve) => {
            Modal.confirm({
              title: '存在包装校验警告',
              centered: true,
              okText: '继续提交',
              cancelText: '返回修改',
              content: (
                <div style={{ lineHeight: 1.7 }}>
                  {validationWarnings.map((txt, idx) => (
                    <div key={`warn-${idx}`} style={{ color: '#cf1322', marginBottom: idx === validationWarnings.length - 1 ? 0 : 6 }}>
                      {idx + 1}. {txt}
                    </div>
                  ))}
                </div>
              ),
              onOk: () => resolve(true),
              onCancel: () => resolve(false),
            });
          });
          if (!proceed) return;
        }
      } catch {}
      let outputCode = '';
      try {
        if (!instrIdState) throw new Error('缺少指令ID');
        const resp = await request.post('/api/v1/storage/output-containers/reserve-code/', {
          instruction_id: instrIdState,
          container_type: ct,
        });
        outputCode = String(resp?.data?.code || '').trim();
      } catch (e: any) {
        message.error(e?.response?.data?.detail || e?.message || '预生成容器系统编码失败');
        return;
      }
      if (!outputCode) { message.error('预生成容器系统编码失败'); return; }
      const cc = String(outputCode || '').trim();
      if (!cc) { message.error('预生成容器系统编码为空'); return; }
      const exists = (boxes || []).some((b: any) => String(b?.code || '').trim() === cc);
      if (!exists) setBoxes((prev) => [...prev, { code: cc, status: 'open', container_type: ct } as any]);
      else setBoxes((prev) => prev.map((b: any) => (String(b?.code || '').trim() === cc ? ({ ...b, status: 'open' }) : b)));
      setCurrentPackBoxCode(cc);
      setBoxMeta((prev) => ({
        ...(prev || {}),
        [cc]: {
          length_cm: l,
          width_cm: w,
          height_cm: (ct === 'pallet' ? undefined : h),
          weight_g: wg != null ? Number(wg) : undefined,
          weight_kg: wg != null ? Number((wg / 1000).toFixed(3)) : undefined,
        },
      }));
      setLocalOperationRecords((prev) => ([
        {
          id: `local-start-${Date.now()}`,
          time: new Date().toISOString(),
          action: '开始容器',
          target: cc,
          result: '成功',
          operator_name: '-',
          remark: '开始新容器',
        },
        ...(Array.isArray(prev) ? prev : []),
      ]));
      message.success(`已开始新容器：${cc}`);
      setStartContainerOpen(false);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || '创建失败');
    } finally {
      setStartContainerLoading(false);
    }
  };
  const normalizeText = (v: any): string => {
    const s = String(v ?? '').trim();
    if (!s) return '-';
    const lowered = s.toLowerCase();
    if (lowered === 'none' || lowered === 'null' || lowered === 'undefined') return '-';
    return s;
  };
  const mapInventoryUnitType = (rawType: any, rawLabel: any): string => {
    const label = String(rawLabel || '').trim();
    if (label) return label;
    const t = String(rawType || '').trim().toUpperCase();
    if (t === 'LIU') return '散货库存单元';
    if (t === 'BIU') return '箱库存单元';
    if (t === 'PIU') return '托盘库存单元';
    if (t === 'AIU') return '异常库存单元';
    if (t === 'NIU') return '非产品库存单元';
    return t || '-';
  };
  const overviewContainerTypeTextMap: Record<string, string> = {
    box: '箱子',
    pallet: '托盘',
    crate: '周转箱',
    bag: '袋',
    envelope: '信封',
    other: '其他',
  };
  const parseOverviewIocCodesFromSelector = (selector: any): string[] => {
    try {
      if (selector && typeof selector === 'object') {
        const arr = Array.isArray(selector?.ioc_codes) ? selector.ioc_codes : [];
        return arr.map((x: any) => String(x || '').trim()).filter(Boolean);
      }
      const s = String(selector || '').trim();
      if (!s) return [];
      try {
        const j = JSON.parse(s);
        const arr = Array.isArray(j?.ioc_codes) ? j.ioc_codes : [];
        return arr.map((x: any) => String(x || '').trim()).filter(Boolean);
      } catch {
        return [];
      }
    } catch {
      return [];
    }
  };
  const normalizeOverviewIocCode = (v: any) => String(v || '').trim().toUpperCase();
  const waybillStatusText = (instr: any): string => {
    const src = String(
      instr?.waybill_source
      || instr?.customer_answers?.waybill_source
      || '',
    ).trim();
    const submitMode = String(
      instr?.waybill_submit_mode
      || instr?.customer_answers?.waybill_submit_mode
      || '',
    ).trim();
    if (!src && !submitMode) return '-';
    if (src.includes('客户') || src.toLowerCase() === 'customer') return '有运单';
    if (src.includes('仓库') || src.toLowerCase() === 'warehouse') {
      if (submitMode.includes('先申请包装数据') || submitMode.toLowerCase() === 'before_pack') return '先申请包装数据';
      return '仓库提供运单';
    }
    return src || submitMode || '-';
  };
  const getAttachmentPrintState = (a: any): 'print' | 'no_print' | 'unset' => {
    try {
      const pc = a?.meta?.print_config ?? a?.print_config;
      if (!pc || typeof pc !== 'object') return 'unset';
      if (pc.print === true) return 'print';
      if (pc.explicit_no_print === true) return 'no_print';
      return 'unset';
    } catch {
      return 'unset';
    }
  };
  const paperSizeLabel = (v: string) => {
    const s = String(v || '').trim().toLowerCase();
    if (s === '4x6' || s === '4x6_thermal') return '4×6英寸热敏纸打印';
    if (s === 'a4') return 'A4激光打印';
    return v || '-';
  };
  const placementLabel = (v: string) => {
    if (v === 'inside') return '放入包裹内';
    if (v === 'outside') return '贴在包裹外';
    return v || '-';
  };
  const getAttachmentPrintText = (a: any): string => {
    try {
      const pc = a?.meta?.print_config ?? a?.print_config;
      const state = getAttachmentPrintState(a);
      if (state === 'print') {
        return `需打印：${pc?.copies ?? '-'}份 · ${paperSizeLabel(String(pc?.paper_size || ''))} · ${placementLabel(String(pc?.placement || ''))}`;
      }
      if (state === 'no_print') return '明确无需打印';
      return '未配置打印需求';
    } catch {
      return '未配置打印需求';
    }
  };
  const hasPrintableRequirement = (a: any): boolean => {
    try {
      const state = getAttachmentPrintState(a);
      if (state === 'print') return true;
      if (state === 'no_print') return false;
      if (a?.print_required === true || a?.meta?.print_required === true) return true;
      return false;
    } catch {
      return false;
    }
  };
  const sanitizeNullableText = (v: any): string => {
    const s = String(v ?? '').trim();
    if (!s) return '';
    const low = s.toLowerCase();
    if (low === 'none' || low === 'null' || low === 'undefined' || s === '-') return '';
    return s;
  };
  useEffect(() => {
    const src = (
      (Array.isArray((metrics as any)?.attachments_detail) && (metrics as any).attachments_detail)
      || (Array.isArray((metrics as any)?.local_attachments_detail) && (metrics as any).local_attachments_detail)
      || (Array.isArray((metrics as any)?.waybill_attachments_detail) && (metrics as any).waybill_attachments_detail)
      || (Array.isArray(instructionDetail?.attachments_detail) && instructionDetail.attachments_detail)
      || (Array.isArray(data?.attachments_detail) && data.attachments_detail)
      || []
    ) as any[];
    setLocalAttachmentsDetail(Array.isArray(src) ? src : []);
  }, [metrics, instructionDetail?.attachments_detail, data?.attachments_detail]);
  useEffect(() => {
    const answers = (instructionDetail?.customer_answers || {}) as any;
    const chargeInfo = (instructionDetail as any)?.buy_waybill_info?.charge_info || {};
    const m = (metrics && typeof metrics === 'object') ? (metrics as any) : {};
    setLocalWaybillCarrier(sanitizeNullableText(m?.waybill_carrier || instructionDetail?.waybill_carrier || answers?.waybill_carrier || ''));
    setLocalWaybillTrackingNo(sanitizeNullableText(m?.waybill_tracking_no || instructionDetail?.waybill_tracking_no || answers?.tracking_number || ''));
    setLocalWaybillChargeAmount(sanitizeNullableText(m?.waybill_charge_amount || instructionDetail?.waybill_charge_amount || chargeInfo?.amount || ''));
    setLocalWaybillChargeCurrency(sanitizeNullableText(m?.waybill_charge_currency || instructionDetail?.waybill_charge_currency || chargeInfo?.currency || ''));
    setLocalWaybillChargeNote(sanitizeNullableText(m?.waybill_charge_note || instructionDetail?.waybill_charge_note || chargeInfo?.note || ''));
  }, [
    metrics,
    instructionDetail?.waybill_carrier,
    instructionDetail?.waybill_tracking_no,
    instructionDetail?.waybill_charge_amount,
    instructionDetail?.waybill_charge_currency,
    instructionDetail?.waybill_charge_note,
    instructionDetail?.buy_waybill_info,
    instructionDetail?.customer_answers,
  ]);
  useEffect(() => {
    if (String(localProcessResultStatus || '').trim()) return;
    const st = String((metrics as any)?.process_result_status || '').trim().toLowerCase();
    if (st) setLocalProcessResultStatus(st);
  }, [metrics?.process_result_status, localProcessResultStatus]);
  useEffect(() => {
    if (String(localProcessUnableReason || '').trim()) return;
    const rs = String((metrics as any)?.process_unable_reason || '').trim();
    if (rs) setLocalProcessUnableReason(rs);
  }, [metrics?.process_unable_reason, localProcessUnableReason]);
  useEffect(() => {
    if (!buyWaybillModalOpen) return;
    if (buyWaybillProviderOptions.length > 0) return;
    (async () => {
      try {
        const resp = await request.get('/api/v1/logistics/providers/', { params: { is_active: 1, page_size: 1000 } });
        const list = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
        const options = list
          .map((item: any) => {
            const code = String(item?.code || '').trim();
            const name = String(item?.name || '').trim();
            if (!code) return null;
            return { value: code, label: name ? `${code} (${name})` : code };
          })
          .filter(Boolean) as Array<{ label: string; value: string }>;
        setBuyWaybillProviderOptions(options);
      } catch {
        setBuyWaybillProviderOptions([]);
      }
    })();
  }, [buyWaybillModalOpen, buyWaybillProviderOptions.length]);
  const instructionCode = String(
    instructionCodeFromParent
    || instructionDetail?.system_code
    || '-',
  );
  const executionTips = normalizeText(instructionDetail?.execution_tips || instructionDetail?.tips || '');
  const instructionRemark = normalizeText(instructionDetail?.remarks || instructionDetail?.remark || '');
  const processResultStatus = String(localProcessResultStatus || (metrics as any)?.process_result_status || '').trim().toLowerCase();
  const processUnableReason = normalizeText(localProcessUnableReason || (metrics as any)?.process_unable_reason || '');
  const attachmentDetailRows = useMemo(() => (
    (Array.isArray(localAttachmentsDetail) ? localAttachmentsDetail : []) as any[]
  ), [localAttachmentsDetail]);
  const waybillStatusForAction = useMemo(
    () => waybillStatusText(instructionDetail || {}),
    [instructionDetail?.waybill_source, instructionDetail?.waybill_submit_mode, instructionDetail?.customer_answers],
  );
  useEffect(() => {
    try {
      const src = String(
        instructionDetail?.waybill_source
        || instructionDetail?.customer_answers?.waybill_source
        || '',
      ).trim();
      const submitMode = String(
        instructionDetail?.waybill_submit_mode
        || instructionDetail?.customer_answers?.waybill_submit_mode
        || '',
      ).trim();
      onChange?.('waybill_source', src);
      onChange?.('waybill_submit_mode', submitMode);
      const isProd = typeof process !== 'undefined' && !!process?.env && process.env.NODE_ENV === 'production';
      if (!isProd) {
        try {
          console.debug('[RO-Waybill][Renderer->Metrics]', {
            instruction_waybill_source: String(instructionDetail?.waybill_source || '').trim(),
            answers_waybill_source: String(instructionDetail?.customer_answers?.waybill_source || '').trim(),
            synced_waybill_source: src,
            instruction_waybill_submit_mode: String(instructionDetail?.waybill_submit_mode || '').trim(),
            answers_waybill_submit_mode: String(instructionDetail?.customer_answers?.waybill_submit_mode || '').trim(),
            synced_waybill_submit_mode: submitMode,
          });
        } catch {}
      }
    } catch {}
  }, [onChange, instructionDetail?.waybill_source, instructionDetail?.waybill_submit_mode, instructionDetail?.customer_answers]);
  const waybillAttachments = useMemo(
    () => attachmentDetailRows.filter((a: any) => {
      const lbl = String(a?.label || '').trim();
      return lbl === '物流运单' || lbl === '运输标签(物流运单/箱唛/托盘标)';
    }),
    [attachmentDetailRows],
  );
  const transportLabelAttachments = useMemo(
    () => attachmentDetailRows.filter((a: any) => String(a?.label || '').trim() === '运输标签(物流运单/箱唛/托盘标)'),
    [attachmentDetailRows],
  );
  const sortedTransportLabelAttachments = useMemo(() => {
    const list = Array.isArray(transportLabelAttachments) ? [...transportLabelAttachments] : [];
    const rank = (att: any): number => {
      const n = String(att?.file_name || att?.name || att?.file || att?.file_path || '').toLowerCase();
      if (/\.pdf$/i.test(n)) return 0;
      if (/\.(png|jpg|jpeg|gif|webp|bmp|svg|apng|ico)$/i.test(n)) return 1;
      return 2;
    };
    return list
      .map((att: any, idx: number) => ({ att, idx }))
      .sort((a: any, b: any) => {
        const ra = rank(a.att);
        const rb = rank(b.att);
        if (ra !== rb) return ra - rb;
        return a.idx - b.idx;
      })
      .map((x: any) => x.att);
  }, [transportLabelAttachments]);
  const transportLabelDisplayLimit = 3;
  const visibleTransportLabelAttachments = useMemo(
    () => (transportLabelListExpanded ? sortedTransportLabelAttachments : sortedTransportLabelAttachments.slice(0, transportLabelDisplayLimit)),
    [sortedTransportLabelAttachments, transportLabelListExpanded],
  );
  const hiddenTransportLabelCount = Math.max(0, sortedTransportLabelAttachments.length - visibleTransportLabelAttachments.length);
  const printableAttachments = useMemo(() => {
    const src = attachmentDetailRows;
    const isOtherShippingAttachment = (a: any): boolean => String(a?.label || '').trim() === '其他发货文件';
    return src
      .filter((a: any) => isOtherShippingAttachment(a))
      .map((a: any, idx: number) => ({
      key: String(a?.id ?? idx),
      raw: a,
      name: normalizeText(a?.file_name || a?.name || a?.file || a?.file_path || `附件${idx + 1}`),
      printState: getAttachmentPrintState(a),
      printText: getAttachmentPrintText(a),
      }));
  }, [attachmentDetailRows]);
  const printableAttachmentsWithPrintRequirement = useMemo(
    () => printableAttachments.filter((a: any) => hasPrintableRequirement(a?.raw)),
    [printableAttachments],
  );
  const disableBuyWaybillBtn = !canOperateExec || waybillStatusForAction !== '仓库提供运单';
  const disablePrintTransportLabelPdfBtn = !canOperateExec || transportLabelPdfFileCount <= 0;
  const disablePrintTransportLabelImageBtn = !canOperateExec || transportLabelImageFileCount <= 0;
  const isWaybillSubmitAfterPack = (() => {
    const raw = String(
      instructionDetail?.waybill_submit_mode
      || instructionDetail?.customer_answers?.waybill_submit_mode
      || '',
    ).trim();
    if (!raw) return false;
    const s = raw.toLowerCase();
    return s === 'after_pack' || s === 'before_pack' || raw.includes('先申请包装数据');
  })();
  const disableLabelingBtn = !canOperateExec || isWaybillSubmitAfterPack;
  const isWarehouseWaybillMode = waybillStatusForAction === '仓库提供运单';
  const repackRawValueForAudit = String(
    instructionDetail?.customer_answers?.is_repack_required
    ?? '',
  ).trim();
  const hasWaybillAttachmentUploaded = waybillAttachments.length > 0;
  const hasWaybillChargeCompleted = (() => {
    const n = Number(String(localWaybillChargeAmount || '').trim());
    return Number.isFinite(n) && n >= 0;
  })();
  const hasWaybillChargeCurrencyCompleted = !!String(localWaybillChargeCurrency || '').trim();
  const warehouseWaybillReadyForCompletion = (
    hasWaybillAttachmentUploaded
    && hasWaybillChargeCompleted
    && hasWaybillChargeCurrencyCompleted
  );
  const disableMarkCompletedBtn = !canOperateExec || (isWarehouseWaybillMode && !warehouseWaybillReadyForCompletion);
  const disableMarkUnableBtn = !canOperateExec;
  const parseRepackRequired = useCallback((): boolean | null => {
    const raw = String(
      repackRawValueForAudit,
    ).trim();
    if (!raw) return null;
    const s = raw.toLowerCase();
    if (s.includes('无需重新包装') || s === 'false' || s === '0' || s === 'no' || s === 'n') return false;
    if (s.includes('需重新包装') || s === 'true' || s === '1' || s === 'yes' || s === 'y') return true;
    return null;
  }, [repackRawValueForAudit]);
  const markProcessCompleted = async () => {
    const repackRequired = parseRepackRequired();
    const isRepack = repackRequired === true;
    const isNoRepack = repackRequired === false;
    const isIocTargetMode = !!hasIocTarget;
    const isIuTargetMode = !isIocTargetMode;
    const canMarkUnableForRemaining = isIuTargetMode && isRepack;
    const rowSummaries = (isIocTargetMode ? iocDisplayRows : inventoryRows).map((row: any, idx: number) => {
      const code = String(
        isIocTargetMode
          ? (row?.output_code || row?.display_code || row?.key || `IOC-${idx + 1}`)
          : (row?.unit_code || row?.key || `IU-${idx + 1}`),
      ).trim();
      const targetQty = Math.max(0, Math.floor(Number(isIocTargetMode ? (row?.target_qty ?? 1) : row?.target_qty) || 0));
      const doneQty = Math.max(
        0,
        Math.floor(Number(isIocTargetMode ? (row?.done_qty ?? row?.completed_qty ?? 0) : (row?.picked_qty ?? 0)) || 0),
      );
      const remainingQty = Math.max(0, targetQty - doneQty);
      return { code: code || '-', target_qty: targetQty, done_qty: doneQty, remaining_qty: remainingQty };
    });
    const remainingRows = rowSummaries.filter((r: any) => Number(r?.remaining_qty || 0) > 0);
    const hasInProgressBox = (Array.isArray(boxes) ? boxes : []).some((b: any) => {
      const st = String(b?.status || '').trim().toLowerCase();
      return st === 'open' || st === 'active';
    });
    if (hasInProgressBox) {
      message.warning('存在进行中容器，请先结束容器或作废后再标注已完成');
      return;
    }

    const waybillSourceRawForConsume = String(
      instructionDetail?.waybill_source
      || instructionDetail?.customer_answers?.waybill_source
      || '',
    ).trim().toLowerCase();
    const isUploadWaybillSource = (
      waybillSourceRawForConsume === 'upload'
      || waybillSourceRawForConsume.includes('上传')
      || waybillSourceRawForConsume.includes('客户')
    );
    const shouldSuggestConsume = (
      waybillStatusForAction === '仓库提供运单'
      || ((waybillStatusForAction === '有运单' || isUploadWaybillSource) && !!isWaybillSubmitImmediate)
    );
    const generatedIocCodes = Array.from(new Set(
      (Array.isArray(boxes) ? boxes : [])
        .map((b: any) => String(b?.code || '').trim())
        .filter((code: string) => !!code && code !== String(adjustPoolCode || '').trim())
        .filter((code: string) => {
          const hit = (Array.isArray(boxes) ? boxes : []).find((b: any) => String(b?.code || '').trim() === code);
          const st = String(hit?.status || '').trim().toLowerCase();
          return st !== 'cancelled';
        }),
    ));
    const historyTargetIocCodes = Array.from(new Set(
      (Array.isArray(iocDisplayRows) ? iocDisplayRows : [])
        .map((r: any) => String(r?.output_code || r?.display_code || '').trim())
        .filter(Boolean),
    ));
    const allCandidateIocCodes = Array.from(new Set([...generatedIocCodes, ...historyTargetIocCodes]));
    let consumeDecision: 'consume_all' | 'keep_all' = shouldSuggestConsume ? 'consume_all' : 'keep_all';
    let remainingDecision: 'mark_unoperable_and_complete' | 'continue_operate' = remainingRows.length > 0 ? 'continue_operate' : 'mark_unoperable_and_complete';
    let iuNoRepackAck = !(isIuTargetMode && isNoRepack);
    let iocNoRepackAck = !(isIocTargetMode && isNoRepack);
    const auditScenarioTitle = isIocTargetMode
      ? (isRepack ? '当前场景: IOC + 需重新包装' : '当前场景: IOC + 无需重新包装')
      : (isRepack ? '当前场景: IU + 需重新包装' : '当前场景: IU + 无需重新包装');
    const auditScenarioRules: string[] = isIocTargetMode
      ? (isRepack
        ? [
          '所有操作对象必须全部处理完成,不可存在无法处理的操作对象',
          '所有操作对象必须已重新包装进入本指令新生成的IOC中,不可单独存放',
        ]
        : [
          '所有操作对象必须全部处理完成,不可存在无法处理的操作对象',
        ])
      : (isRepack
        ? [
          '可存在无法处理的操作对象,但必须显式选择“已核实未实现包装的操作对象均无法处理”',
          '可处理操作对象必须已重新包装进入本指令新生成的IOC中,不可单独存放',
        ]
        : [
          '所有操作对象必须全部处理完成,不可存在无法处理的操作对象',
          '需保证执行结果=指令事实 需人工确认勾选',
        ]);
    const shouldDisableConfirmByIocNoRepackAck = isIocTargetMode && isNoRepack && !iocNoRepackAck;
    const shouldDisableConfirmByIuNoRepackAck = isIuTargetMode && isNoRepack && !iuNoRepackAck;
    const shouldShowUnableConfirm = canMarkUnableForRemaining && remainingRows.length > 0;
    const shouldDisableConfirmByIocRepackRemaining = isIocTargetMode && isRepack && remainingRows.length > 0;
    let remainingUnableConfirmed = !shouldShowUnableConfirm;
    let shouldDisableConfirmBtn = shouldDisableConfirmByIocRepackRemaining || shouldDisableConfirmByIocNoRepackAck || shouldDisableConfirmByIuNoRepackAck || !remainingUnableConfirmed;
    let confirmModalRef: any = null;
    const keepConfirmModalOpen = () => new Promise<void>(() => {});

    const confirmResult = await new Promise<boolean>((resolve) => {
      confirmModalRef = Modal.confirm({
        title: '标注已完成前审核确认',
        width: 860,
        okText: '确认并标注已完成',
        cancelText: '取消',
        okButtonProps: { disabled: shouldDisableConfirmBtn },
        content: (
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6, padding: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  当前指令操作对象：
                  {isIocTargetMode ? '历史指令产出IOC' : '库存单元IU'}
                  {isRepack ? (
                    <span style={{ display: 'inline-block', marginLeft: 6, padding: '0 8px', lineHeight: '22px', borderRadius: 6, background: '#fff1f0', border: '1px solid #ffccc7', color: '#cf1322' }}>需重新包装</span>
                  ) : isNoRepack ? (
                    <span style={{ display: 'inline-block', marginLeft: 6, padding: '0 8px', lineHeight: '22px', borderRadius: 6, background: '#f6ffed', border: '1px solid #b7eb8f', color: '#389e0d' }}>无需重新包装</span>
                  ) : (
                    <span style={{ display: 'inline-block', marginLeft: 6, padding: '0 8px', lineHeight: '22px', borderRadius: 6, background: '#fafafa', border: '1px solid #d9d9d9', color: '#595959' }}>未明确</span>
                  )}
                </div>
                <div>运单模式：{waybillStatusForAction || '-'}（提交方式：{waybillSubmitModeText || '-'}）</div>
              </div>
              <div style={{ marginTop: 8, border: '1px solid #f0f0f0', borderRadius: 6, background: '#fff', padding: 10 }}>
                <div style={{ fontWeight: 500, marginBottom: 6 }}>{auditScenarioTitle}</div>
                <div style={{ display: 'grid', gap: 4 }}>
                  {auditScenarioRules.map((txt: string, idx: number) => (
                    <div key={`${auditScenarioTitle}-rule-${idx}`} style={{ color: '#595959' }}>
                      <span style={{ display: 'inline-block', width: 72 }}>{idx === 0 ? '适用规则:' : ''}</span>
                      <span style={{ display: 'inline-block', width: 20 }}>{`${idx + 1}.`}</span>
                      <span>{txt}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {isIuTargetMode && isNoRepack ? (
              <Checkbox
                defaultChecked={false}
                onChange={(e) => {
                  iuNoRepackAck = !!e?.target?.checked;
                  shouldDisableConfirmBtn = shouldDisableConfirmByIocRepackRemaining || shouldDisableConfirmByIocNoRepackAck || !iuNoRepackAck || !remainingUnableConfirmed;
                  try {
                    confirmModalRef?.update?.({ okButtonProps: { disabled: shouldDisableConfirmBtn } });
                  } catch {}
                }}
              >
                已核实:所有操作对象已处理完成,不存在无法处理的操作对象
              </Checkbox>
            ) : null}
            {isIocTargetMode && isNoRepack ? (
              <Checkbox
                defaultChecked={false}
                onChange={(e) => {
                  iocNoRepackAck = !!e?.target?.checked;
                  shouldDisableConfirmBtn = shouldDisableConfirmByIocRepackRemaining || !iocNoRepackAck || shouldDisableConfirmByIuNoRepackAck || !remainingUnableConfirmed;
                  try {
                    confirmModalRef?.update?.({ okButtonProps: { disabled: shouldDisableConfirmBtn } });
                  } catch {}
                }}
              >
                我确认：IOC 操作对象已全部处理完成，且不存在无法处理项
              </Checkbox>
            ) : null}
            {isIocTargetMode && isRepack && remainingRows.length > 0 ? (
              <div style={{ border: '1px solid #ffe58f', background: '#fffbe6', borderRadius: 6, padding: 10 }}>
                <div style={{ marginBottom: 8, color: '#ad6800' }}>
                  检测到仍有未实现包装的历史指令产出IOC：共{remainingRows.reduce((s: number, r: any) => s + Number(r?.remaining_qty || 0), 0)}件
                </div>
                <div style={{ color: '#ad6800' }}>IOC操作对象不允许留存无法处理项，请先继续完成处理后再标注已完成。</div>
              </div>
            ) : null}
            {canMarkUnableForRemaining && remainingRows.length > 0 ? (
              <div style={{ border: '1px solid #ffe58f', background: '#fffbe6', borderRadius: 6, padding: 10 }}>
                <div style={{ marginBottom: 8, color: '#ad6800' }}>
                  检测到仍有未实现包装的操作对象: 共 {remainingRows.length} 项 | {remainingRows.reduce((s: number, r: any) => s + Number(r?.remaining_qty || 0), 0)} 件
                </div>
                <Checkbox
                  defaultChecked={false}
                  onChange={(e) => {
                    remainingUnableConfirmed = !!e?.target?.checked;
                    remainingDecision = remainingUnableConfirmed ? 'mark_unoperable_and_complete' : 'continue_operate';
                    shouldDisableConfirmBtn = shouldDisableConfirmByIocRepackRemaining || shouldDisableConfirmByIocNoRepackAck || shouldDisableConfirmByIuNoRepackAck || !remainingUnableConfirmed;
                    try {
                      confirmModalRef?.update?.({ okButtonProps: { disabled: shouldDisableConfirmBtn } });
                    } catch {}
                  }}
                >
                  已核实未实现包装的操作对象均为无法处理的操作对象,需在收尾阶段处理
                </Checkbox>
              </div>
            ) : null}
          </div>
        ),
        onOk: () => {
          if (isIocTargetMode && isRepack && remainingRows.length > 0) {
            message.warning('存在未完成的历史指令产出IOC，不允许留存无法处理项，请先继续操作');
            return keepConfirmModalOpen();
          }
          if (isIocTargetMode && isNoRepack && !iocNoRepackAck) {
            message.warning('请先确认“IOC 操作对象已全部处理完成，且不存在无法处理项”');
            return keepConfirmModalOpen();
          }
          if (isIocTargetMode && isRepack && generatedIocCodes.length <= 0) {
            message.warning('需重新包装场景下，请先将操作对象装入本指令新生成IOC后再标注完成');
            return keepConfirmModalOpen();
          }
          if (isIuTargetMode && isNoRepack && !iuNoRepackAck) {
            message.warning('请先确认“已核实:所有操作对象已处理完成,不存在无法处理的操作对象”');
            return keepConfirmModalOpen();
          }
          if (shouldShowUnableConfirm && !remainingUnableConfirmed) {
            message.warning('请先确认“已核实未实现包装的操作对象均无法处理”');
            return keepConfirmModalOpen();
          }
          resolve(true);
        },
        onCancel: () => resolve(false),
      });
    });
    if (!confirmResult) return;

    const consumedIocCodes = consumeDecision === 'consume_all' ? allCandidateIocCodes : [];
    const unconsumedIocCodes = consumeDecision === 'consume_all' ? [] : allCandidateIocCodes;
    const unableRowsFromRemaining = (canMarkUnableForRemaining && remainingRows.length > 0 && remainingDecision === 'mark_unoperable_and_complete')
      ? remainingRows.map((r: any) => ({ target_code: r.code, unable_qty: r.remaining_qty }))
      : [];
    const auditPayload = {
      checked_at: new Date().toISOString(),
      review_at_mark_completed: true,
      is_repack_required: isRepack,
      no_repack_consistency_confirmed: !!(isIuTargetMode && isNoRepack ? iuNoRepackAck : null),
      ioc_no_repack_consistency_confirmed: !!(isIocTargetMode && isNoRepack ? iocNoRepackAck : null),
      repack_target_rows: rowSummaries,
      repack_remaining_rows: remainingRows,
      repack_remaining_decision: remainingDecision,
      unable_rows_from_remaining: unableRowsFromRemaining,
      ioc_consumption_suggestion: shouldSuggestConsume ? 'consume_all' : 'keep_all',
      ioc_consumption_decision: consumeDecision,
      generated_ioc_codes: generatedIocCodes,
      history_target_ioc_codes: historyTargetIocCodes,
      consumed_ioc_codes: consumedIocCodes,
      unconsumed_ioc_codes: unconsumedIocCodes,
      waybill_status_for_action: waybillStatusForAction,
      waybill_submit_mode: waybillSubmitModeText,
    };

    setLocalProcessResultStatus('completed');
    setLocalProcessUnableReason('');
    try {
      onChange?.('process_result_status', 'completed');
      onChange?.('process_unable_reason', '');
      onChange?.('completion_mark_audit', auditPayload);
      onChange?.('ioc_consumption_decision', {
        decision: consumeDecision,
        consumed_ioc_codes: consumedIocCodes,
        unconsumed_ioc_codes: unconsumedIocCodes,
      });
      onChange?.('unable_rows_from_mark_completed', unableRowsFromRemaining);
    } catch {}
    const t = new Date();
    const rec = {
      id: `local-mark-completed-${t.getTime()}-${Math.random()}`,
      time: t.toISOString(),
      event: '标注已完成',
      action: '标注已完成',
      container_code: '-',
      target: '-',
      qty: '',
      result: '成功',
      operator_name: '-',
      remark: `审核通过;${isRepack ? '需重包' : '无需重包'}`,
      review_detail: auditPayload,
    };
    setLocalOperationRecords((prev) => ([rec, ...(Array.isArray(prev) ? prev : [])]));
    message.success('已标注为已完成');
  };
  const openUnableReasonModal = () => {
    const reason = String((metrics as any)?.process_unable_reason || '').trim();
    const quickMatch = reason === '库存不足' || reason === '指令错误' ? reason : '';
    setUnableReasonQuickValue(quickMatch);
    setUnableReasonInput(reason || quickMatch);
    setUnableReasonModalOpen(true);
  };
  const submitUnableReasonModal = () => {
    const finalReason = String(unableReasonInput || unableReasonQuickValue || '').trim();
    if (!finalReason) return void message.warning('请填写无法操作原因');
    setUnableReasonModalOpen(false);
    setLocalProcessResultStatus('unable');
    setLocalProcessUnableReason(finalReason);
    try {
      onChange?.('process_result_status', 'unable');
      onChange?.('process_unable_reason', finalReason);
    } catch {}
    const t = new Date();
    const rec = {
      id: `local-mark-unable-${t.getTime()}-${Math.random()}`,
      time: t.toISOString(),
      event: '标注无法操作',
      action: '标注无法操作',
      container_code: '-',
      target: '-',
      qty: '',
      result: '成功',
      operator_name: '-',
      remark: finalReason,
    };
    setLocalOperationRecords((prev) => ([rec, ...(Array.isArray(prev) ? prev : [])]));
    message.success('已标注为无法操作');
  };
  const openPrintOtherAttachmentsModal = () => {
    if (!printableAttachmentsWithPrintRequirement.length) {
      message.warning('暂无有打印需求的其他附件');
      return;
    }
    setPrintOtherAttachmentsModalOpen(true);
  };
  const openBuyWaybillModal = () => {
    setBuyWaybillCarrier(sanitizeNullableText(localWaybillCarrier));
    setBuyWaybillTrackingNo(sanitizeNullableText(localWaybillTrackingNo));
    const amountRaw = String(localWaybillChargeAmount || '').trim();
    setBuyWaybillChargeAmount(amountRaw ? Number(amountRaw) : null);
    setBuyWaybillChargeCurrency(String(localWaybillChargeCurrency || 'USD').trim().toUpperCase() || 'USD');
    setBuyWaybillChargeNote(String(localWaybillChargeNote || '').trim());
    setBuyWaybillFileList([]);
    setBuyWaybillModalOpen(true);
  };
  const submitBuyWaybillUpload = async () => {
    const instructionId = Number((instructionDetail as any)?.id || 0);
    if (!instructionId) return void message.warning('缺少指令ID');
    const carrier = String(buyWaybillCarrier || '').trim();
    const trackingNo = String(buyWaybillTrackingNo || '').trim();
    const chargeAmountInput = buyWaybillChargeAmount;
    const chargeAmount = Number(chargeAmountInput);
    const chargeCurrency = String(buyWaybillChargeCurrency || 'USD').trim().toUpperCase();
    const chargeNote = String(buyWaybillChargeNote || '').trim();
    const uploadFiles = (Array.isArray(buyWaybillFileList) ? buyWaybillFileList : [])
      .map((f: any) => f?.originFileObj as File | undefined)
      .filter((f: File | undefined) => !!f) as File[];
    const hasExistingWaybill = waybillAttachments.length > 0;
    if (!carrier) return void message.warning('请填写运单所属承运商');
    if (!trackingNo) return void message.warning('请填写物流跟踪号');
    if (uploadFiles.length <= 0 && !hasExistingWaybill) return void message.warning('请上传运单附件');
    if (chargeAmountInput == null || !Number.isFinite(chargeAmount)) return void message.warning('请填写运单费用金额');
    if (chargeAmount < 0) return void message.warning('运单费用金额不能小于0');
    if (!chargeCurrency) return void message.warning('请填写运单费用币种');
    let ignoreDuplicateWarning = false;
    try {
      const precheckResp = await request.post('/api/v1/instruction/instructions/precheck/', {
        waybill_carrier: carrier,
        waybill_tracking_no: trackingNo,
        exclude_instruction_ids: [instructionId],
      });
      const warnings = Array.isArray((precheckResp as any)?.data?.warnings)
        ? (precheckResp as any).data.warnings
        : (Array.isArray((precheckResp as any)?.warnings) ? (precheckResp as any).warnings : []);
      if (warnings.length > 0) {
        const proceed = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: `检测到${warnings.length}条物流跟踪号重复记录，是否继续提交？`,
            content: warnings.map((w: any, i: number) => `${i + 1}. ${w.provider || '-'} / ${w.tracking_number || '-'}`).join('\n'),
            okText: '继续提交',
            cancelText: '取消',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (!proceed) return;
        ignoreDuplicateWarning = true;
      }
    } catch {
      return void message.error('物流跟踪号唯一性校验失败，请稍后重试');
    }
    setBuyWaybillSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('waybill_carrier', carrier);
      formData.append('waybill_tracking_no', trackingNo);
      formData.append('waybill_charge_amount', String(chargeAmount));
      formData.append('waybill_charge_currency', chargeCurrency);
      formData.append('waybill_charge_note', chargeNote);
      formData.append('ignore_duplicate_warning', ignoreDuplicateWarning ? '1' : '0');
      uploadFiles.forEach((f) => formData.append('file', f));
      const resp = await request.post(`/api/v1/instruction/instructions/${instructionId}/waybill-upload/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const payload: any = resp?.data || {};
      setLocalWaybillCarrier(sanitizeNullableText(payload?.waybill_carrier ?? carrier));
      setLocalWaybillTrackingNo(sanitizeNullableText(payload?.waybill_tracking_no ?? trackingNo));
      setLocalWaybillChargeAmount(sanitizeNullableText(payload?.waybill_charge_amount ?? String(chargeAmount)));
      setLocalWaybillChargeCurrency(sanitizeNullableText(payload?.waybill_charge_currency ?? chargeCurrency));
      setLocalWaybillChargeNote(sanitizeNullableText(payload?.waybill_charge_note ?? chargeNote));
      const newAtts = Array.isArray(payload?.attachments_detail) ? payload.attachments_detail : waybillAttachments;
      setLocalAttachmentsDetail(Array.isArray(newAtts) ? newAtts : []);
      try {
        onChange?.('waybill_carrier', sanitizeNullableText(payload?.waybill_carrier ?? carrier));
        onChange?.('waybill_tracking_no', sanitizeNullableText(payload?.waybill_tracking_no ?? trackingNo));
        onChange?.('waybill_charge_amount', sanitizeNullableText(payload?.waybill_charge_amount ?? String(chargeAmount)));
        onChange?.('waybill_charge_currency', sanitizeNullableText(payload?.waybill_charge_currency ?? chargeCurrency));
        onChange?.('waybill_charge_note', sanitizeNullableText(payload?.waybill_charge_note ?? chargeNote));
        onChange?.('waybill_source', String(instructionDetail?.waybill_source || instructionDetail?.customer_answers?.waybill_source || '').trim());
        onChange?.('waybill_submit_mode', String(instructionDetail?.waybill_submit_mode || instructionDetail?.customer_answers?.waybill_submit_mode || '').trim());
        onChange?.('attachments_detail', Array.isArray(newAtts) ? newAtts : []);
      } catch {}
      const t = new Date();
      const rec = {
        id: `local-buy-waybill-${t.getTime()}-${Math.random()}`,
        time: t.toISOString(),
        event: '购买并上传运单',
        action: '购买并上传运单',
        container_code: '-',
        target: `${String(payload?.waybill_carrier ?? carrier).trim() || '-'} / ${String(payload?.waybill_tracking_no ?? trackingNo).trim() || '-'}`,
        qty: '',
        result: '成功',
        operator_name: '-',
        remark: '-',
      };
      setLocalOperationRecords((prev) => ([rec, ...(Array.isArray(prev) ? prev : [])]));
      setBuyWaybillModalOpen(false);
      setBuyWaybillFileList([]);
      message.success(uploadFiles.length > 1 ? `运单信息已更新，已上传${uploadFiles.length}个附件` : '运单信息已更新');
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || '提交失败');
    } finally {
      setBuyWaybillSubmitting(false);
    }
  };
  const previewBuyWaybillUploadFile = async (file: any) => {
    const fileObj: File | undefined = file?.originFileObj;
    if (!fileObj) return;
    const fileName = String(file?.name || fileObj?.name || '运单附件预览');
    const lower = fileName.toLowerCase();
    const isImage = /\.(png|jpg|jpeg|gif|webp)$/i.test(lower);
    const isPdf = /\.pdf$/i.test(lower);
    const isExcel = /\.(xlsx|xls)$/i.test(lower);
    setPreviewTitle(fileName);
    setPreviewPrintText('');
    setPreviewDownloadName(fileName);
    setPreviewDownloadBlob(fileObj);
    setPreviewDownloadUrl('');
    try {
      if (isExcel) {
        const buf = await fileObj.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const firstSheet = String((wb.SheetNames || [])[0] || '');
        const ws = firstSheet ? wb.Sheets[firstSheet] : undefined;
        if (!ws) throw new Error('未读取到工作表');
        const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }) as any[][];
        const maxCols = Math.max(0, ...aoa.map((r) => (Array.isArray(r) ? r.length : 0)));
        const headerRow = (aoa[0] || []) as any[];
        const cols = Array.from({ length: maxCols }).map((_, i) => ({
          title: String(headerRow[i] ?? '').trim() || `列${i + 1}`,
          dataIndex: `c${i}`,
          key: `c${i}`,
          width: 180,
          render: (v: any) => <span style={{ whiteSpace: 'pre-wrap' }}>{String(v ?? '')}</span>,
        }));
        const rows = aoa.slice(1).map((row, ridx) => {
          const rec: any = { __k: ridx };
          for (let i = 0; i < maxCols; i += 1) rec[`c${i}`] = row?.[i] ?? '';
          return rec;
        });
        setPreviewTableCols(cols);
        setPreviewTableData(rows);
        setPreviewType('table');
        setPreviewUrl('');
        setPreviewOpen(true);
        return;
      }
      const objectUrl = URL.createObjectURL(fileObj);
      setPreviewTableCols([]);
      setPreviewTableData([]);
      setPreviewType((isImage || isPdf) ? (isImage ? 'image' : 'file') : 'file');
      setPreviewUrl(objectUrl);
      setPreviewOpen(true);
    } catch {
      message.warning('附件预览失败');
    }
  };
  const resolveAttachmentUrl = (a: any): string => {
    try {
      const raw = String(
        a?.url
        || a?.file_url
        || a?.download_url
        || a?.downloadUrl
        || a?.file
        || a?.file_path
        || a?.path
        || a?.preview_url
        || '',
      ).trim();
      if (!raw) return '';
      if (/^https?:\/\//i.test(raw)) return raw;
      let backendOrigin = window.location.origin;
      try {
        const u = new URL(backendOrigin);
        if (u.port === '3000') {
          u.port = '8000';
          backendOrigin = u.toString().replace(/\/$/, '');
        }
      } catch {}
      let path = raw;
      if (!path.startsWith('/')) path = `/${path}`;
      if (/^\/(instruction|attachments?)/i.test(path)) path = `/media${path}`;
      else if (!/^\/media\//i.test(path)) path = `/media${path}`;
      return `${backendOrigin}${path}`;
    } catch {
      return '';
    }
  };
  const openAttachmentPreview = async (a: any, name: string, printText: string) => {
    const url = resolveAttachmentUrl(a);
    if (!url) {
      message.warning('无可预览附件');
      return;
    }
    setPreviewLoading(true);
    const lowerName = String(a?.file_name || a?.name || a?.file || name || '').toLowerCase();
    const isImage = /\.(png|jpg|jpeg|gif|webp)$/i.test(lowerName);
    const isPdf = /\.pdf$/i.test(lowerName);
    const isExcel = /\.(xlsx|xls)$/i.test(lowerName);
    setPreviewTitle(String(a?.file_name || a?.name || name || '运单附件预览'));
    setPreviewPrintText(printText || '');
    setPreviewDownloadName(String(a?.file_name || a?.name || name || '附件').trim() || '附件');
    setPreviewDownloadBlob(null);
    setPreviewDownloadUrl(url);
    try {
      const resp = await fetch(url, { credentials: 'include' as any });
      if (!resp.ok) throw new Error('预览文件加载失败');
      const blob = await resp.blob();
      setPreviewDownloadBlob(blob);
      if (isExcel) {
        const buf = await blob.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const firstSheet = String((wb.SheetNames || [])[0] || '');
        const ws = firstSheet ? wb.Sheets[firstSheet] : undefined;
        if (!ws) throw new Error('未读取到工作表');
        const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }) as any[][];
        const maxCols = Math.max(0, ...aoa.map((r) => (Array.isArray(r) ? r.length : 0)));
        const headerRow = (aoa[0] || []) as any[];
        const cols = Array.from({ length: maxCols }).map((_, i) => {
          const titleRaw = String(headerRow[i] ?? '').trim();
          return {
            title: titleRaw || `列${i + 1}`,
            dataIndex: `c${i}`,
            key: `c${i}`,
            width: 180,
            render: (v: any) => <span style={{ whiteSpace: 'pre-wrap' }}>{String(v ?? '')}</span>,
          };
        });
        const bodyRows = aoa.slice(1);
        const rows = bodyRows.map((row, ridx) => {
          const rec: any = { __k: ridx };
          for (let i = 0; i < maxCols; i += 1) rec[`c${i}`] = row?.[i] ?? '';
          return rec;
        });
        setPreviewTableCols(cols);
        setPreviewTableData(rows);
        setPreviewType('table');
        setPreviewUrl('');
        setPreviewOpen(true);
        return;
      }
      const objectUrl = URL.createObjectURL(blob);
      setPreviewType((isImage || isPdf) ? (isImage ? 'image' : 'file') : 'file');
      setPreviewUrl(objectUrl);
      setPreviewTableCols([]);
      setPreviewTableData([]);
      setPreviewOpen(true);
    } catch {
      setPreviewTableCols([]);
      setPreviewTableData([]);
      setPreviewType('file');
      setPreviewUrl(url);
      setPreviewDownloadBlob(null);
      setPreviewOpen(true);
    } finally {
      setPreviewLoading(false);
    }
  };
  const downloadBaselineFile = async (f: any) => {
    try {
      const url = resolveAttachmentUrl(f);
      const name = String(f?.file_name || f?.name || '基线文件').trim() || '基线文件';
      if (!url) { message.warning('无可下载文件'); return; }
      const resp = await fetch(url, { credentials: 'include' as any });
      if (!resp.ok) throw new Error('下载失败');
      const blob = await resp.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(href);
    } catch {
      message.error('下载失败');
    }
  };
  const loadReadyBaselineInlineTable = useCallback(async () => {
    const first = firstReadyBaselinePreviewCandidate;
    if (!first?.file) {
      setReadyBaselineInlineCols([]);
      setReadyBaselineInlineData([]);
      setReadyBaselineDoneRowKeys(new Set());
      setReadyBaselineInlineError('');
      setReadyBaselineInlineLoading(false);
      return;
    }
    const fileName = String(first.fileName || '').toLowerCase();
    const isExcel = /\.(xlsx|xls)$/i.test(fileName);
    const isCsv = /\.csv$/i.test(fileName);
    if (!isExcel && !isCsv) {
      setReadyBaselineInlineCols([]);
      setReadyBaselineInlineData([]);
      setReadyBaselineDoneRowKeys(new Set());
      setReadyBaselineInlineError('当前已就绪基线文件不是表格类型，无法直接内嵌展示');
      setReadyBaselineInlineLoading(false);
      return;
    }
    setReadyBaselineInlineLoading(true);
    setReadyBaselineInlineError('');
    try {
      const url = resolveAttachmentUrl(first.file);
      if (!url) throw new Error('无可预览文件');
      const resp = await fetch(url, { credentials: 'include' as any });
      if (!resp.ok) throw new Error('文件加载失败');
      const blob = await resp.blob();
      let aoa: any[][] = [];
      if (isCsv) {
        const txt = await blob.text();
        const wb = XLSX.read(txt, { type: 'string' });
        const sn = String((wb.SheetNames || [])[0] || '');
        const ws = sn ? wb.Sheets[sn] : undefined;
        if (!ws) throw new Error('未读取到工作表');
        aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }) as any[][];
      } else {
        const buf = await blob.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const sn = String((wb.SheetNames || [])[0] || '');
        const ws = sn ? wb.Sheets[sn] : undefined;
        if (!ws) throw new Error('未读取到工作表');
        aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }) as any[][];
      }
      const maxCols = Math.max(0, ...aoa.map((r) => (Array.isArray(r) ? r.length : 0)));
      const headerRow = (aoa[0] || []) as any[];
      const cols = Array.from({ length: maxCols }).map((_, i) => ({
        title: String(headerRow[i] ?? '').trim() || `列${i + 1}`,
        dataIndex: `c${i}`,
        key: `c${i}`,
        width: 180,
        render: (v: any) => {
          const text = String(v ?? '');
          const isCopyCol = i === 0 || i === 2 || i === 3; // A/C/D 列
          if (!isCopyCol) return <span style={{ whiteSpace: 'pre-wrap' }}>{text}</span>;
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <span style={{ whiteSpace: 'pre-wrap' }}>{text}</span>
              {text ? (
                <Button
                  size="small"
                  type="link"
                  style={{ padding: 0, height: 'auto' }}
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(text);
                      message.success('已复制');
                    } catch {
                      message.error('复制失败');
                    }
                  }}
                >
                  <CopyOutlined />
                </Button>
              ) : null}
            </span>
          );
        },
      }));
      const rows = aoa.slice(1).map((row, ridx) => {
        const rec: any = { __k: ridx };
        for (let i = 0; i < maxCols; i += 1) rec[`c${i}`] = row?.[i] ?? '';
        return rec;
      });
      setReadyBaselineInlineCols(cols);
      setReadyBaselineInlineData(rows);
      setReadyBaselineDoneRowKeys(new Set());
      setReadyBaselineInlinePage(1);
    } catch (e: any) {
      setReadyBaselineInlineCols([]);
      setReadyBaselineInlineData([]);
      setReadyBaselineDoneRowKeys(new Set());
      setReadyBaselineInlineError(e?.message || '表格加载失败');
    } finally {
      setReadyBaselineInlineLoading(false);
    }
  }, [firstReadyBaselinePreviewCandidate]);
  useEffect(() => {
    void loadReadyBaselineInlineTable();
  }, [loadReadyBaselineInlineTable]);
  const downloadWaybillAttachment = async (f: any) => {
    try {
      const url = resolveAttachmentUrl(f);
      const name = String(f?.file_name || f?.name || f?.file || f?.file_path || '运单附件').trim() || '运单附件';
      if (!url) { message.warning('无可下载文件'); return; }
      const resp = await fetch(url, { credentials: 'include' as any });
      if (!resp.ok) throw new Error('下载失败');
      const blob = await resp.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(href);
    } catch {
      message.error('下载失败');
    }
  };
  const downloadTransportLabelAttachment = async (f: any) => {
    try {
      const url = resolveAttachmentUrl(f);
      const name = String(f?.file_name || f?.name || f?.file || f?.file_path || '运输标签附件').trim() || '运输标签附件';
      if (!url) { message.warning('无可下载文件'); return; }
      const resp = await fetch(url, { credentials: 'include' as any });
      if (!resp.ok) throw new Error('下载失败');
      const blob = await resp.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(href);
    } catch {
      message.error('下载失败');
    }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = Array.isArray(transportLabelAttachments) ? transportLabelAttachments : [];
        const pdfs = list.filter((att: any) => {
          const n = String(att?.file_name || att?.name || att?.file || att?.file_path || '').toLowerCase();
          return /\.pdf$/i.test(n);
        });
        const images = list.filter((att: any) => {
          const n = String(att?.file_name || att?.name || att?.file || att?.file_path || '').toLowerCase();
          return /\.(png|jpg|jpeg|gif|webp|bmp|svg|apng|ico)$/i.test(n);
        });
        if (cancelled) return;
        setTransportLabelPdfFileCount(pdfs.length);
        setTransportLabelImageFileCount(images.length);
        if (pdfs.length === 0) {
          setTransportLabelPdfPageTotal(0);
          return;
        }
        let totalPages = 0;
        for (const att of pdfs) {
          if (cancelled) return;
          try {
            const url = resolveAttachmentUrl(att);
            if (!url) continue;
            const resp = await fetch(url, { credentials: 'include' as any });
            if (!resp.ok) continue;
            const buf = await resp.arrayBuffer();
            const doc = await PDFDocument.load(buf);
            totalPages += Number(doc.getPageCount() || 0);
          } catch {}
        }
        if (!cancelled) setTransportLabelPdfPageTotal(totalPages);
      } catch {
        if (!cancelled) {
          setTransportLabelPdfFileCount(0);
          setTransportLabelPdfPageTotal(0);
          setTransportLabelImageFileCount(0);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [transportLabelAttachments]);
  const closePreview = () => {
    tlPrintLog('closePreview:enter', {
      previewOpen,
      previewType,
      hasPreviewUrl: !!previewUrl,
      hasPopup: !!transportLabelPrintPopupRef.current,
    });
    try {
      if (previewUrl && previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    } catch {}
    try {
      if (transportLabelPrintPopupRef.current && !transportLabelPrintPopupRef.current.closed) {
        transportLabelPrintPopupRef.current.close();
      }
    } catch {}
    transportLabelPrintPopupRef.current = null;
    setPreviewOpen(false);
    setPreviewUrl('');
    setPreviewPrintText('');
    setPreviewTableCols([]);
    setPreviewTableData([]);
    setPreviewDownloadName('');
    setPreviewDownloadBlob(null);
    setPreviewDownloadUrl('');
    tlPrintLog('closePreview:exit');
  };
  const downloadPreview = async () => {
    try {
      const fileName = String(previewDownloadName || '附件').trim() || '附件';
      if (previewDownloadBlob) {
        const href = URL.createObjectURL(previewDownloadBlob);
        const a = document.createElement('a');
        a.href = href;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(href);
        return;
      }
      if (previewDownloadUrl) {
        const resp = await fetch(previewDownloadUrl, { credentials: 'include' as any });
        if (!resp.ok) throw new Error('下载失败');
        const blob = await resp.blob();
        const href = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = href;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(href);
        return;
      }
    } catch {
      message.warning('下载失败，请稍后重试');
    }
  };
  const printOtherAttachmentFromList = async (att: any, fileName: string, printText: string) => {
    await openAttachmentPreview(att, fileName, printText);
    const t = new Date();
    const rec = {
      id: `local-other-attachment-${t.getTime()}-${Math.random()}`,
      time: t.toISOString(),
      event: '打印其他附件',
      action: '打印其他附件',
      container_code: '-',
      target: String(fileName || '-').trim() || '-',
      qty: '',
      result: '成功',
      operator_name: '-',
      remark: printText || '-',
    };
    setLocalOperationRecords((prev) => ([rec, ...(Array.isArray(prev) ? prev : [])]));
  };
  const normalizeBarcodeValue = (v: any): string => String(v ?? '').replace(/\s+/g, '').trim().toUpperCase();
  const parseBaselineAcdMap = useCallback(async (f: any): Promise<{ colC: Map<string, string>; colD: Map<string, string> }> => {
    const out = { colC: new Map<string, string>(), colD: new Map<string, string>() };
    const url = resolveAttachmentUrl(f);
    if (!url) return out;
    const resp = await fetch(url, { credentials: 'include' as any });
    if (!resp.ok) return out;
    const blob = await resp.blob();
    const fileNameLower = String(f?.file_name || f?.name || f?.file || '').toLowerCase();
    let aoa: any[][] = [];
    if (/\.csv$/i.test(fileNameLower)) {
      const txt = await blob.text();
      const wb = XLSX.read(txt, { type: 'string' });
      const sn = String((wb.SheetNames || [])[0] || '');
      const ws = sn ? wb.Sheets[sn] : undefined;
      if (!ws) return out;
      aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }) as any[][];
    } else {
      const buf = await blob.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const sn = String((wb.SheetNames || [])[0] || '');
      const ws = sn ? wb.Sheets[sn] : undefined;
      if (!ws) return out;
      aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }) as any[][];
    }
    for (const row of (Array.isArray(aoa) ? aoa.slice(1) : [])) {
      if (!Array.isArray(row)) continue;
      const aVal = String(row?.[0] ?? '').trim();
      if (!aVal) continue;
      const cRaw = row?.[2];
      const dRaw = row?.[3];
      const cNorm = normalizeBarcodeValue(cRaw);
      const dNorm = normalizeBarcodeValue(dRaw);
      if (cNorm && !out.colC.has(cNorm)) out.colC.set(cNorm, aVal);
      if (dNorm && !out.colD.has(dNorm)) out.colD.set(dNorm, aVal);
    }
    return out;
  }, []);
  const parseBaselineTargetLabelMap = useCallback(async (f: any): Promise<{ map: Map<string, Set<string>>; matchableRowCount: number }> => {
    const out = new Map<string, Set<string>>();
    let matchableRowCount = 0;
    const url = resolveAttachmentUrl(f);
    if (!url) return { map: out, matchableRowCount };
    const resp = await fetch(url, { credentials: 'include' as any });
    if (!resp.ok) return { map: out, matchableRowCount };
    const blob = await resp.blob();
    const fileNameLower = String(f?.file_name || f?.name || f?.file || '').toLowerCase();
    let aoa: any[][] = [];
    if (/\.csv$/i.test(fileNameLower)) {
      const txt = await blob.text();
      const wb = XLSX.read(txt, { type: 'string' });
      const sn = String((wb.SheetNames || [])[0] || '');
      const ws = sn ? wb.Sheets[sn] : undefined;
      if (!ws) return { map: out, matchableRowCount };
      aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }) as any[][];
    } else {
      const buf = await blob.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const sn = String((wb.SheetNames || [])[0] || '');
      const ws = sn ? wb.Sheets[sn] : undefined;
      if (!ws) return { map: out, matchableRowCount };
      aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }) as any[][];
    }
    for (const row of (Array.isArray(aoa) ? aoa.slice(1) : [])) {
      if (!Array.isArray(row)) continue;
      const target = normalizeBarcodeValue(row?.[0]);
      if (!target) continue;
      const cNorm = normalizeBarcodeValue(row?.[2]);
      const dNorm = normalizeBarcodeValue(row?.[3]);
      if (cNorm || dNorm) {
        matchableRowCount += 1;
      }
      const set = out.get(target) || new Set<string>();
      if (cNorm) set.add(cNorm);
      if (dNorm) set.add(dNorm);
      out.set(target, set);
    }
    return { map: out, matchableRowCount };
  }, []);
  const parseWaybillByFileBlob = useCallback(async (fileBlob: Blob, fileName: string): Promise<any> => {
    const fd = new FormData();
    fd.append('file', fileBlob, fileName || 'attachment.pdf');
    try {
      const resp = await request.post('/api/v1/logistics/waybill-parse/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      } as any);
      return (resp as any)?.data?.parsed || (resp as any)?.parsed || {};
    } catch {
      return {};
    }
  }, []);
  const fileBlobToBase64 = useCallback(async (blob: Blob): Promise<string> => (
    await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => {
        const s = String(r.result || '');
        resolve(s.includes(',') ? s.split(',')[1]! : s);
      };
      r.onerror = () => reject(new Error('文件读取失败'));
      r.readAsDataURL(blob);
    })
  ), []);
  const overlayPdfPageBlobByText = useCallback(async (pageBlob: Blob, text: string): Promise<Blob> => {
    const base64 = await fileBlobToBase64(pageBlob);
    const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000').replace(/\/$/, '');
    const overlayUrl = `${apiBase}/api/v1/logistics/label-overlay/`;
    const token = localStorage.getItem('token');
    const configObj: Record<string, any> = {
      layout_mode: 'sliding_window',
      content_source: 'custom_text',
      show_title: false,
      font_size: 42,
      font_bold: true,
      text_color: '#c41e3a',
      max_text_length: 50,
      max_label_area: 80000,
      blank_ratio_threshold: 0.98,
      blank_ratio_step: 2,
      bottom_band_ratio: 0.25,
      side_band_ratio: 0.2,
      slide_step: 30,
      margin: 24,
      custom_text: text,
    };
    const resp = await fetch(overlayUrl, {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        file_base64: base64,
        file_name: 'transport_label_page.pdf',
        config: configObj,
      }),
    });
    if (!resp.ok) throw new Error('叠字失败');
    return await resp.blob();
  }, [fileBlobToBase64]);
  const overlayPdfPageBlobByTextForImage = useCallback(async (pageBlob: Blob, text: string): Promise<Blob> => {
    const base64 = await fileBlobToBase64(pageBlob);
    const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000').replace(/\/$/, '');
    const overlayUrl = `${apiBase}/api/v1/logistics/label-overlay/`;
    const token = localStorage.getItem('token');
    let pageW = 0;
    let pageH = 0;
    try {
      const buf = await pageBlob.arrayBuffer();
      const d = await PDFDocument.load(buf);
      const p = d.getPage(0);
      pageW = Number(p?.getWidth?.() || 0);
      pageH = Number(p?.getHeight?.() || 0);
    } catch {
      pageW = 0;
      pageH = 0;
    }
    const maxSide = Math.max(pageW, pageH, 1);
    const minSide = Math.max(1, Math.min(pageW || maxSide, pageH || maxSide));
    // 图片页常常尺寸非常大，按页面尺寸动态放大字体与标注面积，避免叠字显得过小。
    const fontSize = Math.max(42, Math.min(220, Math.round(maxSide / 16)));
    const maxLabelArea = Math.max(80000, Math.min(900000, Math.round((maxSide * minSide) / 6)));
    const margin = Math.max(24, Math.min(80, Math.round(minSide / 22)));
    const configObj: Record<string, any> = {
      layout_mode: 'sliding_window',
      content_source: 'custom_text',
      show_title: false,
      font_size: fontSize,
      font_bold: true,
      text_color: '#c41e3a',
      max_text_length: 50,
      max_label_area: maxLabelArea,
      blank_ratio_threshold: 0.98,
      blank_ratio_step: 2,
      bottom_band_ratio: 0.25,
      side_band_ratio: 0.2,
      slide_step: 30,
      margin,
      custom_text: text,
    };
    const resp = await fetch(overlayUrl, {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        file_base64: base64,
        file_name: 'transport_label_image_page.pdf',
        config: configObj,
      }),
    });
    if (!resp.ok) throw new Error('叠字失败');
    return await resp.blob();
  }, [fileBlobToBase64]);
  const mergeTransportLabelPdfWithBaseline = useCallback(async (pdfList: any[], baselineMap: { colC: Map<string, string>; colD: Map<string, string> }): Promise<Blob | null> => {
    if (!Array.isArray(pdfList) || pdfList.length <= 0) return null;
    const mergedDoc = await PDFDocument.create();
    for (const item of pdfList) {
      const oneUrl = resolveAttachmentUrl(item);
      if (!oneUrl) continue;
      const fileName = String(item?.file_name || item?.name || item?.file || item?.file_path || 'transport_label.pdf');
      const resp = await fetch(oneUrl, { credentials: 'include' as any });
      if (!resp.ok) continue;
      const originalBlob = await resp.blob();
      const originalBuf = await originalBlob.arrayBuffer();
      const srcDoc = await PDFDocument.load(originalBuf);
      const parsed = await parseWaybillByFileBlob(originalBlob, fileName);
      const pages = Array.isArray(parsed?.pages) ? parsed.pages : [];
      const pageCount = Number(srcDoc.getPageCount() || 0);
      for (let i = 0; i < pageCount; i += 1) {
        const singleDoc = await PDFDocument.create();
        const copied = await singleDoc.copyPages(srcDoc, [i]);
        copied.forEach((p) => singleDoc.addPage(p));
        const singleBytes = await singleDoc.save();
        const singleBlob = new Blob([singleBytes as any], { type: 'application/pdf' });
        const pInfo = pages?.[i] || {};
        const bNums = Array.isArray(pInfo?.print_simulation?.barcode_numbers) ? pInfo.print_simulation.barcode_numbers : [];
        const tNums = Array.isArray(pInfo?.tracking_numbers) ? pInfo.tracking_numbers : [];
        const candidates: string[] = [];
        [...bNums, ...tNums].forEach((v: any) => {
          const n = normalizeBarcodeValue(v);
          if (n && !candidates.includes(n)) candidates.push(n);
        });
        let overlayText = '';
        for (const c of candidates) {
          overlayText = baselineMap.colC.get(c) || baselineMap.colD.get(c) || '';
          if (overlayText) break;
        }
        let finalPageBlob = singleBlob;
        if (overlayText) {
          try {
            finalPageBlob = await overlayPdfPageBlobByText(singleBlob, overlayText);
          } catch {
            finalPageBlob = singleBlob;
          }
        }
        const finalPageBuf = await finalPageBlob.arrayBuffer();
        const finalDoc = await PDFDocument.load(finalPageBuf);
        const finalIndices = finalDoc.getPageIndices();
        const finalCopied = await mergedDoc.copyPages(finalDoc, finalIndices);
        finalCopied.forEach((p) => mergedDoc.addPage(p));
      }
    }
    if (mergedDoc.getPageCount() <= 0) return null;
    const bytes = await mergedDoc.save();
    const normalizedBytes = new Uint8Array(bytes.length);
    normalizedBytes.set(bytes);
    return new Blob([normalizedBytes.buffer], { type: 'application/pdf' });
  }, [overlayPdfPageBlobByText, parseWaybillByFileBlob]);
  const imageBlobToSinglePagePdfBlob = useCallback(async (imgBlob: Blob, fileName: string): Promise<Blob | null> => {
    try {
      const lower = String(fileName || '').toLowerCase();
      const srcBuf = await imgBlob.arrayBuffer();
      const srcBytes = new Uint8Array(srcBuf);
      const toPngViaCanvas = async (blob: Blob): Promise<Uint8Array | null> => {
        try {
          const objectUrl = URL.createObjectURL(blob);
          const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const el = new Image();
            el.onload = () => resolve(el);
            el.onerror = () => reject(new Error('图片加载失败'));
            el.src = objectUrl;
          });
          const cvs = document.createElement('canvas');
          cvs.width = Math.max(1, Math.floor(Number(img.width || 0) || 1));
          cvs.height = Math.max(1, Math.floor(Number(img.height || 0) || 1));
          const ctx = cvs.getContext('2d');
          if (!ctx) {
            URL.revokeObjectURL(objectUrl);
            return null;
          }
          ctx.drawImage(img, 0, 0, cvs.width, cvs.height);
          const pngBlob = await new Promise<Blob | null>((resolve) => cvs.toBlob((b) => resolve(b), 'image/png'));
          URL.revokeObjectURL(objectUrl);
          if (!pngBlob) return null;
          const pngBuf = await pngBlob.arrayBuffer();
          return new Uint8Array(pngBuf);
        } catch {
          return null;
        }
      };
      const pdfDoc = await PDFDocument.create();
      let embeddedImg: any = null;
      if (/\.png$/i.test(lower) || String(imgBlob.type || '').toLowerCase().includes('png')) {
        embeddedImg = await pdfDoc.embedPng(srcBytes);
      } else if (/\.(jpg|jpeg)$/i.test(lower) || String(imgBlob.type || '').toLowerCase().includes('jpeg') || String(imgBlob.type || '').toLowerCase().includes('jpg')) {
        embeddedImg = await pdfDoc.embedJpg(srcBytes);
      } else {
        const pngBytes = await toPngViaCanvas(imgBlob);
        if (!pngBytes) return null;
        embeddedImg = await pdfDoc.embedPng(pngBytes);
      }
      if (!embeddedImg) return null;
      // 固定 4×6 英寸页面（PDF point: 72/inch => 288×432）。
      const pageW = 4 * 72;
      const pageH = 6 * 72;
      const imgW = Number(embeddedImg.width || 0) || 1;
      const imgH = Number(embeddedImg.height || 0) || 1;
      const scale = Math.min(pageW / imgW, pageH / imgH);
      const drawW = Math.max(1, imgW * scale);
      const drawH = Math.max(1, imgH * scale);
      const drawX = (pageW - drawW) / 2;
      const drawY = (pageH - drawH) / 2;
      const page = pdfDoc.addPage([pageW, pageH]);
      (page.drawImage as (img: any, opts: any) => void)(embeddedImg, { x: drawX, y: drawY, width: drawW, height: drawH });
      const outBytes = await pdfDoc.save();
      const normalizedBytes = new Uint8Array(outBytes.length);
      normalizedBytes.set(outBytes);
      return new Blob([normalizedBytes.buffer], { type: 'application/pdf' });
    } catch {
      return null;
    }
  }, []);
  const mergeTransportLabelImagesForPrint = useCallback(async (
    imageList: any[],
    baselineMap?: { colC: Map<string, string>; colD: Map<string, string> } | null,
  ): Promise<Blob | null> => {
    if (!Array.isArray(imageList) || imageList.length <= 0) return null;
    const mergedDoc = await PDFDocument.create();
    for (const item of imageList) {
      const oneUrl = resolveAttachmentUrl(item);
      if (!oneUrl) continue;
      const fileName = String(item?.file_name || item?.name || item?.file || item?.file_path || 'transport_label_image');
      const resp = await fetch(oneUrl, { credentials: 'include' as any });
      if (!resp.ok) continue;
      const imageBlob = await resp.blob();
      let singlePdfBlob = await imageBlobToSinglePagePdfBlob(imageBlob, fileName);
      if (!singlePdfBlob) continue;
      if (baselineMap) {
        try {
          const parsed = await parseWaybillByFileBlob(imageBlob, fileName);
          const pInfo = (Array.isArray(parsed?.pages) ? parsed.pages[0] : {}) || {};
          const bNums = Array.isArray(pInfo?.print_simulation?.barcode_numbers) ? pInfo.print_simulation.barcode_numbers : [];
          const tNums = Array.isArray(pInfo?.tracking_numbers) ? pInfo.tracking_numbers : [];
          const candidates: string[] = [];
          [...bNums, ...tNums].forEach((v: any) => {
            const n = normalizeBarcodeValue(v);
            if (n && !candidates.includes(n)) candidates.push(n);
          });
          let overlayText = '';
          for (const c of candidates) {
            overlayText = baselineMap.colC.get(c) || baselineMap.colD.get(c) || '';
            if (overlayText) break;
          }
          if (overlayText) {
            try {
              singlePdfBlob = await overlayPdfPageBlobByTextForImage(singlePdfBlob, overlayText);
            } catch {}
          }
        } catch {}
      }
      const finalBuf = await singlePdfBlob.arrayBuffer();
      const finalDoc = await PDFDocument.load(finalBuf);
      const finalIndices = finalDoc.getPageIndices();
      const copiedPages = await mergedDoc.copyPages(finalDoc, finalIndices);
      copiedPages.forEach((p) => mergedDoc.addPage(p));
    }
    if (mergedDoc.getPageCount() <= 0) return null;
    const mergedBytes = await mergedDoc.save();
    const normalized = new Uint8Array(mergedBytes.length);
    normalized.set(mergedBytes);
    return new Blob([normalized.buffer], { type: 'application/pdf' });
  }, [imageBlobToSinglePagePdfBlob, overlayPdfPageBlobByTextForImage, parseWaybillByFileBlob]);
  const printTransportLabelImagesDirectly = async () => {
    if (printTransportLabelImageRunning) return;
    const list = Array.isArray(transportLabelAttachments) ? transportLabelAttachments : [];
    const imageList = list.filter((att: any) => {
      const n = String(att?.file_name || att?.name || att?.file || att?.file_path || '').toLowerCase();
      return /\.(png|jpg|jpeg|gif|webp|bmp|svg|apng|ico)$/i.test(n);
    });
    if (imageList.length <= 0) return void message.warning('暂无可打印的图片运输标签附件');
    setPrintTransportLabelImageRunning(true);
    let fileName = `运输标签图片合并(${imageList.length}个).pdf`;
    let fallbackUrl = '';
    try {
      setPreviewLoading(true);
      const hasReadyBaseline = !!firstReadyBaselinePreviewCandidate?.file;
      let baselineMap: { colC: Map<string, string>; colD: Map<string, string> } | null = null;
      if (hasReadyBaseline) {
        try {
          baselineMap = await parseBaselineAcdMap(firstReadyBaselinePreviewCandidate.file);
        } catch {
          baselineMap = { colC: new Map(), colD: new Map() };
        }
      }
      const mergedBlob = await mergeTransportLabelImagesForPrint(imageList, baselineMap);
      if (!mergedBlob) {
        message.warning('图片合并失败，请稍后重试');
        return;
      }
      const objectUrl = URL.createObjectURL(mergedBlob);
      fallbackUrl = objectUrl;
      setPreviewTitle(fileName);
      setPreviewPrintText('');
      setPreviewDownloadName(fileName);
      setPreviewDownloadBlob(mergedBlob);
      setPreviewDownloadUrl('');
      setPreviewType('file');
      setPreviewTableCols([]);
      setPreviewTableData([]);
      setPreviewUrl(objectUrl);
      setPreviewOpen(true);
    } catch {
      message.warning('图片合并失败，请稍后重试');
      return;
    } finally {
      setPreviewLoading(false);
      setPrintTransportLabelImageRunning(false);
    }
    const t = new Date();
    const rec = {
      id: `local-print-transport-label-image-${t.getTime()}-${Math.random()}`,
      time: t.toISOString(),
      event: '打印运输标签(图片)',
      action: '打印运输标签(图片)',
      container_code: '-',
      target: fileName,
      qty: '',
      result: '成功',
      operator_name: '-',
      remark: '-',
    };
    setLocalOperationRecords((prev) => ([rec, ...(Array.isArray(prev) ? prev : [])]));
    setTimeout(() => {
      let finished = false;
      let closedCheckTimer: any = null;
      let fallbackTimer: any = null;
      let quickCloseTimer: any = null;
      let focusFallbackBound = false;
      const listeners: Array<{ target: any; handler: any }> = [];
      const mediaListeners: Array<{ mql: any; handler: any }> = [];
      const cleanupAndClose = (source: string) => {
        if (finished) return;
        finished = true;
        try {
          listeners.forEach(({ target, handler }) => {
            try { target?.removeEventListener?.('afterprint', handler as any); } catch {}
          });
        } catch {}
        try { if (closedCheckTimer) clearInterval(closedCheckTimer); } catch {}
        try { if (fallbackTimer) clearTimeout(fallbackTimer); } catch {}
        try { if (quickCloseTimer) clearTimeout(quickCloseTimer); } catch {}
        try {
          mediaListeners.forEach(({ mql, handler }) => {
            try {
              if (mql?.removeEventListener) mql.removeEventListener('change', handler as any);
              else if (mql?.removeListener) mql.removeListener(handler as any);
            } catch {}
          });
        } catch {}
        if (focusFallbackBound) {
          try { window.removeEventListener('focus', onFocusFallback as any); } catch {}
          focusFallbackBound = false;
        }
        closePreview();
      };
      const onFocusFallback = () => {
        try { window.removeEventListener('focus', onFocusFallback as any); } catch {}
        focusFallbackBound = false;
        setTimeout(() => { cleanupAndClose('window-focus-fallback'); }, 180);
      };
      const addAfterPrintListener = (target: any) => {
        if (!target?.addEventListener) return;
        const handler = () => { cleanupAndClose('afterprint'); };
        try {
          target.addEventListener('afterprint', handler as any);
          listeners.push({ target, handler });
        } catch {}
      };
      const addPrintMediaEndListener = (targetWindow: any, targetType: string) => {
        try {
          if (!targetWindow?.matchMedia) return;
          const mql = targetWindow.matchMedia('print');
          const handler = (e: any) => {
            if (e?.matches === true) {
              try { if (quickCloseTimer) clearTimeout(quickCloseTimer); } catch {}
              quickCloseTimer = setTimeout(() => { cleanupAndClose(`timeout-1.8s-${targetType}`); }, 1800);
            } else if (e?.matches === false) {
              cleanupAndClose(`matchMedia-${targetType}-print-end`);
            }
          };
          if (mql?.addEventListener) mql.addEventListener('change', handler as any);
          else if (mql?.addListener) mql.addListener(handler as any);
          mediaListeners.push({ mql, handler });
        } catch {}
      };
      try {
        addAfterPrintListener(window);
        addPrintMediaEndListener(window, 'window');
        const frameWin = previewFrameRef.current?.contentWindow;
        if (frameWin) {
          addAfterPrintListener(frameWin);
          addPrintMediaEndListener(frameWin, 'iframe');
          try {
            window.addEventListener('focus', onFocusFallback as any, { once: true } as any);
            focusFallbackBound = true;
          } catch {}
          fallbackTimer = setTimeout(() => { cleanupAndClose('timeout-15s-frame'); }, 15000);
          frameWin.focus();
          frameWin.print();
          return;
        }
        if (!fallbackUrl) return;
        const w = window.open(fallbackUrl, '_blank');
        if (!w) {
          message.warning('请允许弹窗后重试打印');
          return;
        }
        transportLabelPrintPopupRef.current = w;
        addAfterPrintListener(w);
        addPrintMediaEndListener(w, 'popup');
        try {
          window.addEventListener('focus', onFocusFallback as any, { once: true } as any);
          focusFallbackBound = true;
        } catch {}
        fallbackTimer = setTimeout(() => { cleanupAndClose('timeout-15s-popup'); }, 15000);
        closedCheckTimer = setInterval(() => {
          try {
            if (w.closed) cleanupAndClose('popup-closed-check');
          } catch {}
        }, 300);
        w.focus();
        w.print?.();
      } catch {
        message.warning('打印失败，请稍后重试');
      }
    }, 260);
  };
  const printTransportLabelDirectly = async () => {
    if (printTransportLabelPdfRunning) return;
    const list = Array.isArray(transportLabelAttachments) ? transportLabelAttachments : [];
    const pdfList = list.filter((att: any) => {
      const n = String(att?.file_name || att?.name || att?.file || att?.file_path || '').toLowerCase();
      return /\.pdf$/i.test(n);
    });
    if (pdfList.length <= 0) return void message.warning('暂无可打印的PDF运输标签附件');
    setPrintTransportLabelPdfRunning(true);
    let selected: any = pdfList[0];
    let fileName = String(selected?.file_name || selected?.name || selected?.file || selected?.file_path || '运输标签附件');
    let fallbackUrl = resolveAttachmentUrl(selected);
    tlPrintLog('print:start', { fileName, attachmentsCount: transportLabelAttachments.length, pdfCount: pdfList.length });
    try {
      setPreviewLoading(true);
      let mergedBlob: Blob | null = null;
      const hasReadyBaseline = !!firstReadyBaselinePreviewCandidate?.file;
      if (!hasReadyBaseline) {
        if (pdfList.length <= 1) {
          await openAttachmentPreview(selected, fileName, '');
        } else {
          const mergedDoc = await PDFDocument.create();
          for (const item of pdfList) {
            const oneUrl = resolveAttachmentUrl(item);
            if (!oneUrl) continue;
            const resp = await fetch(oneUrl, { credentials: 'include' as any });
            if (!resp.ok) continue;
            const buf = await resp.arrayBuffer();
            const srcDoc = await PDFDocument.load(buf);
            const pageIndices = srcDoc.getPageIndices();
            const copiedPages = await mergedDoc.copyPages(srcDoc, pageIndices);
            copiedPages.forEach((p) => mergedDoc.addPage(p));
          }
          const bytes = await mergedDoc.save();
          const normalizedBytes = new Uint8Array(bytes.length);
          normalizedBytes.set(bytes);
          mergedBlob = new Blob([normalizedBytes.buffer], { type: 'application/pdf' });
        }
      } else {
        let baselineMap: { colC: Map<string, string>; colD: Map<string, string> } = { colC: new Map(), colD: new Map() };
        try {
          baselineMap = await parseBaselineAcdMap(firstReadyBaselinePreviewCandidate.file);
        } catch {}
        mergedBlob = await mergeTransportLabelPdfWithBaseline(pdfList, baselineMap);
      }
      if (mergedBlob) {
        const objectUrl = URL.createObjectURL(mergedBlob);
        fileName = `运输标签合并(${pdfList.length}个).pdf`;
        fallbackUrl = objectUrl;
        setPreviewTitle(fileName);
        setPreviewPrintText('');
        setPreviewDownloadName(fileName);
        setPreviewDownloadBlob(mergedBlob);
        setPreviewDownloadUrl('');
        setPreviewType('file');
        setPreviewTableCols([]);
        setPreviewTableData([]);
        setPreviewUrl(objectUrl);
        setPreviewOpen(true);
      }
      if (!mergedBlob && pdfList.length > 1) {
        message.warning('合并PDF失败，请稍后重试');
        return;
      }
    } catch {
      message.warning('合并PDF失败，请稍后重试');
      return;
    } finally {
      setPreviewLoading(false);
      setPrintTransportLabelPdfRunning(false);
    }
    tlPrintLog('print:after-open-preview', {
      previewOpen,
      previewType,
      hasPreviewUrl: !!previewUrl,
      hasFrame: !!previewFrameRef.current?.contentWindow,
    });
    const t = new Date();
    const rec = {
      id: `local-print-transport-label-${t.getTime()}-${Math.random()}`,
      time: t.toISOString(),
      event: '打印运输标签',
      action: '打印运输标签',
      container_code: '-',
      target: fileName,
      qty: '',
      result: '成功',
      operator_name: '-',
      remark: '-',
    };
    setLocalOperationRecords((prev) => ([rec, ...(Array.isArray(prev) ? prev : [])]));
    tlPrintLog('print:resolved-fallback-url', { hasFallbackUrl: !!fallbackUrl, fallbackUrl });
    setTimeout(() => {
      let finished = false;
      let closedCheckTimer: any = null;
      let fallbackTimer: any = null;
      let quickCloseTimer: any = null;
      let focusFallbackBound = false;
      const listeners: Array<{ target: any; handler: any }> = [];
      const mediaListeners: Array<{ mql: any; handler: any }> = [];
      const cleanupAndClose = (source: string) => {
        if (finished) return;
        finished = true;
        tlPrintLog('cleanupAndClose:triggered', { source });
        try {
          listeners.forEach(({ target, handler }) => {
            try { target?.removeEventListener?.('afterprint', handler as any); } catch {}
          });
        } catch {}
        try { if (closedCheckTimer) clearInterval(closedCheckTimer); } catch {}
        try { if (fallbackTimer) clearTimeout(fallbackTimer); } catch {}
        try { if (quickCloseTimer) clearTimeout(quickCloseTimer); } catch {}
        try {
          mediaListeners.forEach(({ mql, handler }) => {
            try {
              if (mql?.removeEventListener) mql.removeEventListener('change', handler as any);
              else if (mql?.removeListener) mql.removeListener(handler as any);
            } catch {}
          });
        } catch {}
        if (focusFallbackBound) {
          try { window.removeEventListener('focus', onFocusFallback as any); } catch {}
          focusFallbackBound = false;
        }
        closePreview();
      };
      const onFocusFallback = () => {
        tlPrintLog('focusFallback:fired');
        try { window.removeEventListener('focus', onFocusFallback as any); } catch {}
        focusFallbackBound = false;
        setTimeout(() => { cleanupAndClose('window-focus-fallback'); }, 180);
      };
      const addAfterPrintListener = (target: any) => {
        if (!target?.addEventListener) return;
        const handler = () => {
          tlPrintLog('afterprint:fired', {
            targetType: target === window ? 'window' : (target === previewFrameRef.current?.contentWindow ? 'iframe' : 'popup'),
          });
          cleanupAndClose('afterprint');
        };
        try {
          target.addEventListener('afterprint', handler as any);
          listeners.push({ target, handler });
          tlPrintLog('afterprint:listener-bound', {
            targetType: target === window ? 'window' : (target === previewFrameRef.current?.contentWindow ? 'iframe' : 'popup'),
          });
        } catch {}
      };
      const addPrintMediaEndListener = (targetWindow: any, targetType: string) => {
        try {
          if (!targetWindow?.matchMedia) return;
          const mql = targetWindow.matchMedia('print');
          const handler = (e: any) => {
            // e.matches: true 进入打印态；false 退出打印态
            if (e?.matches === true) {
              tlPrintLog('matchMedia-print-start:fired', { targetType });
              try { if (quickCloseTimer) clearTimeout(quickCloseTimer); } catch {}
              quickCloseTimer = setTimeout(() => { cleanupAndClose(`timeout-1.8s-${targetType}`); }, 1800);
            } else if (e?.matches === false) {
              tlPrintLog('matchMedia-print-end:fired', { targetType });
              cleanupAndClose(`matchMedia-${targetType}-print-end`);
            }
          };
          if (mql?.addEventListener) mql.addEventListener('change', handler as any);
          else if (mql?.addListener) mql.addListener(handler as any);
          mediaListeners.push({ mql, handler });
          tlPrintLog('matchMedia-print-end:listener-bound', { targetType });
        } catch {}
      };
      try {
        addAfterPrintListener(window);
        addPrintMediaEndListener(window, 'window');
        const frameWin = previewFrameRef.current?.contentWindow;
        if (frameWin) {
          addAfterPrintListener(frameWin);
          addPrintMediaEndListener(frameWin, 'iframe');
          try {
            window.addEventListener('focus', onFocusFallback as any, { once: true } as any);
            focusFallbackBound = true;
            tlPrintLog('focusFallback:listener-bound');
          } catch {}
          fallbackTimer = setTimeout(() => { cleanupAndClose('timeout-15s-frame'); }, 15000);
          tlPrintLog('print:use-iframe-print');
          frameWin.focus();
          frameWin.print();
          return;
        }
        if (!fallbackUrl) return;
        const w = window.open(fallbackUrl, '_blank');
        if (!w) {
          tlPrintLog('print:popup-blocked');
          message.warning('请允许弹窗后重试打印');
          return;
        }
        transportLabelPrintPopupRef.current = w;
        addAfterPrintListener(w);
        addPrintMediaEndListener(w, 'popup');
        try {
          window.addEventListener('focus', onFocusFallback as any, { once: true } as any);
          focusFallbackBound = true;
          tlPrintLog('focusFallback:listener-bound');
        } catch {}
        fallbackTimer = setTimeout(() => { cleanupAndClose('timeout-15s-popup'); }, 15000);
        tlPrintLog('print:use-popup-print');
        closedCheckTimer = setInterval(() => {
          try {
            if (w.closed) {
              tlPrintLog('popup:closed-detected');
              cleanupAndClose('popup-closed-check');
            }
          } catch {}
        }, 300);
        w.focus();
        w.print?.();
      } catch {
        tlPrintLog('print:exception');
        message.warning('打印失败，请稍后重试');
      }
    }, 260);
  };
  const openContentModal = (unitCode: string, lines: string[]) => {
    setContentModalUnitCode(String(unitCode || '-'));
    setContentModalLines(Array.isArray(lines) ? lines : []);
    setContentModalOpen(true);
  };
  const copyContentModalInfo = async () => {
    try {
      const lines = Array.isArray(contentModalLines) ? contentModalLines : [];
      const text = [
        '库存单元内容物明细',
        `库存单元系统编码：${contentModalUnitCode || '-'}`,
        ...lines.map((line, idx) => `${idx + 1}. ${line}`),
      ].join('\n');
      await navigator.clipboard.writeText(text);
      message.success('已复制');
    } catch {
      message.error('复制失败');
    }
  };
  const answers = (instructionDetail?.customer_answers || {}) as any;
  const getByKey = (key: string): any => answers?.[key];
  const displayVal = (v: any): string => {
    if (v === undefined || v === null) return '-';
    const s = String(v).trim();
    return s ? s : '-';
  };
  // 仅按你给定的问题 key 取值，不做兜底/猜测
  const repackRequiredText = displayVal(getByKey('is_repack_required'));
  const isNoRepackMode = useMemo(() => {
    const raw = String(repackRequiredText || '').trim().toLowerCase();
    return raw.includes('无需重新包装') || raw === 'false' || raw === '0' || raw === 'no' || raw === 'n';
  }, [repackRequiredText]);
  useEffect(() => {
    if (!isNoRepackMode) return;
    setCollapsedSections((prev) => {
      if (prev.pack_ops && prev.progress) return prev;
      return {
        ...prev,
        pack_ops: true,
        progress: true,
      };
    });
  }, [isNoRepackMode]);
  const mixPackText = displayVal(getByKey('mix_pack'));
  const packingModeText = displayVal(getByKey('repack_method'));
  const cartonSourceText = displayVal(getByKey('carton_source'));
  const cartonMaxWeightText = displayVal(getByKey('carton_max_weight'));
  const limitWeightKg = useMemo(() => {
    try {
      const raw = String(cartonMaxWeightText || '').trim();
      if (!raw || raw === '-') return undefined;
      const m = raw.match(/-?\d+(\.\d+)?/);
      if (!m) return undefined;
      const n = Number(m[0]);
      if (!Number.isFinite(n) || n <= 0) return undefined;
      const lower = raw.toLowerCase();
      if (lower.includes('kg') || lower.includes('千克')) return n;
      if (lower.includes('g') || lower.includes('克')) return n / 1000.0;
      return n;
    } catch {
      return undefined;
    }
  }, [cartonMaxWeightText]);
  const cartonMaxLengthText = displayVal(getByKey('carton_max_length'));
  const palletTypeText = displayVal(getByKey('pallet_type'));
  const palletMaxHeightText = displayVal(getByKey('pallet_max_height'));
  const repackRequirementNotesText = displayVal(getByKey('repack_requirement_notes'));
  const waybillModeText = displayVal(getByKey('waybill_source'));
  const waybillSubmitModeText = displayVal(getByKey('waybill_submit_mode'));
  const waybillCarrierText = displayVal(getByKey('waybill_carrier'));
  const shippingLabelMappingText = displayVal(getByKey('shipping_label_to_target_mapping'));
  const trackingDisplay = displayVal(getByKey('tracking_number'));
  const waybillCarrierDisplay = displayVal(localWaybillCarrier || waybillCarrierText);
  const waybillTrackingDisplay = displayVal(localWaybillTrackingNo || trackingDisplay);
  const waybillChargeDisplay = useMemo(() => {
    const amountRaw = String(localWaybillChargeAmount || '').trim();
    if (!amountRaw) return '-';
    const currency = String(localWaybillChargeCurrency || '').trim().toUpperCase();
    return currency ? `${amountRaw} ${currency}` : amountRaw;
  }, [localWaybillChargeAmount, localWaybillChargeCurrency]);
  const waybillChargeNoteDisplay = displayVal(localWaybillChargeNote);
  const isWaybillSubmitImmediate = useMemo(() => {
    const raw = String(getByKey('waybill_submit_mode') ?? '').trim();
    if (!raw) return false;
    const s = raw.toLowerCase();
    return s === 'immediate' || raw.includes('立即上传');
  }, [answers]);
  const renderMappingRelationTag = (v: any) => {
    const raw = String(v ?? '').trim();
    if (!raw || raw === '-') return '-';
    const s = raw.toLowerCase();
    const hasMapping = s === 'has_mapping' || s === 'true' || s === '1' || s === 'yes' || raw.includes('有对应关系');
    const noMapping = s === 'no_mapping' || s === 'false' || s === '0' || s === 'no' || raw.includes('无对应关系');
    if (!hasMapping && !noMapping) return raw;
    const isRed = hasMapping;
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          border: `1px solid ${isRed ? '#ff4d4f' : '#95de64'}`,
          background: isRed ? '#fff1f0' : '#f6ffed',
          color: isRed ? '#cf1322' : '#389e0d',
          borderRadius: 4,
          padding: '0 8px',
          height: 22,
        }}
      >
        {hasMapping ? '有对应关系' : '无对应关系'}
      </span>
    );
  };
  const recipientAuxInfo = useMemo(() => {
    const answersObj = (instructionDetail as any)?.customer_answers || {};
    const joinNonEmpty = (arr: any[], sep: string) => arr
      .map((v) => displayVal(v))
      .filter((v) => v !== '-')
      .join(sep);
    const parseBool = (v: any): boolean | null => {
      if (v === true || v === false) return v;
      const s = String(v ?? '').trim().toLowerCase();
      if (!s) return null;
      if (['1', 'true', 'yes', 'y', '是', '需要'].includes(s)) return true;
      if (['0', 'false', 'no', 'n', '否', '不需要'].includes(s)) return false;
      return null;
    };
    // 按问题集 key 严格取值：不使用兜底/猜测字段名
    const line1 = displayVal(answersObj?.recipient_name);
    const line2 = displayVal(answersObj?.company_name);
    const line3 = joinNonEmpty([
      answersObj?.address_line_1,
      answersObj?.address_line_2,
    ], ', ');
    const line4 = joinNonEmpty([
      answersObj?.city,
      answersObj?.state,
      answersObj?.postal_code,
    ], ' ');
    const line5 = joinNonEmpty([
      answersObj?.phone,
      answersObj?.email,
      answersObj?.country,
    ], ' ');

    const insuranceRequired = parseBool(answersObj?.insurance_required);
    const signatureRequired = parseBool(answersObj?.signature_required);
    const liftgateRequired = parseBool(answersObj?.is_liftgate_required);
    const appointmentRequired = parseBool(answersObj?.is_appointment_required);
    const limitedAccessDelivery = parseBool(answersObj?.limited_access_delivery);
    const insuredAmount = answersObj?.insured_amount;
    const insuranceInfo = insuranceRequired === true
      ? `需要${insuredAmount != null && String(insuredAmount).trim() ? `（$${insuredAmount}）` : ''}`
      : insuranceRequired === false
        ? '不需要'
        : '-';
    const signatureInfo = signatureRequired === true ? '需要' : signatureRequired === false ? '不需要' : '-';
    const liftgateInfo = liftgateRequired === true ? '需要' : liftgateRequired === false ? '不需要' : '-';
    const appointmentInfo = appointmentRequired === true ? '需要' : appointmentRequired === false ? '不需要' : '-';
    const limitedAccessInfo = limitedAccessDelivery === true ? '是' : limitedAccessDelivery === false ? '否' : '-';
    const referenceNumberText = displayVal(answersObj?.reference_number);
    const instructionRemarksText = displayVal((instructionDetail as any)?.remarks);
    const buyerRemarksText = displayVal(answersObj?.shipping_notes);
    const executionTipsText = displayVal((instructionDetail as any)?.execution_tips);

    return {
      line1: line1 || '-',
      line2: line2 || '-',
      line3: line3 || '-',
      line4: line4 || '-',
      line5: line5 || '-',
      copyText: [line1, line2, line3, line4, line5].filter((x) => String(x || '').trim()).join('\n'),
      preferredCarrierText: displayVal(answersObj?.preferred_carrier),
      insuranceInfo,
      insuranceRequired,
      signatureInfo,
      signatureRequired,
      liftgateInfo,
      liftgateRequired,
      appointmentInfo,
      appointmentRequired,
      limitedAccessInfo,
      limitedAccessDelivery,
      referenceNumberText: referenceNumberText || '-',
      instructionRemarksText: instructionRemarksText || '-',
      buyerRemarksText: buyerRemarksText || '-',
      executionTipsText: executionTipsText || '-',
    };
  }, [instructionDetail]);
  const copyRecipientAuxInfo = async () => {
    if (!recipientAuxInfo.copyText) return void message.warning('无可复制信息');
    try {
      await navigator.clipboard.writeText(recipientAuxInfo.copyText);
      message.success('已复制');
    } catch {
      message.error('复制失败');
    }
  };
  const copyBuyWaybillPackagingInfo = async () => {
    const rows = Array.isArray(buyWaybillPackagingRows) ? buyWaybillPackagingRows : [];
    if (rows.length === 0) return void message.warning('无可复制包装数据');
    const text = [
      '包装数据',
      ...rows.map((r: any, idx: number) => (
        `${idx + 1}. 容器系统编码：${String(r?.container_code || '-')}\n容器层级：${String(r?.hierarchyLevel || '-')}\n尺寸：${String(r?.dimsMetric || '-')} | ${String(r?.dimsImperial || '-')}\n重量：${String(r?.weightMetric || '-')} | ${String(r?.weightImperial || '-')}`
      )),
    ].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      message.success('已复制');
    } catch {
      message.error('复制失败');
    }
  };
  const renderSoftTag = (
    text: string,
    kind: 'green' | 'red',
    fallback: string[] = [],
  ) => {
    const t = String(text || '-').trim();
    const isFallback = fallback.includes(t) || t === '-';
    if (isFallback) return t;
    const style = kind === 'green'
      ? { background: '#f6ffed', border: '1px solid #b7eb8f', color: '#389e0d' }
      : { background: '#fff1f0', border: '1px solid #ffa39e', color: '#cf1322' };
    return (
      <span
        style={{
          ...style,
          display: 'inline-block',
          borderRadius: 4,
          padding: '1px 8px',
          fontWeight: 400,
          lineHeight: 1.5,
        }}
      >
        {t}
      </span>
    );
  };
  const renderInfoTipIcon = (title: string) => (
    <Tooltip title={title}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 16,
          height: 16,
          borderRadius: '50%',
          border: '1px solid #bfbfbf',
          color: '#8c8c8c',
          fontSize: 11,
          lineHeight: '16px',
          cursor: 'help',
          userSelect: 'none',
          fontWeight: 700,
          boxSizing: 'border-box',
        }}
      >
        ?
      </span>
    </Tooltip>
  );
  const targetsDetail = useMemo(() => (
    Array.isArray(instructionDetail?.targets_detail) ? instructionDetail.targets_detail : []
  ), [instructionDetail?.targets_detail]);
  const hasIocTarget = useMemo(
    () => targetsDetail.some((t: any) => String(t?.target_type || '').trim() === 'instruction_output'),
    [targetsDetail],
  );
  const objectCardTitle = hasIocTarget ? '操作对象-历史指令执行结果' : '操作对象-当前可用库存';
  const backendOperationSeedRows = useMemo(() => {
    const backend = (
      (Array.isArray(data?.operation_records) && data.operation_records)
      || (Array.isArray(metrics?.operation_records) && metrics.operation_records)
      || (Array.isArray(data?.records) && data.records)
      || []
    ) as any[];
    return backend.map((r: any, idx: number) => {
      const ev = String(r?.event || r?.action || r?.op || r?.type || '').trim();
      const isStart = ev === '开始容器';
      const cc = String(r?.container_code || r?.container || r?.container_system_code || '').trim();
      const target = String(r?.target || r?.target_code || r?.code || '').trim();
      const qtyRaw = Math.floor(Number(r?.qty ?? r?.quantity ?? r?.amount ?? 0) || 0);
      const inferredType = hasIocTarget ? 'instruction_output_container' : 'inventory_unit';
      return {
        id: `seed-${String(r?.id ?? idx)}`,
        time: r?.time || r?.created_at || r?.created_time || new Date().toISOString(),
        event: ev || '-',
        action: ev || '-',
        target_type: String(r?.target_type || (isStart ? '' : inferredType)).trim(),
        container_code: cc || (isStart ? target : ''),
        target: isStart ? '' : target,
        qty: isStart ? '' : (qtyRaw > 0 ? qtyRaw : (hasIocTarget ? 1 : '')),
        result: String(r?.result || '').trim() || '成功',
        operator_name: String(r?.operator_name || r?.operator || '').trim() || '-',
        remark: String(r?.remark || '').trim(),
      };
    }).filter((x: any) => String(x?.event || '').trim());
  }, [data?.operation_records, data?.records, metrics?.operation_records, hasIocTarget]);
  useEffect(() => {
    if (seededLedgerRef.current) return;
    if ((Array.isArray(localOperationRecords) ? localOperationRecords : []).length > 0) {
      seededLedgerRef.current = true;
      return;
    }
    if (!backendOperationSeedRows.length) return;
    setLocalOperationRecords(backendOperationSeedRows);
    seededLedgerRef.current = true;
  }, [backendOperationSeedRows, localOperationRecords]);
  const resolveLedgerTargetKind = useCallback((r: any, fallbackHasIoc: boolean): 'iu' | 'ioc' => {
    const ttype = String(r?.target_type || '').trim();
    if (ttype === 'instruction_output_container') return 'ioc';
    if (ttype === 'inventory_unit') return 'iu';
    return fallbackHasIoc ? 'ioc' : 'iu';
  }, []);
  const resolveLedgerSignedQty = useCallback((r: any, fallbackHasIoc: boolean): number => {
    const ev = String(r?.event || r?.action || '').trim();
    if (!ev || ev === '-' || ev === '开始容器' || ev === '结束容器' || ev === '新增到已有容器' || ev === '结束新增') return 0;
    const kind = resolveLedgerTargetKind(r, fallbackHasIoc);
    const qtyRaw = Number(r?.qty ?? r?.quantity ?? r?.amount ?? 0) || 0;
    const qtyBase = kind === 'ioc' ? 1 : Math.max(0, Math.floor(Math.abs(qtyRaw)));
    if (!Number.isFinite(qtyBase) || qtyBase <= 0) return 0;
    const isOut = /取出|冲销|移除|移出/.test(ev);
    const isIn = /放入|加入|加回/.test(ev);
    if (isOut) return -qtyBase;
    if (isIn) return qtyBase;
    return 0;
  }, [resolveLedgerTargetKind]);
  const ledgerDoneByTarget = useMemo(() => {
    const m = new Map<string, number>();
    (Array.isArray(localOperationRecords) ? localOperationRecords : []).forEach((r: any) => {
      const code = String(r?.target || r?.target_code || r?.code || '').trim().toUpperCase();
      if (!code || code === '-') return;
      const prefix = resolveLedgerTargetKind(r, hasIocTarget);
      const delta = resolveLedgerSignedQty(r, hasIocTarget);
      if (!Number.isFinite(delta) || delta === 0) return;
      const key = `${prefix}:${code}`;
      m.set(key, (m.get(key) || 0) + delta);
    });
    return m;
  }, [localOperationRecords, hasIocTarget, resolveLedgerSignedQty, resolveLedgerTargetKind]);
  const getLedgerDoneQty = useCallback((targetCode: any, kind: 'iu' | 'ioc') => {
    const code = String(targetCode || '').trim().toUpperCase();
    if (!code) return 0;
    const n = Number(ledgerDoneByTarget.get(`${kind}:${code}`) || 0);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.floor(n));
  }, [ledgerDoneByTarget]);
  const endContainerStats = useMemo(() => {
    const rows = Array.isArray(endContainerContentRows) ? endContainerContentRows : [];
    const targetKinds = rows.length;
    const totalQty = rows.reduce((acc: number, r: any) => acc + Math.max(0, Math.floor(Number(r?.qty || 0) || 0)), 0);
    return { targetKinds, totalQty };
  }, [endContainerContentRows]);
  const loadOutputDrafts = useCallback(async () => {
    try {
      const instrId = Number(instrIdState);
      if (!Number.isFinite(instrId) || instrId <= 0) return;
      const resp = await request.get('/api/v1/storage/output-containers/drafts/', {
        params: { instruction_id: instrId, include_items: 1 },
      } as any);
      const drafts: any[] = Array.isArray(resp?.data?.drafts) ? resp.data.drafts : [];
      const next: Record<string, any> = {};
      drafts.forEach((d: any) => {
        const code = String(d?.code || '').trim();
        if (!code) return;
        next[code] = d;
      });
      setOutputDraftByCode(next);
    } catch {}
  }, [instrIdState]);
  useEffect(() => {
    if (!boxFinalOpen) return;
    void loadOutputDrafts();
  }, [boxFinalOpen, loadOutputDrafts]);
  useEffect(() => {
    try {
      if (!boxFinalOpen) return;
      setBoxFinalContentPage(1);
      setBoxFinalLinkedPage(1);
      const cc = String(boxFinalCode || '').trim();
      if (!cc) return;
      const draft: any = (outputDraftByCode as any)?.[cc] || {};
      const meta: any = (boxMeta as any)?.[cc] || {};
      const isEditingEnd = endContainerOpen && String(endContainerDraft?.code || '').trim() === cc;
      const draftLike: any = isEditingEnd ? endContainerDraft : draft;
      const l = draftLike?.length_cm != null ? Math.floor(Number(draftLike.length_cm) || 0) : (meta?.length_cm != null ? Math.floor(Number(meta.length_cm) || 0) : undefined);
      const w = draftLike?.width_cm != null ? Math.floor(Number(draftLike.width_cm) || 0) : (meta?.width_cm != null ? Math.floor(Number(meta.width_cm) || 0) : undefined);
      const h = draftLike?.height_cm != null ? Math.floor(Number(draftLike.height_cm) || 0) : (meta?.height_cm != null ? Math.floor(Number(meta.height_cm) || 0) : undefined);
      const wg = draftLike?.weight_g != null ? Math.floor(Number(draftLike.weight_g) || 0) : (meta?.weight_g != null ? Math.floor(Number(meta.weight_g) || 0) : undefined);
      setBoxFinalDraft({ length_cm: l, width_cm: w, height_cm: h, weight_g: wg });
      const cmToInCeil = (cm?: number) => {
        const n = Number(cm);
        if (!Number.isFinite(n) || n <= 0) return undefined;
        return Math.ceil(n / 2.54);
      };
      const gToKgCeil = (g?: number) => {
        const n = Number(g);
        if (!Number.isFinite(n) || n <= 0) return undefined;
        return Math.ceil(n / 1000);
      };
      const kgToLbCeil = (kg?: number) => {
        const n = Number(kg);
        if (!Number.isFinite(n) || n <= 0) return undefined;
        return Math.ceil(n * 2.2046226218);
      };
      const lin = draftLike?.length_in != null ? Math.floor(Number(draftLike.length_in) || 0) : (meta?.length_in != null ? Math.floor(Number(meta.length_in) || 0) : undefined);
      const win = draftLike?.width_in != null ? Math.floor(Number(draftLike.width_in) || 0) : (meta?.width_in != null ? Math.floor(Number(meta.width_in) || 0) : undefined);
      const hin = draftLike?.height_in != null ? Math.floor(Number(draftLike.height_in) || 0) : (meta?.height_in != null ? Math.floor(Number(meta.height_in) || 0) : undefined);
      const wlb = draftLike?.weight_lb != null ? Math.floor(Number(draftLike.weight_lb) || 0) : (meta?.weight_lb != null ? Math.floor(Number(meta.weight_lb) || 0) : undefined);
      setBoxFinalLenIn(lin && lin > 0 ? lin : cmToInCeil(l));
      setBoxFinalWidIn(win && win > 0 ? win : cmToInCeil(w));
      setBoxFinalHeiIn(hin && hin > 0 ? hin : cmToInCeil(h));
      setBoxFinalLb(wlb && wlb > 0 ? wlb : kgToLbCeil(gToKgCeil(wg)));
      setBoxFinalDirty(false);
    } catch {}
  }, [boxFinalOpen, boxFinalCode, outputDraftByCode, boxMeta, endContainerOpen, endContainerDraft]);
  useEffect(() => {
    if (!endContainerOpen) return;
    setEndContainerContentPage(1);
    setEndContainerLinkedPage(1);
    if (!endContainerContentPageSize) setEndContainerContentPageSize(10);
    if (!endContainerLinkedPageSize) setEndContainerLinkedPageSize(5);
  }, [endContainerOpen, endContainerContentPageSize, endContainerLinkedPageSize]);
  const openEndCurrentContainerModal = useCallback(() => {
    if (!canOperateExec) return;
    const cc = String(currentPackBoxCode || '').trim();
    if (!cc) {
      message.warning('当前无打开中的容器');
      return;
    }
    const meta: any = (boxMeta as any)?.[cc] || {};
    const hit: any = (boxes || []).find((b: any) => String(b?.code || '').trim() === cc) || {};
    const ct = String(hit?.container_type || meta?.container_type || 'box').trim() || 'box';
    const defaultWeightG = (() => {
      try {
        const isParent = (hierarchyRowsByParent.get(cc) || []).length > 0;
        if (isParent) {
          const base = calcContainerTheory(cc);
          if (base && (base as any).ok) {
            const linkedKg = resolveLinkedChildrenWeightKg(cc);
            const kg = Number((Number((base as any).totalKg || 0) + linkedKg).toFixed(2));
            if (Number.isFinite(kg) && kg > 0) return Math.ceil(kg) * 1000;
          }
        }
        if (currentPackTheory && (currentPackTheory as any).ok) {
          const kg = Number((currentPackTheory as any).totalKg);
          if (Number.isFinite(kg) && kg > 0) return Math.ceil(kg) * 1000;
        }
      } catch {}
      const wg = Number(meta?.weight_g);
      if (Number.isFinite(wg) && wg > 0) return Math.round(wg);
      return undefined;
    })();
    setEndContainerDraft({
      code: cc,
      container_type: ct,
      length_cm: meta?.length_cm,
      width_cm: meta?.width_cm,
      height_cm: meta?.height_cm,
      weight_g: defaultWeightG,
    });
    const initLenCm = Number(meta?.length_cm);
    const initWidCm = Number(meta?.width_cm);
    const initHeiCm = Number(meta?.height_cm);
    const initKg = Number.isFinite(Number(defaultWeightG)) && Number(defaultWeightG) > 0
      ? Math.ceil(Number(defaultWeightG) / 1000)
      : undefined;
    setEndContainerLenInDraft(Number.isFinite(initLenCm) && initLenCm > 0 ? Math.ceil(initLenCm / 2.54) : undefined);
    setEndContainerWidInDraft(Number.isFinite(initWidCm) && initWidCm > 0 ? Math.ceil(initWidCm / 2.54) : undefined);
    setEndContainerHeiInDraft(Number.isFinite(initHeiCm) && initHeiCm > 0 ? Math.ceil(initHeiCm / 2.54) : undefined);
    setEndContainerKgDraft(initKg);
    setEndContainerLbDraft(Number.isFinite(Number(initKg)) && Number(initKg) > 0 ? Math.ceil(Number(initKg) * 2.2046226218) : undefined);
    try {
      const list = (Array.isArray(localOperationRecords) ? localOperationRecords : []).filter((r: any) => String(r?.container_code || '').trim() === cc);
      const agg = new Map<string, number>();
      list.forEach((r: any) => {
        const t = String(r?.target || r?.target_code || r?.code || '').trim().toUpperCase();
        if (!t || t === '-') return;
        const delta = resolveLedgerSignedQty(r, hasIocTarget);
        if (!Number.isFinite(delta) || delta === 0) return;
        agg.set(t, (agg.get(t) || 0) + delta);
      });
      const rows = Array.from(agg.entries())
        .map(([target, qty]) => ({ key: target, target, qty: Math.floor(Number(qty || 0) || 0) }))
        .filter((x) => Number.isFinite(x.qty) && x.qty > 0)
        .sort((a, b) => (b.qty || 0) - (a.qty || 0));
      setEndContainerContentRows(rows);
    } catch {
      setEndContainerContentRows([]);
    }
    setEndContainerOpen(true);
  }, [canOperateExec, currentPackBoxCode, boxMeta, boxes, currentPackTheory, localOperationRecords, resolveLedgerSignedQty, hasIocTarget, hierarchyRowsByParent, calcContainerTheory, resolveLinkedChildrenWeightKg]);
  const submitEndCurrentContainer = useCallback(async () => {
    if (!canOperateExec) return;
    const cc = String(endContainerDraft?.code || '').trim();
    if (!cc) {
      message.error('容器系统编码无效');
      return;
    }
    const isPosInt = (n: any) => Number.isFinite(Number(n)) && Number(n) > 0 && Math.floor(Number(n)) === Number(n);
    const l = Number(endContainerDraft?.length_cm);
    const w = Number(endContainerDraft?.width_cm);
    const h = Number(endContainerDraft?.height_cm);
    const wg = Number(endContainerDraft?.weight_g);
    const miss: string[] = [];
    if (!isPosInt(l)) miss.push('长度');
    if (!isPosInt(w)) miss.push('宽度');
    if (!isPosInt(h)) miss.push('高度');
    if (!isPosInt(wg)) miss.push('容器实重');
    if (miss.length) {
      message.error(`请先填写完整的物理数据：${miss.join('、')}（均为正整数）`);
      return;
    }
    try {
      const m = String(cartonMaxWeightText || '').trim().match(/-?\d+(\.\d+)?/);
      const limit = m ? Number(m[0]) : NaN;
      const actualKg = Math.ceil(wg / 1000);
      if (Number.isFinite(limit) && limit > 0 && actualKg > limit) {
        const ok = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: '超重提醒',
            content: `容器 ${cc} 实重 ${actualKg}kg 已超过限重 ${Number(limit).toFixed(3)}kg，是否仍要提交？`,
            okText: '仍然提交',
            cancelText: '取消提交',
            okButtonProps: { danger: true },
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (!ok) return;
      }
      // 托盘最大高度提醒：仅托盘容器类型校验，不阻断提交
      const ct = String(endContainerDraft?.container_type || '').trim().toLowerCase();
      if (ct === 'pallet') {
        const mh = String(palletMaxHeightText || '').trim().match(/-?\d+(\.\d+)?/);
        const maxHeightLimit = mh ? Number(mh[0]) : NaN;
        if (Number.isFinite(maxHeightLimit) && maxHeightLimit > 0) {
          const actualHeight = Number(h);
          if (Number.isFinite(actualHeight) && actualHeight > maxHeightLimit) {
            const proceed = await new Promise<boolean>((resolve) => {
              Modal.confirm({
                title: '托盘高度提醒',
                content: `托盘容器 ${cc} 高度 ${actualHeight}cm 已超过托盘最大高度 ${Number(maxHeightLimit).toFixed(3)}cm，是否仍要提交？`,
                okText: '继续提交',
                cancelText: '返回修改',
                onOk: () => resolve(true),
                onCancel: () => resolve(false),
              });
            });
            if (!proceed) return;
          }
        }
      }
    } catch {}
    setEndContainerSubmitting(true);
    try {
      const payload: any = {
        instruction_id: instrIdState,
        code: cc,
        container_type: String(endContainerDraft?.container_type || 'box'),
        status: 'closed',
        length_cm: endContainerDraft?.length_cm,
        width_cm: endContainerDraft?.width_cm,
        height_cm: endContainerDraft?.height_cm,
        weight_g: endContainerDraft?.weight_g,
        execution_session_id: String(executionSessionId || '').trim() || undefined,
        defer_inventory_settlement: true,
      };
      await request.post('/api/v1/storage/output-containers/finalize/', payload);
      setBoxMeta((prev) => ({
        ...(prev || {}),
        [cc]: {
          ...(prev as any)?.[cc],
          length_cm: Number(endContainerDraft?.length_cm),
          width_cm: Number(endContainerDraft?.width_cm),
          height_cm: Number(endContainerDraft?.height_cm),
          weight_g: Number(endContainerDraft?.weight_g),
          weight_kg: Math.ceil(Number(endContainerDraft?.weight_g || 0) / 1000),
        },
      }));
      setLocalOperationRecords((prev) => ([
        {
          id: `local-end-${Date.now()}`,
          time: new Date().toISOString(),
          event: '结束容器',
          action: '结束容器',
          container_code: cc,
          target: '',
          qty: 0,
          result: '成功',
          operator_name: '-',
          remark: `结束容器 ${cc}`,
        },
        ...(Array.isArray(prev) ? prev : []),
      ]));
      setBoxes((prev) => prev.map((b: any) => (String(b?.code || '').trim() === cc ? ({ ...b, status: 'closed' }) : b)));
      setAddToExistingEnabled(false);
      setAddToExistingCode('');
      setPickExistingCode('');
      setPickExistingQuery('');
      setPickExistingOpen(false);
      setCurrentPackBoxCode('');
      setEndContainerOpen(false);
      message.success(`已结束容器：${cc}`);
      try {
        setTimeout(() => {
          void openOutputBoxLabelPreview(cc);
        }, 0);
      } catch {}
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || '结束容器失败（请重试）');
    } finally {
      setEndContainerSubmitting(false);
    }
  }, [canOperateExec, endContainerDraft, cartonMaxWeightText, palletMaxHeightText, instrIdState, executionSessionId]);

  const voidEmptyContainer = useCallback(async (codeRaw: any) => {
    const code = String(codeRaw || '').trim();
    if (!canOperateExec) return;
    if (!code) return;
    if (!instrIdState) { message.error('缺少指令ID'); return; }
    if (voidingContainerCodes.has(code)) return;
    const ok = await new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: '确认作废容器?',
        content: `容器 ${code} 将被标记为“已作废”，系统编码不会回收复用。`,
        okText: '确认作废',
        cancelText: '取消',
        okButtonProps: { danger: true },
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!ok) return;
    setVoidingContainerCodes((prev) => {
      const next = new Set(prev || []);
      next.add(code);
      return next;
    });
    try {
      const resp = await request.post('/api/v1/storage/output-containers/void/', {
        instruction_id: instrIdState,
        codes: [code],
        defer_inventory_settlement: true,
      });
      const voidedCodes = Array.isArray(resp?.data?.voided_codes) ? resp.data.voided_codes : [];
      const blockedRows = Array.isArray(resp?.data?.blocked_codes) ? resp.data.blocked_codes : [];
      if ((voidedCodes || []).includes(code)) {
        setBoxes((prev) => (Array.isArray(prev) ? prev.map((b: any) => (
          String(b?.code || '').trim() === code ? ({ ...b, status: 'cancelled' }) : b
        )) : prev));
        setHierarchyLinks((prev) => (Array.isArray(prev)
          ? prev.filter((ln: any) => String(ln?.parent_code || '').trim() !== code && String(ln?.child_code || '').trim() !== code)
          : prev));
        setLocalOperationRecords((prev) => ([
          {
            id: `local-void-${Date.now()}`,
            time: new Date().toISOString(),
            event: '作废容器',
            action: '作废容器',
            container_code: code,
            target: '',
            qty: 0,
            result: '成功',
            operator_name: '-',
            remark: `作废空容器 ${code}`,
          },
          ...(Array.isArray(prev) ? prev : []),
        ]));
        message.success(`已作废容器：${code}`);
      } else if (blockedRows.length > 0) {
        const hit = blockedRows.find((x: any) => String(x?.code || '').trim() === code);
        message.warning(hit ? `作废失败：${String(hit?.reason || '不满足作废条件')}` : '作废失败：不满足作废条件');
      } else {
        message.warning('作废未生效，请刷新后重试');
      }
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || '作废容器失败');
    } finally {
      setVoidingContainerCodes((prev) => {
        const next = new Set(prev || []);
        next.delete(code);
        return next;
      });
    }
  }, [canOperateExec, instrIdState, voidingContainerCodes]);

  const inventoryRows = useMemo(() => {
    const td = targetsDetail.filter((t: any) => String(t?.target_type || '').trim() !== 'instruction_output');
    const toBool = (v: any): boolean => {
      if (typeof v === 'boolean') return v;
      if (typeof v === 'number') return v === 1;
      const s = String(v ?? '').trim().toLowerCase();
      return ['1', 'true', 'yes', 'y', '是'].includes(s);
    };
    return td.map((t: any, idx: number) => {
      const snapshot = t?.snapshot || t?.target?.snapshot || {};
      const unitCode = String(
        snapshot?.system_barcode
        || t?.inventory_unit_system_barcode
        || t?.target?.inventory_unit_system_barcode
        || t?.fingerprint
        || '',
      ).trim();
      const targetQty = Number(
        t?.expected_qty
        ?? t?.target_qty
        ?? t?.target_quantity
        ?? t?.quantity
        ?? snapshot?.quantity
        ?? 0,
      ) || 0;
      const unitTypeCode = String(
        t?.inventory_unit_type
        || t?.meta?.inventory_unit_type
        || t?.meta?.iu_type
        || t?.target?.inventory_unit_type
        || t?.inventory_unit_type
        || snapshot?.inventory_unit_type
        || '',
      ).trim();
      const unitTypeLabel = mapInventoryUnitType(
        unitTypeCode,
        t?.meta?.inventory_unit_type_display
        || t?.meta?.iu_type_display
        || t?.target?.inventory_unit_type_display
        || t?.inventory_unit_type_display
        || snapshot?.inventory_unit_type_display,
      );
      const backendSortingCodes = Array.isArray(t?.sorting_barcodes) ? t.sorting_barcodes : [];
      const backendCustomCodes = Array.isArray(t?.custom_codes) ? t.custom_codes : [];
      const backendContentItems = Array.isArray(t?.inventory_content_items) ? t.inventory_content_items : [];
      const isLiu = (
        unitTypeCode.toUpperCase() === 'LIU'
        || unitTypeLabel.includes('散货')
        || backendSortingCodes.length > 0
      );
      const items = Array.isArray(snapshot?.content?.items) ? snapshot.content.items : [];
      const contentLines = (() => {
        if (backendContentItems.length > 0) {
          return backendContentItems.map((it: any) => {
            const code = String(it?.product_custom_code || '-').trim() || '-';
            const q = Number(it?.quantity || 0) || 0;
            const sArr = Array.isArray(it?.sorting_barcodes) ? it.sorting_barcodes : [];
            const sText = sArr.length > 0 ? sArr.join(' | ') : '';
            return sText ? `${code}(${sText}) × ${q}` : `${code} × ${q}`;
          });
        }
        if (backendCustomCodes.length > 0) return backendCustomCodes.map((c: any) => String(c || '-'));
        if (items.length > 0) {
          return items.map((it: any) => {
            const barcode = String(it?.product_system_code || it?.system_code || it?.product_code || '-').trim();
            const q = Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0;
            return `${barcode} × ${q}`;
          });
        }
        return [];
      })();
      const sortingBarcodes = (() => {
        if (!isLiu) return '-';
        if (backendContentItems.length > 0) {
          const liuCodes = Array.isArray(backendContentItems[0]?.sorting_barcodes) ? backendContentItems[0].sorting_barcodes : [];
          if (liuCodes.length > 0) return liuCodes.join(' / ');
        }
        if (backendSortingCodes.length > 0) return backendSortingCodes.join(' / ');
        const set = new Set<string>();
        items.forEach((it: any) => {
          const sc = Array.isArray(it?.sorting_codes) ? it.sorting_codes : [];
          sc.forEach((x: any) => {
            const c = String(x?.code || '').trim();
            if (c) set.add(c);
          });
          const c1 = String(it?.product_sorting_code || it?.sorting_code || '').trim();
          if (c1) set.add(c1);
        });
        const arr = Array.from(set);
        return arr.length > 0 ? arr.join(' / ') : '-';
      })();
      const sortingBarcodeList = (() => {
        if (!isLiu) return [] as string[];
        const set = new Set<string>();
        if (backendContentItems.length > 0) {
          backendContentItems.forEach((it: any) => {
            const liuCodes = Array.isArray(it?.sorting_barcodes) ? it.sorting_barcodes : [];
            liuCodes.forEach((x: any) => {
              const c = String(x || '').trim().toUpperCase();
              if (c) set.add(c);
            });
          });
        }
        if (set.size === 0 && backendSortingCodes.length > 0) {
          backendSortingCodes.forEach((x: any) => {
            const c = String(x || '').trim().toUpperCase();
            if (c) set.add(c);
          });
        }
        if (set.size === 0 && items.length > 0) {
          items.forEach((it: any) => {
            const sc = Array.isArray(it?.sorting_codes) ? it.sorting_codes : [];
            sc.forEach((x: any) => {
              const c = String(x?.code || '').trim().toUpperCase();
              if (c) set.add(c);
            });
            const c1 = String(it?.product_sorting_code || it?.sorting_code || '').trim().toUpperCase();
            if (c1) set.add(c1);
          });
        }
        return Array.from(set);
      })();
      const pickedQty = getLedgerDoneQty(unitCode, 'iu');
      const useAllAvailable = toBool(
        t?.use_all_available
        ?? t?.target?.use_all_available
        ?? snapshot?.use_all_available
        ?? t?.meta?.use_all_available,
      );
      return {
        key: String(t?.id ?? `${idx}-${unitCode || 'target'}`),
        index: idx + 1,
        unit_type_code: unitTypeCode || '-',
        unit_type: unitTypeLabel || '-',
        unit_code: unitCode || '-',
        sorting_barcodes: sortingBarcodes,
        sorting_barcode_list: sortingBarcodeList,
        content_lines: contentLines,
        use_all_available: useAllAvailable,
        target_qty: targetQty,
        picked_qty: pickedQty,
      };
    });
  }, [targetsDetail, getLedgerDoneQty]);
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        if (!hasIocTarget) {
          if (!cancelled) {
            setIocDisplayRows([]);
            setIocDisplayLoading(false);
          }
          return;
        }
        setIocDisplayLoading(true);
        const d = instructionDetail || {};
        const list = Array.isArray(targetsDetail) ? targetsDetail : [];
        const historyTargets = (list || []).filter((r: any) => String(r?.target_type || '').trim() === 'instruction_output');
        if (historyTargets.length === 0) {
          if (!cancelled) setIocDisplayRows([]);
          return;
        }
        const projectById = new Map<number, { code?: string; name?: string }>();
        try {
          const prows = Array.isArray(d?.projects_detail) ? d.projects_detail : [];
          prows.forEach((p: any) => {
            const id = Number(p?.id);
            if (!Number.isFinite(id)) return;
            projectById.set(id, { code: p?.project_code, name: p?.name });
          });
        } catch {}
        const baseRows = historyTargets.map((r: any, idx: number) => {
          const snap = r?.snapshot || {};
          const selector = r?.output_selector_obj ?? r?.output_selector;
          const codes = parseOverviewIocCodesFromSelector(selector);
          const pid = Number(r?.project_id);
          const p = Number.isFinite(pid) ? projectById.get(pid) : undefined;
          return {
            __key: `op-hoc-${String(r?.id || idx)}`,
            project_id: Number.isFinite(pid) ? pid : undefined,
            project_code: r?.project_code || snap?.project_code || p?.code,
            project_name: r?.project_name || snap?.project_name || p?.name,
            instruction_id: Number(r?.prev_instruction_id || snap?.prev_instruction_id || snap?.instruction_id || 0) || undefined,
            instruction_code: String(snap?.prev_instruction_code || snap?.instruction_code || '').trim() || undefined,
            output_container_id: snap?.output_container_id ?? snap?.container_id ?? undefined,
            output_code: String(codes?.[0] || snap?.output_code || snap?.system_code || snap?.code || '').trim(),
            display_code: String(codes?.[0] || snap?.output_code || snap?.system_code || snap?.code || '').trim(),
            container_type_display: snap?.container_type_display || snap?.container_type || '-',
            output_content_kind: snap?.output_content_kind || '',
            output_content_kind_display: snap?.output_content_kind_display || '-',
            output_contents_summary: snap?.output_contents_summary || '-',
            output_contents_detail: snap?.output_contents_detail || snap?.contents_detail || '',
            output_contents_total_qty: Number(snap?.output_contents_total_qty || snap?.total_qty || 0) || 0,
            length_cm: snap?.length_cm,
            width_cm: snap?.width_cm,
            height_cm: snap?.height_cm,
            weight_g: snap?.weight_g,
            created_at: snap?.created_at,
          } as any;
        });
        let mergedRows = baseRows;
        const prevIds: number[] = Array.from(new Set(
          (baseRows || [])
            .map((x: any) => Number(x?.instruction_id))
            .filter((v: number) => Number.isFinite(v) && v > 0),
        ));
        if (prevIds.length > 0) {
          const detailByCode = new Map<string, any>();
          const detailById = new Map<string, any>();
          await Promise.all(prevIds.map(async (pid: number) => {
            try {
              const rr = await request.get(`/api/v1/instruction/instructions/${pid}/ioc/with-contents/`);
              const arr = Array.isArray(rr?.data?.results) ? rr.data.results : [];
              (arr || []).forEach((oc: any) => {
                const idKey = `${pid}::id::${String(oc?.id || '')}`;
                const codeKey = `${pid}::code::${normalizeOverviewIocCode(oc?.code)}`;
                if (String(oc?.id || '').trim()) detailById.set(idKey, oc);
                if (normalizeOverviewIocCode(oc?.code)) detailByCode.set(codeKey, oc);
              });
            } catch {}
          }));
          mergedRows = (baseRows || []).map((row: any) => {
            const pid = Number(row?.instruction_id);
            if (!Number.isFinite(pid) || pid <= 0) return row;
            const byId = row?.output_container_id ? detailById.get(`${pid}::id::${String(row.output_container_id)}`) : undefined;
            const byCode = detailByCode.get(`${pid}::code::${normalizeOverviewIocCode(row?.output_code || row?.display_code)}`);
            const hit = byId || byCode;
            if (!hit) return row;
            const contents = Array.isArray(hit?.contents) ? hit.contents : [];
            const totalQty = contents.reduce((acc: number, c: any) => acc + (Number(c?.quantity || 0) || 0), 0);
            const kind = String(hit?.content_kind || row?.output_content_kind || '').trim().toLowerCase();
            const contentKindDisplay = kind === 'exception' ? '异常内容容器' : '正常内容容器';
            const detailText = (contents || []).map((c: any) => {
              const code = String(c?.inventory_unit_system_barcode || c?.new_barcode || c?.code || '').trim();
              const qty = Number(c?.quantity || 0) || 0;
              return code ? `${code}×${qty}` : '';
            }).filter(Boolean).join('；');
            return {
              ...row,
              output_container_id: hit?.id ?? row?.output_container_id,
              output_code: String(hit?.code || row?.output_code || row?.display_code || '').trim(),
              display_code: String(hit?.code || row?.display_code || row?.output_code || '').trim(),
              container_type_display: row?.container_type_display && String(row.container_type_display).trim() !== '-'
                ? row.container_type_display
                : (String(hit?.container_type || '').trim() || '-'),
              output_content_kind: kind || row?.output_content_kind || '',
              output_content_kind_display: row?.output_content_kind_display && String(row.output_content_kind_display).trim() !== '-'
                ? row.output_content_kind_display
                : contentKindDisplay,
              output_contents_summary: row?.output_contents_summary && String(row.output_contents_summary).trim() !== '-'
                ? row.output_contents_summary
                : (contents.length > 0 ? `${contents.length}项` : '-'),
              output_contents_detail: String(row?.output_contents_detail || '').trim() || detailText,
              output_contents_total_qty: Number(row?.output_contents_total_qty || 0) > 0 ? Number(row.output_contents_total_qty || 0) : totalQty,
              length_cm: hit?.length_cm ?? row?.length_cm,
              width_cm: hit?.width_cm ?? row?.width_cm,
              height_cm: hit?.height_cm ?? row?.height_cm,
              weight_g: hit?.weight_g ?? row?.weight_g,
              created_at: hit?.created_at ?? row?.created_at,
            };
          });
        }
        try {
          const projectIds: number[] = Array.from(new Set(
            (mergedRows || [])
              .map((r: any) => Number(r?.project_id))
              .filter((v: number) => Number.isFinite(v) && v > 0),
          ));
          if (projectIds.length > 0) {
            const warehouseCode = String(d?.warehouse_detail?.code || d?.warehouse?.code || '').trim();
            const unifiedByCode = new Map<string, any>();
            await Promise.all(projectIds.map(async (pid: number) => {
              try {
                const params: any = {
                  page: 1,
                  page_size: 500,
                  container_kind: 'output',
                  project: String(pid),
                  instruction_id: Number(d?.id || 0) || undefined,
                  ordering: '-created_at',
                };
                if (warehouseCode) params.warehouse_code = warehouseCode;
                const resp = await request.get('/api/v1/storage/containers/unified/', { params });
                const rows = Array.isArray(resp?.data?.results) ? resp.data.results : [];
                (rows || []).forEach((r: any) => {
                  const code = normalizeOverviewIocCode(r?.system_code || r?.display_code || '');
                  if (code) unifiedByCode.set(`${pid}::${code}`, r);
                });
              } catch {}
            }));
            mergedRows = (mergedRows || []).map((row: any) => {
              const pid = Number(row?.project_id);
              const code = normalizeOverviewIocCode(row?.output_code || row?.display_code);
              if (!Number.isFinite(pid) || pid <= 0 || !code) return row;
              const hit = unifiedByCode.get(`${pid}::${code}`);
              if (!hit) return row;
              return {
                ...row,
                output_code: String(hit?.system_code || hit?.display_code || row?.output_code || row?.display_code || '').trim(),
                display_code: String(hit?.display_code || hit?.system_code || row?.display_code || row?.output_code || '').trim(),
                container_type_display: hit?.container_type_display || row?.container_type_display || '-',
                output_content_kind: hit?.output_content_kind || row?.output_content_kind || '',
                output_content_kind_display: hit?.output_content_kind_display || row?.output_content_kind_display || '-',
                output_contents_summary: hit?.output_contents_summary || row?.output_contents_summary || '-',
                output_contents_detail: hit?.output_contents_detail || row?.output_contents_detail || '',
                output_contents_total_qty: Number(hit?.output_contents_total_qty || row?.output_contents_total_qty || 0) || 0,
                length_cm: hit?.length_cm ?? row?.length_cm,
                width_cm: hit?.width_cm ?? row?.width_cm,
                height_cm: hit?.height_cm ?? row?.height_cm,
                weight_g: hit?.weight_g ?? row?.weight_g,
              };
            });
          }
        } catch {}
        const mergedWithDone = (mergedRows || []).map((row: any) => {
          const code = normalizeOverviewIocCode(row?.output_code || row?.display_code);
          const done = getLedgerDoneQty(code, 'ioc');
          return { ...row, done_qty: done };
        });
        if (!cancelled) setIocDisplayRows(mergedWithDone || []);
      } catch {
        if (!cancelled) setIocDisplayRows([]);
      } finally {
        if (!cancelled) setIocDisplayLoading(false);
      }
    };
    void run();
    return () => { cancelled = true; };
  }, [hasIocTarget, instructionDetail, targetsDetail, getLedgerDoneQty]);

  const labelingTargetCandidates = useMemo(() => {
    const set = new Set<string>();
    (Array.isArray(iocDisplayRows) ? iocDisplayRows : []).forEach((r: any) => {
      const code = normalizeBarcodeValue(r?.output_code || r?.display_code || r?.key || '');
      if (code) set.add(code);
    });
    (Array.isArray(inventoryRows) ? inventoryRows : []).forEach((r: any) => {
      const code = normalizeBarcodeValue(r?.unit_code || r?.key || '');
      if (code) set.add(code);
    });
    (Array.isArray(boxes) ? boxes : []).forEach((r: any) => {
      const code = normalizeBarcodeValue(r?.code || '');
      if (code) set.add(code);
    });
    Object.keys(outputDraftByCode || {}).forEach((k) => {
      const code = normalizeBarcodeValue(k);
      if (code) set.add(code);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [iocDisplayRows, inventoryRows, boxes, outputDraftByCode]);
  const parseAndValidateLabelingTargetCode = useCallback((raw: any) => {
    const code = normalizeBarcodeValue(raw);
    if (!code) return { ok: false as const, code: '', reason: '请先扫描出库对象编码' };
    const validPrefixes = ['IOC', 'LIU', 'BIU', 'PIU', 'NIU', 'AIU'];
    const matched = validPrefixes.some((p) => code.startsWith(p));
    if (!matched) {
      return { ok: false as const, code, reason: '出库对象编码必须以 IOC/LIU/BIU/PIU/NIU/AIU 开头' };
    }
    return { ok: true as const, code, reason: '' };
  }, []);
  const handleLabelingTargetInvalid = useCallback((reason: string) => {
    setLabelingLastResult({ ok: false, text: reason });
    if (labelingWarnBeep) playLabelingTone('warn');
    setLabelingTargetCode('');
    setLabelingLabelCode('');
    setLabelingStep('target');
    setTimeout(() => {
      try { labelingTargetInputRef.current?.focus?.(); } catch {}
    }, 0);
  }, [labelingWarnBeep, playLabelingTone]);
  const focusLabelingTargetInput = useCallback(() => {
    setTimeout(() => {
      try { labelingTargetInputRef.current?.focus?.(); } catch {}
    }, 0);
  }, []);
  const resetLabelingSession = useCallback((tip = '已重置贴单会话，请重新扫描') => {
    setLabelingTargetCode('');
    setLabelingLabelCode('');
    setLabelingStep('target');
    setLabelingSessionRows([]);
    setLabelingSessionPage(1);
    setLabelingSessionPageSize(8);
    setLabelingPendingOperationRecords([]);
    setReadyBaselineDoneRowKeys(new Set());
    setLabelingConsumedMatchKeys(new Set());
    setLabelingLastResult({ ok: true, text: tip });
    focusLabelingTargetInput();
  }, [focusLabelingTargetInput]);
  const closeLabelingModalDirectly = useCallback(() => {
    resetLabelingSession('已直接关闭并清空贴单会话记录');
    setLabelingModalOpen(false);
  }, [resetLabelingSession]);
  const commitLabelingSessionRecords = useCallback(() => {
    const pending = Array.isArray(labelingPendingOperationRecords) ? labelingPendingOperationRecords : [];
    if (pending.length <= 0) {
      setLabelingLastResult({ ok: false, text: '当前无可提交贴单记录，请先执行扫码' });
      if (labelingWarnBeep) playLabelingTone('warn');
      return;
    }
    setLocalOperationRecords((prev) => ([...pending, ...(Array.isArray(prev) ? prev : [])]));
    const count = pending.length;
    setLabelingPendingOperationRecords([]);
    setLabelingSessionRows([]);
    setLabelingSessionPage(1);
    setLabelingSessionPageSize(8);
    setLabelingLastResult({ ok: true, text: `已提交${count}条贴单记录到页面操作记录` });
    message.success(`已提交${count}条贴单记录`);
    setLabelingTargetCode('');
    setLabelingLabelCode('');
    setLabelingStep('target');
    setLabelingModalOpen(false);
    focusLabelingTargetInput();
  }, [labelingPendingOperationRecords, labelingWarnBeep, playLabelingTone, focusLabelingTargetInput]);
  const reloadLabelingConstraint = useCallback(async () => {
    const candidate = firstReadyBaselinePreviewCandidate;
    if (!candidate?.file) {
      setLabelingConstraintLoading(false);
      setLabelingConstraintReady(false);
      setLabelingConstraintName('');
      setLabelingConstraintMap(new Map());
      setLabelingConstraintMatchableRowCount(0);
      setLabelingConsumedMatchKeys(new Set());
      return;
    }
    setLabelingConstraintLoading(true);
    try {
      const parsed = await parseBaselineTargetLabelMap(candidate.file);
      setLabelingConstraintMap(parsed.map);
      setLabelingConstraintMatchableRowCount(parsed.matchableRowCount);
      setLabelingConstraintReady(parsed.matchableRowCount > 0);
      setLabelingConstraintName(String(candidate.fileName || '基线文件'));
      setLabelingConsumedMatchKeys(new Set());
      if (parsed.matchableRowCount <= 0) {
        setLabelingLastResult({ ok: true, text: '当前基线文件无可用贴单映射，将按无约束模式记录' });
      } else {
        setLabelingLastResult({ ok: true, text: `已加载贴单约束映射 ${parsed.matchableRowCount} 条，请扫描` });
      }
    } catch {
      setLabelingConstraintMap(new Map());
      setLabelingConstraintMatchableRowCount(0);
      setLabelingConstraintReady(false);
      setLabelingConstraintName(String(candidate.fileName || '基线文件'));
      setLabelingConsumedMatchKeys(new Set());
      setLabelingLastResult({ ok: false, text: '基线文件解析失败，将按无约束模式记录' });
    } finally {
      setLabelingConstraintLoading(false);
    }
  }, [firstReadyBaselinePreviewCandidate, parseBaselineTargetLabelMap]);
  const openLabelingModal = useCallback(() => {
    setLabelingModalOpen(true);
    setLabelingTargetCode('');
    setLabelingLabelCode('');
    setLabelingStep('target');
    setLabelingLastResult({ ok: true, text: '请先扫描出库对象编码' });
    setLabelingSessionRows([]);
    setLabelingSessionPage(1);
    setLabelingSessionPageSize(8);
    setLabelingPendingOperationRecords([]);
    void reloadLabelingConstraint();
  }, [reloadLabelingConstraint]);
  const submitLabelingRecord = useCallback(() => {
    const targetParsed = parseAndValidateLabelingTargetCode(labelingTargetCode);
    const target = targetParsed.code;
    const label = normalizeBarcodeValue(labelingLabelCode);
    if (!targetParsed.ok) {
      handleLabelingTargetInvalid(targetParsed.reason);
      return;
    }
    if (!label) {
      setLabelingLastResult({ ok: false, text: '请扫描运输标签编码' });
      if (labelingWarnBeep) playLabelingTone('warn');
      return;
    }
    const hasConstraint = labelingConstraintReady && labelingConstraintMap.size > 0;
    let ok = true;
    let result = '成功';
    let reason = '无约束直接记录';
    let matchedBaselineRowNo: number | null = null;
    let matchedBaselineColumn: 'C' | 'D' | '-' = '-';
    let matchedBaselineRowKeys: number[] = [];
    if (hasConstraint) {
      const matchKey = `${target}::${label}`;
      if (labelingConsumedMatchKeys.has(matchKey)) {
        ok = false;
        result = '警告';
        reason = `该记录已匹配完成，禁止重复匹配 ${target} -> ${label}`;
      } else {
        const matchedRows = (Array.isArray(readyBaselineInlineData) ? readyBaselineInlineData : [])
          .filter((r: any) => {
            const rowTarget = normalizeBarcodeValue(r?.c0);
            if (rowTarget !== target) return false;
            const c = normalizeBarcodeValue(r?.c2);
            const d = normalizeBarcodeValue(r?.c3);
            return c === label || d === label;
          })
          .map((r: any) => {
            const rk = Number(r?.__k);
            const c = normalizeBarcodeValue(r?.c2);
            const d = normalizeBarcodeValue(r?.c3);
            let col: 'C' | 'D' | '-' = '-';
            if (c === label) col = 'C';
            else if (d === label) col = 'D';
            return {
              rowKey: rk,
              rowNo: Number.isFinite(rk) ? rk + 2 : null, // +1(从1开始) +1(跳过表头)
              col,
            };
          })
          .filter((x: any) => Number.isFinite(x?.rowKey))
          .map((x: any) => x as { rowKey: number; rowNo: number | null; col: 'C' | 'D' | '-' });
        matchedBaselineRowKeys = matchedRows.map((x: any) => Number(x?.rowKey))
          .filter((n: number) => Number.isFinite(n));
        if (matchedRows.length > 0) {
          matchedBaselineRowNo = Number(matchedRows[0]?.rowNo || 0) || null;
          matchedBaselineColumn = (matchedRows[0]?.col || '-') as 'C' | 'D' | '-';
        }
        const hitDoneRow = matchedBaselineRowKeys.some((rk) => readyBaselineDoneRowKeys.has(rk));
        if (hitDoneRow) {
          ok = false;
          result = '警告';
          reason = `该行记录已匹配完成，禁止再次匹配 ${target} -> ${label}`;
        } else {
          const allowed = labelingConstraintMap.get(target) || new Set<string>();
        if (!allowed.size) {
          ok = false;
          result = '警告';
          reason = `未找到匹配的出库对象 ${target}`;
        } else if (!allowed.has(label)) {
          ok = false;
          result = '警告';
          reason = `与约束不匹配（允许：${Array.from(allowed).slice(0, 3).join(' / ') || '-'})`;
        } else {
          ok = true;
          result = '成功';
          reason = '与贴单约束匹配';
        }
        }
      }
    }
    const now = new Date();
    const rec = {
      id: `local-labeling-${now.getTime()}-${Math.random()}`,
      time: now.toISOString(),
      event: '贴单记录',
      action: '贴单记录',
      target_type: '-',
      container_code: '-',
      target,
      label_code: label,
      verify_mode: hasConstraint ? 'constraint' : 'no_constraint',
      verify_result: ok ? 'pass' : 'warn',
      verify_reason: reason,
      baseline_file_name: hasConstraint ? (labelingConstraintName || '-') : '',
      baseline_row_no: matchedBaselineRowNo || undefined,
      matched_column: matchedBaselineColumn,
      qty: '',
      result,
      operator_name: '-',
      remark: `操作对象:${target}；运输标签:${label}；校验:${reason}`,
    };
    setLabelingPendingOperationRecords((prev) => ([rec, ...(Array.isArray(prev) ? prev : [])]));
    setLabelingSessionRows((prev) => ([{
      key: rec.id,
      time: now.toLocaleTimeString(),
      target,
      label,
      result,
      remark: reason,
    }, ...(Array.isArray(prev) ? prev : [])]));
    if (ok && hasConstraint) {
      const matchKey = `${target}::${label}`;
      setLabelingConsumedMatchKeys((prev) => {
        const next = new Set<string>(prev);
        next.add(matchKey);
        return next;
      });
      if (matchedBaselineRowKeys.length > 0) {
        setReadyBaselineDoneRowKeys((prev) => {
          const next = new Set<number>(prev);
          matchedBaselineRowKeys.forEach((k) => next.add(k));
          return next;
        });
      }
    }
    if (!ok && labelingWarnBeep) playLabelingTone('warn');
    if (ok) playLabelingTone('ok');
    setLabelingLastResult({ ok, text: ok ? `记录成功：${target} → ${label}` : `警告:${reason}` });
    if (labelingAutoNext) {
      setLabelingTargetCode('');
      setLabelingLabelCode('');
      setLabelingStep('target');
      focusLabelingTargetInput();
    } else {
      setLabelingLabelCode('');
      setLabelingStep('label');
    }
  }, [labelingTargetCode, labelingLabelCode, labelingConstraintReady, labelingConstraintMap, labelingConsumedMatchKeys, labelingWarnBeep, playLabelingTone, labelingAutoNext, parseAndValidateLabelingTargetCode, handleLabelingTargetInvalid, focusLabelingTargetInput, readyBaselineInlineData, readyBaselineDoneRowKeys, labelingConstraintName]);
  useEffect(() => {
    if (!labelingModalOpen) return;
    const t = setTimeout(() => {
      try {
        if (labelingStep === 'target') labelingTargetInputRef.current?.focus?.();
        else labelingLabelInputRef.current?.focus?.();
      } catch {}
    }, 50);
    return () => clearTimeout(t);
  }, [labelingModalOpen, labelingStep]);
  useEffect(() => {
    if (!labelingModalOpen) return;
    void reloadLabelingConstraint();
  }, [
    labelingModalOpen,
    firstReadyBaselinePreviewCandidate?.baselineId,
    firstReadyBaselinePreviewCandidate?.fileId,
    firstReadyBaselinePreviewCandidate?.fileName,
    reloadLabelingConstraint,
  ]);

  const operationRows = useMemo(() => {
    const src = ((Array.isArray(localOperationRecords) && localOperationRecords) || []) as any[];
    const merged = src.map((r: any, idx: number) => ({ __source: 'local', __idx: idx, ...r }));
    return merged.map((r: any, idx: number) => {
      const key = String(r?.id ?? `${idx}-${r?.time || ''}-${r?.action || ''}`);
      const event = normalizeText(r?.event || r?.action || r?.op || r?.type);
      const isStart = event === '开始容器';
      return {
        key,
        raw_id: r?.id,
        source: String(r?.__source || 'backend'),
        raw: r,
        index: idx + 1,
        raw_time: (r?.time || r?.created_at || r?.created_time),
        time: normalizeText(r?.time || r?.created_at || r?.created_time),
        event,
        target_type: normalizeText(r?.target_type),
        container_code: normalizeText(r?.container_code || r?.container || r?.container_system_code || (isStart ? (r?.target || r?.target_code || r?.code) : '')),
        target: isStart ? '-' : normalizeText(r?.target || r?.target_code || r?.code),
        label_code: normalizeText(r?.label_code || r?.shipping_label_code),
        verify_mode: normalizeText(r?.verify_mode),
        verify_result: normalizeText(r?.verify_result),
        verify_reason: normalizeText(r?.verify_reason || r?.remark),
        baseline_row_no: r?.baseline_row_no,
        matched_column: normalizeText(r?.matched_column),
        baseline_file_name: normalizeText(r?.baseline_file_name),
        remark: normalizeText(r?.remark),
        qty: normalizeText(r?.qty ?? r?.quantity ?? r?.amount),
      };
    });
  }, [localOperationRecords]);
  useEffect(() => {
    const cc = String(activePackBoxCode || '').trim();
    if (!cc) return;
    const recs = (Array.isArray(localOperationRecords) ? localOperationRecords : []).filter((r: any) =>
      String(r?.event || r?.action || '').trim() === '加入记录并放入容器'
      && String(r?.container_code || '').trim() === cc,
    );
    if (!recs.length) return;
    // 先写入记录里已带的重量，减少后续请求
    setTargetWeightGByKey((prev) => {
      const next = { ...(prev || {}) } as Record<string, number | null>;
      let changed = false;
      recs.forEach((r: any) => {
        const tcode = String(r?.target || '').trim().toUpperCase();
        if (!tcode) return;
        const ttype = String(r?.target_type || '').trim();
        const key = (ttype === 'instruction_output_container' ? `ioc:${tcode}` : `iu:${tcode}`);
        const w = Number(r?.target_weight_g);
        if (Number.isFinite(w) && w > 0 && next[key] !== w) {
          next[key] = w;
          changed = true;
        }
      });
      return changed ? next : (prev || {});
    });
    // IU 缺重量时按条码补拉
    recs.forEach((r: any) => {
      const tcode = String(r?.target || '').trim().toUpperCase();
      if (!tcode) return;
      const ttype = String(r?.target_type || '').trim();
      if (ttype !== 'inventory_unit') return;
      const key = `iu:${tcode}`;
      const hit = (targetWeightGByKey as any)?.[key];
      if (hit != null) return;
      if (targetWeightLoadingRef.current.has(key)) return;
      targetWeightLoadingRef.current.add(key);
      void (async () => {
        try {
          const resp = await getInventoryUnitByBarcode(tcode);
          const d: any = (resp as any)?.data || {};
          const w = Number(d?.actual_weight ?? d?.actual_weight_g ?? d?.weight ?? 0);
          setTargetWeightGByKey((prev) => ({ ...(prev || {}), [key]: (Number.isFinite(w) && w > 0) ? w : null }));
        } catch {
          setTargetWeightGByKey((prev) => ({ ...(prev || {}), [key]: null }));
        } finally {
          targetWeightLoadingRef.current.delete(key);
        }
      })();
    });
  }, [activePackBoxCode, localOperationRecords, targetWeightGByKey]);
  const targetPickerFilteredRows = useMemo(() => {
    const rows = (hasIocTarget ? iocDisplayRows : inventoryRows) as any[];
    const q = String(targetPickerSearch || '').trim().toLowerCase();
    if (!q) return Array.isArray(rows) ? rows : [];
    const list = Array.isArray(rows) ? rows : [];
    return list.filter((row: any) => {
      if (hasIocTarget) {
        const systemCode = String(row?.output_code || row?.display_code || '').toLowerCase();
        const contentText = String(row?.output_contents_detail || '').toLowerCase();
        return systemCode.includes(q) || contentText.includes(q);
      }
      const systemCode = String(row?.unit_code || '').toLowerCase();
      const contentText = (Array.isArray(row?.content_lines) ? row.content_lines : [])
        .map((x: any) => String(x || ''))
        .join(' ')
        .toLowerCase();
      return systemCode.includes(q) || contentText.includes(q);
    });
  }, [hasIocTarget, iocDisplayRows, inventoryRows, targetPickerSearch]);
  const resolveTargetQty = useCallback((row: any): number => {
    const cands = [
      row?.target_qty,
      row?.expected_qty,
      row?.output_contents_total_qty,
    ];
    for (const v of cands) {
      const n = Number(v);
      if (Number.isFinite(n)) return Math.floor(n);
    }
    return 0;
  }, []);
  const resolveDoneQty = useCallback((row: any): number => {
    const cands = [
      row?.done_qty,
      row?.completed_qty,
      row?.picked_qty,
    ];
    for (const v of cands) {
      const n = Number(v);
      if (Number.isFinite(n)) return Math.floor(n);
    }
    return 0;
  }, []);
  const isUseAllAvailableTarget = useCallback((row: any): boolean => {
    const v = row?.use_all_available;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v === 1;
    const s = String(v ?? '').trim().toLowerCase();
    return ['1', 'true', 'yes', 'y', '是'].includes(s);
  }, []);
  const resolveAllowQty = useCallback((row: any): number => {
    const done = Math.max(0, resolveDoneQty(row));
    const targetType = hasIocTarget ? 'instruction_output_container' : 'inventory_unit';
    const targetCode = normalizeAdjustTargetCode(
      row?.output_code
      || row?.display_code
      || row?.unit_code
      || row?.key
      || row?.__key
      || '',
    );
    const poolHeld = targetCode ? Math.max(0, Math.floor(Number(adjustPoolHeldByTargetKey.get(`${targetType}:${targetCode}`) || 0) || 0)) : 0;
    const effectiveDone = Math.max(0, done + poolHeld);
    if (hasIocTarget) return Math.max(0, 1 - effectiveDone);
    if (isUseAllAvailableTarget(row)) return Number.POSITIVE_INFINITY;
    const target = Math.max(0, resolveTargetQty(row));
    return Math.max(0, target - effectiveDone);
  }, [hasIocTarget, resolveDoneQty, resolveTargetQty, isUseAllAvailableTarget, normalizeAdjustTargetCode, adjustPoolHeldByTargetKey]);
  const resolveOccupiedQty = useCallback((row: any): number => {
    const done = Math.max(0, resolveDoneQty(row));
    const targetType = hasIocTarget ? 'instruction_output_container' : 'inventory_unit';
    const targetCode = normalizeAdjustTargetCode(
      row?.output_code
      || row?.display_code
      || row?.unit_code
      || row?.key
      || row?.__key
      || '',
    );
    const poolHeld = targetCode ? Math.max(0, Math.floor(Number(adjustPoolHeldByTargetKey.get(`${targetType}:${targetCode}`) || 0) || 0)) : 0;
    return Math.max(0, done + poolHeld);
  }, [hasIocTarget, resolveDoneQty, normalizeAdjustTargetCode, adjustPoolHeldByTargetKey]);
  const playErrorBeep = useCallback(() => {
    try {
      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = beepAudioCtxRef.current || new AudioCtx();
      beepAudioCtxRef.current = ctx;
      try {
        if (ctx.state === 'suspended') {
          void ctx.resume();
        }
      } catch {}
      const beep = (at: number, freq: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'square';
        osc.frequency.setValueAtTime(freq, at);
        gain.gain.setValueAtTime(0.0001, at);
        gain.gain.exponentialRampToValueAtTime(0.22, at + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.2);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(at);
        osc.stop(at + 0.22);
      };
      const t0 = ctx.currentTime;
      beep(t0, 980);
      beep(t0 + 0.24, 820);
    } catch {}
  }, []);
  const resetScanEntryAndFocus = useCallback(() => {
    setPackTargetScan('');
    setPackTargetQty(1);
    try {
      setTimeout(() => {
        try { scanTargetInputRef.current?.focus?.(); } catch {}
      }, 0);
    } catch {}
  }, []);
  const showOverLimitWarningModal = useCallback((
    rows: Array<{ code: string; requested: number; accepted: number; remaining: number }>,
    onClose?: () => void,
  ) => {
    const list = (Array.isArray(rows) ? rows : [])
      .map((r) => ({
        code: String(r?.code || '').trim() || '-',
        requested: Math.max(0, Math.floor(Number(r?.requested || 0) || 0)),
        accepted: Math.max(0, Math.floor(Number(r?.accepted || 0) || 0)),
        remaining: Math.max(0, Math.floor(Number(r?.remaining || 0) || 0)),
      }))
      .filter((r) => r.remaining > 0);
    if (!list.length) return;
    const totalRequested = list.reduce((acc, r) => acc + r.requested, 0);
    const totalAccepted = list.reduce((acc, r) => acc + r.accepted, 0);
    const totalRemaining = list.reduce((acc, r) => acc + r.remaining, 0);
    playErrorBeep();
    Modal.warning({
      title: '数量超过可允许总和，部分无法生成记录',
      centered: true,
      icon: null,
      width: 760,
      onOk: () => { try { onClose?.(); } catch {} },
      onCancel: () => { try { onClose?.(); } catch {} },
      content: (
        <div style={{ whiteSpace: 'pre-line', lineHeight: 1.7 }}>
          <div
            style={{
              background: '#fa541c',
              color: '#fff566',
              borderRadius: 6,
              padding: '8px 10px',
              fontWeight: 700,
              marginBottom: 10,
            }}
          >
            请将超过数量限制的产品单独放置,后续会放回仓位,不要和无法操作的破损LPN等产品混放在一起
          </div>
          <div style={{ background: '#ffffff', border: '1px solid #f0f0f0', borderRadius: 6, padding: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ padding: '4px 8px', border: '1px solid #f0f0f0' }}>{`输入数量：${totalRequested}`}</td>
                  <td style={{ padding: '4px 8px', border: '1px solid #f0f0f0', color: '#389e0d', fontWeight: 700 }}>{`成功生成记录数量：${totalAccepted}`}</td>
                </tr>
                <tr>
                  <td style={{ padding: '4px 8px', border: '1px solid #f0f0f0', color: '#cf1322', fontWeight: 700 }}>{`剩余无法生成记录数量：${totalRemaining}`}</td>
                  <td style={{ padding: '4px 8px', border: '1px solid #f0f0f0' }} />
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 10 }}>
            <div style={{ fontWeight: 700, color: '#595959', marginBottom: 6 }}>本次拆分明细（按操作对象）</div>
            <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6, padding: 10 }}>
              {list.map((r) => (
                <div key={`over_${r.code}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                  <span style={{ fontFamily: 'monospace' }}>{r.code}</span>
                  <span>{`输入 ${r.requested} / 成功 ${r.accepted} / 剩余 ${r.remaining}`}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    });
  }, [playErrorBeep]);
  type AdjustLot = { target_type: string; target: string; qty: number; target_weight_g?: number };
  const pushLocalOperationRecords = useCallback((recs: any[]) => {
    const list = (Array.isArray(recs) ? recs : []).filter(Boolean);
    if (!list.length) return;
    setLocalOperationRecords((prev) => ([...list, ...(Array.isArray(prev) ? prev : [])]));
  }, []);
  const adjustWarn = useCallback((text: string) => {
    try { playErrorBeep(); } catch {}
    message.warning(text);
  }, [playErrorBeep]);
  const adjustError = useCallback((text: string) => {
    try { playErrorBeep(); } catch {}
    message.error(text);
  }, [playErrorBeep]);
  const buildAdjustStacks = useCallback((containerCodeRaw: any) => {
    const cc = String(containerCodeRaw || '').trim();
    const byTarget = new Map<string, AdjustLot[]>();
    try {
      const list: any[] = Array.isArray(localOperationRecords) ? localOperationRecords.slice().reverse() : [];
      for (const r of list) {
        if (String(r?.container_code || '').trim() !== cc) continue;
        const targetCode = normalizeAdjustTargetCode(r?.target || r?.target_code || r?.code);
        if (!targetCode || targetCode === '-') continue;
        const rawType = String(r?.target_type || '').trim();
        const targetType = rawType || resolveAdjustTargetType();
        if (!targetType) continue;
        const key = makeAdjustTargetKey(targetType, targetCode);
        if (!key) continue;
        const qty = Math.floor(Number(r?.qty ?? r?.quantity ?? r?.amount ?? 0) || 0);
        if (!Number.isFinite(qty) || qty === 0) continue;
        const stack = byTarget.get(key) || [];
        if (qty > 0) {
          stack.push({
            target_type: targetType,
            target: targetCode,
            qty,
            target_weight_g: Number.isFinite(Number(r?.target_weight_g)) && Number(r?.target_weight_g) > 0 ? Number(r?.target_weight_g) : undefined,
          });
        } else {
          let need = Math.abs(qty);
          while (need > 0 && stack.length) {
            const last = stack[stack.length - 1];
            if (last.qty > need) {
              last.qty -= need;
              need = 0;
              break;
            }
            need -= last.qty;
            stack.pop();
          }
        }
        byTarget.set(key, stack);
      }
    } catch {}
    return byTarget;
  }, [localOperationRecords, normalizeAdjustTargetCode, resolveAdjustTargetType, makeAdjustTargetKey]);
  const consumeFromAdjustStacks = useCallback((stacks: Map<string, AdjustLot[]>, targetKeyRaw: any, qtyRaw: any): { ok: boolean; lots: AdjustLot[]; reason?: string } => {
    const targetKey = String(targetKeyRaw || '').trim();
    const qty = Math.floor(Number(qtyRaw || 0) || 0);
    if (!targetKey || qty <= 0) return { ok: false, lots: [], reason: '参数无效' };
    const stack = stacks.get(targetKey) || [];
    let avail = 0;
    stack.forEach((x) => { avail += Math.floor(Number(x?.qty || 0) || 0); });
    if (avail < qty) return { ok: false, lots: [], reason: `可用数量不足：可用${avail}，需要${qty}` };
    let need = qty;
    const out: AdjustLot[] = [];
    while (need > 0 && stack.length) {
      const last = stack[stack.length - 1];
      const take = Math.min(need, Math.floor(Number(last?.qty || 0) || 0));
      if (take <= 0) {
        stack.pop();
        continue;
      }
      out.push({ ...last, qty: take });
      last.qty -= take;
      need -= take;
      if (last.qty <= 0) stack.pop();
    }
    stacks.set(targetKey, stack);
    return { ok: true, lots: out };
  }, []);
  const resolveTargetKeyFromStacks = useCallback((stacks: Map<string, AdjustLot[]>, targetCodeRaw: any) => {
    const code = normalizeAdjustTargetCode(targetCodeRaw);
    if (!code || code === '-') return '';
    const preferred = makeAdjustTargetKey(resolveAdjustTargetType(), code);
    if (preferred && stacks.has(preferred)) return preferred;
    const keys = Array.from(stacks.keys()).filter((k) => String(k).endsWith(`:${code}`));
    if (keys.length === 1) return keys[0];
    if (keys.length > 1) {
      const withAvail = keys
        .map((k) => ({ k, n: (stacks.get(k) || []).reduce((s, x) => s + Math.max(0, Math.floor(Number(x?.qty || 0) || 0)), 0) }))
        .sort((a, b) => (b.n - a.n) || a.k.localeCompare(b.k));
      if (withAvail[0]?.n > 0) return withAvail[0].k;
      return withAvail[0]?.k || '';
    }
    return preferred || '';
  }, [normalizeAdjustTargetCode, makeAdjustTargetKey, resolveAdjustTargetType]);
  const markContainerLabelDirty = useCallback((containerCodeRaw: any) => {
    const cc = String(containerCodeRaw || '').trim();
    if (!cc || cc === String(adjustPoolCode || '').trim()) return;
    setLabelDirtyByCode((prev) => ({ ...(prev || {}), [cc]: true }));
  }, [adjustPoolCode]);
  const takeOutToAdjustPool = useCallback(async () => {
    const from = String(adjustFromBoxCode || '').trim();
    const targetCode = normalizeAdjustTargetCode(adjustTargetCode);
    const qty = hasIocTarget ? 1 : Math.max(1, Math.floor(Number(adjustQty || 1)));
    if (!from) { adjustWarn('请选择取出容器'); return; }
    if (!targetCode) { adjustWarn('请输入操作目标编码'); return; }
    if (!Number.isFinite(qty) || qty <= 0) { adjustWarn('数量需为正整数'); return; }
    setAdjustLoading(true);
    try {
      const stacks = buildAdjustStacks(from);
      const targetKey = resolveTargetKeyFromStacks(stacks, targetCode);
      if (!targetKey) { adjustWarn('该容器中未找到该操作目标'); return; }
      const got = consumeFromAdjustStacks(stacks, targetKey, qty);
      if (!got.ok) { adjustError(got.reason || '取出失败'); return; }
      const now = Date.now();
      const recs: any[] = [];
      got.lots.forEach((lot) => {
        recs.push({
          id: `local-adjust-take-${now}-${Math.random()}`,
          time: new Date(now).toISOString(),
          event: '调整-取出',
          action: '调整-取出',
          container_code: from,
          target_type: lot.target_type,
          target_weight_g: lot.target_weight_g,
          target: lot.target,
          qty: -Math.abs(Math.floor(Number(lot.qty || 0) || 0)),
          result: '成功',
          operator_name: '-',
          remark: `调整取出 -> 记录池 ${adjustPoolCode}`,
        });
        recs.push({
          id: `local-adjust-pool-in-${now}-${Math.random()}`,
          time: new Date(now).toISOString(),
          event: '调整-入池',
          action: '调整-入池',
          container_code: adjustPoolCode,
          target_type: lot.target_type,
          target_weight_g: lot.target_weight_g,
          target: lot.target,
          qty: Math.abs(Math.floor(Number(lot.qty || 0) || 0)),
          result: '成功',
          operator_name: '-',
          remark: `来自容器 ${from}`,
        });
      });
      pushLocalOperationRecords(recs);
      markContainerLabelDirty(from);
      message.success('已取出到调整记录池');
      setAdjustTargetCode('');
      if (!hasIocTarget) setAdjustQty(1);
    } finally {
      setAdjustLoading(false);
    }
  }, [adjustFromBoxCode, adjustTargetCode, adjustQty, hasIocTarget, buildAdjustStacks, resolveTargetKeyFromStacks, consumeFromAdjustStacks, adjustPoolCode, pushLocalOperationRecords, markContainerLabelDirty, adjustWarn, adjustError]);
  const putInFromAdjustPool = useCallback(async (toContainerCodeRaw: any) => {
    const to = String(toContainerCodeRaw || '').trim();
    const targetCode = normalizeAdjustTargetCode(adjustTargetCode);
    const qty = hasIocTarget ? 1 : Math.max(1, Math.floor(Number(adjustQty || 1)));
    if (!to) { adjustWarn('请选择放入容器'); return; }
    if (!targetCode) { adjustWarn('请输入操作目标编码'); return; }
    if (!Number.isFinite(qty) || qty <= 0) { adjustWarn('数量需为正整数'); return; }
    setAdjustLoading(true);
    try {
      const stacks = buildAdjustStacks(adjustPoolCode);
      const targetKey = resolveTargetKeyFromStacks(stacks, targetCode);
      if (!targetKey) { adjustWarn('调整记录池中未找到该操作目标'); return; }
      const got = consumeFromAdjustStacks(stacks, targetKey, qty);
      if (!got.ok) { adjustError(got.reason || '记录池数量不足'); return; }
      const now = Date.now();
      const recs: any[] = [];
      got.lots.forEach((lot) => {
        recs.push({
          id: `local-adjust-pool-out-${now}-${Math.random()}`,
          time: new Date(now).toISOString(),
          event: '调整-出池',
          action: '调整-出池',
          container_code: adjustPoolCode,
          target_type: lot.target_type,
          target_weight_g: lot.target_weight_g,
          target: lot.target,
          qty: -Math.abs(Math.floor(Number(lot.qty || 0) || 0)),
          result: '成功',
          operator_name: '-',
          remark: `放入容器 ${to}`,
        });
        recs.push({
          id: `local-adjust-put-${now}-${Math.random()}`,
          time: new Date(now).toISOString(),
          event: '调整-放入',
          action: '调整-放入',
          container_code: to,
          target_type: lot.target_type,
          target_weight_g: lot.target_weight_g,
          target: lot.target,
          qty: Math.abs(Math.floor(Number(lot.qty || 0) || 0)),
          result: '成功',
          operator_name: '-',
          remark: `来自调整记录池 ${adjustPoolCode}`,
        });
      });
      pushLocalOperationRecords(recs);
      markContainerLabelDirty(to);
      message.success('已从调整记录池放入目标容器');
      setAdjustTargetCode('');
      if (!hasIocTarget) setAdjustQty(1);
    } finally {
      setAdjustLoading(false);
    }
  }, [adjustPoolCode, adjustTargetCode, adjustQty, hasIocTarget, buildAdjustStacks, resolveTargetKeyFromStacks, consumeFromAdjustStacks, pushLocalOperationRecords, markContainerLabelDirty, adjustWarn, adjustError]);
  const resolveAdjustCandidatesByScan = useCallback((scanCodeRaw: any, sourceContainerCodeRaw: any, qtyRaw: any) => {
    if (hasIocTarget) return { allocs: [] as Array<{ row: any; key: string; qty: number }>, remaining: 0, candidates: [] as any[] };
    const scanCode = String(scanCodeRaw || '').trim().toUpperCase();
    const sourceCode = String(sourceContainerCodeRaw || '').trim();
    const requestedQty = Math.max(0, Math.floor(Number(qtyRaw || 0) || 0));
    if (!scanCode || !sourceCode || requestedQty <= 0) return { allocs: [] as Array<{ row: any; key: string; qty: number }>, remaining: requestedQty, candidates: [] as any[] };
    const rows = Array.isArray(inventoryRows) ? inventoryRows : [];
    const sourceStacks = buildAdjustStacks(sourceCode);
    const availByKey = new Map<string, number>();
    Array.from(sourceStacks.entries()).forEach(([k, lots]) => {
      const q = (lots || []).reduce((s, x) => s + Math.max(0, Math.floor(Number(x?.qty || 0) || 0)), 0);
      availByKey.set(String(k || ''), Math.max(0, q));
    });
    const toKey = (row: any) => {
      const code = normalizeAdjustTargetCode(row?.unit_code || row?.key || row?.__key || '');
      if (!code || code === '-') return '';
      return makeAdjustTargetKey('inventory_unit', code);
    };
    const exact = rows.filter((row: any) => String(row?.unit_code || '').trim().toUpperCase() === scanCode);
    const candidatesRaw = exact.length > 0 ? exact : rows.filter((row: any) => {
      const unitTypeCode = String(row?.unit_type_code || '').trim().toUpperCase();
      const unitTypeText = String(row?.unit_type || '').trim();
      const isLiu = unitTypeCode === 'LIU' || unitTypeText.includes('散货');
      if (!isLiu) return false;
      const codes = Array.isArray(row?.sorting_barcode_list) ? row.sorting_barcode_list : [];
      return codes.some((c: any) => String(c || '').trim().toUpperCase() === scanCode);
    });
    const candidates = candidatesRaw.filter((row: any) => {
      const key = toKey(row);
      return !!key && Math.max(0, Number(availByKey.get(key) || 0)) > 0;
    });
    if (!candidates.length) return { allocs: [] as Array<{ row: any; key: string; qty: number }>, remaining: requestedQty, candidates };
    const nonAll = candidates
      .filter((r: any) => !isUseAllAvailableTarget(r))
      .slice()
      .sort((a: any, b: any) => {
        const da = Math.max(0, resolveTargetQty(a) - resolveDoneQty(a));
        const db = Math.max(0, resolveTargetQty(b) - resolveDoneQty(b));
        if (da !== db) return da - db;
        return String(a?.unit_code || '').localeCompare(String(b?.unit_code || ''));
      });
    const all = candidates
      .filter((r: any) => isUseAllAvailableTarget(r))
      .slice()
      .sort((a: any, b: any) => {
        const da = Math.max(0, resolveTargetQty(a) - resolveDoneQty(a));
        const db = Math.max(0, resolveTargetQty(b) - resolveDoneQty(b));
        if (da !== db) return da - db;
        return String(a?.unit_code || '').localeCompare(String(b?.unit_code || ''));
      });
    let remaining = requestedQty;
    const allocs: Array<{ row: any; key: string; qty: number }> = [];
    const consume = (row: any, opts?: { allMode?: boolean; lastAll?: boolean }) => {
      if (remaining <= 0) return;
      const key = toKey(row);
      if (!key) return;
      const avail = Math.max(0, Number(availByKey.get(key) || 0));
      if (avail <= 0) return;
      // 调整场景按“当前容器/调整池可用量”分配，不使用 target-done 差额口径。
      // 仍保留 nonAll -> all 的分组顺序，以保证与现有行为一致。
      void opts;
      const cap = Math.max(0, Math.min(avail, remaining));
      if (cap <= 0) return;
      const take = Math.min(remaining, cap);
      if (take <= 0) return;
      allocs.push({ row, key, qty: take });
      remaining -= take;
      availByKey.set(key, Math.max(0, avail - take));
    };
    nonAll.forEach((row: any) => consume(row, { allMode: false, lastAll: false }));
    all.forEach((row: any, idx: number) => consume(row, { allMode: true, lastAll: idx === all.length - 1 }));
    return { allocs, remaining, candidates };
  }, [hasIocTarget, inventoryRows, buildAdjustStacks, normalizeAdjustTargetCode, makeAdjustTargetKey, isUseAllAvailableTarget, resolveTargetQty, resolveDoneQty]);
  const execAdjustByMode = useCallback(async () => {
    const mode = adjustMode;
    const from = String(adjustFromBoxCode || '').trim();
    const to = String(adjustToBoxCode || '').trim();
    const targetCode = normalizeAdjustTargetCode(adjustTargetCode);
    const qty = hasIocTarget ? 1 : Math.max(1, Math.floor(Number(adjustQty || 1)));
    const isMoveAll = mode === 'take_put' && !!adjustMoveAll;
    if (!isMoveAll) {
      if (!targetCode) { adjustWarn('请输入操作目标编码'); return; }
      if (!Number.isFinite(qty) || qty <= 0) { adjustWarn('数量需为正整数'); return; }
    }
    if (mode !== 'put_only' && !from) { adjustWarn('请选择取出容器'); return; }
    if (mode !== 'take_only' && !to) { adjustWarn('请选择放入容器'); return; }
    if (mode === 'take_put' && from && to && from === to) { adjustWarn('取出容器与放入容器不能相同'); return; }
    if (mode === 'take_only') {
      if (!hasIocTarget) {
        const planned = resolveAdjustCandidatesByScan(targetCode, from, qty);
        if (planned.allocs.length > 0) {
          setAdjustLoading(true);
          try {
            const stacks = buildAdjustStacks(from);
            const now = Date.now();
            const recs: any[] = [];
            planned.allocs.forEach((it: any) => {
              const got = consumeFromAdjustStacks(stacks, it.key, it.qty);
              if (!got.ok) return;
              (got.lots || []).forEach((lot: any) => {
                const q = Math.max(0, Math.floor(Number(lot?.qty || 0) || 0));
                if (q <= 0) return;
                recs.push({
                  id: `local-adjust-take-${now}-${Math.random()}`,
                  time: new Date(now).toISOString(),
                  event: '调整-取出',
                  action: '调整-取出',
                  container_code: from,
                  target_type: lot.target_type,
                  target_weight_g: lot.target_weight_g,
                  target: lot.target,
                  qty: -q,
                  result: '成功',
                  operator_name: '-',
                  remark: `调整取出 -> 记录池 ${adjustPoolCode}`,
                });
                recs.push({
                  id: `local-adjust-pool-in-${now}-${Math.random()}`,
                  time: new Date(now).toISOString(),
                  event: '调整-入池',
                  action: '调整-入池',
                  container_code: adjustPoolCode,
                  target_type: lot.target_type,
                  target_weight_g: lot.target_weight_g,
                  target: lot.target,
                  qty: q,
                  result: '成功',
                  operator_name: '-',
                  remark: `来自容器 ${from}`,
                });
              });
            });
            if (!recs.length) { adjustWarn('本次未生成可用记录'); return; }
            pushLocalOperationRecords(recs);
            markContainerLabelDirty(from);
            if (planned.remaining > 0) adjustWarn(`已取出 ${Math.max(0, qty - planned.remaining)} 件，仍有 ${planned.remaining} 件超出可取出数量`);
            else message.success('已取出到调整记录池');
            setAdjustTargetCode('');
            setAdjustQty(1);
          } finally {
            setAdjustLoading(false);
          }
          return;
        }
      }
      await takeOutToAdjustPool();
      return;
    }
    if (mode === 'put_only') {
      if (!hasIocTarget) {
        const planned = resolveAdjustCandidatesByScan(targetCode, adjustPoolCode, qty);
        if (planned.allocs.length > 0) {
          setAdjustLoading(true);
          try {
            const stacks = buildAdjustStacks(adjustPoolCode);
            const now = Date.now();
            const recs: any[] = [];
            planned.allocs.forEach((it: any) => {
              const got = consumeFromAdjustStacks(stacks, it.key, it.qty);
              if (!got.ok) return;
              (got.lots || []).forEach((lot: any) => {
                const q = Math.max(0, Math.floor(Number(lot?.qty || 0) || 0));
                if (q <= 0) return;
                recs.push({
                  id: `local-adjust-pool-out-${now}-${Math.random()}`,
                  time: new Date(now).toISOString(),
                  event: '调整-出池',
                  action: '调整-出池',
                  container_code: adjustPoolCode,
                  target_type: lot.target_type,
                  target_weight_g: lot.target_weight_g,
                  target: lot.target,
                  qty: -q,
                  result: '成功',
                  operator_name: '-',
                  remark: `放入容器 ${to}`,
                });
                recs.push({
                  id: `local-adjust-put-${now}-${Math.random()}`,
                  time: new Date(now).toISOString(),
                  event: '调整-放入',
                  action: '调整-放入',
                  container_code: to,
                  target_type: lot.target_type,
                  target_weight_g: lot.target_weight_g,
                  target: lot.target,
                  qty: q,
                  result: '成功',
                  operator_name: '-',
                  remark: `来自调整记录池 ${adjustPoolCode}`,
                });
              });
            });
            if (!recs.length) { adjustWarn('本次未生成可用记录'); return; }
            pushLocalOperationRecords(recs);
            markContainerLabelDirty(to);
            if (planned.remaining > 0) adjustWarn(`已放入 ${Math.max(0, qty - planned.remaining)} 件，仍有 ${planned.remaining} 件超出可用数量`);
            else message.success('已从调整记录池放入目标容器');
            setAdjustTargetCode('');
            setAdjustQty(1);
          } finally {
            setAdjustLoading(false);
          }
          return;
        }
      }
      await putInFromAdjustPool(to);
      return;
    }
    if (isMoveAll) {
      setAdjustLoading(true);
      try {
        const stacks = buildAdjustStacks(from);
        const now = Date.now();
        const recs: any[] = [];
        let moved = 0;
        Array.from(stacks.entries()).forEach(([, lots]) => {
          (lots || []).forEach((lot) => {
            const q = Math.max(0, Math.floor(Number(lot?.qty || 0) || 0));
            if (q <= 0) return;
            moved += q;
            recs.push({
              id: `local-adjust-take-all-${now}-${Math.random()}`,
              time: new Date(now).toISOString(),
              event: '调整-取出',
              action: '调整-取出',
              container_code: from,
              target_type: lot.target_type,
              target_weight_g: lot.target_weight_g,
              target: lot.target,
              qty: -q,
              result: '成功',
              operator_name: '-',
              remark: `全量移动 -> 记录池 ${adjustPoolCode}`,
            });
            recs.push({
              id: `local-adjust-pool-in-all-${now}-${Math.random()}`,
              time: new Date(now).toISOString(),
              event: '调整-入池',
              action: '调整-入池',
              container_code: adjustPoolCode,
              target_type: lot.target_type,
              target_weight_g: lot.target_weight_g,
              target: lot.target,
              qty: q,
              result: '成功',
              operator_name: '-',
              remark: `来自容器 ${from}`,
            });
            recs.push({
              id: `local-adjust-pool-out-all-${now}-${Math.random()}`,
              time: new Date(now).toISOString(),
              event: '调整-出池',
              action: '调整-出池',
              container_code: adjustPoolCode,
              target_type: lot.target_type,
              target_weight_g: lot.target_weight_g,
              target: lot.target,
              qty: -q,
              result: '成功',
              operator_name: '-',
              remark: `全量移动至容器 ${to}`,
            });
            recs.push({
              id: `local-adjust-put-all-${now}-${Math.random()}`,
              time: new Date(now).toISOString(),
              event: '调整-放入',
              action: '调整-放入',
              container_code: to,
              target_type: lot.target_type,
              target_weight_g: lot.target_weight_g,
              target: lot.target,
              qty: q,
              result: '成功',
              operator_name: '-',
              remark: `来自调整记录池 ${adjustPoolCode}`,
            });
          });
        });
        if (!recs.length || moved <= 0) {
          message.info('该容器当前无可移动的产品');
          return;
        }
        pushLocalOperationRecords(recs);
        markContainerLabelDirty(from);
        markContainerLabelDirty(to);
        message.success(`已移动容器内全部产品（合计${moved}件）`);
      } finally {
        setAdjustLoading(false);
      }
      return;
    }
    setAdjustLoading(true);
    try {
      if (!hasIocTarget) {
        const planned = resolveAdjustCandidatesByScan(targetCode, from, qty);
        if (planned.allocs.length > 0) {
          const stacks = buildAdjustStacks(from);
          const now = Date.now();
          const recs: any[] = [];
          planned.allocs.forEach((it: any) => {
            const got = consumeFromAdjustStacks(stacks, it.key, it.qty);
            if (!got.ok) return;
            (got.lots || []).forEach((lot: any) => {
              const q = Math.max(0, Math.floor(Number(lot?.qty || 0) || 0));
              if (!q) return;
              recs.push({
                id: `local-adjust-take-move-${now}-${Math.random()}`,
                time: new Date(now).toISOString(),
                event: '调整-取出',
                action: '调整-取出',
                container_code: from,
                target_type: lot.target_type,
                target_weight_g: lot.target_weight_g,
                target: lot.target,
                qty: -q,
                result: '成功',
                operator_name: '-',
                remark: `移动 -> 记录池 ${adjustPoolCode}`,
              });
              recs.push({
                id: `local-adjust-pool-in-move-${now}-${Math.random()}`,
                time: new Date(now).toISOString(),
                event: '调整-入池',
                action: '调整-入池',
                container_code: adjustPoolCode,
                target_type: lot.target_type,
                target_weight_g: lot.target_weight_g,
                target: lot.target,
                qty: q,
                result: '成功',
                operator_name: '-',
                remark: `来自容器 ${from}`,
              });
              recs.push({
                id: `local-adjust-pool-out-move-${now}-${Math.random()}`,
                time: new Date(now).toISOString(),
                event: '调整-出池',
                action: '调整-出池',
                container_code: adjustPoolCode,
                target_type: lot.target_type,
                target_weight_g: lot.target_weight_g,
                target: lot.target,
                qty: -q,
                result: '成功',
                operator_name: '-',
                remark: `移动至容器 ${to}`,
              });
              recs.push({
                id: `local-adjust-put-move-${now}-${Math.random()}`,
                time: new Date(now).toISOString(),
                event: '调整-放入',
                action: '调整-放入',
                container_code: to,
                target_type: lot.target_type,
                target_weight_g: lot.target_weight_g,
                target: lot.target,
                qty: q,
                result: '成功',
                operator_name: '-',
                remark: `来自调整记录池 ${adjustPoolCode}`,
              });
            });
          });
          if (!recs.length) { adjustWarn('本次未生成可用记录'); return; }
          pushLocalOperationRecords(recs);
          markContainerLabelDirty(from);
          markContainerLabelDirty(to);
          if (planned.remaining > 0) adjustWarn(`已移动 ${Math.max(0, qty - planned.remaining)} 件，仍有 ${planned.remaining} 件超出可移动数量`);
          else message.success('已取出并放入目标容器（经调整记录池记账）');
          setAdjustTargetCode('');
          setAdjustQty(1);
          return;
        }
      }
      const stacks = buildAdjustStacks(from);
      const targetKey = resolveTargetKeyFromStacks(stacks, targetCode);
      if (!targetKey) { adjustWarn('源容器中未找到该操作目标'); return; }
      const got = consumeFromAdjustStacks(stacks, targetKey, qty);
      if (!got.ok) { adjustError(got.reason || '移动失败：数量不足'); return; }
      const now = Date.now();
      const recs: any[] = [];
      got.lots.forEach((lot) => {
        const q = Math.max(0, Math.floor(Number(lot.qty || 0) || 0));
        if (!q) return;
        recs.push({
          id: `local-adjust-take-move-${now}-${Math.random()}`,
          time: new Date(now).toISOString(),
          event: '调整-取出',
          action: '调整-取出',
          container_code: from,
          target_type: lot.target_type,
          target_weight_g: lot.target_weight_g,
          target: lot.target,
          qty: -q,
          result: '成功',
          operator_name: '-',
          remark: `移动 -> 记录池 ${adjustPoolCode}`,
        });
        recs.push({
          id: `local-adjust-pool-in-move-${now}-${Math.random()}`,
          time: new Date(now).toISOString(),
          event: '调整-入池',
          action: '调整-入池',
          container_code: adjustPoolCode,
          target_type: lot.target_type,
          target_weight_g: lot.target_weight_g,
          target: lot.target,
          qty: q,
          result: '成功',
          operator_name: '-',
          remark: `来自容器 ${from}`,
        });
        recs.push({
          id: `local-adjust-pool-out-move-${now}-${Math.random()}`,
          time: new Date(now).toISOString(),
          event: '调整-出池',
          action: '调整-出池',
          container_code: adjustPoolCode,
          target_type: lot.target_type,
          target_weight_g: lot.target_weight_g,
          target: lot.target,
          qty: -q,
          result: '成功',
          operator_name: '-',
          remark: `移动至容器 ${to}`,
        });
        recs.push({
          id: `local-adjust-put-move-${now}-${Math.random()}`,
          time: new Date(now).toISOString(),
          event: '调整-放入',
          action: '调整-放入',
          container_code: to,
          target_type: lot.target_type,
          target_weight_g: lot.target_weight_g,
          target: lot.target,
          qty: q,
          result: '成功',
          operator_name: '-',
          remark: `来自调整记录池 ${adjustPoolCode}`,
        });
      });
      pushLocalOperationRecords(recs);
      markContainerLabelDirty(from);
      markContainerLabelDirty(to);
      message.success('已取出并放入目标容器（经调整记录池记账）');
      setAdjustTargetCode('');
      if (!hasIocTarget) setAdjustQty(1);
    } finally {
      setAdjustLoading(false);
    }
  }, [adjustMode, adjustFromBoxCode, adjustToBoxCode, adjustTargetCode, adjustQty, hasIocTarget, adjustMoveAll, normalizeAdjustTargetCode, takeOutToAdjustPool, putInFromAdjustPool, buildAdjustStacks, resolveTargetKeyFromStacks, consumeFromAdjustStacks, adjustPoolCode, pushLocalOperationRecords, markContainerLabelDirty, adjustWarn, adjustError, resolveAdjustCandidatesByScan]);
  const buildRescanNetByKey = useCallback((stacks: Map<string, AdjustLot[]>) => {
    const net: Record<string, number> = {};
    try {
      Array.from(stacks.entries()).forEach(([k, lots]) => {
        const q = (lots || []).reduce((s: number, x: any) => s + Math.max(0, Math.floor(Number(x?.qty || 0) || 0)), 0);
        if (q > 0) net[String(k || '')] = q;
      });
    } catch {}
    return net;
  }, []);
  const splitTargetKey = useCallback((keyRaw: any) => {
    const key = String(keyRaw || '').trim();
    const idx = key.indexOf(':');
    if (idx < 0) return { target_type: '', target: key };
    return { target_type: key.slice(0, idx), target: key.slice(idx + 1) };
  }, []);
  const resetRescanContext = useCallback(() => {
    setRescanStarted(false);
    setRescanFromBoxCode('');
    setRescanTargetCode('');
    setRescanQty(1);
    setRescanCompareOpen(false);
    setRescanBaselineRows([]);
    setRescanBaselineLotsNetByKey({});
    setRescanPoolNetBaselineByKey({});
  }, []);
  const startRescanFromContainer = useCallback(async () => {
    const from = String(rescanContainerCode || '').trim();
    if (!from) { adjustWarn('请先选择要重扫的容器'); return; }
    if (String(from) === String(adjustPoolCode || '').trim()) { adjustWarn('调整池容器不支持重扫'); return; }
    const ok = await new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: '确认开始重扫？',
        content: `将把容器 ${from} 的当前内容全量取出到调整池，随后可按目标编码逐步加回原容器。`,
        okText: '开始重扫（全量取出）',
        cancelText: '取消',
        okButtonProps: { danger: true },
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!ok) return;
    setRescanLoading(true);
    try {
      const fromStacks = buildAdjustStacks(from);
      const baselineNet = buildRescanNetByKey(fromStacks);
      const baselineRows = Object.keys(baselineNet).map((k) => {
        const sp = splitTargetKey(k);
        return { target_type: sp.target_type || '-', target: sp.target || '-', qty: Math.max(0, Math.floor(Number(baselineNet[k] || 0) || 0)) };
      }).filter((x) => x.qty > 0).sort((a, b) => (b.qty - a.qty) || String(a.target).localeCompare(String(b.target)));
      if (!baselineRows.length) {
        message.info('该容器当前无可重扫记录');
        return;
      }
      const poolBaseline = buildRescanNetByKey(buildAdjustStacks(adjustPoolCode));
      const now = Date.now();
      const recs: any[] = [];
      Array.from(fromStacks.entries()).forEach(([, lots]) => {
        (lots || []).forEach((lot: any) => {
          const q = Math.max(0, Math.floor(Number(lot?.qty || 0) || 0));
          if (q <= 0) return;
          recs.push({
            id: `local-rescan-take-${now}-${Math.random()}`,
            time: new Date(now).toISOString(),
            event: '调整-取出',
            action: '调整-取出',
            container_code: from,
            target_type: lot.target_type,
            target_weight_g: lot.target_weight_g,
            target: lot.target,
            qty: -q,
            result: '成功',
            operator_name: '-',
            remark: `重扫全量取出 -> 调整池 ${adjustPoolCode}`,
          });
          recs.push({
            id: `local-rescan-pool-in-${now}-${Math.random()}`,
            time: new Date(now).toISOString(),
            event: '调整-入池',
            action: '调整-入池',
            container_code: adjustPoolCode,
            target_type: lot.target_type,
            target_weight_g: lot.target_weight_g,
            target: lot.target,
            qty: q,
            result: '成功',
            operator_name: '-',
            remark: `重扫来自容器 ${from}`,
          });
        });
      });
      if (!recs.length) {
        message.info('该容器当前无可重扫记录');
        return;
      }
      pushLocalOperationRecords(recs);
      markContainerLabelDirty(from);
      setRescanFromBoxCode(from);
      setRescanBaselineRows(baselineRows);
      setRescanBaselineLotsNetByKey(Object.keys(baselineNet).reduce((acc: any, k: string) => {
        const sp = splitTargetKey(k);
        const q = Math.max(0, Math.floor(Number(baselineNet[k] || 0) || 0));
        if (q > 0) acc[k] = { target_type: sp.target_type || '', target: sp.target || '', qty: q };
        return acc;
      }, {}));
      setRescanPoolNetBaselineByKey(poolBaseline);
      setRescanTargetCode('');
      setRescanQty(1);
      setRescanStarted(true);
      message.success('已全量取出到调整池，请按目标编码逐步加回原容器');
    } finally {
      setRescanLoading(false);
    }
  }, [rescanContainerCode, adjustPoolCode, buildAdjustStacks, buildRescanNetByKey, splitTargetKey, pushLocalOperationRecords, markContainerLabelDirty, adjustWarn]);
  const addRescanBackToOrigin = useCallback(async () => {
    const to = String(rescanFromBoxCode || '').trim();
    const raw = String(rescanTargetCode || '').trim();
    const scanCode = String(raw || '').trim().toUpperCase();
    const requestedQty = hasIocTarget ? 1 : Math.max(1, Math.floor(Number(rescanQty || 1)));
    if (!rescanStarted || !to) { adjustWarn('请先开始重扫'); return; }
    if (!scanCode) { adjustWarn('请输入操作目标编码'); return; }
    if (!Number.isFinite(requestedQty) || requestedQty <= 0) { adjustWarn('数量需为正整数'); return; }
    setRescanLoading(true);
    try {
      const stacks = buildAdjustStacks(adjustPoolCode);
      const rows = (hasIocTarget ? iocDisplayRows : inventoryRows) as any[];
      const list = Array.isArray(rows) ? rows : [];
      const baselineKeySet = new Set<string>(Object.keys(rescanBaselineLotsNetByKey || {}));
      const resolveRowTargetCode = (row: any) => String(
        row?.output_code
        || row?.display_code
        || row?.unit_code
        || row?.key
        || row?.__key
        || '-',
      ).trim().toUpperCase();
      const resolveRowTargetType = () => (hasIocTarget ? 'instruction_output_container' : 'inventory_unit');
      const resolveRowPoolKey = (row: any) => {
        const code = resolveRowTargetCode(row);
        if (!code || code === '-') return '';
        return `${resolveRowTargetType()}:${code}`;
      };
      const poolAvailByKey = new Map<string, number>();
      Array.from(stacks.entries()).forEach(([k, lots]) => {
        const q = (lots || []).reduce((s, x) => s + Math.max(0, Math.floor(Number(x?.qty || 0) || 0)), 0);
        poolAvailByKey.set(String(k || ''), Math.max(0, q));
      });
      const pickCandidatesByPriority = (): any[] => {
        if (hasIocTarget) {
          const exact = list.filter((row: any) => {
            const candidates = [row?.output_code, row?.display_code, row?.key, row?.__key];
            return candidates.some((c) => String(c || '').trim().toUpperCase() === scanCode);
          });
          return exact;
        }
        const exactBySystemBarcode = list.filter((row: any) =>
          String(row?.unit_code || '').trim().toUpperCase() === scanCode,
        );
        if (exactBySystemBarcode.length > 0) return exactBySystemBarcode;
        const liuBySorting = list.filter((row: any) => {
          const unitTypeCode = String(row?.unit_type_code || '').trim().toUpperCase();
          const unitTypeText = String(row?.unit_type || '').trim();
          const isLiu = unitTypeCode === 'LIU' || unitTypeText.includes('散货');
          if (!isLiu) return false;
          const codes = Array.isArray(row?.sorting_barcode_list) ? row.sorting_barcode_list : [];
          return codes.some((c: any) => String(c || '').trim().toUpperCase() === scanCode);
        });
        return liuBySorting;
      };
      const candidates = pickCandidatesByPriority().filter((row: any) => {
        const key = resolveRowPoolKey(row);
        if (!key) return false;
        if (baselineKeySet.size > 0 && !baselineKeySet.has(key)) return false;
        return Math.max(0, Number(poolAvailByKey.get(key) || 0)) > 0;
      });
      if (!candidates.length) { adjustWarn('未匹配到可加回原容器的操作目标'); return; }
      const allocByRule = (rowsIn: any[], qtyIn: number) => {
        const nonAll = (rowsIn || [])
          .filter((r: any) => !isUseAllAvailableTarget(r))
          .slice()
          .sort((a: any, b: any) => {
            const da = Math.max(0, resolveTargetQty(a) - resolveDoneQty(a));
            const db = Math.max(0, resolveTargetQty(b) - resolveDoneQty(b));
            if (da !== db) return da - db;
            return String(a?.unit_code || a?.output_code || '').localeCompare(String(b?.unit_code || b?.output_code || ''));
          });
        const all = (rowsIn || [])
          .filter((r: any) => isUseAllAvailableTarget(r))
          .slice()
          .sort((a: any, b: any) => {
            const da = Math.max(0, resolveTargetQty(a) - resolveDoneQty(a));
            const db = Math.max(0, resolveTargetQty(b) - resolveDoneQty(b));
            if (da !== db) return da - db;
            return String(a?.unit_code || a?.output_code || '').localeCompare(String(b?.unit_code || b?.output_code || ''));
          });
        let remaining = Math.max(0, Math.floor(Number(qtyIn || 0) || 0));
        const allocs: Array<{ row: any; qty: number }> = [];
        const resolveDiffCap = (row: any) => Math.max(0, resolveTargetQty(row) - resolveDoneQty(row));
        const consume = (row: any, opts?: { allMode?: boolean; lastAll?: boolean }) => {
          if (remaining <= 0) return;
          const allMode = Boolean(opts?.allMode);
          const lastAll = Boolean(opts?.lastAll);
          const key = resolveRowPoolKey(row);
          const poolCap = Math.max(0, Number(poolAvailByKey.get(key) || 0));
          if (poolCap <= 0) return;
          let ruleCap = 0;
          if (allMode) {
            ruleCap = lastAll ? remaining : resolveDiffCap(row);
          } else {
            // 重扫加回按“池可用量 + 基线目标”口径，不复用新增装箱的 allow 限制，
            // 避免 IOC / target_qty=1 在池内可回填时被误拦截。
            ruleCap = Math.max(0, resolveDiffCap(row));
          }
          let cap = Math.max(0, Math.min(ruleCap, poolCap));
          if (!allMode && cap <= 0 && hasIocTarget) {
            // IOC 在重扫场景下允许按池可用量回填
            cap = poolCap;
          }
          if (cap <= 0) return;
          const take = Math.min(remaining, cap);
          if (take <= 0) return;
          allocs.push({ row, qty: take });
          remaining -= take;
          poolAvailByKey.set(key, Math.max(0, poolCap - take));
        };
        nonAll.forEach((row: any) => consume(row, { allMode: false, lastAll: false }));
        all.forEach((row: any, idx: number) => consume(row, { allMode: true, lastAll: idx === all.length - 1 }));
        return { allocs, remaining };
      };
      const { allocs, remaining } = allocByRule(candidates, requestedQty);
      const acceptedTotal = Math.max(0, requestedQty - remaining);
      if (acceptedTotal <= 0 || !allocs.length) { adjustWarn('该扫描目标物在当前可加回数量下无法生成记录'); return; }
      const now = Date.now();
      const recs: any[] = [];
      allocs.forEach((x: any) => {
        const row = x?.row || {};
        const key = resolveRowPoolKey(row);
        if (!key) return;
        const take = Math.max(0, Math.floor(Number(x?.qty || 0) || 0));
        if (!take) return;
        const got = consumeFromAdjustStacks(stacks, key, take);
        if (!got.ok) return;
        (got.lots || []).forEach((lot: any) => {
          const q = Math.max(0, Math.floor(Number(lot?.qty || 0) || 0));
          if (!q) return;
          recs.push({
            id: `local-rescan-pool-out-${now}-${Math.random()}`,
            time: new Date(now).toISOString(),
            event: '调整-出池',
            action: '调整-出池',
            container_code: adjustPoolCode,
            target_type: lot.target_type,
            target_weight_g: lot.target_weight_g,
            target: lot.target,
            qty: -q,
            result: '成功',
            operator_name: '-',
            remark: `重扫加回容器 ${to}`,
          });
          recs.push({
            id: `local-rescan-put-back-${now}-${Math.random()}`,
            time: new Date(now).toISOString(),
            event: '调整-放入',
            action: '调整-放入',
            container_code: to,
            target_type: lot.target_type,
            target_weight_g: lot.target_weight_g,
            target: lot.target,
            qty: q,
            result: '成功',
            operator_name: '-',
            remark: `重扫从调整池加回 ${adjustPoolCode}`,
          });
        });
      });
      if (!recs.length) { adjustWarn('本次未生成可用加回记录'); return; }
      pushLocalOperationRecords(recs);
      markContainerLabelDirty(to);
      if (remaining > 0) {
        message.info(`已加回 ${acceptedTotal} 件`);
        Modal.confirm({
          title: '重扫数量超过理论数量',
          centered: true,
          okText: '去“开始打包并装入容器”处理',
          cancelText: '稍后处理',
          okButtonProps: { danger: true },
          content: (
            <div style={{ color: '#595959', lineHeight: 1.6 }}>
              <div>记录池可用数量不足，无法直接把多出来的数量“加回”。</div>
              <div style={{ marginTop: 6 }}>
                建议将多出的部分作为新增扫描处理（进入“开始打包并装入容器”tab继续操作）。
              </div>
              <div style={{ marginTop: 10 }}>
                <Tag style={{ marginInlineEnd: 6, borderRadius: 999 }}>{`容器 ${to || '-'}`}</Tag>
                <Tag style={{ marginInlineEnd: 6, borderRadius: 999, background: '#e6f4ff', borderColor: '#91caff', color: '#0958d9' }}>{`操作目标 ${scanCode}`}</Tag>
                <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#fff7e6', borderColor: '#ffd591', color: '#ad4e00' }}>{`剩余 ${remaining} 件`}</Tag>
              </div>
            </div>
          ),
          onOk: () => {
            setPackConsoleTab('pack_start');
            setPackTargetQty(Math.max(1, Math.floor(Number(remaining || 1) || 1)));
            setPackTargetScan(scanCode);
            setTimeout(() => {
              try { scanTargetInputRef.current?.focus?.(); } catch {}
            }, 0);
          },
        });
      } else {
        message.success(`已加回原容器 ${acceptedTotal} 件`);
      }
      setRescanTargetCode('');
      if (!hasIocTarget) setRescanQty(1);
    } finally {
      setRescanLoading(false);
    }
  }, [rescanFromBoxCode, rescanTargetCode, rescanQty, rescanStarted, hasIocTarget, adjustPoolCode, buildAdjustStacks, consumeFromAdjustStacks, pushLocalOperationRecords, markContainerLabelDirty, adjustWarn, iocDisplayRows, inventoryRows, rescanBaselineLotsNetByKey, isUseAllAvailableTarget, resolveTargetQty, resolveDoneQty]);
  const cleanupRescanPoolRemainder = useCallback(() => {
    if (!rescanStarted) { adjustWarn('请先开始重扫'); return; }
    const poolNow = buildRescanNetByKey(buildAdjustStacks(adjustPoolCode));
    const basePool = rescanPoolNetBaselineByKey || {};
    const baseLots = rescanBaselineLotsNetByKey || {};
    const recs: any[] = [];
    const now = Date.now();
    let cleared = 0;
    Object.keys(baseLots).forEach((k) => {
      const lot = (baseLots as any)[k] || {};
      const baselinePoolQty = Math.max(0, Math.floor(Number((basePool as any)[k] || 0) || 0));
      const currentPoolQty = Math.max(0, Math.floor(Number((poolNow as any)[k] || 0) || 0));
      const delta = Math.max(0, currentPoolQty - baselinePoolQty);
      const cap = Math.max(0, Math.floor(Number(lot?.qty || 0) || 0));
      const canClear = Math.min(delta, cap);
      if (canClear <= 0) return;
      cleared += canClear;
      recs.push({
        id: `local-rescan-clean-${now}-${Math.random()}`,
        time: new Date(now).toISOString(),
        event: '调整-出池',
        action: '调整-出池',
        container_code: adjustPoolCode,
        target_type: String(lot?.target_type || '').trim() || resolveAdjustTargetType(),
        target: String(lot?.target || '').trim(),
        qty: -Math.abs(canClear),
        result: '成功',
        operator_name: '-',
        remark: '重扫清理池余量（作废）',
      });
    });
    if (!recs.length || cleared <= 0) {
      message.info('未发现可清理的本次重扫池余量');
      return;
    }
    pushLocalOperationRecords(recs);
    message.success(`已清理本次重扫池余量 ${cleared} 件`);
  }, [rescanStarted, buildRescanNetByKey, buildAdjustStacks, adjustPoolCode, rescanPoolNetBaselineByKey, rescanBaselineLotsNetByKey, pushLocalOperationRecords, resolveAdjustTargetType, adjustWarn]);
  const rescanCompareRows = useMemo(() => {
    try {
      const cc = String(rescanFromBoxCode || '').trim();
      if (!cc) return [];
      const before = new Map<string, { target_type: string; target: string; qty: number }>();
      (Array.isArray(rescanBaselineRows) ? rescanBaselineRows : []).forEach((r: any) => {
        const tp = String(r?.target_type || '').trim();
        const tg = String(r?.target || '').trim().toUpperCase();
        const q = Math.max(0, Math.floor(Number(r?.qty || 0) || 0));
        if (!tp || !tg || q <= 0) return;
        before.set(`${tp}:${tg}`, { target_type: tp, target: tg, qty: q });
      });
      const afterNet = buildRescanNetByKey(buildAdjustStacks(cc));
      const keys = new Set<string>([...Array.from(before.keys()), ...Object.keys(afterNet || {})]);
      const rows = Array.from(keys).map((k) => {
        const sp = splitTargetKey(k);
        const b = Math.max(0, Math.floor(Number(before.get(k)?.qty || 0) || 0));
        const a = Math.max(0, Math.floor(Number((afterNet as any)?.[k] || 0) || 0));
        return {
          key: k,
          target_type: sp.target_type || '-',
          target: sp.target || '-',
          before: b,
          after: a,
          delta: a - b,
        };
      });
      return rows.sort((x, y) => (Math.abs(y.delta) - Math.abs(x.delta)) || (y.after - x.after) || String(x.target).localeCompare(String(y.target)));
    } catch {
      return [];
    }
  }, [rescanFromBoxCode, rescanBaselineRows, buildRescanNetByKey, buildAdjustStacks, splitTargetKey]);
  const rescanCompareSummary = useMemo(() => {
    const rows = Array.isArray(rescanCompareRows) ? rescanCompareRows : [];
    const beforeTotal = rows.reduce((s: number, r: any) => s + Math.max(0, Math.floor(Number(r?.before || 0) || 0)), 0);
    const afterTotal = rows.reduce((s: number, r: any) => s + Math.max(0, Math.floor(Number(r?.after || 0) || 0)), 0);
    return { beforeTotal, afterTotal, diff: afterTotal - beforeTotal };
  }, [rescanCompareRows]);
  const IOC_LOCKED_TIP = '此IOC已加入现有容器,无法再次添加';
  const isIocRowLocked = useCallback((row: any): boolean => {
    if (!hasIocTarget) return false;
    const diff = 1 - resolveOccupiedQty(row);
    return diff <= 0;
  }, [hasIocTarget, resolveOccupiedQty]);
  const isOverviewTargetCompleted = useCallback((row: any): boolean => {
    const done = Math.max(0, resolveOccupiedQty(row));
    const target = hasIocTarget
      ? 1
      : Math.max(0, resolveTargetQty(row));
    if (!Number.isFinite(target) || target <= 0) return false;
    return done >= target;
  }, [hasIocTarget, resolveOccupiedQty, resolveTargetQty]);
  const isOverviewPackedCompleted = useCallback((row: any): boolean => {
    const done = Math.max(0, resolveDoneQty(row));
    const target = hasIocTarget
      ? 1
      : Math.max(0, resolveTargetQty(row));
    if (!Number.isFinite(target) || target <= 0) return false;
    return done >= target;
  }, [hasIocTarget, resolveDoneQty, resolveTargetQty]);
  const resolveTargetWeightG = useCallback((row: any, targetType: string, targetCode: string) => {
    const cands = [
      row?.actual_weight_g,
      row?.actual_weight,
      row?.weight_g,
      row?.weight,
    ];
    for (const v of cands) {
      const n = Number(v);
      if (Number.isFinite(n) && n > 0) return n;
    }
    const keyPrefix = targetType === 'instruction_output_container' ? 'ioc' : 'iu';
    const cacheKey = `${keyPrefix}:${String(targetCode || '').trim().toUpperCase()}`;
    const cached = Number((targetWeightGByKey as any)?.[cacheKey]);
    if (Number.isFinite(cached) && cached > 0) return cached;
    return undefined;
  }, [targetWeightGByKey]);
  useEffect(() => {
    const justOpened = !!targetPickerOpen && !targetPickerOpenPrevRef.current;
    targetPickerOpenPrevRef.current = !!targetPickerOpen;
    if (!justOpened) return;
    const rows = (hasIocTarget ? iocDisplayRows : inventoryRows) as any[];
    setTargetPickerAddQtyByKey((prev) => {
      const next = { ...(prev || {}) };
      (Array.isArray(rows) ? rows : []).forEach((row: any, idx: number) => {
        const key = targetPickerRowKey(row, idx);
        const diff = hasIocTarget ? (1 - resolveOccupiedQty(row)) : (resolveTargetQty(row) - resolveOccupiedQty(row));
        next[key] = Math.max(0, Number.isFinite(diff) ? diff : 0);
      });
      return next;
    });
  }, [targetPickerOpen, hasIocTarget, iocDisplayRows, inventoryRows, targetPickerRowKey, resolveTargetQty, resolveOccupiedQty]);
  const targetPickerSelectedStats = useMemo(() => {
    const rows = Array.isArray(targetPickerFilteredRows) ? targetPickerFilteredRows : [];
    const keySet = new Set(targetPickerSelectedKeys as React.Key[]);
    let items = 0;
    let qty = 0;
    rows.forEach((row: any, idx: number) => {
      const key = targetPickerRowKey(row, idx);
      if (!keySet.has(key)) return;
      items += 1;
      const cur = Number(targetPickerAddQtyByKey[key] ?? 0);
      qty += Math.max(0, Math.floor(Number.isFinite(cur) ? cur : 0));
    });
    return { items, qty };
  }, [targetPickerFilteredRows, targetPickerSelectedKeys, targetPickerRowKey, targetPickerAddQtyByKey]);
  const targetPickerSelectedKeySet = useMemo(
    () => new Set((targetPickerSelectedKeys as React.Key[]).map((k) => String(k))),
    [targetPickerSelectedKeys],
  );
  const resolveTargetPickerDefaultAddQty = useCallback((row: any) => {
    const diff = hasIocTarget ? (1 - resolveOccupiedQty(row)) : (resolveTargetQty(row) - resolveOccupiedQty(row));
    return Math.max(0, Number.isFinite(diff) ? diff : 0);
  }, [hasIocTarget, resolveTargetQty, resolveOccupiedQty]);
  const isRowToggleIgnoredTarget = useCallback((evt: any): boolean => {
    const el = (evt?.target as HTMLElement | null);
    // 不拦截复选框/按钮/输入控件自身交互
    return !!el?.closest?.('.ant-checkbox-wrapper, .ant-checkbox, .ant-radio-wrapper, .ant-radio, .ant-btn, button, a, .ant-input, input, textarea, .ant-input-number, .ant-select');
  }, []);
  const resetTargetPickerAddQtyForRows = useCallback((rows: any[]) => {
    setTargetPickerAddQtyByKey((prev) => {
      const next = { ...(prev || {}) } as Record<string, number>;
      let changed = false;
      (Array.isArray(rows) ? rows : []).forEach((row: any) => {
        const key = String(targetPickerRowKey(row));
        if (!key) return;
        const n = resolveTargetPickerDefaultAddQty(row);
        if (next[key] !== n) {
          next[key] = n;
          changed = true;
        }
      });
      return changed ? next : (prev || {});
    });
  }, [targetPickerRowKey, resolveTargetPickerDefaultAddQty]);
  const toggleTargetPickerRowSelection = useCallback((row: any, idx?: number) => {
    if (isIocRowLocked(row)) {
      message.warning(IOC_LOCKED_TIP);
      return;
    }
    const key = String(targetPickerRowKey(row, idx));
    const wasSelected = targetPickerSelectedKeySet.has(key);
    setTargetPickerSelectedKeys((prev) => {
      const set = new Set<React.Key>(prev);
      if (set.has(key)) set.delete(key);
      else set.add(key);
      return Array.from(set);
    });
    // 反选（变为未选中）时，恢复“本次添加”为差额默认值
    if (wasSelected) resetTargetPickerAddQtyForRows([row]);
  }, [targetPickerRowKey, targetPickerSelectedKeySet, resetTargetPickerAddQtyForRows, isIocRowLocked]);
  const submitTargetPickerSelection = useCallback(() => {
    const containerCode = String(activePackBoxCode || '').trim();
    if (!containerCode) {
      message.warning('请先开始新容器或选择已结束容器');
      return;
    }
    const selectedSet = new Set(targetPickerSelectedKeys as React.Key[]);
    if (selectedSet.size === 0) {
      message.warning('请先选择操作目标');
      return;
    }
    const allRows = (hasIocTarget ? iocDisplayRows : inventoryRows) as any[];
    const selectedRows = (Array.isArray(allRows) ? allRows : []).filter((row: any, idx: number) => selectedSet.has(targetPickerRowKey(row, idx)));
    if (selectedRows.length === 0) {
      message.warning('未找到可提交的选中目标');
      return;
    }
    const resolveDefaultQty = (row: any) => Math.max(0, resolveAllowQty(row));
    const now = Date.now();
    const partials: Array<{ code: string; requested: number; accepted: number; remaining: number }> = [];
    const nextRecords = selectedRows.map((row: any, idx: number) => {
      const key = targetPickerRowKey(row, idx);
      const requestedQty = hasIocTarget
        ? 1
        : Math.max(0, Math.floor(Number(targetPickerAddQtyByKey[String(key)] ?? resolveDefaultQty(row))));
      const allow = resolveAllowQty(row);
      const acceptedQty = Number.isFinite(allow)
        ? Math.max(0, Math.min(requestedQty, Math.floor(Number(allow) || 0)))
        : requestedQty;
      const remainingQty = Math.max(0, requestedQty - acceptedQty);
      const targetCode = String(
        row?.output_code
        || row?.display_code
        || row?.unit_code
        || row?.key
        || key
        || '-',
      ).trim() || '-';
      if (remainingQty > 0) {
        partials.push({
          code: targetCode,
          requested: requestedQty,
          accepted: acceptedQty,
          remaining: remainingQty,
        });
      }
      const targetType = hasIocTarget ? 'instruction_output_container' : 'inventory_unit';
      const targetWeightG = resolveTargetWeightG(row, targetType, targetCode);
      return {
        id: `local-target-submit-${now}-${idx}`,
        time: new Date(now).toISOString(),
        event: '加入记录并放入容器',
        action: '加入记录并放入容器',
        container_code: containerCode,
        target_type: targetType,
        target_weight_g: targetWeightG,
        target: targetCode,
        qty: acceptedQty,
        result: '成功',
        operator_name: '-',
        remark: `容器:${containerCode} 数量:${acceptedQty}`,
      };
    }).filter((r: any) => Number(r?.qty || 0) > 0);
    if (!nextRecords.length) {
      showOverLimitWarningModal(partials);
      message.warning('本次添加数量为0，未生成记录');
      return;
    }
    setLocalOperationRecords((prev) => ([...nextRecords, ...(Array.isArray(prev) ? prev : [])]));
    setTargetPickerOpen(false);
    setTargetPickerSearch('');
    setTargetPickerPage(1);
    setTargetPickerPageSize(10);
    setTargetPickerSelectedKeys([]);
    message.success(`已加入${nextRecords.length}个操作目标到容器 ${containerCode}`);
    showOverLimitWarningModal(partials);
    if (addToExistingEnabled && String(addToExistingCode || '').trim() === containerCode) {
      setLabelDirtyByCode((prev) => ({ ...(prev || {}), [containerCode]: true }));
    }
  }, [activePackBoxCode, targetPickerSelectedKeys, hasIocTarget, iocDisplayRows, inventoryRows, targetPickerRowKey, targetPickerAddQtyByKey, resolveAllowQty, resolveTargetWeightG, showOverLimitWarningModal, addToExistingEnabled, addToExistingCode]);
  const submitScanAddToContainer = useCallback(() => {
    const containerCode = String(activePackBoxCode || '').trim();
    if (!containerCode) {
      message.warning('请先开始新容器或选择已结束容器');
      resetScanEntryAndFocus();
      return;
    }
    const raw = String(packTargetScan || '').trim();
    if (!raw) {
      message.warning('请先扫描/输入目标物编码');
      resetScanEntryAndFocus();
      return;
    }
    const scanCode = raw.toUpperCase();
    const rows = (hasIocTarget ? iocDisplayRows : inventoryRows) as any[];
    const list = Array.isArray(rows) ? rows : [];
    const requestedQty = hasIocTarget ? 1 : Math.max(1, Math.floor(Number(packTargetQty || 1)));
    const pickCandidatesByPriority = (): any[] => {
      if (hasIocTarget) {
        const exact = list.filter((row: any) => {
          const candidates = [row?.output_code, row?.display_code, row?.key, row?.__key];
          return candidates.some((c) => String(c || '').trim().toUpperCase() === scanCode);
        });
        return exact;
      }
      // 当前库存场景：
      // 1) 先在所有库存单元类型中按 system_barcode（前端映射为 unit_code）精确匹配
      const exactBySystemBarcode = list.filter((row: any) =>
        String(row?.unit_code || '').trim().toUpperCase() === scanCode,
      );
      if (exactBySystemBarcode.length > 0) return exactBySystemBarcode;
      // 2) 精确匹配失败后，仅对 LIU 做内容物分拣条码匹配
      const liuBySorting = list.filter((row: any) => {
        const unitTypeCode = String(row?.unit_type_code || '').trim().toUpperCase();
        const unitTypeText = String(row?.unit_type || '').trim();
        const isLiu = unitTypeCode === 'LIU' || unitTypeText.includes('散货');
        if (!isLiu) return false;
        const codes = Array.isArray(row?.sorting_barcode_list) ? row.sorting_barcode_list : [];
        return codes.some((c: any) => String(c || '').trim().toUpperCase() === scanCode);
      });
      return liuBySorting;
    };
    const candidates = pickCandidatesByPriority();
    if (!candidates.length) {
      playErrorBeep();
      message.warning('未匹配到可加入的操作目标');
      resetScanEntryAndFocus();
      return;
    }
    const allocByRule = (rowsIn: any[], qtyIn: number) => {
      const nonAll = (rowsIn || [])
        .filter((r: any) => !isUseAllAvailableTarget(r))
        .slice()
        .sort((a: any, b: any) => {
          const da = Math.max(0, resolveTargetQty(a) - resolveDoneQty(a));
          const db = Math.max(0, resolveTargetQty(b) - resolveDoneQty(b));
          if (da !== db) return da - db;
          return String(a?.unit_code || a?.output_code || '').localeCompare(String(b?.unit_code || b?.output_code || ''));
        });
      const all = (rowsIn || [])
        .filter((r: any) => isUseAllAvailableTarget(r))
        .slice()
        .sort((a: any, b: any) => {
          const da = Math.max(0, resolveTargetQty(a) - resolveDoneQty(a));
          const db = Math.max(0, resolveTargetQty(b) - resolveDoneQty(b));
          if (da !== db) return da - db;
          return String(a?.unit_code || a?.output_code || '').localeCompare(String(b?.unit_code || b?.output_code || ''));
        });
      let remaining = Math.max(0, Math.floor(Number(qtyIn || 0) || 0));
      const allocs: Array<{ row: any; qty: number }> = [];
      const resolveDiffCap = (row: any) => Math.max(0, resolveTargetQty(row) - resolveDoneQty(row));
      const consume = (row: any, opts?: { allMode?: boolean; lastAll?: boolean }) => {
        if (remaining <= 0) return;
        const allMode = Boolean(opts?.allMode);
        const lastAll = Boolean(opts?.lastAll);
        let cap = 0;
        if (allMode) {
          // 操作全部库存：仅最后一条吃掉全部剩余；其余条目仅按差额分配
          cap = lastAll ? remaining : resolveDiffCap(row);
        } else {
          const allow = resolveAllowQty(row);
          cap = Number.isFinite(allow) ? Math.max(0, Math.floor(Number(allow) || 0)) : remaining;
        }
        if (cap <= 0) return;
        const take = Math.min(remaining, cap);
        if (take <= 0) return;
        allocs.push({ row, qty: take });
        remaining -= take;
      };
      nonAll.forEach((row: any) => consume(row, { allMode: false, lastAll: false }));
      all.forEach((row: any, idx: number) => consume(row, { allMode: true, lastAll: idx === all.length - 1 }));
      return { allocs, remaining };
    };
    const { allocs, remaining } = allocByRule(candidates, requestedQty);
    const acceptedTotal = Math.max(0, requestedQty - remaining);
    if (acceptedTotal <= 0 || !allocs.length) {
      resetScanEntryAndFocus();
      showOverLimitWarningModal([{ code: scanCode, requested: requestedQty, accepted: 0, remaining: requestedQty }], resetScanEntryAndFocus);
      message.warning('该扫描目标物在当前可允许总和下无法生成记录');
      return;
    }
    const now = Date.now();
    const nextRecords = allocs.map((x: any, idx: number) => {
      const row = x.row || {};
      const targetType = hasIocTarget ? 'instruction_output_container' : 'inventory_unit';
      const targetCode = String(
        row?.output_code
        || row?.display_code
        || row?.unit_code
        || row?.key
        || '-',
      ).trim() || '-';
      const targetWeightG = resolveTargetWeightG(row, targetType, targetCode);
      return {
        id: `local-scan-submit-${now}-${idx}`,
        time: new Date(now).toISOString(),
        event: '加入记录并放入容器',
        action: '加入记录并放入容器',
        container_code: containerCode,
        target_type: targetType,
        target_weight_g: targetWeightG,
        target: targetCode,
        qty: Math.max(0, Math.floor(Number(x?.qty || 0) || 0)),
        result: '成功',
        operator_name: '-',
        remark: `容器:${containerCode} 数量:${Math.max(0, Math.floor(Number(x?.qty || 0) || 0))}`,
      };
    }).filter((r: any) => Number(r?.qty || 0) > 0);
    if (!nextRecords.length) {
      message.warning('本次扫描未生成可用记录');
      resetScanEntryAndFocus();
      return;
    }
    setLocalOperationRecords((prev) => ([...nextRecords, ...(Array.isArray(prev) ? prev : [])]));
    if (addToExistingEnabled && String(addToExistingCode || '').trim() === containerCode) {
      setLabelDirtyByCode((prev) => ({ ...(prev || {}), [containerCode]: true }));
    }
    resetScanEntryAndFocus();
    message.success(`已将扫描目标加入容器 ${containerCode}（生成${nextRecords.length}条记录）`);
    if (remaining > 0) {
      showOverLimitWarningModal([{ code: scanCode, requested: requestedQty, accepted: acceptedTotal, remaining }], resetScanEntryAndFocus);
    }
  }, [activePackBoxCode, packTargetScan, hasIocTarget, iocDisplayRows, inventoryRows, packTargetQty, resolveAllowQty, resolveTargetWeightG, showOverLimitWarningModal, playErrorBeep, resetScanEntryAndFocus, addToExistingEnabled, addToExistingCode]);
  const toTargetCodeUpper = useCallback((v: any): string => String(v || '').trim().toUpperCase(), []);
  const getTargetOperationRows = useCallback((targetCodeRaw: any) => {
    const tc = toTargetCodeUpper(targetCodeRaw);
    if (!tc) return [] as any[];
    return (Array.isArray(operationRows) ? operationRows : []).filter((r: any) => toTargetCodeUpper(r?.target) === tc);
  }, [operationRows, toTargetCodeUpper]);
  const hasAnyTargetOps = useCallback((targetCodeRaw: any) => {
    const rows = getTargetOperationRows(targetCodeRaw);
    return rows.some((r: any) => {
      const delta = resolveLedgerSignedQty((r?.raw || r), hasIocTarget);
      return Number.isFinite(delta) && delta !== 0;
    });
  }, [getTargetOperationRows, resolveLedgerSignedQty, hasIocTarget]);
  const openTargetRecordDetail = useCallback((targetCodeRaw: any, label?: string) => {
    const tc = String(targetCodeRaw || '').trim();
    if (!tc) return;
    setTargetDetailCode(tc);
    setTargetDetailTitle(label ? `操作对象 ${label} 记录明细` : `操作对象 ${tc} 记录明细`);
    setTargetDetailPage(1);
    setTargetDetailOpen(true);
  }, []);
  const openTargetBoxSummary = useCallback((targetCodeRaw: any, label?: string) => {
    const tc = toTargetCodeUpper(targetCodeRaw);
    if (!tc) return;
    const list = getTargetOperationRows(tc);
    const byBox = new Map<string, { container_code: string; qty: number; cnt: number; lastTs: number }>();
    (Array.isArray(list) ? list : []).forEach((x: any) => {
      const cc = String(x?.container_code || '').trim();
      if (!cc || cc === '-') return;
      const delta = resolveLedgerSignedQty((x?.raw || x), hasIocTarget);
      if (!Number.isFinite(delta) || delta === 0) return;
      const ts = Number(new Date(x?.raw_time || x?.time || '').getTime()) || 0;
      const cur = byBox.get(cc) || { container_code: cc, qty: 0, cnt: 0, lastTs: 0 };
      cur.qty += delta;
      cur.cnt += 1;
      if (ts > cur.lastTs) cur.lastTs = ts;
      byBox.set(cc, cur);
    });
    const rows = Array.from(byBox.values())
      .filter((r: any) => Math.floor(Number(r?.qty || 0) || 0) > 0)
      .sort((a: any, b: any) => (Number(b?.lastTs || 0) - Number(a?.lastTs || 0)) || String(a?.container_code || '').localeCompare(String(b?.container_code || '')));
    setTargetBoxRows(rows);
    setTargetBoxTitle(label ? `操作对象 ${label} · 装箱汇总` : `操作对象 ${tc} · 装箱汇总`);
    setTargetBoxPage(1);
    setTargetBoxOpen(true);
  }, [toTargetCodeUpper, getTargetOperationRows, resolveLedgerSignedQty, hasIocTarget]);
  useEffect(() => {
    if (!targetPickerOpen) return;
    setTargetPickerPage(1);
  }, [targetPickerSearch, targetPickerOpen]);
  useEffect(() => {
    setTargetOverviewPage(1);
  }, [hasIocTarget]);
  useEffect(() => {
    const rows = (hasIocTarget ? iocDisplayRows : inventoryRows) as any[];
    const total = Array.isArray(rows) ? rows.length : 0;
    const ps = Math.max(1, Number(targetOverviewPageSize || 10));
    const maxPage = Math.max(1, Math.ceil(total / ps));
    if (targetOverviewPage > maxPage) setTargetOverviewPage(maxPage);
  }, [hasIocTarget, iocDisplayRows, inventoryRows, targetOverviewPage, targetOverviewPageSize]);
  useEffect(() => {
    const total = Array.isArray(hierarchyDisplayRows) ? hierarchyDisplayRows.length : 0;
    const ps = Math.max(1, Number(hierarchyDisplayPageSize || 10));
    const maxPage = Math.max(1, Math.ceil(total / ps));
    if (hierarchyDisplayPage > maxPage) setHierarchyDisplayPage(maxPage);
  }, [hierarchyDisplayRows, hierarchyDisplayPage, hierarchyDisplayPageSize]);
  useEffect(() => {
    const total = Array.isArray(operationRows) ? operationRows.length : 0;
    const ps = Math.max(1, Number(operationRecordsPageSize || 10));
    const maxPage = Math.max(1, Math.ceil(total / ps));
    if (operationRecordsPage > maxPage) setOperationRecordsPage(maxPage);
  }, [operationRows, operationRecordsPage, operationRecordsPageSize]);

  const inventoryColumns: Array<any> = [
    { title: '序号', dataIndex: 'index', width: 72 },
    { title: '库存单元类型', dataIndex: 'unit_type', width: 140 },
    { title: '库存单元系统编码', dataIndex: 'unit_code', width: 180 },
    {
      title: '内容物',
      dataIndex: 'content_lines',
      width: 300,
      render: (v: any, row: any) => {
        const lines = Array.isArray(v) ? v : [];
        if (lines.length === 0) return '-';
        const shown = lines.slice(0, 3);
        const more = lines.length > 3;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {shown.map((line: string, i: number) => <span key={`${line}-${i}`}>{line}</span>)}
            {more ? (
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: 'auto', width: 'fit-content' }}
                onClick={() => openContentModal(String(row?.unit_code || '-'), lines)}
              >
                [...]
              </Button>
            ) : null}
          </div>
        );
      },
    },
    {
      title: '操作全部库存',
      dataIndex: 'use_all_available',
      width: 130,
      align: 'center' as const,
      render: (v: any) => {
        const isAll = !!v;
        const style: React.CSSProperties = isAll
          ? { backgroundColor: '#f6ffed', color: '#389e0d', border: '1px solid #b7eb8f', borderRadius: 4, padding: '1px 8px', fontWeight: 600, display: 'inline-block', minWidth: 36, textAlign: 'center' }
          : { backgroundColor: '#fff0f6', color: '#c41d7f', border: '1px solid #ffadd2', borderRadius: 4, padding: '1px 8px', fontWeight: 600, display: 'inline-block', minWidth: 36, textAlign: 'center' };
        return <span style={style}>{isAll ? '是' : '否'}</span>;
      },
    },
    { title: '目标数量', dataIndex: 'target_qty', width: 100, align: 'center' as const },
    {
      title: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span>已装入容器</span>
          {renderInfoTipIcon('未包含调整池内数量')}
        </span>
      ),
      dataIndex: 'picked_qty',
      width: 150,
      align: 'center' as const,
    },
    {
      title: '操作',
      key: 'ops',
      width: 180,
      render: (_: any, row: any) => {
        const code = String(row?.unit_code || row?.key || '').trim();
        const hasRec = hasAnyTargetOps(code);
        return (
          <Space size={8}>
            <Button
              size="small"
              disabled={!hasRec}
              style={hasRec ? { background: '#e6f4ff', borderColor: '#91caff', color: '#0958d9' } : undefined}
              onClick={() => openTargetRecordDetail(code)}
            >
              查看记录
            </Button>
            <Button
              size="small"
              disabled={!hasRec}
              style={hasRec ? { background: '#ff7a45', borderColor: '#ff7a45', color: '#fff' } : undefined}
              onClick={() => openTargetBoxSummary(code)}
            >
              查看容器
            </Button>
          </Space>
        );
      },
    },
  ];
  const iocColumns: Array<any> = [
    { title: '容器系统编码', key: 'output_code', width: 170, render: (_: any, row: any) => {
      const code = String(row?.output_code || row?.display_code || '-');
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontFamily: 'monospace' }}>{code}</span>
          {code && code !== '-' ? (
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: 'auto', lineHeight: 1 }}
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  await navigator.clipboard.writeText(code);
                  message.success('已复制');
                } catch {
                  message.error('复制失败');
                }
              }}
            >
              <CopyOutlined />
            </Button>
          ) : null}
        </span>
      );
    }},
    { title: '容器类型', dataIndex: 'container_type_display', width: 120, render: (v: any) => {
      const key = String(v || '').trim().toLowerCase();
      return overviewContainerTypeTextMap[key] || String(v || '-');
    }},
    { title: '内容类型', dataIndex: 'output_content_kind_display', width: 140, render: (v: any, row: any) => {
      const kind = String(row?.output_content_kind || '').toLowerCase();
      const isException = kind === 'exception';
      return (
        <span style={{
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: 4,
          border: `1px solid ${isException ? '#ffa940' : '#73d13d'}`,
          background: isException ? '#fff7e6' : '#f6ffed',
          color: isException ? '#fa8c16' : '#389e0d',
          lineHeight: 1.5,
        }}>
          {String(v || '-')}
        </span>
      );
    }},
    { title: '容器内容统计', dataIndex: 'output_contents_summary', width: 150, render: (_: any, row: any) => {
      const kind = String(row?.output_content_kind || '').toLowerCase();
      const qty = Number(row?.output_contents_total_qty || 0);
      if (kind === 'exception') return `${Number.isFinite(qty) ? qty : 0}件`;
      const raw = String(row?.output_contents_summary || '').trim();
      if (raw && raw.includes('款') && raw.includes('件')) return raw;
      const m = String(row?.output_contents_detail || '').split(/[；;]/).map((x: any) => String(x || '').trim()).filter(Boolean).length;
      return `${Math.max(0, m)}款${Math.max(0, qty)}件`;
    }},
    { title: '容器内容', dataIndex: 'output_contents_detail', width: 180, render: (v: any, row: any) => {
      const kind = String(row?.output_content_kind || '').toLowerCase();
      if (kind === 'exception') {
        return (
          <Tooltip title="无法操作等异常产品">
            <div style={{ color: '#595959', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              无法操作等异常产品
            </div>
          </Tooltip>
        );
      }
      const full = String(v || '').trim();
      if (!full) return '-';
      const lines = full.split(/[；;]/).map((x) => String(x || '').trim()).filter(Boolean);
      if (lines.length === 0) return '-';
      const preview = lines.slice(0, 3);
      const hasMore = lines.length > 3;
      return (
        <div style={{ maxWidth: 300 }}>
          {preview.map((line, idx) => (
            <div key={`${line}-${idx}`} style={{ minHeight: 20, marginBottom: idx === preview.length - 1 ? 0 : 2 }}>
              <Tooltip title={line}>
                <div style={{
                  color: '#595959',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {line}
                </div>
              </Tooltip>
            </div>
          ))}
          {hasMore ? (
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: 'auto', lineHeight: 1.2, marginTop: 2 }}
              onClick={(e) => {
                e.stopPropagation();
                const iocCode = String(row?.output_code || row?.display_code || '-');
                Modal.info({
                  title: (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 12, paddingRight: 24 }}>
                      <span style={{ justifySelf: 'start' }}>容器内容完整记录</span>
                      <span style={{ justifySelf: 'center', color: '#1677ff', fontWeight: 700, fontFamily: 'monospace' }}>{iocCode}</span>
                      <span />
                    </div>
                  ),
                  width: 760,
                  centered: true,
                  content: (
                    <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                      {lines.map((x, i) => (
                        <div key={`${x}-${i}`} style={{ marginBottom: 6, wordBreak: 'break-all' }}>
                          {i + 1}. {x}
                        </div>
                      ))}
                    </div>
                  ),
                });
              }}
            >
              [...]
            </Button>
          ) : null}
        </div>
      );
    }},
    { title: '容器尺寸', key: 'dims', width: 170, render: (_: any, row: any) => (
      row?.length_cm != null && row?.width_cm != null && row?.height_cm != null
        ? `${row.length_cm}×${row.width_cm}×${row.height_cm} cm`
        : ''
    )},
    { title: '容器重量', dataIndex: 'weight_g', width: 110, render: (v: any) => (v != null ? `${v}g` : '') },
    {
      title: '目标数量',
      key: 'target_qty',
      width: 100,
      align: 'center' as const,
      render: () => '1',
    },
    {
      title: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span>已装入容器</span>
          {renderInfoTipIcon('未包含调整池内数量')}
        </span>
      ),
      key: 'done_qty',
      width: 150,
      align: 'center' as const,
      render: (_: any, row: any) => {
        const v = row?.done_qty ?? row?.completed_qty;
        if (v === undefined || v === null || String(v).trim() === '') return '-';
        const n = Number(v);
        return Number.isFinite(n) ? String(Math.floor(n)) : String(v);
      },
    },
    {
      title: '操作',
      key: 'ops',
      width: 180,
      render: (_: any, row: any) => {
        const code = String(row?.output_code || row?.display_code || row?.key || '').trim();
        const hasRec = hasAnyTargetOps(code);
        return (
          <Space size={8}>
            <Button
              size="small"
              disabled={!hasRec}
              style={hasRec ? { background: '#e6f4ff', borderColor: '#91caff', color: '#0958d9' } : undefined}
              onClick={() => openTargetRecordDetail(code)}
            >
              查看记录
            </Button>
            <Button
              size="small"
              disabled={!hasRec}
              style={hasRec ? { background: '#ff7a45', borderColor: '#ff7a45', color: '#fff' } : undefined}
              onClick={() => openTargetBoxSummary(code)}
            >
              查看容器
            </Button>
          </Space>
        );
      },
    },
  ];
  const targetPickerColumns = useMemo(() => {
    const baseRaw = ((hasIocTarget ? iocColumns : inventoryColumns) as any[]).filter((c: any) => String(c?.title || '') !== '操作');
    const base = hasIocTarget
      ? baseRaw.filter((c: any) => {
        const t = String(c?.title || '').trim();
        return t !== '容器尺寸' && t !== '容器重量';
      })
      : baseRaw;
    const isDoneColumn = (c: any) => {
      const k = String(c?.key || '').trim();
      const d = String(c?.dataIndex || '').trim();
      const t = String(c?.title || '').trim();
      return (
        k === 'done_qty'
        || d === 'done_qty'
        || d === 'picked_qty'
        || k === 'occupied_qty'
        || t === '已完成'
        || t === '已装入容器'
        || t === '已装入容器及调整池占用'
      );
    };
    const baseWithOccupied = base.map((c: any) => {
      if (!isDoneColumn(c)) return c;
      return {
        ...c,
        title: '已装入容器及调整池占用',
        key: 'occupied_qty',
        render: (_: any, row: any) => String(resolveOccupiedQty(row)),
      };
    });
    const doneIdx = baseWithOccupied.findIndex((c: any) => isDoneColumn(c));
    if (doneIdx < 0) return baseWithOccupied;
    const extra = [
      {
        title: '差额',
        key: 'diff_qty',
        width: 90,
        align: 'center' as const,
        render: (_: any, row: any) => {
          const diff = hasIocTarget ? (1 - resolveOccupiedQty(row)) : (resolveTargetQty(row) - resolveOccupiedQty(row));
          return String(diff);
        },
      },
      {
        title: '本次添加',
        key: 'add_qty',
        width: 120,
        align: 'center' as const,
        render: (_: any, row: any, idx: number) => {
          if (hasIocTarget) {
            const locked = isIocRowLocked(row);
            return (
              <div onClick={(e) => e.stopPropagation()}>
                <InputNumber
                  min={0}
                  precision={0}
                  step={1}
                  value={locked ? 0 : 1}
                  disabled
                  style={{ width: 88 }}
                />
              </div>
            );
          }
          const key = targetPickerRowKey(row, idx);
          const selected = targetPickerSelectedKeySet.has(String(key));
          const cur = Number(targetPickerAddQtyByKey[key] ?? 0);
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <InputNumber
                min={0}
                precision={0}
                step={1}
                value={Number.isFinite(cur) && cur >= 0 ? cur : 0}
                disabled={!selected}
                onChange={(v) => {
                  const n = Math.max(0, Math.floor(Number(v ?? 0)));
                  setTargetPickerAddQtyByKey((prev) => ({ ...(prev || {}), [key]: n }));
                }}
                style={{ width: 88 }}
              />
            </div>
          );
        },
      },
    ];
    return [...baseWithOccupied.slice(0, doneIdx + 1), ...extra, ...baseWithOccupied.slice(doneIdx + 1)];
  }, [hasIocTarget, iocColumns, inventoryColumns, resolveTargetQty, targetPickerRowKey, targetPickerAddQtyByKey, targetPickerSelectedKeySet, isIocRowLocked, resolveOccupiedQty]);
  const renderOperationEventTag = useCallback((v: any) => {
    const ev = String(v || '').trim();
    if (!ev || ev === '-') return '-';
    if (ev === '开始容器') {
      return (
        <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#1677ff', borderColor: '#1677ff', color: '#fff' }}>
          开始新容器
        </Tag>
      );
    }
    if (ev === '结束容器') {
      return (
        <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#fa541c', borderColor: '#fa541c', color: '#fff' }}>
          结束新容器
        </Tag>
      );
    }
    if (ev === '新增到已有容器') {
      return (
        <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#13c2c2', borderColor: '#13c2c2', color: '#fff' }}>
          新增到已有容器
        </Tag>
      );
    }
    if (ev === '结束新增') {
      return (
        <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#fa541c', borderColor: '#fa541c', color: '#fff' }}>
          结束新增
        </Tag>
      );
    }
    if (ev === '加入记录并放入容器') {
      return <Tag style={{ marginInlineEnd: 0, borderRadius: 999 }}>放入容器</Tag>;
    }
    return <Tag style={{ marginInlineEnd: 0, borderRadius: 999 }}>{ev}</Tag>;
  }, []);
  const canDeleteOperationRow = useCallback((row: any) => {
    const ev = String(row?.event || '').trim();
    const cc = String(row?.container_code || '').trim();
    const src = String(row?.source || '');
    if (!canOperateExec) return false;
    if (src !== 'local') return false;
    if (ev !== '加入记录并放入容器') return false;
    if (!cc || cc === '-') return false;
    const hit = (boxes || []).find((b: any) => String(b?.code || '').trim() === cc) as any;
    const st = String((hit as any)?.status || '').trim();
    if (st === 'open') return true;
    const cur = String(activePackBoxCode || '').trim();
    return !!cur && cur === cc;
  }, [canOperateExec, boxes, activePackBoxCode]);
  const deleteOperationRowById = useCallback((row: any) => {
    const id = row?.raw_id;
    if (id === undefined || id === null || String(id).trim() === '') return;
    const cc = String(row?.container_code || '').trim();
    setLocalOperationRecords((prev) => (Array.isArray(prev) ? prev.filter((x: any) => String(x?.id ?? '') !== String(id)) : prev));
    if (addToExistingEnabled && cc && cc === String(addToExistingCode || '').trim()) {
      setLabelDirtyByCode((prev) => ({ ...(prev || {}), [cc]: true }));
    }
  }, [addToExistingEnabled, addToExistingCode]);
  const canEditOperationQty = useCallback((row: any) => {
    if (!canOperateExec) return false;
    const ev = String(row?.event || '').trim();
    if (ev !== '加入记录并放入容器') return false;
    const src = String(row?.source || '').trim();
    if (src !== 'local') return false;
    const cc = String(row?.container_code || '').trim();
    if (!cc || cc === '-') return false;
    const hit = (boxes || []).find((b: any) => String(b?.code || '').trim() === cc) as any;
    const st = String((hit as any)?.status || '').trim();
    const cur = String(activePackBoxCode || '').trim();
    const isContainerEditable = st === 'open' || (!!cur && cur === cc);
    if (!isContainerEditable) return false;
    const kind = resolveLedgerTargetKind((row?.raw || row), hasIocTarget);
    return kind === 'iu';
  }, [canOperateExec, boxes, activePackBoxCode, resolveLedgerTargetKind, hasIocTarget]);
  const getOperationQtyEditUpper = useCallback((row: any) => {
    const code = String(row?.target || '').trim().toUpperCase();
    if (!code || code === '-') return Number.POSITIVE_INFINITY;
    const hit = (Array.isArray(inventoryRows) ? inventoryRows : []).find(
      (x: any) => String(x?.unit_code || '').trim().toUpperCase() === code,
    );
    if (!hit) return Number.POSITIVE_INFINITY;
    if (Boolean(hit?.use_all_available)) return Number.POSITIVE_INFINITY;
    const targetQty = Math.max(0, Math.floor(Number(hit?.target_qty || 0) || 0));
    const doneQty = Math.max(0, Math.floor(Number(hit?.picked_qty || 0) || 0));
    const curQty = Math.max(0, Math.floor(Number(row?.qty || 0) || 0));
    return Math.max(0, targetQty - doneQty + curQty);
  }, [inventoryRows]);
  const updateOperationQty = useCallback((row: any, nextRaw: any) => {
    if (!canEditOperationQty(row)) return;
    const id = row?.raw_id;
    if (id === undefined || id === null || String(id).trim() === '') return;
    const next = Math.max(0, Math.floor(Number(nextRaw) || 0));
    const maxAllowed = getOperationQtyEditUpper(row);
    if (Number.isFinite(maxAllowed) && next > maxAllowed) {
      message.warning(`数量超出可编辑上限：最多 ${Math.floor(Number(maxAllowed) || 0)} 件`);
      return;
    }
    setLocalOperationRecords((prev) => {
      if (!Array.isArray(prev)) return prev;
      return prev.map((r: any) => (String(r?.id ?? '') === String(id)
        ? { ...r, qty: next }
        : r));
    });
    const cc = String(row?.container_code || '').trim();
    if (addToExistingEnabled && cc && cc === String(addToExistingCode || '').trim()) {
      setLabelDirtyByCode((prev) => ({ ...(prev || {}), [cc]: true }));
    }
  }, [canEditOperationQty, getOperationQtyEditUpper, addToExistingEnabled, addToExistingCode]);

  const operationColumns: Array<any> = [
    { title: '序号', dataIndex: 'index', width: 72 },
    {
      title: '事件',
      dataIndex: 'event',
      width: 160,
      render: renderOperationEventTag,
    },
    { title: '容器系统编码', dataIndex: 'container_code', width: 220 },
    { title: '操作对象', dataIndex: 'target', width: 220 },
    {
      title: '运输标签编码',
      dataIndex: 'label_code',
      width: 220,
      render: (v: any, row: any) => {
        const event = String(row?.event || '').trim();
        if (event !== '贴单记录') return <span style={{ color: '#999' }}>-</span>;
        const s = String(v || '-').trim() || '-';
        return <span style={{ fontFamily: 'monospace' }}>{s}</span>;
      },
    },
    {
      title: '贴单校验',
      key: 'label_verify',
      width: 360,
      render: (_: any, row: any) => {
        const event = String(row?.event || '').trim();
        if (event !== '贴单记录') return <span style={{ color: '#999' }}>-</span>;
        const modeRaw = String(row?.verify_mode || '').trim();
        const mode = modeRaw === 'constraint' ? '有约束' : modeRaw === 'no_constraint' ? '无约束' : '-';
        const vrRaw = String(row?.verify_result || '').trim();
        const vr = vrRaw === 'pass' ? '通过' : vrRaw === 'warn' ? '警告' : '-';
        const rowNo = Number(row?.baseline_row_no);
        const col = String(row?.matched_column || '').trim().toUpperCase();
        const hit = Number.isFinite(rowNo) && rowNo > 0 ? `行${Math.floor(rowNo)}${col === 'C' || col === 'D' ? `(${col}列)` : ''}` : '-';
        const reason = String(row?.verify_reason || '-').trim() || '-';
        return (
          <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.25 }}>
            <span>{`模式:${mode} 结果:${vr} 命中:${hit}`}</span>
            <span style={{ color: vrRaw === 'warn' ? '#cf1322' : '#595959' }}>{`说明:${reason}`}</span>
          </span>
        );
      },
    },
    {
      title: '数量',
      dataIndex: 'qty',
      width: 120,
      align: 'center' as const,
      render: (v: any, row: any) => {
        if (!canEditOperationQty(row)) {
          const n = Math.floor(Number(v || 0) || 0);
          return <span style={{ fontWeight: 700 }}>{Number.isFinite(n) ? n : '-'}</span>;
        }
        const maxAllowed = getOperationQtyEditUpper(row);
        return (
          <InputNumber
            min={0}
            precision={0}
            step={1}
            value={Math.max(0, Math.floor(Number(v || 0) || 0)) as any}
            onChange={(nv) => { updateOperationQty(row, nv); }}
            style={{ width: 90 }}
            {...(Number.isFinite(maxAllowed) ? { max: Math.max(0, Math.floor(Number(maxAllowed) || 0)) } : {})}
          />
        );
      },
    },
    {
      title: '时间',
      dataIndex: 'time',
      width: 180,
      render: (_: any, row: any) => {
        const raw = row?.raw_time ?? row?.time;
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return normalizeText(row?.time);
        const y = d.getFullYear();
        const mo = d.getMonth() + 1;
        const da = d.getDate();
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        const ss = String(d.getSeconds()).padStart(2, '0');
        return (
          <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.25 }}>
            <span>{`${y}/${mo}/${da}`}</span>
            <span>{`${hh}:${mm}:${ss}`}</span>
          </span>
        );
      },
    },
    {
      title: '操作',
      key: 'ops',
      width: 120,
      render: (_: any, row: any) => {
        const canDel = canDeleteOperationRow(row);
        if (!canDel) return <span style={{ color: '#999' }}>-</span>;
        return (
          <Button
            size="small"
            danger
            style={{ background: '#fff', color: '#ff4d4f', borderColor: '#ff4d4f' }}
            onClick={() => { deleteOperationRowById(row); }}
          >
            删除
          </Button>
        );
      },
    },
  ];
  const operationHeaderStats = useMemo(() => {
    const recordCount = Math.max(0, Math.floor(Number(operationRows.length || 0) || 0));
    const packedQty = Array.from(ledgerDoneByTarget.values()).reduce((acc: number, n: number) => {
      const v = Math.max(0, Math.floor(Number(n || 0) || 0));
      return acc + v;
    }, 0);
    return { recordCount, packedQty };
  }, [operationRows.length, ledgerDoneByTarget]);
  const overallHeaderStats = useMemo(() => {
    try {
      const closedCodes = (Array.isArray(boxes) ? boxes : [])
        .filter((b: any) => String(b?.status || '').trim() === 'closed')
        .map((b: any) => String(b?.code || '').trim())
        .filter(Boolean);
      const closedCodesForMassVolume = closedCodes.filter((cc: string) => !hierarchyParentByChild.has(cc));
      let totalVolM3 = 0;
      let totalKg = 0;
      closedCodesForMassVolume.forEach((code: string) => {
        const meta: any = (boxMeta as any)?.[code] || {};
        const l = Math.floor(Number(meta?.length_cm || 0));
        const w = Math.floor(Number(meta?.width_cm || 0));
        const h = Math.floor(Number(meta?.height_cm || 0));
        if (l > 0 && w > 0 && h > 0) totalVolM3 += (l * w * h) / 1_000_000;
        const kg = (() => {
          const v = Number(meta?.weight_kg);
          if (Number.isFinite(v) && v > 0) return v;
          const g = Number(meta?.weight_g);
          if (Number.isFinite(g) && g > 0) return g / 1000;
          const lb = Number(meta?.weight_lb);
          if (Number.isFinite(lb) && lb > 0) return lb / 2.2046226218;
          return 0;
        })();
        if (kg > 0) totalKg += kg;
      });
      const sumBy = (rows: any[], key: string) => rows.reduce((acc: number, row: any) => {
        const n = Number(row?.[key] || 0);
        return Number.isFinite(n) && n > 0 ? (acc + n) : acc;
      }, 0);
      const iocRows = Array.isArray(iocDisplayRows) ? iocDisplayRows : [];
      const iuRows = Array.isArray(inventoryRows) ? inventoryRows : [];
      const targetQty = hasIocTarget
        ? iocRows.length
        : Math.floor(sumBy(iuRows, 'target_qty'));
      const doneQtyRaw = hasIocTarget
        ? sumBy(iocRows, 'done_qty') || sumBy(iocRows, 'completed_qty')
        : sumBy(iuRows, 'picked_qty');
      const doneQty = Math.floor(Number(doneQtyRaw || 0) || 0);
      const rate = targetQty > 0 ? (doneQty / targetQty) * 100 : null;
      const fmt1 = (n: number) => (!Number.isFinite(n) ? '-' : String(Number(n.toFixed(1))));
      const fmt2 = (n: number) => (!Number.isFinite(n) || n <= 0 ? '0' : String(Number(n.toFixed(2))));
      const fmt3 = (n: number) => (!Number.isFinite(n) || n <= 0 ? '0' : String(Number(n.toFixed(3))));
      return {
        closedCount: closedCodes.length,
        volM3Text: fmt3(totalVolM3),
        kgText: fmt2(totalKg),
        doneQty,
        targetQty,
        rateValue: (rate == null || !Number.isFinite(rate)) ? null : Number(rate),
        rateText: (rate == null || !Number.isFinite(rate)) ? '-' : `${fmt1(rate)}%`,
      };
    } catch {
      return { closedCount: 0, volM3Text: '0', kgText: '0', doneQty: 0, targetQty: 0, rateValue: null, rateText: '-' };
    }
  }, [boxes, boxMeta, hasIocTarget, iocDisplayRows, inventoryRows, hierarchyParentByChild]);
  const containerDetailRows = useMemo(
    () => operationRows.filter((r: any) => String(r?.container_code || '').trim() === String(containerDetailCode || '').trim()),
    [operationRows, containerDetailCode],
  );
  const targetDetailRows = useMemo(
    () => getTargetOperationRows(targetDetailCode),
    [getTargetOperationRows, targetDetailCode],
  );
  useEffect(() => {
    if (!containerDetailOpen) return;
    setContainerDetailPage(1);
  }, [containerDetailOpen, containerDetailCode]);
  useEffect(() => {
    if (!targetDetailOpen) return;
    setTargetDetailPage(1);
  }, [targetDetailOpen, targetDetailCode]);
  useEffect(() => {
    if (!containerDetailOpen) return;
    const total = containerDetailRows.length;
    const ps = Math.max(1, Number(containerDetailPageSize || 10));
    const maxPage = Math.max(1, Math.ceil(total / ps));
    if (containerDetailPage > maxPage) setContainerDetailPage(maxPage);
  }, [containerDetailRows.length, containerDetailPage, containerDetailPageSize, containerDetailOpen]);
  useEffect(() => {
    if (!targetDetailOpen) return;
    const total = targetDetailRows.length;
    const ps = Math.max(1, Number(targetDetailPageSize || 10));
    const maxPage = Math.max(1, Math.ceil(total / ps));
    if (targetDetailPage > maxPage) setTargetDetailPage(maxPage);
  }, [targetDetailRows.length, targetDetailPage, targetDetailPageSize, targetDetailOpen]);
  const containerDetailHeaderStats = useMemo(() => {
    const recordCount = Math.max(0, Math.floor(Number(containerDetailRows.length || 0) || 0));
    const net = (Array.isArray(containerDetailRows) ? containerDetailRows : []).reduce((acc: number, row: any) => {
      const delta = resolveLedgerSignedQty((row?.raw || row), hasIocTarget);
      return acc + (Number.isFinite(delta) ? delta : 0);
    }, 0);
    const packedQty = Math.max(0, Math.floor(Number(net || 0) || 0));
    return { recordCount, packedQty };
  }, [containerDetailRows, resolveLedgerSignedQty, hasIocTarget]);
  const targetDetailHeaderStats = useMemo(() => {
    const recordCount = Math.max(0, Math.floor(Number(targetDetailRows.length || 0) || 0));
    const boxCount = new Set(
      (Array.isArray(targetDetailRows) ? targetDetailRows : [])
        .map((r: any) => String(r?.container_code || '').trim())
        .filter((x: string) => !!x && x !== '-'),
    ).size;
    const net = (Array.isArray(targetDetailRows) ? targetDetailRows : []).reduce((acc: number, row: any) => {
      const delta = resolveLedgerSignedQty((row?.raw || row), hasIocTarget);
      return acc + (Number.isFinite(delta) ? delta : 0);
    }, 0);
    const totalQty = Math.max(0, Math.floor(Number(net || 0) || 0));
    return { recordCount, boxCount, totalQty };
  }, [targetDetailRows, resolveLedgerSignedQty, hasIocTarget]);
  const targetBoxStats = useMemo(() => {
    const rows = Array.isArray(targetBoxRows) ? targetBoxRows : [];
    const boxCount = rows.length;
    const totalQty = rows.reduce((acc: number, r: any) => acc + Math.max(0, Math.floor(Number(r?.qty || 0) || 0)), 0);
    return { boxCount, totalQty };
  }, [targetBoxRows]);
  const resolveContainerTypeText = useCallback((containerCode: string, row?: any) => {
    const ctRaw = String(row?.container_type || row?.container_type_code || '').trim().toLowerCase();
    if (ctRaw === 'box') return '纸箱';
    if (ctRaw === 'pallet') return '托盘';
    if (ctRaw === 'other') return '其他';
    const seg = String(containerCode || '').trim().split('-');
    const mid = String(seg?.[1] || '').trim().toUpperCase();
    if (mid === 'B') return '纸箱';
    if (mid === 'P') return '托盘';
    if (mid === 'O') return '其他';
    return '-';
  }, []);
  const boxStatRows = useMemo(() => {
    try {
      const byCode = new Map<string, any>();
      (Array.isArray(boxes) ? boxes : []).forEach((b: any) => {
        const cc = String(b?.code || '').trim();
        if (!cc) return;
        byCode.set(cc, { container_code: cc, ops: 0, lastTs: 0, raw_box: b });
      });
      (Array.isArray(operationRows) ? operationRows : []).forEach((r: any) => {
        const cc = String(r?.container_code || '').trim();
        if (!cc || cc === '-') return;
        const cur = byCode.get(cc) || { container_code: cc, ops: 0, lastTs: 0, raw_box: undefined };
        cur.ops += 1;
        const ts = Number(new Date(r?.raw_time || r?.time || '').getTime());
        if (Number.isFinite(ts) && ts > 0) cur.lastTs = Math.max(Number(cur.lastTs || 0), ts);
        byCode.set(cc, cur);
      });
      const aggByContainer = new Map<string, Map<string, number>>();
      (Array.isArray(operationRows) ? operationRows : []).forEach((r: any) => {
        const cc = String(r?.container_code || '').trim();
        if (!cc || cc === '-') return;
        const target = String(r?.target || '').trim().toUpperCase();
        if (!target || target === '-') return;
        const delta = resolveLedgerSignedQty((r?.raw || r), hasIocTarget);
        if (!Number.isFinite(delta) || delta === 0) return;
        const mm = aggByContainer.get(cc) || new Map<string, number>();
        mm.set(target, (mm.get(target) || 0) + delta);
        aggByContainer.set(cc, mm);
      });
      return Array.from(byCode.values()).map((it: any) => {
        const cc = String(it?.container_code || '').trim();
        const mm = aggByContainer.get(cc) || new Map<string, number>();
        let objectKinds = 0;
        let qty = 0;
        mm.forEach((v: number) => {
          const n = Math.floor(Number(v || 0) || 0);
          if (Number.isFinite(n) && n > 0) {
            objectKinds += 1;
            qty += n;
          }
        });
        return { ...it, objectKinds, qty };
      }).sort((a: any, b: any) => Number(b?.lastTs || 0) - Number(a?.lastTs || 0));
    } catch {
      return [];
    }
  }, [boxes, operationRows, resolveLedgerSignedQty, hasIocTarget]);
  const boxDirectQtyByCode = useMemo(() => {
    const m = new Map<string, number>();
    const pool = String(adjustPoolCode || '').trim();
    (Array.isArray(boxStatRows) ? boxStatRows : []).forEach((row: any) => {
      const code = String(row?.container_code || '').trim();
      if (!code) return;
      const isAdjustPool = !!pool && code === pool;
      const directQty = isAdjustPool
        ? Math.floor(Number(adjustPoolSummary?.totalPos || 0) || 0)
        : Math.floor(Number(row?.qty || 0) || 0);
      m.set(code, Math.max(0, directQty));
    });
    return m;
  }, [boxStatRows, adjustPoolCode, adjustPoolSummary]);
  const boxAggregatedQtyByCode = useMemo(() => {
    const byCode = new Map<string, number>();
    const statusByCode = new Map<string, string>();
    (Array.isArray(boxes) ? boxes : []).forEach((b: any) => {
      const code = String(b?.code || '').trim();
      if (!code) return;
      statusByCode.set(code, String(b?.status || '').trim().toLowerCase());
    });
    const visiting = new Set<string>();
    const memo = new Map<string, number>();
    const dfs = (codeRaw: any): number => {
      const code = String(codeRaw || '').trim();
      if (!code) return 0;
      if (memo.has(code)) return Number(memo.get(code) || 0);
      if (visiting.has(code)) return Math.max(0, Number(boxDirectQtyByCode.get(code) || 0));
      visiting.add(code);
      let total = Math.max(0, Number(boxDirectQtyByCode.get(code) || 0));
      const children = hierarchyRowsByParent.get(code) || [];
      (Array.isArray(children) ? children : []).forEach((ln: any) => {
        const childCode = String(ln?.child_code || '').trim();
        if (!childCode || childCode === code) return;
        const childStatus = String(statusByCode.get(childCode) || '').trim().toLowerCase();
        if (childStatus === 'cancelled') return;
        total += dfs(childCode);
      });
      visiting.delete(code);
      memo.set(code, Math.max(0, Math.floor(Number(total) || 0)));
      return Math.max(0, Math.floor(Number(total) || 0));
    };
    (Array.isArray(boxStatRows) ? boxStatRows : []).forEach((row: any) => {
      const code = String(row?.container_code || '').trim();
      if (!code) return;
      byCode.set(code, dfs(code));
    });
    return byCode;
  }, [boxStatRows, boxDirectQtyByCode, hierarchyRowsByParent, boxes]);
  const containerDetailQtyStats = useMemo(() => {
    const code = String(containerDetailCode || '').trim();
    const pool = String(adjustPoolCode || '').trim();
    if (!code) {
      return { directQty: 0, aggregatedQty: 0, isAdjustPool: false, hasChildren: false };
    }
    const isAdjustPool = !!pool && code === pool;
    const childLinks = hierarchyRowsByParent.get(code) || [];
    const hasChildren = (Array.isArray(childLinks) ? childLinks : []).some((ln: any) => !!String(ln?.child_code || '').trim());
    const directQty = Math.max(0, Math.floor(Number(boxDirectQtyByCode.get(code) || 0) || 0));
    const aggregatedQty = Math.max(
      0,
      Math.floor(Number(isAdjustPool ? directQty : (boxAggregatedQtyByCode.get(code) || directQty)) || 0),
    );
    return { directQty, aggregatedQty, isAdjustPool, hasChildren };
  }, [containerDetailCode, adjustPoolCode, boxDirectQtyByCode, boxAggregatedQtyByCode, hierarchyRowsByParent]);
  useEffect(() => {
    const total = Array.isArray(boxStatRows) ? boxStatRows.length : 0;
    const ps = Math.max(1, Number(boxStatPageSize || 10));
    const maxPage = Math.max(1, Math.ceil(total / ps));
    if (boxStatPage > maxPage) setBoxStatPage(maxPage);
  }, [boxStatRows, boxStatPage, boxStatPageSize]);
  const progressTabsSnapshot = useMemo(() => {
    const statusZh = (stRaw: any): string => {
      const st = String(stRaw || '').trim().toLowerCase();
      if (st === 'open' || st === 'active') return '进行中';
      if (st === 'closed') return '已结束';
      if (st === 'cancelled') return '已作废';
      if (st === 'consumed') return '已消耗';
      if (st === 'lost') return '丢失';
      return st || '-';
    };
    const byTargetRows = (hasIocTarget ? iocDisplayRows : inventoryRows).map((row: any, idx: number) => {
      if (hasIocTarget) {
        const doneQty = Math.max(0, Math.floor(Number(row?.done_qty ?? row?.completed_qty ?? 0) || 0));
        return {
          key: String(row?.__key || row?.key || row?.output_code || idx),
          output_code: String(row?.output_code || row?.display_code || '-'),
          container_type_display: String(row?.container_type_display || '-'),
          output_content_kind_display: String(row?.output_content_kind_display || '-'),
          output_contents_summary: String(row?.output_contents_summary || '-'),
          output_contents_detail: String(row?.output_contents_detail || '').trim(),
          length_cm: row?.length_cm,
          width_cm: row?.width_cm,
          height_cm: row?.height_cm,
          weight_g: row?.weight_g,
          target_qty: 1,
          done_qty: doneQty,
        };
      }
      return {
        key: String(row?.key || row?.unit_code || idx),
        unit_type: String(row?.unit_type || '-'),
        unit_code: String(row?.unit_code || '-'),
        content_lines: Array.isArray(row?.content_lines) ? row.content_lines : [],
        use_all_available: !!row?.use_all_available,
        target_qty: Math.max(0, Math.floor(Number(row?.target_qty || 0) || 0)),
        picked_qty: Math.max(0, Math.floor(Number(row?.picked_qty || 0) || 0)),
      };
    });
      const byContainerRows = (Array.isArray(boxStatRows) ? boxStatRows : []).map((row: any, idx: number) => {
      const code = String(row?.container_code || '').trim();
      const st = String(row?.raw_box?.status || '').trim().toLowerCase();
      const isAdjustPool = !!String(adjustPoolCode || '').trim() && code === String(adjustPoolCode || '').trim();
      const isChild = hierarchyParentByChild.has(code);
      const childRows = hierarchyRowsByParent.get(code) || [];
      const isParent = childRows.length > 0;
      const qtyNum = isAdjustPool
        ? Math.floor(Number(adjustPoolSummary?.totalPos || 0) || 0)
        : Math.floor(Number(row?.qty || 0) || 0);
      const aggregatedQty = Math.max(0, Math.floor(Number(boxAggregatedQtyByCode.get(code) || qtyNum) || 0));
      const hierarchyLevel = st === 'cancelled' || isAdjustPool
        ? '-'
        : (isChild ? '子' : (isParent ? '父' : '独立'));
      const meta: any = (boxMeta as any)?.[code] || {};
      const weightDisplay = (() => {
        if (!code || !st || st === 'open' || st === 'cancelled') return '-';
        const wg = Number(meta?.weight_g);
        if (!Number.isFinite(wg) || wg <= 0) return '-';
        const kg = Math.ceil(wg / 1000);
        const lb = (() => {
          const v = Number(meta?.weight_lb);
          if (Number.isFinite(v) && v > 0) return Math.floor(v);
          return Math.ceil(kg * 2.2046226218);
        })();
        return `${kg} | ${lb}`;
      })();
      const dimsDisplay = (() => {
        if (!code || !st || st === 'open' || st === 'cancelled') return '-';
        const l = Math.floor(Number(meta?.length_cm || 0) || 0);
        const w = Math.floor(Number(meta?.width_cm || 0) || 0);
        const h = Math.floor(Number(meta?.height_cm || 0) || 0);
        if (!(l > 0 && w > 0 && h > 0)) return '-';
        const li = Number(meta?.length_in) > 0 ? Math.floor(Number(meta?.length_in)) : Math.ceil(l / 2.54);
        const wi = Number(meta?.width_in) > 0 ? Math.floor(Number(meta?.width_in)) : Math.ceil(w / 2.54);
        const hi = Number(meta?.height_in) > 0 ? Math.floor(Number(meta?.height_in)) : Math.ceil(h / 2.54);
        return `${l}×${w}×${h} | ${li}×${wi}×${hi}`;
      })();
      return {
        key: String(code || idx),
        container_code: code || '-',
        container_type_display: st === 'cancelled' ? '-' : resolveContainerTypeText(code, row?.raw_box),
        status: st,
        status_zh: statusZh(st),
        hierarchy_level: hierarchyLevel,
        ops: Math.max(0, Math.floor(Number(row?.ops || 0) || 0)),
        objectKinds: Math.max(0, Math.floor(Number(row?.objectKinds || 0) || 0)),
        qty: Math.max(0, qtyNum),
        direct_qty: Math.max(0, qtyNum),
        aggregated_qty: aggregatedQty,
        weight_display: weightDisplay,
        dims_display: dimsDisplay,
        last_ts: Number(row?.lastTs || 0) || 0,
        is_cancelled: st === 'cancelled',
      };
    });
    const byHierarchyRows = (Array.isArray(hierarchyDisplayRows) ? hierarchyDisplayRows : []).map((row: any, idx: number) => ({
      key: String(row?.id || `${row?.parent_code || '-'}:${row?.child_code || '-'}:${idx}`),
      parent_code: String(row?.parent_code || '-'),
      parent_status: String(row?.parent_status || '').trim(),
      parent_status_zh: statusZh(row?.parent_status),
      child_code: String(row?.child_code || '-'),
      child_status: String(row?.child_status || '').trim(),
      child_status_zh: statusZh(row?.child_status),
      group_idx: Number(row?.__groupIdx || 0) || 0,
      is_first_in_group: !!row?.__isFirstInParentGroup,
    }));
    return {
      has_ioc_target: !!hasIocTarget,
      by_target_label: hasIocTarget ? '按操作对象统计(历史指令产出)' : '按操作对象统计(当前库存)',
      by_target_rows: byTargetRows,
      by_container_rows: byContainerRows,
      by_hierarchy_rows: byHierarchyRows,
    };
  }, [
    hasIocTarget,
    iocDisplayRows,
    inventoryRows,
    boxStatRows,
    adjustPoolCode,
    hierarchyParentByChild,
    hierarchyRowsByParent,
    adjustPoolSummary,
    boxAggregatedQtyByCode,
    boxMeta,
    resolveContainerTypeText,
    hierarchyDisplayRows,
  ]);
  const palletDirectOperationObjectSnapshot = useMemo(() => {
    const palletCodeSet = new Set<string>();
    (Array.isArray(boxes) ? boxes : []).forEach((b: any) => {
      const code = String(b?.code || '').trim();
      const st = String(b?.status || '').trim().toLowerCase();
      const ct = String(b?.container_type || '').trim().toLowerCase();
      if (!code || st === 'cancelled') return;
      if (ct === 'pallet') palletCodeSet.add(code);
    });
    const acc = new Map<string, { direct_operation_object_count: number; direct_iu_qty: number; direct_ioc_count: number }>();
    const rows = Array.isArray(localOperationRecords) ? localOperationRecords : [];
    rows.forEach((r: any) => {
      const containerCode = String(r?.container_code || '').trim();
      if (!containerCode || !palletCodeSet.has(containerCode)) return;
      const ev = String(r?.event || r?.action || '').trim();
      if (!ev || ev === '-' || ev === '开始容器' || ev === '结束容器' || ev === '新增到已有容器' || ev === '结束新增') return;
      const targetType = String(r?.target_type || '').trim();
      const qtyRaw = Number(r?.qty ?? r?.quantity ?? r?.amount ?? 0) || 0;
      const isIoc = targetType === 'instruction_output_container';
      const qtyBase = isIoc ? 1 : Math.max(0, Math.floor(Math.abs(qtyRaw)));
      if (!Number.isFinite(qtyBase) || qtyBase <= 0) return;
      const isOut = /取出|冲销|移除|移出/.test(ev);
      const isIn = /放入|加入|加回/.test(ev);
      if (!isOut && !isIn) return;
      const delta = isOut ? -qtyBase : qtyBase;
      const cur = acc.get(containerCode) || { direct_operation_object_count: 0, direct_iu_qty: 0, direct_ioc_count: 0 };
      cur.direct_operation_object_count += delta;
      if (isIoc) cur.direct_ioc_count += delta;
      else cur.direct_iu_qty += delta;
      acc.set(containerCode, cur);
    });
    const counts: Record<string, number> = {};
    const breakdown: Array<{ pallet_code: string; direct_operation_object_count: number; direct_iu_qty: number; direct_ioc_count: number }> = [];
    Array.from(palletCodeSet.values())
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
      .forEach((code) => {
        const cur = acc.get(code) || { direct_operation_object_count: 0, direct_iu_qty: 0, direct_ioc_count: 0 };
        const total = Math.max(0, Math.floor(Number(cur.direct_operation_object_count || 0) || 0));
        const iuQty = Math.max(0, Math.floor(Number(cur.direct_iu_qty || 0) || 0));
        const iocCnt = Math.max(0, Math.floor(Number(cur.direct_ioc_count || 0) || 0));
        counts[code] = total;
        breakdown.push({
          pallet_code: code,
          direct_operation_object_count: total,
          direct_iu_qty: iuQty,
          direct_ioc_count: iocCnt,
        });
      });
    return { by_pallet_code: counts, breakdown };
  }, [boxes, localOperationRecords]);
  useEffect(() => {
    try { onChange?.('progress_tabs_snapshot', progressTabsSnapshot); } catch {}
  }, [onChange, progressTabsSnapshot]);
  useEffect(() => {
    try { onChange?.('operation_records', Array.isArray(localOperationRecords) ? localOperationRecords.slice(0, 4000) : []); } catch {}
  }, [onChange, localOperationRecords]);
  useEffect(() => {
    try { onChange?.('pallet_direct_operation_object_counts', palletDirectOperationObjectSnapshot.by_pallet_code || {}); } catch {}
    try { onChange?.('pallet_direct_operation_object_breakdown', Array.isArray(palletDirectOperationObjectSnapshot.breakdown) ? palletDirectOperationObjectSnapshot.breakdown : []); } catch {}
  }, [onChange, palletDirectOperationObjectSnapshot]);
  useEffect(() => {
    try { onChange?.('boxes', Array.isArray(boxes) ? boxes : []); } catch {}
  }, [onChange, boxes]);
  useEffect(() => {
    try { onChange?.('hierarchy_links', Array.isArray(hierarchyLinks) ? hierarchyLinks : []); } catch {}
  }, [onChange, hierarchyLinks]);
  useEffect(() => {
    try { onChange?.('box_meta', boxMeta || {}); } catch {}
  }, [onChange, boxMeta]);
  useEffect(() => {
    try { onChange?.('output_draft_by_code', outputDraftByCode || {}); } catch {}
  }, [onChange, outputDraftByCode]);
  const buyWaybillPackagingRows = useMemo(() => {
    const rows = Array.isArray(boxStatRows) ? boxStatRows : [];
    const cur = String(activePackBoxCode || '').trim();
    const pool = String(adjustPoolCode || '').trim();
    return rows
      .map((row: any) => {
        const code = String(row?.container_code || '').trim();
        if (!code) return null;
        if (pool && code === pool) return null;
        if (cur && code === cur) return null;
        const st = String(row?.raw_box?.status || '').trim();
        if (!st || st === 'open' || st === 'cancelled') return null;
        const isChild = hierarchyParentByChild.has(code);
        const childRows = hierarchyRowsByParent.get(code) || [];
        const isParent = childRows.length > 0;
        // 弹窗包装数据仅展示父容器和独立容器，子容器不展示。
        if (isChild) return null;
        const meta: any = (boxMeta as any)?.[code] || {};
        const l = Math.floor(Number(meta?.length_cm || 0) || 0);
        const w = Math.floor(Number(meta?.width_cm || 0) || 0);
        const h = Math.floor(Number(meta?.height_cm || 0) || 0);
        const dimsMetric = (l > 0 && w > 0 && h > 0) ? `${l}×${w}×${h} cm` : '-';
        const li = Number(meta?.length_in) > 0 ? Math.floor(Number(meta?.length_in)) : (l > 0 ? Math.ceil(l / 2.54) : 0);
        const wi = Number(meta?.width_in) > 0 ? Math.floor(Number(meta?.width_in)) : (w > 0 ? Math.ceil(w / 2.54) : 0);
        const hi = Number(meta?.height_in) > 0 ? Math.floor(Number(meta?.height_in)) : (h > 0 ? Math.ceil(h / 2.54) : 0);
        const dimsImperial = (li > 0 && wi > 0 && hi > 0) ? `${li}×${wi}×${hi} inch` : '-';
        const wg = Number(meta?.weight_g);
        const kgNum = Number.isFinite(wg) && wg > 0 ? Math.ceil(wg / 1000) : 0;
        const lbNum = (() => {
          const v = Number(meta?.weight_lb);
          if (Number.isFinite(v) && v > 0) return Math.floor(v);
          if (kgNum > 0) return Math.ceil(kgNum * 2.2046226218);
          return 0;
        })();
        const weightMetric = kgNum > 0 ? `${kgNum} kg` : '-';
        const weightImperial = lbNum > 0 ? `${lbNum} lb` : '-';
        return {
          key: code,
          container_code: code,
          hierarchyLevel: isParent ? '父' : '独立',
          dimsMetric,
          dimsImperial,
          weightMetric,
          weightImperial,
          lastTs: Number(row?.lastTs || 0) || 0,
        };
      })
      .filter(Boolean)
      .sort((a: any, b: any) => Number(b?.lastTs || 0) - Number(a?.lastTs || 0));
  }, [boxStatRows, activePackBoxCode, adjustPoolCode, boxMeta, hierarchyParentByChild, hierarchyRowsByParent]);
  const boxFinalData = useMemo(() => {
    const code = String(boxFinalCode || '').trim();
    if (!code) return null;
    const statHit = (Array.isArray(boxStatRows) ? boxStatRows : []).find((r: any) => String(r?.container_code || '').trim() === code);
    const draft: any = (outputDraftByCode as any)?.[code] || {};
    const meta: any = (boxMeta as any)?.[code] || {};
    const isEditingEnd = endContainerOpen && String(endContainerDraft?.code || '').trim() === code;
    const draftLike: any = isEditingEnd ? endContainerDraft : draft;
    const lcmRaw = Number(draftLike?.length_cm ?? meta?.length_cm ?? 0);
    const wcmRaw = Number(draftLike?.width_cm ?? meta?.width_cm ?? 0);
    const hcmRaw = Number(draftLike?.height_cm ?? meta?.height_cm ?? 0);
    const weightGRaw = Number(draftLike?.weight_g ?? meta?.weight_g ?? 0);
    const lcm = Number.isFinite(lcmRaw) && lcmRaw > 0 ? Math.floor(lcmRaw) : 0;
    const wcm = Number.isFinite(wcmRaw) && wcmRaw > 0 ? Math.floor(wcmRaw) : 0;
    const hcm = Number.isFinite(hcmRaw) && hcmRaw > 0 ? Math.floor(hcmRaw) : 0;
    const kgNum = Number.isFinite(weightGRaw) && weightGRaw > 0 ? Number((weightGRaw / 1000).toFixed(2)) : 0;
    const lbNumRaw = Number(draftLike?.weight_lb ?? meta?.weight_lb ?? 0);
    const lbNum = lbNumRaw > 0 ? Number(lbNumRaw.toFixed ? lbNumRaw.toFixed(2) : lbNumRaw) : (kgNum > 0 ? Number((kgNum * 2.2046226218).toFixed(2)) : 0);
    const liRaw = Number(draftLike?.length_in ?? meta?.length_in ?? 0);
    const wiRaw = Number(draftLike?.width_in ?? meta?.width_in ?? 0);
    const hiRaw = Number(draftLike?.height_in ?? meta?.height_in ?? 0);
    const li = liRaw > 0 ? Math.floor(liRaw) : (lcm > 0 ? Math.ceil(lcm / 2.54) : 0);
    const wi = wiRaw > 0 ? Math.floor(wiRaw) : (wcm > 0 ? Math.ceil(wcm / 2.54) : 0);
    const hi = hiRaw > 0 ? Math.floor(hiRaw) : (hcm > 0 ? Math.ceil(hcm / 2.54) : 0);
    const rows = (Array.isArray(localOperationRecords) ? localOperationRecords : []).filter(
      (r: any) => String(r?.container_code || '').trim() === code,
    );
    const timeRows = (Array.isArray(operationRows) ? operationRows : []).filter(
      (r: any) => String(r?.container_code || '').trim() === code,
    );
    let startTs = 0;
    let endTs = 0;
    let lastTs = 0;
    const detailMap = new Map<string, number>();
    rows.forEach((r: any) => {
      const ts = Number(new Date(r?.time || r?.created_at || '').getTime());
      if (Number.isFinite(ts) && ts > 0) {
        lastTs = Math.max(lastTs, ts);
      }
      const target = String(r?.target || '').trim().toUpperCase();
      if (!target || target === '-') return;
      const delta = resolveLedgerSignedQty(r, hasIocTarget);
      if (!Number.isFinite(delta) || delta === 0) return;
      detailMap.set(target, (detailMap.get(target) || 0) + delta);
    });
    timeRows.forEach((row: any) => {
      const raw = row?.raw_time ?? row?.time;
      const ts = Number(new Date(raw).getTime());
      if (!Number.isFinite(ts) || ts <= 0) return;
      const ev = String(row?.event || '').trim();
      if (ev === '开始容器') startTs = startTs > 0 ? Math.min(startTs, ts) : ts;
      if (ev === '结束容器') endTs = Math.max(endTs, ts);
    });
    const iocByCode = new Map<string, any>();
    (Array.isArray(iocDisplayRows) ? iocDisplayRows : []).forEach((x: any) => {
      const k1 = String(x?.output_code || '').trim().toUpperCase();
      const k2 = String(x?.display_code || '').trim().toUpperCase();
      if (k1) iocByCode.set(k1, x);
      if (k2) iocByCode.set(k2, x);
    });
    const iuByCode = new Map<string, any>();
    (Array.isArray(inventoryRows) ? inventoryRows : []).forEach((x: any) => {
      const kk = String(x?.unit_code || '').trim().toUpperCase();
      if (kk) iuByCode.set(kk, x);
    });
    const splitContentLines = (raw: any): string[] => {
      return String(raw || '')
        .split(/[；;]/)
        .map((x) => String(x || '').trim())
        .filter(Boolean);
    };
    const resolveTargetContentLines = (targetRaw: any): string[] => {
      const t = String(targetRaw || '').trim().toUpperCase();
      if (!t || t === '-') return ['-'];
      const hitIoc = iocByCode.get(t);
      if (hitIoc) {
        const kind = String(hitIoc?.output_content_kind || '').trim().toLowerCase();
        if (kind === 'exception') return ['无法操作等异常产品'];
        const lines = splitContentLines(hitIoc?.output_contents_detail);
        if (lines.length > 0) return lines;
        const summary = String(hitIoc?.output_contents_summary || '').trim();
        if (summary) return [summary];
        return ['-'];
      }
      const hitIu = iuByCode.get(t);
      if (hitIu) {
        const lines = Array.isArray(hitIu?.content_lines) ? hitIu.content_lines.map((x: any) => String(x || '').trim()).filter(Boolean) : [];
        return lines.length > 0 ? lines : ['-'];
      }
      return ['-'];
    };
    const detailRows = Array.from(detailMap.entries())
      .map(([target, qty]) => ({
        target,
        qty: Math.floor(Number(qty || 0) || 0),
        content_lines: resolveTargetContentLines(target),
      }))
      .filter((x) => x.qty > 0)
      .sort((a, b) => b.qty - a.qty);
    const totalQty = detailRows.reduce((acc, cur) => acc + cur.qty, 0);
    const objectKinds = detailRows.length;
    const childLinks = hierarchyRowsByParent.get(code) || [];
    const hasChildren = (Array.isArray(childLinks) ? childLinks : []).some((ln: any) => !!String(ln?.child_code || '').trim());
    const directQty = Math.max(0, Math.floor(Number(boxDirectQtyByCode.get(code) || totalQty) || 0));
    const aggregatedQty = Math.max(0, Math.floor(Number(boxAggregatedQtyByCode.get(code) || directQty) || 0));
    const statusRaw = String(
      (Array.isArray(boxes) ? boxes : []).find((b: any) => String(b?.code || '').trim() === code)?.status
      || statHit?.raw_box?.status
      || '',
    ).trim();
    const statusText = statusRaw === 'open' ? '进行中' : statusRaw === 'cancelled' ? '已作废' : '已结束';
    const durationHours = (startTs > 0 && endTs > 0 && endTs >= startTs) ? Number((((endTs - startTs) / 3600000)).toFixed(2)) : 0;
    return {
      code,
      containerTypeText: resolveContainerTypeText(code, { container_type: draftLike?.container_type || statHit?.raw_box?.container_type }),
      statusText,
      lcm,
      wcm,
      hcm,
      li,
      wi,
      hi,
      kgNum,
      lbNum,
      detailRows,
      totalQty,
      directQty,
      aggregatedQty,
      hasChildren,
      objectKinds,
      recordCount: rows.length,
      startTs,
      endTs,
      lastTs,
      durationHours,
    };
  }, [
    boxFinalCode,
    boxStatRows,
    outputDraftByCode,
    boxMeta,
    endContainerOpen,
    endContainerDraft,
    localOperationRecords,
    operationRows,
    resolveLedgerSignedQty,
    hasIocTarget,
    boxes,
    resolveContainerTypeText,
    boxDirectQtyByCode,
    boxAggregatedQtyByCode,
    hierarchyRowsByParent,
    iocDisplayRows,
    inventoryRows,
  ]);
  const boxStatDirtyCount = useMemo(() => {
    try {
      const rows = Array.isArray(boxStatRows) ? boxStatRows : [];
      const set = new Set<string>();
      const pool = String(adjustPoolCode || '').trim();
      rows.forEach((r: any) => {
        const code = String(r?.container_code || '').trim();
        if (!code) return;
        const st = String(r?.raw_box?.status || '').trim().toLowerCase();
        if (st === 'cancelled') return;
        const labelDirty = !!(labelDirtyByCode as any)?.[code];
        const isAdjustPool = !!pool && code === pool;
        const qtyNum = Math.floor(Number(r?.qty || 0) || 0);
        const isChild = hierarchyParentByChild.has(code);
        const childRows = hierarchyRowsByParent.get(code) || [];
        const isParent = childRows.length > 0;
        const contentWarn = !isAdjustPool && qtyNum === 0 && (isChild || (!isChild && !isParent));
        if (labelDirty || contentWarn) set.add(code);
      });
      return set.size;
    } catch {
      return 0;
    }
  }, [boxStatRows, labelDirtyByCode, adjustPoolCode, hierarchyParentByChild, hierarchyRowsByParent]);
  const hasBoxStatDirty = boxStatDirtyCount > 0;
  const persistBoxFinalDraft = useCallback(() => {
    try {
      if (!canOperateExec) return;
      const cc = String(boxFinalCode || '').trim();
      if (!cc || !boxFinalDirty) return;
      const l = (boxFinalDraft as any)?.length_cm;
      const w = (boxFinalDraft as any)?.width_cm;
      const h = (boxFinalDraft as any)?.height_cm;
      const wg = (boxFinalDraft as any)?.weight_g;
      const lin = (boxFinalLenIn != null && Number.isFinite(Number(boxFinalLenIn)) && Number(boxFinalLenIn) > 0) ? Math.floor(Number(boxFinalLenIn)) : undefined;
      const win = (boxFinalWidIn != null && Number.isFinite(Number(boxFinalWidIn)) && Number(boxFinalWidIn) > 0) ? Math.floor(Number(boxFinalWidIn)) : undefined;
      const hin = (boxFinalHeiIn != null && Number.isFinite(Number(boxFinalHeiIn)) && Number(boxFinalHeiIn) > 0) ? Math.floor(Number(boxFinalHeiIn)) : undefined;
      const wlb = (boxFinalLb != null && Number.isFinite(Number(boxFinalLb)) && Number(boxFinalLb) > 0) ? Math.floor(Number(boxFinalLb)) : undefined;
      setBoxMeta((prev) => ({
        ...(prev || {}),
        [cc]: {
          ...(prev as any)?.[cc],
          length_cm: l != null ? Number(l) : undefined,
          width_cm: w != null ? Number(w) : undefined,
          height_cm: h != null ? Number(h) : undefined,
          weight_g: wg != null ? Number(wg) : undefined,
          length_in: lin,
          width_in: win,
          height_in: hin,
          weight_lb: wlb,
        },
      }));
      setOutputDraftByCode((prev) => ({
        ...(prev || {}),
        [cc]: {
          ...((prev as any)?.[cc] || { code: cc }),
          code: cc,
          length_cm: l != null ? Number(l) : undefined,
          width_cm: w != null ? Number(w) : undefined,
          height_cm: h != null ? Number(h) : undefined,
          weight_g: wg != null ? Number(wg) : undefined,
          length_in: lin,
          width_in: win,
          height_in: hin,
          weight_lb: wlb,
          status: String(((prev as any)?.[cc]?.status || 'closed_pending_final')),
          updated_at: new Date().toISOString(),
        },
      }));
      try {
        request.post('/api/v1/storage/output-containers/drafts/upsert/', {
          instruction_id: instrIdState,
          code: cc,
          status: 'closed_pending_final',
          length_cm: l,
          width_cm: w,
          height_cm: h,
          weight_g: wg,
          length_in: lin,
          width_in: win,
          height_in: hin,
          weight_lb: wlb,
          replace_items: false,
        }).catch((e: any) => {
          message.error(e?.response?.data?.detail || e?.message || '保存容器草稿失败');
        });
      } catch {}
      setBoxFinalDirty(false);
    } catch {}
  }, [
    canOperateExec,
    boxFinalCode,
    boxFinalDirty,
    boxFinalDraft,
    boxFinalLenIn,
    boxFinalWidIn,
    boxFinalHeiIn,
    boxFinalLb,
    instrIdState,
  ]);
  const closeBoxFinalModal = useCallback(async () => {
    try {
      if (canOperateExec && boxFinalDirty) {
        const cc = String(boxFinalCode || '').trim();
        if (cc) {
          const hit: any = (Array.isArray(boxes) ? boxes : []).find((b: any) => String(b?.code || '').trim() === cc) || {};
          const draft: any = (outputDraftByCode as any)?.[cc] || {};
          const meta: any = (boxMeta as any)?.[cc] || {};
          const proceed = await remindContainerWeightAndHeight({
            code: cc,
            containerType: String(hit?.container_type || draft?.container_type || meta?.container_type || 'box').trim(),
            heightCm: Number((boxFinalDraft as any)?.height_cm ?? draft?.height_cm ?? meta?.height_cm),
            weightG: Number((boxFinalDraft as any)?.weight_g ?? draft?.weight_g ?? meta?.weight_g),
            scene: '最终包装数据',
          });
          if (!proceed) return;
        }
      }
    } catch {}
    persistBoxFinalDraft();
    setBoxFinalOpen(false);
    setBoxFinalCode('');
  }, [
    canOperateExec,
    boxFinalDirty,
    boxFinalCode,
    boxes,
    outputDraftByCode,
    boxMeta,
    boxFinalDraft,
    remindContainerWeightAndHeight,
    persistBoxFinalDraft,
  ]);
  const closeBoxLabelPreview = useCallback(() => {
    setBoxLabelOpen(false);
    setBoxLabelCode('');
    try {
      if (boxLabelPopupRef.current && !boxLabelPopupRef.current.closed) boxLabelPopupRef.current.close();
    } catch {}
    boxLabelPopupRef.current = null;
    try {
      if (boxLabelUrl) URL.revokeObjectURL(boxLabelUrl);
    } catch {}
    setBoxLabelUrl('');
  }, [boxLabelUrl]);
  async function openOutputBoxLabelPreview(codeRaw: string) {
    const code = String(codeRaw || '').trim();
    if (!code) return;
    setBoxLabelLoading(true);
    try {
      if (boxLabelUrl) URL.revokeObjectURL(boxLabelUrl);
    } catch {}
    try {
      const draft: any = (outputDraftByCode as any)?.[code] || {};
      const meta: any = (boxMeta as any)?.[code] || {};
      const isEditingEnd = endContainerOpen && String(endContainerDraft?.code || '').trim() === code;
      const draftLike: any = isEditingEnd ? endContainerDraft : draft;
      const lcm = Math.floor(Number(draftLike?.length_cm ?? meta?.length_cm ?? 0) || 0) || '-';
      const wcm = Math.floor(Number(draftLike?.width_cm ?? meta?.width_cm ?? 0) || 0) || '-';
      const hcm = Math.floor(Number(draftLike?.height_cm ?? meta?.height_cm ?? 0) || 0) || '-';
      const kg = (() => {
        const v = Number(draftLike?.weight_kg);
        if (Number.isFinite(v) && v > 0) return Math.ceil(v);
        const g = Number(draftLike?.weight_g ?? meta?.weight_g);
        if (Number.isFinite(g) && g > 0) return Math.ceil(g / 1000);
        return '-';
      })();
      const lb = (() => {
        const v = Number(draftLike?.weight_lb ?? meta?.weight_lb);
        if (Number.isFinite(v) && v > 0) return Math.floor(v);
        if (kg !== '-') return Math.ceil(Number(kg) * 2.2046226218);
        return '-';
      })();
      const lin = lcm === '-' ? '-' : (Number(draftLike?.length_in) > 0 ? Math.floor(Number(draftLike?.length_in)) : Math.ceil(Number(lcm) / 2.54));
      const win = wcm === '-' ? '-' : (Number(draftLike?.width_in) > 0 ? Math.floor(Number(draftLike?.width_in)) : Math.ceil(Number(wcm) / 2.54));
      const hin = hcm === '-' ? '-' : (Number(draftLike?.height_in) > 0 ? Math.floor(Number(draftLike?.height_in)) : Math.ceil(Number(hcm) / 2.54));
      const rows = (Array.isArray(localOperationRecords) ? localOperationRecords : []).filter(
        (r: any) => String(r?.container_code || '').trim() === code,
      );
      const detailMap = new Map<string, number>();
      rows.forEach((r: any) => {
        const t = String(r?.target || '').trim().toUpperCase();
        if (!t || t === '-') return;
        const delta = resolveLedgerSignedQty((r?.raw || r), hasIocTarget);
        if (!Number.isFinite(delta) || delta === 0) return;
        detailMap.set(t, (detailMap.get(t) || 0) + delta);
      });
      let totalQty = 0;
      const details = Array.from(detailMap.entries()).filter(([, qty]) => Number(qty) > 0).map(([target, qty]) => {
        totalQty += qty;
        return { new_code: target, code: target, qty };
      });
      const linkedRows = resolveLinkedChildrenRows(code);
      const linkedChildrenCount = linkedRows.length;
      const linkedChildrenWeightKg = Number(
        linkedRows.reduce((acc: number, r: any) => acc + (Number.isFinite(Number(r?.child_weight_kg)) ? Number(r?.child_weight_kg) : 0), 0).toFixed(2),
      );
      const linkedChildrenCodes = linkedRows.map((r: any) => String(r?.child_code || '').trim()).filter(Boolean);
      const projectSummary = String(
        instructionDetail?.project_detail?.project_code
        || instructionDetail?.project_code
        || instructionDetail?.project_detail?.code
        || '-',
      ).trim() || '-';
      const payload: any = {
        instruction_id: instrIdState,
        container_code: code,
        instruction_code: String(instructionCode || '').trim(),
        project_summary: projectSummary,
        container_type_display: resolveContainerTypeText(code, boxStatRows.find((r: any) => String(r?.container_code || '').trim() === code)?.raw_box),
        created_at: new Date().toLocaleString(),
        dims_cm: { l: lcm, w: wcm, h: hcm },
        dims_in: { l: lin, w: win, h: hin },
        weight: { kg, lb },
        new_kinds: details.length,
        total_qty: totalQty,
        details,
        is_parent_container: linkedChildrenCount > 0,
        linked_children_count: linkedChildrenCount,
        linked_children_weight_kg: linkedChildrenWeightKg,
        linked_children_codes: linkedChildrenCodes,
      };
      const resp = await request.post('/api/v1/storage/output-containers/labels/preview/', payload, { responseType: 'blob' as any });
      const blob = (resp as any)?.data as Blob;
      const url = URL.createObjectURL(blob);
      setBoxLabelCode(code);
      setBoxLabelUrl(url);
      setLabelDirtyByCode((prev) => ({ ...(prev || {}), [code]: false }));
      setBoxLabelOpen(true);
    } catch (e: any) {
      message.error(e?.response?.data?.error || e?.response?.data?.detail || e?.message || '生成箱标预览失败');
    } finally {
      setBoxLabelLoading(false);
    }
  }

  return (
    <div style={{ width: '100%', display: 'flex', gap: 12 }}>
      <div style={{ flex: '0 0 48%', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Card size="small" title={renderSectionTitle('指令概览', 'overview')} headStyle={{ background: '#e6f4ff' }} bodyStyle={{ padding: collapsedSections.overview ? 0 : 12 }} style={{ border: '2px solid #91caff', borderRadius: 8 }}>
        {!collapsedSections.overview ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
            <span style={{ whiteSpace: 'nowrap' }}>指令系统编码：{normalizeText(instructionCode)}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 30, flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
              <span>处理结果：</span>
              {processResultStatus === 'completed' ? (
                <Button size="small" style={{ background: '#52c41a', borderColor: '#52c41a', color: '#fff' }}>已完成</Button>
              ) : processResultStatus === 'unable' ? (
                <>
                  <Button size="small" style={{ background: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' }}>无法操作</Button>
                  <span>{`(原因:${processUnableReason || '-'})`}</span>
                </>
              ) : (
                <span>未标注</span>
              )}
            </span>
          </div>
          <div>是否需重新包装：{
            repackRequiredText === '需重新包装'
              ? renderSoftTag(repackRequiredText, 'red')
              : repackRequiredText === '无需重新包装'
                ? renderSoftTag(repackRequiredText, 'green')
                : repackRequiredText
          }</div>
          <div>执行提醒：<span style={{ color: '#cf1322' }}>{executionTips}</span></div>
          <div>指令备注信息：{instructionRemark}</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <span style={{ whiteSpace: 'nowrap' }}>其他附件：</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {printableAttachments.length === 0 ? (
                <span>-</span>
              ) : printableAttachments.map((a) => (
                <span key={a.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, height: 'auto' }}
                    onClick={() => { void openAttachmentPreview(a.raw, a.name, a.printText); }}
                  >
                    {a.name}
                  </Button>
                  {a.printState === 'print' ? (
                    <Tooltip title={a.printText}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#cf1322' }}>
                        <PrinterOutlined style={{ color: '#ff4d4f' }} />
                        <span>(需打印)</span>
                      </span>
                    </Tooltip>
                  ) : (
                    <span style={{ color: '#8c8c8c' }}>（{a.printText}）</span>
                  )}
                </span>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ whiteSpace: 'nowrap' }}>基线文件(贴单约束)状态:</span>
            {baselineLoading ? (
              <span style={{ color: '#8c8c8c' }}>加载中...</span>
            ) : !hasBaselineFiles ? (
              <span style={{ color: '#8c8c8c' }}>无基线文件</span>
            ) : (
              <>
                <Tag color={baselineSummary.mappingReady ? 'green' : 'orange'}>
                  {baselineSummary.mappingReady ? '贴单映射基线已就绪' : '贴单映射基线未就绪'}
                </Tag>
              </>
            )}
            {hasBaselineFiles ? (
              <>
                <Button
                  size="small"
                  onClick={() => {
                    const first = baselinePreviewCandidates[0];
                    if (!first?.file) {
                      message.warning('当前无可预览基线文件');
                      return;
                    }
                    void openAttachmentPreview(first.file, first.fileName, '基线文件');
                  }}
                >
                  预览基线文件
                </Button>
                <Button size="small" onClick={() => { void loadInstructionBaselines(); }} loading={baselineLoading}>刷新</Button>
              </>
            ) : null}
        </div>
        </div>
        ) : null}
      </Card>

      <Card size="small" title={renderSectionTitle('包装信息与操作', 'pack_ops')} headStyle={{ background: '#fff7e6' }} bodyStyle={{ padding: collapsedSections.pack_ops ? 0 : 12 }} style={{ border: '2px solid #ffd591', borderRadius: 8 }}>
        {!collapsedSections.pack_ops ? (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>是否需重新包装：{
              repackRequiredText === '需重新包装'
                ? renderSoftTag(repackRequiredText, 'red')
                : repackRequiredText === '无需重新包装'
                  ? renderSoftTag(repackRequiredText, 'green')
                  : repackRequiredText
            }</div>
            <div>是否可混装：{
              mixPackText === '可混装'
                ? renderSoftTag(mixPackText, 'green')
                : mixPackText === '不可混装'
                  ? renderSoftTag(mixPackText, 'red')
                  : mixPackText
            }</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>包装方式：{packingModeText}</div>
            <div>装箱包材选择：{cartonSourceText}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>最大装箱重量：{cartonMaxWeightText !== '-' ? `${cartonMaxWeightText} KG` : '-'}</div>
            <div>装箱最长边：{cartonMaxLengthText !== '-' ? `${cartonMaxLengthText} CM` : '-'}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>托盘种类：{
              palletTypeText === '普通木托盘'
                ? renderSoftTag(palletTypeText, 'green')
                : palletTypeText === '熏蒸木托盘'
                  ? renderSoftTag(palletTypeText, 'red')
                  : palletTypeText
            }</div>
            <div>托盘最大高度：{palletMaxHeightText !== '-' ? `${palletMaxHeightText} CM` : '-'}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
            <div>包装需求备注：<span style={{ color: '#cf1322' }}>{repackRequirementNotesText}</span></div>
          </div>
          <div style={{ height: 6 }} />
          <Divider style={{ margin: 0, borderColor: '#f0f0f0' }} />
          <div style={{ height: 8 }} />
          <Tabs
            size="small"
            activeKey={packConsoleTab}
            onChange={(k) => setPackConsoleTab((k as any) || 'pack_start')}
            items={[
              {
                key: 'pack_start',
                label: '开始打包并装入容器',
                children: (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {(() => {
                      const disStyle = { background: '#f5f5f5', borderColor: '#d9d9d9', color: '#8c8c8c' } as any;
                      const startDis = !canOperate || !!String(currentPackBoxCode || '').trim() || !!addToExistingEnabled;
                      const endNewDis = !canOperate || !String(currentPackBoxCode || '').trim();
                      const addExistingDis = !canOperate || !!String(currentPackBoxCode || '').trim() || !!addToExistingEnabled;
                      const endAddDis = !canOperate || !addToExistingEnabled || !!String(currentPackBoxCode || '').trim();
                      return (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                            <Button
                              type="primary"
                              onClick={openStartNewContainerModal}
                              disabled={startDis}
                              style={startDis ? disStyle : { color: '#fff' }}
                            >
                              开始新容器
                            </Button>
                            <Button
                              type="primary"
                              disabled={endNewDis}
                              onClick={openEndCurrentContainerModal}
                              style={endNewDis ? disStyle : { background: '#fa541c', borderColor: '#fa541c', color: '#fff' }}
                            >
                              结束新容器
                            </Button>

                            <span style={{ color: '#d9d9d9', margin: '0 22px' }}>|</span>

                            <Button
                              disabled={addExistingDis}
                              style={addExistingDis ? disStyle : { background: '#13c2c2', borderColor: '#13c2c2', color: '#fff', fontWeight: 800 }}
                              onClick={openPickExistingContainerModal}
                            >
                              新增到已有容器
                            </Button>
                            <Button
                              type="primary"
                              disabled={endAddDis}
                              style={endAddDis ? disStyle : { background: '#fa541c', borderColor: '#fa541c', color: '#fff' }}
                              onClick={endAddToExistingContainer}
                            >
                              结束新增
                            </Button>

                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'nowrap', color: '#595959', fontWeight: 700, marginLeft: 6, maxWidth: '100%', overflowX: 'auto' }}>
                              <span>
                                容器号：<span style={{ color: '#1677ff' }}>{activePackBoxCode || '-'}</span>
                              </span>
                              {!String(currentPackBoxCode || '').trim() && addToExistingEnabled && activePackBoxCode ? (
                                <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#fff7e6', borderColor: '#ffd591', color: '#ad4e00', fontWeight: 700 }}>
                                  已有容器新增
                                </Tag>
                              ) : null}
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
                                {currentPackTheory.ok ? (
                                  <>
                                    {typeof limitWeightKg === 'number' && Number.isFinite(limitWeightKg) && limitWeightKg > 0 ? (
                                      <>
                                        <span style={{ color: '#595959' }}>
                                          理论总重 <span style={{ color: '#1677ff' }}>{currentPackTheory.totalKg.toFixed(3)} kg</span>
                                        </span>
                                        <span style={{ color: '#d9d9d9' }}>|</span>
                                        <span style={{ color: '#595959' }}>
                                          限重 <span style={{ color: '#1677ff' }}>{Number(limitWeightKg).toFixed(3)} kg</span>
                                        </span>
                                        {currentPackTheory.totalKg > limitWeightKg ? (
                                          <Tag style={{ background: '#fffb8f', borderColor: '#fffb8f', color: '#cf1322', fontWeight: 800, fontSize: 14, padding: '2px 10px', lineHeight: '20px', borderRadius: 999 }}>
                                            超重
                                          </Tag>
                                        ) : currentPackTheory.totalKg >= limitWeightKg * 0.9 ? (
                                          <Tag style={{ background: '#fa541c', borderColor: '#fa541c', color: '#fff', fontWeight: 800, fontSize: 14, padding: '2px 10px', lineHeight: '20px', borderRadius: 999 }}>
                                            接近
                                          </Tag>
                                        ) : (
                                          <Tag style={{ background: '#237804', borderColor: '#237804', color: '#fff', fontWeight: 800, fontSize: 14, padding: '2px 10px', lineHeight: '20px', borderRadius: 999 }}>
                                            正常
                                          </Tag>
                                        )}
                                      </>
                                    ) : (
                                      <span style={{ color: '#595959' }}>
                                        理论总重 <span style={{ color: '#1677ff' }}>{currentPackTheory.totalKg.toFixed(3)} kg</span>
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <span style={{ color: '#8c8c8c' }}>无法估算理论总重量</span>
                                )}
                              </span>
                            </span>
                          </div>

                          <div style={{ height: 1, background: '#f0f0f0', margin: '2px 0' }} />

                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr auto',
                              gridTemplateRows: 'auto auto',
                              columnGap: 12,
                              rowGap: 8,
                              alignItems: 'center',
                            }}
                          >
                            <div style={{ gridColumn: 1, gridRow: 1, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap' }}>
                              <span style={{ color: '#595959' }}>批量添加模式:</span>
                              <Button
                                size="small"
                                disabled={!canOperate || !hasActivePackContainer}
                                onClick={() => setTargetPickerOpen(true)}
                                style={(!canOperate || !hasActivePackContainer) ? undefined : { background: '#faad14', borderColor: '#faad14', color: '#fff' }}
                              >
                                <span style={{ display: 'inline-block', minWidth: 128, textAlign: 'center' }}>选择目标并放入容器</span>
                              </Button>
                            </div>

                            <div style={{ gridColumn: 1, gridRow: 2, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap' }}>
                              <span style={{ color: '#595959' }}>扫描添加模式:</span>
                              <Tooltip title="可输入数量,一次性添加多件扫描目标物">
                                <InputNumber
                                  min={1}
                                  precision={0}
                                  step={1}
                                  value={packTargetQty as any}
                                  onChange={(v) => setPackTargetQty(v == null ? 1 : Math.max(1, Math.floor(Number(v || 1))))}
                                  style={{ width: 72 }}
                                  disabled={!canOperate || !hasActivePackContainer}
                                />
                              </Tooltip>
                              <Input
                                ref={scanTargetInputRef}
                                value={packTargetScan}
                                onChange={(e) => setPackTargetScan(String((e as any)?.target?.value ?? ''))}
                                onPressEnter={submitScanAddToContainer}
                                placeholder="扫描目标物"
                                style={{ width: 260, fontFamily: 'monospace' }}
                                allowClear
                                disabled={!canOperate || !hasActivePackContainer}
                              />
                            </div>

                            <div style={{ gridColumn: 2, gridRow: 2, alignSelf: 'center', marginLeft: -180 }}>
                              <Button type="primary" size="small" disabled={!canOperate || !hasActivePackContainer} onClick={submitScanAddToContainer}>
                                放入容器
                              </Button>
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                ),
              },
              {
                key: 'pack_adjust',
                label: '容器内容调整',
                children: (
                  <Space direction="vertical" style={{ width: '100%' }} size={8}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden' }}>
                      <span style={{ color: '#595959', fontWeight: 700 }}>操作模式：</span>
                      <Select
                        value={adjustMode}
                        style={{ width: 140 }}
                        disabled={!canOperateExec}
                        options={[
                          { value: 'take_only', label: '仅取出' },
                          { value: 'take_put', label: '容器间移动' },
                          { value: 'put_only', label: '仅放入' },
                        ] as any}
                        onChange={(v) => {
                          const m = v as any;
                          setAdjustMode(m);
                          if (m !== 'take_put') {
                            setAdjustMoveAll(false);
                            setAdjustMoveAllPreviewOpen(false);
                            setAdjustMoveAllPreviewRows([]);
                            setAdjustMoveAllPreviewTotal(0);
                          }
                        }}
                      />
                      {adjustMode !== 'take_put' && adjustMode !== 'put_only' ? (
                        <>
                          <span style={{ color: '#595959', fontWeight: 700, marginLeft: 6 }}>取出:</span>
                          <Select
                            value={adjustFromBoxCode || undefined}
                            style={{ width: 260 }}
                            placeholder="选择取出容器（可选已结束容器）"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            options={adjustContainerOptions as any}
                            onChange={(v) => {
                              const cc = String(v || '').trim();
                              setAdjustFromBoxCode(cc);
                            }}
                            onClear={() => setAdjustFromBoxCode('')}
                          />
                        </>
                      ) : null}
                      {adjustMode !== 'take_put' && adjustMode !== 'take_only' ? (
                        <>
                          <span style={{ color: '#595959', fontWeight: 700, marginLeft: 6 }}>放入:</span>
                          <Select
                            value={adjustToBoxCode || undefined}
                            style={{ width: 260 }}
                            placeholder="选择放入容器（可选已结束容器）"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            options={adjustContainerOptions as any}
                            onChange={(v) => {
                              const cc = String(v || '').trim();
                              setAdjustToBoxCode(cc);
                            }}
                            onClear={() => setAdjustToBoxCode('')}
                          />
                        </>
                      ) : null}
                    </div>
                    {adjustMode === 'take_put' ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden' }}>
                        <span style={{ color: '#595959', fontWeight: 700 }}>取出:</span>
                        <Select
                          value={adjustFromBoxCode || undefined}
                          style={{ width: 260 }}
                          placeholder="选择取出容器（可选已结束容器）"
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          options={adjustContainerOptions.map((x: any) => {
                            const other = String(adjustToBoxCode || '').trim();
                            return { ...x, disabled: !!other && x.value === other };
                          }) as any}
                          onChange={(v) => {
                            const cc = String(v || '').trim();
                            const other = String(adjustToBoxCode || '').trim();
                            if (cc && other && cc === other) {
                              setAdjustToBoxCode('');
                              message.warning('容器间移动：取出容器与移入容器不能相同，已自动清空移入容器');
                            }
                            setAdjustFromBoxCode(cc);
                          }}
                          onClear={() => setAdjustFromBoxCode('')}
                        />
                        <span style={{ color: '#595959', fontWeight: 700, marginLeft: 6 }}>放入:</span>
                        <Select
                          value={adjustToBoxCode || undefined}
                          style={{ width: 260 }}
                          placeholder="选择放入容器（可选已结束容器）"
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          options={adjustContainerOptions.map((x: any) => {
                            const other = String(adjustFromBoxCode || '').trim();
                            return { ...x, disabled: !!other && x.value === other };
                          }) as any}
                          onChange={(v) => {
                            const cc = String(v || '').trim();
                            const other = String(adjustFromBoxCode || '').trim();
                            if (cc && other && cc === other) {
                              setAdjustFromBoxCode('');
                              message.warning('容器间移动：取出容器与移入容器不能相同，已自动清空取出容器');
                            }
                            setAdjustToBoxCode(cc);
                          }}
                          onClear={() => setAdjustToBoxCode('')}
                        />
                      </div>
                    ) : null}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ color: '#595959' }}>数量</span>
                      <InputNumber
                        min={1}
                        precision={0}
                        step={1}
                        value={(hasIocTarget ? 1 : adjustQty) as any}
                        disabled={!canOperateExec || hasIocTarget || (adjustMode === 'take_put' && adjustMoveAll)}
                        onChange={(v) => setAdjustQty(Math.max(1, Math.floor(Number(v || 1))))}
                        style={{ width: 72 }}
                      />
                      <span style={{ color: '#595959' }}>操作目标</span>
                      <Input
                        value={adjustTargetCode}
                        onChange={(e) => setAdjustTargetCode(String((e as any)?.target?.value ?? ''))}
                        onPressEnter={execAdjustByMode}
                        placeholder={hasIocTarget ? '输入IOC容器编码' : '输入库存单元系统编码'}
                        style={{ width: 220, fontFamily: 'monospace' }}
                        allowClear
                        disabled={!canOperateExec || (adjustMode === 'take_put' && adjustMoveAll)}
                      />
                      {adjustMode === 'take_put' ? (
                        <>
                          <span style={{ color: '#595959', marginLeft: 4 }}>容器内所有产品</span>
                          <Switch
                            checked={adjustMoveAll}
                            disabled={!canOperateExec}
                            onChange={(checked) => {
                              if (!checked) {
                                setAdjustMoveAll(false);
                                return;
                              }
                              const from = String(adjustFromBoxCode || '').trim();
                              const to = String(adjustToBoxCode || '').trim();
                              if (!from) { adjustWarn('请先选择取出容器'); return; }
                              if (!to) { adjustWarn('请先选择放入容器'); return; }
                              if (from === to) { adjustWarn('容器间移动：取出容器与移入容器不能相同'); return; }
                              try {
                                const stacks = buildAdjustStacks(from);
                                const rows: any[] = [];
                                let total = 0;
                                Array.from(stacks.entries()).forEach(([key, lots]) => {
                                  const [tp, tg] = String(key || '').split(':');
                                  const qty = (lots || []).reduce((s: number, x: any) => s + Math.max(0, Math.floor(Number(x?.qty || 0) || 0)), 0);
                                  if (qty <= 0) return;
                                  total += qty;
                                  rows.push({ target_type: tp || '-', target: tg || '-', qty });
                                });
                                if (!rows.length) { message.info('该容器当前无可移动的产品'); return; }
                                rows.sort((a, b) => (b.qty || 0) - (a.qty || 0));
                                setAdjustMoveAll(true);
                                setAdjustMoveAllPreviewRows(rows);
                                setAdjustMoveAllPreviewTotal(total);
                                setAdjustMoveAllPreviewOpen(true);
                              } catch {
                                adjustError('读取容器内容失败');
                              }
                            }}
                          />
                        </>
                      ) : null}
                      <Button type="primary" loading={adjustLoading} disabled={!canOperateExec} onClick={execAdjustByMode}>
                        执行
                      </Button>
                    </div>
                    <div style={{ color: '#8c8c8c', lineHeight: 1.4 }}>
                      说明：容器内容调整遵循“先取出到调整记录池，再从池放入目标容器”的记账口径；池内数量已计入占用，不可通过扫描/批量重复生成记录。
                    </div>
                    <Divider style={{ margin: '6px 0' }} />
                    <Card
                      size="small"
                      title={(
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                          <span>调整池现状</span>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d', fontWeight: 800 }}>
                              {`可用${adjustPoolSummary.totalPos}件`}
                            </Tag>
                            <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#f9f0ff', borderColor: '#d3adf7', color: '#531dab', fontWeight: 800 }}>
                              {`操作对象种类${adjustPoolSummary.targetKinds}`}
                            </Tag>
                          </span>
                        </div>
                      )}
                      headStyle={{ background: '#f6ffed' }}
                    >
                      <Table
                        size="small"
                        rowKey={(r: any, i?: number) => String(`${r?.target_type || '-'}:${r?.target || '-'}:${i || 0}`)}
                        pagination={{ pageSize: 8, showSizeChanger: true, pageSizeOptions: ['8', '20', '50'] as any }}
                        dataSource={adjustPoolSummary.rowsByTarget}
                        columns={[
                          { title: '序号', width: 70, align: 'center' as const, render: (_: any, __: any, idx: number) => idx + 1 },
                          {
                            title: '操作对象类型',
                            dataIndex: 'target_type',
                            width: 160,
                            render: (v: any) => {
                              const t = String(v || '').trim();
                              if (t === 'instruction_output_container') return 'IOC容器';
                              if (t === 'inventory_unit') return '库存单元';
                              return t || '-';
                            },
                          },
                          { title: '操作对象编码', dataIndex: 'target', width: 260, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                          { title: '可用数量', dataIndex: 'qty', width: 120, render: (v: any) => <span style={{ fontWeight: 800, color: '#cf1322' }}>{Math.floor(Number(v || 0) || 0)}</span> },
                        ] as any}
                        locale={{ emptyText: '调整池暂无可用数据' }}
                      />
                    </Card>
                  </Space>
                ),
              },
              {
                key: 'pack_rescan',
                label: '容器内容重新扫描',
                children: (
                  <Space direction="vertical" style={{ width: '100%' }} size={8}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ color: '#595959', fontWeight: 700 }}>操作容器：</span>
                      <Select
                        value={rescanContainerCode || undefined}
                        style={{ minWidth: 280 }}
                        placeholder="选择容器（可选已结束容器）"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        options={adjustContainerOptions as any}
                        onChange={(v) => setRescanContainerCode(String(v || '').trim())}
                        onClear={() => setRescanContainerCode('')}
                      />
                      <Button
                        loading={rescanLoading}
                        disabled={!canOperateExec || rescanStarted}
                        onClick={startRescanFromContainer}
                        style={(!canOperateExec || rescanStarted)
                          ? { background: '#f5f5f5', borderColor: '#d9d9d9', color: '#8c8c8c' }
                          : { background: '#ff7a45', borderColor: '#ff7a45', color: '#fff' }
                        }
                      >
                        开始重扫
                      </Button>
                      <Button
                        disabled={!canOperateExec || !rescanStarted}
                        onClick={() => setRescanCompareOpen(true)}
                        style={(!canOperateExec || !rescanStarted)
                          ? { background: '#f5f5f5', borderColor: '#d9d9d9', color: '#8c8c8c' }
                          : { background: '#13c2c2', borderColor: '#13c2c2', color: '#fff' }
                        }
                      >
                        完成与结果比对
                      </Button>
                      {rescanStarted ? (
                        <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#fff7e6', borderColor: '#ffd591', color: '#ad4e00', fontWeight: 800 }}>
                          {`重扫容器 ${String(rescanFromBoxCode || '').trim() || '-'}`}
                        </Tag>
                      ) : null}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ color: '#595959' }}>数量</span>
                      <InputNumber
                        min={1}
                        precision={0}
                        step={1}
                        value={(hasIocTarget ? 1 : rescanQty) as any}
                        disabled={!canOperateExec || !rescanStarted || hasIocTarget}
                        onChange={(v) => setRescanQty(Math.max(1, Math.floor(Number(v || 1))))}
                        style={{ width: 72 }}
                      />
                      <span style={{ color: '#595959' }}>操作目标</span>
                      <Input
                        value={rescanTargetCode}
                        onChange={(e) => setRescanTargetCode(String((e as any)?.target?.value ?? ''))}
                        onPressEnter={addRescanBackToOrigin}
                        placeholder={hasIocTarget ? '输入IOC容器编码' : '输入库存单元系统编码'}
                        style={{ width: 220, fontFamily: 'monospace' }}
                        allowClear
                        disabled={!canOperateExec || !rescanStarted}
                      />
                      <Button type="primary" loading={rescanLoading} disabled={!canOperateExec || !rescanStarted} onClick={addRescanBackToOrigin}>
                        加回原容器
                      </Button>
                    </div>
                    <div style={{ color: '#8c8c8c', lineHeight: 1.4 }}>
                      说明：重扫等价于“全量取出到调整池→按操作目标编码逐步加回原容器”。完成后可查看重扫前后理论内容物差异，并支持一键清理本次重扫残留的池内数量。
                    </div>
                    <Divider style={{ margin: '6px 0' }} />
                    <Card
                      size="small"
                      title={(
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                          <span>调整池现状</span>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d', fontWeight: 800 }}>
                              {`可用${adjustPoolSummary.totalPos}件`}
                            </Tag>
                            <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#f9f0ff', borderColor: '#d3adf7', color: '#531dab', fontWeight: 800 }}>
                              {`操作对象种类${adjustPoolSummary.targetKinds}`}
                            </Tag>
                          </span>
                        </div>
                      )}
                      headStyle={{ background: '#f6ffed' }}
                    >
                      <Table
                        size="small"
                        rowKey={(r: any, i?: number) => String(`${r?.target_type || '-'}:${r?.target || '-'}:${i || 0}`)}
                        pagination={{ pageSize: 8, showSizeChanger: true, pageSizeOptions: ['8', '20', '50'] as any }}
                        dataSource={adjustPoolSummary.rowsByTarget}
                        columns={[
                          { title: '序号', width: 70, align: 'center' as const, render: (_: any, __: any, idx: number) => idx + 1 },
                          {
                            title: '操作对象类型',
                            dataIndex: 'target_type',
                            width: 160,
                            render: (v: any) => {
                              const t = String(v || '').trim();
                              if (t === 'instruction_output_container') return 'IOC容器';
                              if (t === 'inventory_unit') return '库存单元';
                              return t || '-';
                            },
                          },
                          { title: '操作对象编码', dataIndex: 'target', width: 260, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                          { title: '可用数量', dataIndex: 'qty', width: 120, render: (v: any) => <span style={{ fontWeight: 800, color: '#cf1322' }}>{Math.floor(Number(v || 0) || 0)}</span> },
                        ] as any}
                        locale={{ emptyText: '调整池暂无可用数据' }}
                      />
                    </Card>
                  </Space>
                ),
              },
              {
                key: 'pack_sep',
                label: <span style={{ display: 'inline-block', width: 1, height: 14, background: '#d9d9d9' }} />,
                disabled: true,
                children: null,
              },
              {
                key: 'pack_hierarchy',
                label: '容器层级管理',
                children: (
                  <Space direction="vertical" style={{ width: '100%' }} size={8}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ color: '#595959', fontWeight: 700 }}>选择父容器</span>
                      <Select
                        value={hierarchyParentCode || undefined}
                        style={{ minWidth: 300 }}
                        placeholder="选择父容器（可选开放/已结束）"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        options={hierarchyParentOptions as any}
                        onChange={(v) => setHierarchyParentCode(String(v || '').trim())}
                        onClear={() => setHierarchyParentCode('')}
                      />
                      <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d', fontWeight: 800 }}>
                        {`已关联子容器 ${hierarchyCurrentChildren.length}`}
                      </Tag>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <Input
                        ref={hierarchyScanInputRef}
                        value={hierarchyChildScanCode}
                        onChange={(e) => setHierarchyChildScanCode(String((e as any)?.target?.value ?? ''))}
                        onPressEnter={() => {
                          const p = String(hierarchyParentCode || '').trim();
                          const c = String(hierarchyChildScanCode || '').trim().toUpperCase();
                          if (!p) { hierarchyWarn('请先选择父容器'); return; }
                          if (!c) { hierarchyWarn('请输入子容器系统编码'); return; }
                          addHierarchyChildrenToParent(p, [c], 'scan');
                          setHierarchyChildScanCode('');
                        }}
                        placeholder="扫描/输入子容器系统编码"
                        style={{ width: 300, fontFamily: 'monospace' }}
                        allowClear
                      />
                      <Button
                        type="primary"
                        disabled={!canOperateExec}
                        onClick={() => {
                          const p = String(hierarchyParentCode || '').trim();
                          const c = String(hierarchyChildScanCode || '').trim().toUpperCase();
                          if (!p) { hierarchyWarn('请先选择父容器'); return; }
                          if (!c) { hierarchyWarn('请输入子容器系统编码'); return; }
                          addHierarchyChildrenToParent(p, [c], 'scan');
                          setHierarchyChildScanCode('');
                          setTimeout(() => {
                            try { hierarchyScanInputRef.current?.focus?.(); } catch {}
                          }, 0);
                        }}
                      >
                        扫描关联子容器
                      </Button>
                      <Button
                        disabled={!canOperateExec}
                        onClick={() => {
                          if (!String(hierarchyParentCode || '').trim()) { hierarchyWarn('请先选择父容器'); return; }
                          setHierarchyBatchOpen(true);
                        }}
                      >
                        批量关联子容器
                      </Button>
                    </div>
                  </Space>
                ),
              },
            ]}
          />
        </Space>
        ) : null}
      </Card>
      <div ref={waybillOpsRef}>
      <Card size="small" title={renderSectionTitle('运单信息与操作', 'waybill_ops')} headStyle={{ background: '#e6fffb' }} bodyStyle={{ padding: collapsedSections.waybill_ops ? 0 : 12 }} style={{ border: '2px solid #ffd591', borderRadius: 8 }}>
        {!collapsedSections.waybill_ops ? (
        <>
        <Tabs
          size="small"
          defaultActiveKey="waybill_basic"
          items={[
            {
              key: 'waybill_basic',
              label: '基础信息',
              children: (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>运单提供方式：{waybillModeText}</div>
            <div>运单上传时机：{waybillSubmitModeText}</div>
          </div>
                  {isWaybillSubmitImmediate ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                        <span>运输标签与出库对象是否存在对应关系：</span>
                        {renderMappingRelationTag(shippingLabelMappingText)}
                      </div>
            <div />
          </div>
                  ) : null}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>运单所属物流承运商：{waybillCarrierDisplay}</div>
            <div />
          </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                      <span>物流跟踪号/BOL提单号码/PRO号码：</span>
                      <span>{waybillTrackingDisplay}</span>
                    </div>
                    <div />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>运单金额：{waybillChargeDisplay}</div>
                    <div>费用备注：{waybillChargeNoteDisplay}</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <span style={{ whiteSpace: 'nowrap' }}>运输标签(物流运单/箱唛/托盘标)：</span>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {transportLabelAttachments.length === 0 ? (
                          <span style={{ color: '#8c8c8c' }}>暂无附件</span>
                        ) : (
                          <>
                            <div style={transportLabelListExpanded ? { maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4, paddingRight: 4 } : { display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {visibleTransportLabelAttachments.map((att: any, idx: number) => {
                                const fileName = String(att?.file_name || att?.name || att?.file || att?.file_path || `运输标签附件${idx + 1}`);
                                return (
                                  <span key={String(att?.id || `transport-label-${idx}`)} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
                                    <Tooltip title={fileName} placement="topLeft">
                                      <span style={{ maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fileName}</span>
                                    </Tooltip>
                                    <Button size="small" type="link" onClick={() => { void openAttachmentPreview(att, fileName, ''); }} style={{ padding: 0 }}>预览</Button>
                                    <Button size="small" type="link" onClick={() => { void downloadTransportLabelAttachment(att); }} style={{ padding: 0 }}>下载</Button>
                                  </span>
                                );
                              })}
                            </div>
                            {hiddenTransportLabelCount > 0 ? (
                              <Button type="link" size="small" style={{ padding: 0, height: 'auto', alignSelf: 'flex-start' }} onClick={() => setTransportLabelListExpanded(true)}>
                                {`展开全部(共${sortedTransportLabelAttachments.length}个)`}
                              </Button>
                            ) : null}
                            {transportLabelListExpanded && sortedTransportLabelAttachments.length > transportLabelDisplayLimit ? (
                              <Button type="link" size="small" style={{ padding: 0, height: 'auto', alignSelf: 'flex-start' }} onClick={() => setTransportLabelListExpanded(false)}>
                                收起
                              </Button>
                            ) : null}
                          </>
                        )}
                      </div>
                    </div>
                    <div />
                  </div>
                </Space>
              ),
            },
            {
              key: 'baseline_files',
              label: baselineFilesTabLabel,
              children: (
                <div>{renderReadyBaselineInlinePreview()}</div>
              ),
            },
              {
                key: 'recipient_aux_info',
              label: '收件人与辅助信息',
                children: (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ border: '1px solid #52c41a', borderRadius: 6, padding: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <div style={{ fontWeight: 600, color: '#389e0d' }}>收件人完整信息</div>
                        <Button size="small" onClick={() => { void copyRecipientAuxInfo(); }}>
                          复制信息
                        </Button>
                      </div>
                      <div>{recipientAuxInfo.line1}</div>
                      <div>{recipientAuxInfo.line2}</div>
                      <div>{recipientAuxInfo.line3}</div>
                      <div>{recipientAuxInfo.line4}</div>
                      <div>{recipientAuxInfo.line5}</div>
                    </div>
                    <div style={{ border: '1px solid #1677ff', borderRadius: 6, padding: 10 }}>
                      <div style={{ fontWeight: 600, color: '#1677ff', marginBottom: 6 }}>辅助信息</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div>偏好承运商：{recipientAuxInfo.preferredCarrierText}</div>
                        <div>
                          保险信息：
                          {recipientAuxInfo.insuranceRequired === true ? (
                            <span style={{ marginLeft: 6, padding: '1px 8px', borderRadius: 4, background: '#ff4d4f', color: '#fff', fontWeight: 600 }}>
                              {recipientAuxInfo.insuranceInfo}
                            </span>
                          ) : (
                            <span>{recipientAuxInfo.insuranceInfo}</span>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div>
                          签名需求：
                          {recipientAuxInfo.signatureRequired === true ? (
                            <span style={{ marginLeft: 6, padding: '1px 8px', borderRadius: 4, background: '#ff4d4f', color: '#fff', fontWeight: 600 }}>
                              {recipientAuxInfo.signatureInfo}
                            </span>
                          ) : (
                            <span>{recipientAuxInfo.signatureInfo}</span>
                          )}
                        </div>
                        <div>
                          是否需要升降尾板服务：
                          {recipientAuxInfo.liftgateRequired === true ? (
                            <span style={{ marginLeft: 6, padding: '1px 8px', borderRadius: 4, background: '#ff4d4f', color: '#fff', fontWeight: 600 }}>
                              {recipientAuxInfo.liftgateInfo}
                            </span>
                          ) : (
                            <span>{recipientAuxInfo.liftgateInfo}</span>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div>
                          送货是否需提前预约：
                          {recipientAuxInfo.appointmentRequired === true ? (
                            <span style={{ marginLeft: 6, padding: '1px 8px', borderRadius: 4, background: '#ff4d4f', color: '#fff', fontWeight: 600 }}>
                              {recipientAuxInfo.appointmentInfo}
                            </span>
                          ) : (
                            <span>{recipientAuxInfo.appointmentInfo}</span>
                          )}
                        </div>
                        <div>
                          派送车辆是否受限：
                          {recipientAuxInfo.limitedAccessDelivery === true ? (
                            <span style={{ marginLeft: 6, padding: '1px 8px', borderRadius: 4, background: '#ff4d4f', color: '#fff', fontWeight: 600 }}>
                              {recipientAuxInfo.limitedAccessInfo}
                            </span>
                          ) : (
                            <span>{recipientAuxInfo.limitedAccessInfo}</span>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div>参考号：{recipientAuxInfo.referenceNumberText}</div>
                        <div>指令备注：{recipientAuxInfo.instructionRemarksText}</div>
                      </div>
                    </div>
                  </div>
                ),
              },
            ]}
          />
        <div style={{ height: 8 }} />
        <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 10 }}>
          <Space size={8} wrap>
            <Button
              size="small"
              disabled={!canOperateExec || printableAttachmentsWithPrintRequirement.length === 0}
              onClick={openPrintOtherAttachmentsModal}
            >
              打印其他附件
            </Button>
            <Button size="small" disabled={disableBuyWaybillBtn} onClick={openBuyWaybillModal}>准备并上传运输标签</Button>
            <Button size="small" disabled={disablePrintTransportLabelPdfBtn || printTransportLabelPdfRunning} onClick={() => { void printTransportLabelDirectly(); }}>{`打印运输标签(pdf ${transportLabelPdfFileCount}个${transportLabelPdfPageTotal}页)`}</Button>
            <Button size="small" disabled={disablePrintTransportLabelImageBtn || printTransportLabelImageRunning} onClick={() => { void printTransportLabelImagesDirectly(); }}>{`打印运输标签(图片 ${transportLabelImageFileCount}个)`}</Button>
            <Button size="small" disabled={disableLabelingBtn} onClick={() => { void openLabelingModal(); }}>贴单</Button>
            <Button size="small" type="primary" disabled={disableMarkCompletedBtn} onClick={markProcessCompleted}>标注已完成</Button>
            <Button size="small" danger disabled={disableMarkUnableBtn} onClick={openUnableReasonModal}>标注无法操作</Button>
          </Space>
        </div>
        </>
        ) : null}
      </Card>
      </div>
      </div>

      <div style={{ flex: '0 0 52%', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Card
        size="small"
        title={(
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <span>操作对象与包装进度</span>
            {renderSectionToggleIcon('progress')}
            <span
              style={{
                display: 'inline-block',
                padding: '1px 6px',
                border: (overallHeaderStats.rateValue != null && overallHeaderStats.rateValue >= 100) ? '1px solid #b7eb8f' : '1px solid #ffadd2',
                background: (overallHeaderStats.rateValue != null && overallHeaderStats.rateValue >= 100) ? '#f6ffed' : '#fff0f6',
                color: (overallHeaderStats.rateValue != null && overallHeaderStats.rateValue >= 100) ? '#389e0d' : '#c41d7f',
                borderRadius: 4,
                fontWeight: 700,
                fontSize: 12,
                lineHeight: '18px',
              }}
            >
              包装完成率 {overallHeaderStats.rateText}
            </span>
            {(overallHeaderStats.rateValue != null && overallHeaderStats.rateValue >= 100) ? (
              <span
                style={{
                  color: '#389e0d',
                  fontWeight: 900,
                  fontSize: 18,
                  lineHeight: '18px',
                }}
              >
                √
              </span>
            ) : null}
            <span style={{ color: '#d9d9d9', fontWeight: 400 }}>|</span>
          </div>
        )}
        extra={(
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                flexWrap: 'nowrap',
                maxWidth: 760,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: 12,
                lineHeight: '18px',
              }}
            >
              <Tooltip title="口径：状态为已结束(不含进行中、已作废)的容器数量；允许直接装载件数为0的父容器计入。">
                <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #91caff', background: '#e6f4ff', color: '#0958d9', borderRadius: 4, fontWeight: 700 }}>
                  已完成容器 {overallHeaderStats.closedCount}
                </span>
              </Tooltip>
              <Tooltip title="口径：仅统计已完成的父容器和独立容器；子容器不计入；按容器长×宽×高换算体积后汇总。">
                <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #87e8de', background: '#e6fffb', color: '#006d75', borderRadius: 4, fontWeight: 700 }}>
                  总体积 {overallHeaderStats.volM3Text}m3
                </span>
              </Tooltip>
              <Tooltip title="口径：仅统计已完成的父容器和独立容器；子容器不计入；优先 weight_kg，再回退 weight_g / weight_lb 换算。">
                <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #b7eb8f', background: '#f6ffed', color: '#389e0d', borderRadius: 4, fontWeight: 700 }}>
                  总重量 {overallHeaderStats.kgText}kg
                </span>
              </Tooltip>
              <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #d3adf7', background: '#f9f0ff', color: '#531dab', borderRadius: 4, fontWeight: 700 }}>
                已装入容器 {overallHeaderStats.doneQty}件
              </span>
              <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #ffe58f', background: '#fffbe6', color: '#ad8b00', borderRadius: 4, fontWeight: 700 }}>
                目标 {overallHeaderStats.targetQty}件
              </span>
            </div>
        )}
        headStyle={{ background: '#f6ffed' }}
        bodyStyle={{ padding: collapsedSections.progress ? 0 : 12 }}
        style={{ border: '2px solid #b7eb8f', borderRadius: 8 }}
      >
        {!collapsedSections.progress ? (
        <Tabs
          size="small"
          items={[
            {
              key: 'by_target',
              label: hasIocTarget ? '按操作对象统计(历史指令产出)' : '按操作对象统计(当前库存)',
              children: (
                <Table
                  size="small"
                  rowKey={(r: any, i?: number) => String(r?.key || r?.__key || r?.output_container_id || r?.output_code || i)}
                  columns={(hasIocTarget ? iocColumns : inventoryColumns) as any}
                  dataSource={hasIocTarget ? iocDisplayRows : inventoryRows}
                  loading={hasIocTarget ? iocDisplayLoading : false}
                  onRow={(record: any) => ({
                    style: isOverviewPackedCompleted(record) ? { background: '#b7eb8f' } : undefined,
                  })}
                  pagination={{
                    current: targetOverviewPage,
                    pageSize: targetOverviewPageSize,
                    total: (hasIocTarget ? iocDisplayRows : inventoryRows).length,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: ['10', '20', '50', '100'] as any,
                    showTotal: (total: number, range: [number, number]) => `第${range[0]}-${range[1]}条 共${total}条记录`,
                    onChange: (p: number, ps: number) => {
                      setTargetOverviewPage(p);
                      setTargetOverviewPageSize(ps);
                    },
                  }}
                  scroll={{ x: hasIocTarget ? 1200 : 860 }}
                  locale={{ emptyText: hasIocTarget ? '暂无历史指令执行结果' : '暂无当前可用库存' }}
                />
              ),
            },
            {
              key: 'by_container',
              label: (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span>按容器统计(含调整池)</span>
                  {hasBoxStatDirty ? (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '0 8px',
                        borderRadius: 999,
                        border: '1px solid #ffa39e',
                        background: '#fff1f0',
                        color: '#cf1322',
                        fontSize: 12,
                        fontWeight: 400,
                        lineHeight: '20px',
                      }}
                    >
                      <WarningOutlined />
                      {`待核对 ${boxStatDirtyCount}`}
                    </span>
                  ) : null}
                </span>
              ),
              children: (
                <div>
                  <style>{`
                    .regular-outbound-by-container-table .regular-outbound-cancelled-row > td {
                      background: #d9d9d9 !important;
                      color: #8c8c8c !important;
                    }
                    .regular-outbound-by-container-table .regular-outbound-cancelled-row:hover > td {
                      background: #d9d9d9 !important;
                    }
                  `}</style>
                <Table
                  className="regular-outbound-by-container-table"
                  size="small"
                  rowKey={(r: any) => String(r?.container_code || '')}
                  rowClassName={(record: any) => {
                    const st = String(record?.raw_box?.status || '').trim().toLowerCase();
                    return st === 'cancelled' ? 'regular-outbound-cancelled-row' : '';
                  }}
                  dataSource={boxStatRows}
                  pagination={{
                    current: boxStatPage,
                    pageSize: boxStatPageSize,
                    total: boxStatRows.length,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: ['10', '20', '50', '100'] as any,
                    showTotal: (total: number, range: [number, number]) => `第${range[0]}-${range[1]}条 共${total}条记录`,
                    onChange: (p: number, ps: number) => { setBoxStatPage(p); setBoxStatPageSize(ps); },
                  }}
                  columns={[
                    { title: '序号', width: 70, align: 'center' as const, render: (_: any, __: any, idx: number) => ((Math.max(1, Number(boxStatPage) || 1) - 1) * (Math.max(1, Number(boxStatPageSize) || 10)) + idx + 1) },
                    {
                      title: '容器系统编码',
                      dataIndex: 'container_code',
                      width: 190,
                      render: (v: any) => {
                        const code = String(v || '').trim();
                        const cur = String(activePackBoxCode || '').trim();
                        const labelDirty = !!(labelDirtyByCode as any)?.[code];
                        const isAdjustPool = !!String(adjustPoolCode || '').trim() && code === String(adjustPoolCode || '').trim();
                        const row = (Array.isArray(boxStatRows) ? boxStatRows : []).find((x: any) => String(x?.container_code || '').trim() === code) || {};
                        const statusRaw = String(row?.raw_box?.status || '').trim().toLowerCase();
                        const isCancelled = statusRaw === 'cancelled';
                        const qtyNum = Math.floor(Number(row?.qty || 0) || 0);
                        const isChild = hierarchyParentByChild.has(code);
                        const childRows = hierarchyRowsByParent.get(code) || [];
                        const isParent = childRows.length > 0;
                        const needWarn = !isCancelled && !isAdjustPool && qtyNum === 0 && (isChild || (!isChild && !isParent));
                        const warnText = isChild
                          ? '该容器作为子容器但当前为空，请确认是否需要移除关联或补充装载'
                          : '该容器未作为父容器使用，且当前为空容器';
                        if (!code) return '-';
                        const displayCode = (String(adjustPoolCode || '').trim() && code === String(adjustPoolCode || '').trim())
                          ? `${code}(调整池)`
                          : code;
                        return (
                          <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <Tag color={(cur && code === cur) ? 'blue' : 'default'} style={{ marginInlineEnd: 0 }}>
                                <span style={{ fontFamily: 'monospace' }}>{displayCode}</span>
                              </Tag>
                              <Button
                                type="link"
                                size="small"
                                style={{ padding: 0, height: 'auto', lineHeight: 1 }}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    await navigator.clipboard.writeText(displayCode);
                                    message.success('已复制');
                                  } catch {
                                    message.error('复制失败');
                                  }
                                }}
                              >
                                <CopyOutlined />
                              </Button>
                            </span>
                            {!isCancelled && labelDirty ? (
                              <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#fff2f0', borderColor: '#ffccc7', color: '#cf1322', fontWeight: 700 }}>
                                核对尺寸-重量-箱标
                              </Tag>
                            ) : null}
                            {needWarn ? (
                              <Tooltip title={warnText}>
                                <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#fff1f0', borderColor: '#ffa39e', color: '#cf1322', fontWeight: 700, cursor: 'help' }}>
                                  核对容器内容
                                </Tag>
                              </Tooltip>
                            ) : null}
                          </span>
                        );
                      },
                    },
                    {
                      title: '容器类型',
                      key: 'container_type',
                      width: 110,
                      render: (_: any, row: any) => {
                        const st = String(row?.raw_box?.status || '').trim().toLowerCase();
                        if (st === 'cancelled') return '-';
                        const code = String(row?.container_code || '').trim();
                        const text = resolveContainerTypeText(code, row?.raw_box);
                        return <Tag>{text}</Tag>;
                      },
                    },
                    {
                      title: '状态',
                      key: 'status',
                      width: 100,
                      render: (_: any, row: any) => {
                        const code = String(row?.container_code || '').trim();
                        const cur = String(activePackBoxCode || '').trim();
                        if (cur && code && cur === code) return <Tag color="blue">进行中</Tag>;
                        const st = String(row?.raw_box?.status || '').trim();
                        if (st === 'cancelled') return <Tag color="default">已作废</Tag>;
                        if (st === 'open') return <Tag color="blue">进行中</Tag>;
                        return <Tag color="green">已结束</Tag>;
                      },
                    },
                    {
                      title: (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span>容器层级</span>
                          <Tooltip
                            trigger={['click', 'hover']}
                            title={(
                              <div style={{ lineHeight: 1.7 }}>
                                <div>子：作为其他容器的子容器</div>
                                <div>父：作为其他容器的父容器</div>
                                <div>独立：既不是父也不是子</div>
                                <div>红色胶囊“核对容器内容”：该容器为“子/独立”且总件数为0，可能是无用空容器,需重点关注</div>
                              </div>
                            )}
                          >
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 16,
                                height: 16,
                                borderRadius: '50%',
                                border: '1px solid #bfbfbf',
                                color: '#8c8c8c',
                                fontSize: 11,
                                lineHeight: '16px',
                                cursor: 'help',
                                userSelect: 'none',
                                fontWeight: 700,
                                boxSizing: 'border-box',
                              }}
                            >
                              ?
                            </span>
                          </Tooltip>
                        </span>
                      ),
                      key: 'hierarchy_level',
                      width: 110,
                      render: (_: any, row: any) => {
                        const code = String(row?.container_code || '').trim();
                        if (!code) return '-';
                        const st = String(row?.raw_box?.status || '').trim().toLowerCase();
                        if (st === 'cancelled') return '-';
                        const isAdjustPool = !!String(adjustPoolCode || '').trim() && code === String(adjustPoolCode || '').trim();
                        if (isAdjustPool) return '-';
                        const qtyNum = Math.floor(Number(row?.qty || 0) || 0);
                        const isChild = hierarchyParentByChild.has(code);
                        const childRows = hierarchyRowsByParent.get(code) || [];
                        const isParent = childRows.length > 0;
                        if (isChild) {
                          return (
                            <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#fff1f0', borderColor: '#ffa39e', color: '#cf1322', fontWeight: 700 }}>
                              子
                            </Tag>
                          );
                        }
                        if (isParent) {
                          return (
                            <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#e6f4ff', borderColor: '#91caff', color: '#0958d9', fontWeight: 700 }}>
                              父
                            </Tag>
                          );
                        }
                        return (
                          <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d', fontWeight: 700 }}>
                            独立
                          </Tag>
                        );
                      },
                    },
                    { title: '记录数', dataIndex: 'ops', width: 90, align: 'center' as const },
                    { title: '操作对象种类', dataIndex: 'objectKinds', width: 120, align: 'center' as const, render: (v: any) => <span style={{ color: '#722ed1', fontWeight: 700 }}>{Math.floor(Number(v || 0) || 0)}</span> },
                    {
                      title: '直接装载件数',
                      dataIndex: 'qty',
                      width: 90,
                      align: 'center' as const,
                      render: (v: any, row: any) => {
                        const code = String(row?.container_code || '').trim();
                        const isAdjustPool = !!String(adjustPoolCode || '').trim() && code === String(adjustPoolCode || '').trim();
                        const isChild = hierarchyParentByChild.has(code);
                        const childRows = hierarchyRowsByParent.get(code) || [];
                        const isParent = childRows.length > 0;
                        const n = isAdjustPool
                          ? Math.floor(Number(adjustPoolSummary?.totalPos || 0) || 0)
                          : Math.floor(Number(v || 0) || 0);
                        const needWarn = !isAdjustPool && n === 0 && (isChild || (!isChild && !isParent));
                        if (!needWarn) return <span style={{ fontWeight: 700 }}>{n}</span>;
                        return (
                          <span
                            style={{
                              display: 'inline-block',
                              minWidth: 26,
                              textAlign: 'center',
                              padding: '0 8px',
                              borderRadius: 6,
                              border: '1px solid #ffe58f',
                              background: '#fffbe6',
                              color: '#cf1322',
                              fontWeight: 700,
                              lineHeight: '20px',
                            }}
                          >
                            {n}
                          </span>
                        );
                      },
                    },
                    {
                      title: '含子容器汇总件数',
                      key: 'aggregated_qty',
                      width: 150,
                      align: 'center' as const,
                      render: (_: any, row: any) => {
                        const code = String(row?.container_code || '').trim();
                        const st = String(row?.raw_box?.status || '').trim().toLowerCase();
                        if (!code || st === 'cancelled') return '-';
                        const childRows = hierarchyRowsByParent.get(code) || [];
                        const isParent = (Array.isArray(childRows) ? childRows : []).length > 0;
                        if (!isParent) return '-';
                        const direct = Math.max(0, Math.floor(Number(boxDirectQtyByCode.get(code) || 0) || 0));
                        const agg = Math.max(0, Math.floor(Number(boxAggregatedQtyByCode.get(code) || direct) || 0));
                        if (agg <= direct) return <span style={{ fontWeight: 700 }}>{agg}</span>;
                        return (
                          <span
                            style={{
                              display: 'inline-block',
                              minWidth: 26,
                              textAlign: 'center',
                              padding: '0 8px',
                              borderRadius: 6,
                              border: '1px solid #91caff',
                              background: '#e6f4ff',
                              color: '#0958d9',
                              fontWeight: 700,
                              lineHeight: '20px',
                            }}
                            title={`直接装载:${direct}，含子容器汇总:${agg}`}
                          >
                            {agg}
                          </span>
                        );
                      },
                    },
                    {
                      title: '重量(kg | Lb)',
                      key: 'weight',
                      width: 150,
                      render: (_: any, row: any) => {
                        const code = String(row?.container_code || '').trim();
                        const cur = String(activePackBoxCode || '').trim();
                        const st = String(row?.raw_box?.status || '').trim();
                        if (!code || (cur && cur === code) || !st || st === 'open' || st === 'cancelled') return '-';
                        const meta: any = (boxMeta as any)?.[code] || {};
                        const wg = Number(meta?.weight_g);
                        if (!Number.isFinite(wg) || wg <= 0) return '-';
                        const kg = Math.ceil(wg / 1000);
                        const lb = (() => {
                          const v = Number(meta?.weight_lb);
                          if (Number.isFinite(v) && v > 0) return Math.floor(v);
                          return Math.ceil(kg * 2.2046226218);
                        })();
                        return <span style={{ fontWeight: 700 }}>{`${kg} | ${lb}`}</span>;
                      },
                    },
                    {
                      title: '尺寸(cm | inch)',
                      key: 'dims',
                      width: 220,
                      render: (_: any, row: any) => {
                        const code = String(row?.container_code || '').trim();
                        const cur = String(activePackBoxCode || '').trim();
                        const st = String(row?.raw_box?.status || '').trim();
                        if (!code || (cur && cur === code) || !st || st === 'open' || st === 'cancelled') return '-';
                        const meta: any = (boxMeta as any)?.[code] || {};
                        const l = Math.floor(Number(meta?.length_cm || 0) || 0);
                        const w = Math.floor(Number(meta?.width_cm || 0) || 0);
                        const h = Math.floor(Number(meta?.height_cm || 0) || 0);
                        if (!(l > 0 && w > 0 && h > 0)) return '-';
                        const li = Number(meta?.length_in) > 0 ? Math.floor(Number(meta?.length_in)) : Math.ceil(l / 2.54);
                        const wi = Number(meta?.width_in) > 0 ? Math.floor(Number(meta?.width_in)) : Math.ceil(w / 2.54);
                        const hi = Number(meta?.height_in) > 0 ? Math.floor(Number(meta?.height_in)) : Math.ceil(h / 2.54);
                        return <span style={{ fontWeight: 700 }}>{`${l}×${w}×${h} | ${li}×${wi}×${hi}`}</span>;
                      },
                    },
                    { title: '最近时间', dataIndex: 'lastTs', width: 180, render: (v: any) => (v ? new Date(Number(v)).toLocaleString() : '-') },
                    {
                      title: '操作',
                      key: 'ops2',
                      width: 340,
                      render: (_: any, row: any) => {
                        const code = String(row?.container_code || '').trim();
                        const cur = String(activePackBoxCode || '').trim();
                        const st = String(row?.raw_box?.status || '').trim();
                        const stRaw = st.toLowerCase();
                        const isAdjustPool = !!String(adjustPoolCode || '').trim() && code === String(adjustPoolCode || '').trim();
                        const qtyNum = Math.floor(Number(row?.qty || 0) || 0);
                        const childRows = hierarchyRowsByParent.get(code) || [];
                        const isParent = childRows.length > 0;
                        const isCancelled = stRaw === 'cancelled';
                        const canVoid = (
                          !!code
                          && canOperateExec
                          && !isAdjustPool
                          && !isParent
                          && qtyNum <= 0
                          && stRaw !== 'open'
                          && stRaw !== 'cancelled'
                          && stRaw !== 'consumed'
                          && stRaw !== 'lost'
                        );
                        const voiding = voidingContainerCodes.has(code);
                        const canFinal = !!code && (!cur || code !== cur) && !!st && st !== 'open' && st !== 'cancelled';
                        return (
                          <Space size={8}>
                            <Button
                              size="small"
                              style={{ background: '#e6f4ff', borderColor: '#91caff', color: '#0958d9' }}
                              onClick={() => {
                                setContainerDetailCode(code);
                                setContainerDetailOpen(true);
                              }}
                            >
                              查看记录
                            </Button>
                            {!isAdjustPool ? (
                              <>
                                <Button
                                  size="small"
                                  disabled={!canFinal}
                                  style={canFinal ? { background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d' } : undefined}
                                  onClick={() => {
                                    if (!canFinal) return;
                                    setBoxFinalCode(code);
                                    setBoxFinalOpen(true);
                                  }}
                                >
                                  最终包装数据
                                </Button>
                                <Tooltip title="按照当前记录生成指令产出容器箱标，可先修改操作记录或容器物理数据后再生成此箱标">
                                  <Button
                                    size="small"
                                    disabled={!canFinal}
                                    style={canFinal ? { background: '#ff7a45', borderColor: '#ff7a45', color: '#fff' } : undefined}
                                    onClick={() => {
                                      if (!canFinal) return;
                                      void openOutputBoxLabelPreview(code);
                                    }}
                                  >
                                    生成箱标
                                  </Button>
                                </Tooltip>
                                <Tooltip title={canVoid ? '作废空容器（系统编码不回收复用）' : '仅空容器且非父容器可作废'}>
                                  {isCancelled ? (
                                    <Button size="small" disabled>已作废</Button>
                                  ) : (canVoid ? (
                                    <Button
                                      size="small"
                                      danger
                                      loading={voiding}
                                      disabled={voiding}
                                      onClick={() => { if (canVoid) { void voidEmptyContainer(code); } }}
                                    >
                                      作废
                                    </Button>
                                  ) : null)}
                                </Tooltip>
                              </>
                            ) : null}
                          </Space>
                        );
                      },
                    },
                  ] as any}
                  scroll={{ x: 'max-content' as any }}
                  locale={{ emptyText: '暂无容器统计数据' }}
                />
                </div>
              ),
            },
            {
              key: 'by_hierarchy',
              label: '容器层级展示',
              children: (
                <Table
                  size="small"
                  rowKey={(r: any, i?: number) => String(`${r?.parent_code || '-'}:${r?.child_code || '-'}:${i || 0}`)}
                  dataSource={hierarchyDisplayRows}
                  pagination={{
                    current: hierarchyDisplayPage,
                    pageSize: hierarchyDisplayPageSize,
                    total: hierarchyDisplayRows.length,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: ['10', '20', '50', '100'] as any,
                    showTotal: (total: number, range: [number, number]) => `第${range[0]}-${range[1]}条 共${total}条记录`,
                    onChange: (p: number, ps: number) => { setHierarchyDisplayPage(p); setHierarchyDisplayPageSize(ps); },
                  }}
                  onRow={(record: any) => {
                    const g = Number(record?.__groupIdx || 0);
                    return {
                      style: {
                        background: g % 2 === 0 ? '#f6ffed' : '#e6f7ff',
                      },
                    } as any;
                  }}
                  columns={[
                    { title: '序号', width: 70, align: 'center' as const, render: (_: any, __: any, idx: number) => ((Math.max(1, Number(hierarchyDisplayPage) || 1) - 1) * (Math.max(1, Number(hierarchyDisplayPageSize) || 10)) + idx + 1) },
                    {
                      title: '父容器系统编码',
                      dataIndex: 'parent_code',
                      width: 220,
                      render: (v: any, row: any) => {
                        const first = !!row?.__isFirstInParentGroup;
                        const code = String(v || '-');
                        return (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontFamily: 'monospace', color: first ? undefined : '#bfbfbf' }}>
                              {code}
                            </span>
                            {code && code !== '-' ? (
                              <Button
                                type="link"
                                size="small"
                                style={{ padding: 0, height: 'auto', lineHeight: 1, color: first ? undefined : '#bfbfbf' }}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    await navigator.clipboard.writeText(code);
                                    message.success('已复制');
                                  } catch {
                                    message.error('复制失败');
                                  }
                                }}
                              >
                                <CopyOutlined />
                              </Button>
                            ) : null}
                          </span>
                        );
                      },
                    },
                    {
                      title: '父容器状态',
                      dataIndex: 'parent_status',
                      width: 120,
                      render: (v: any) => {
                        const st = String(v || '').trim();
                        if (st === 'open') return <Tag color="blue">进行中</Tag>;
                        if (st === 'cancelled') return <Tag>已作废</Tag>;
                        return <Tag color="green">已结束</Tag>;
                      },
                    },
                    {
                      title: '子容器系统编码',
                      dataIndex: 'child_code',
                      width: 220,
                      render: (v: any) => {
                        const code = String(v || '-');
                        return (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontFamily: 'monospace' }}>{code}</span>
                            {code && code !== '-' ? (
                              <Button
                                type="link"
                                size="small"
                                style={{ padding: 0, height: 'auto', lineHeight: 1 }}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    await navigator.clipboard.writeText(code);
                                    message.success('已复制');
                                  } catch {
                                    message.error('复制失败');
                                  }
                                }}
                              >
                                <CopyOutlined />
                              </Button>
                            ) : null}
                          </span>
                        );
                      },
                    },
                    {
                      title: '子容器状态',
                      dataIndex: 'child_status',
                      width: 120,
                      render: (v: any) => {
                        const st = String(v || '').trim();
                        if (st === 'open') return <Tag color="blue">进行中</Tag>;
                        if (st === 'cancelled') return <Tag>已作废</Tag>;
                        return <Tag color="green">已结束</Tag>;
                      },
                    },
                    {
                      title: '操作',
                      key: 'actions',
                      width: 120,
                      render: (_: any, row: any) => (
                        <Button
                          type="default"
                          danger
                          size="small"
                          disabled={!canOperateExec}
                          onClick={() => removeHierarchyLinkById(row?.id)}
                        >
                          解除关联
                        </Button>
                      ),
                    },
                  ] as any}
                  locale={{ emptyText: '暂无容器层级关系' }}
                />
              ),
            },
          ]}
        />
        ) : null}
      </Card>

      <Card
        size="small"
        title={renderSectionTitle('操作记录', 'records')}
        extra={(
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, lineHeight: '18px' }}>
            <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #91caff', background: '#e6f4ff', color: '#0958d9', borderRadius: 4, fontWeight: 700 }}>
              记录数 {operationHeaderStats.recordCount}
            </span>
            <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #87e8de', background: '#e6fffb', color: '#006d75', borderRadius: 4, fontWeight: 700 }}>
              已装入容器 {operationHeaderStats.packedQty}件
            </span>
          </div>
        )}
        headStyle={{ background: '#fff1f0' }}
        bodyStyle={{ padding: collapsedSections.records ? 0 : 12 }}
        style={{ border: '2px solid #d3adf7', borderRadius: 8 }}
      >
        {!collapsedSections.records ? (
        <Table
          size="small"
          rowKey="key"
          columns={operationColumns}
          dataSource={operationRows}
          pagination={{
            current: operationRecordsPage,
            pageSize: operationRecordsPageSize,
            total: operationRows.length,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'] as any,
            showTotal: (total: number, range: [number, number]) => `第${range[0]}-${range[1]}条 共${total}条记录`,
            onChange: (p: number, ps: number) => { setOperationRecordsPage(p); setOperationRecordsPageSize(ps); },
          }}
          scroll={{ x: 980 }}
          locale={{ emptyText: '暂无操作记录' }}
        />
        ) : null}
      </Card>
      </div>
      <Modal
        title={(
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 12, width: '100%', paddingRight: 30 }}>
            <span style={{ justifySelf: 'start' }}>批量关联子容器</span>
            <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#e6f4ff', borderColor: '#91caff', color: '#0958d9', fontSize: 16, fontWeight: 400, justifySelf: 'center' }}>
              {`当前选定父容器 ${String(hierarchyParentCode || '-').trim() || '-'}`}
            </Tag>
            <span />
          </div>
        )}
        open={hierarchyBatchOpen}
        width={860}
        destroyOnClose
        onCancel={() => {
          setHierarchyBatchOpen(false);
          setHierarchyBatchQuery('');
          setHierarchyBatchLinkedQuery('');
          setHierarchyBatchSelectedKeys([]);
          setHierarchyBatchPage(1);
          setHierarchyBatchPageSize(10);
          setHierarchyBatchLinkedPage(1);
          setHierarchyBatchLinkedPageSize(10);
        }}
        onOk={() => {
          const parent = String(hierarchyParentCode || '').trim();
          const selected = (Array.isArray(hierarchyBatchSelectedKeys) ? hierarchyBatchSelectedKeys : []).map((k) => String(k || '').trim()).filter(Boolean);
          addHierarchyChildrenToParent(parent, selected, 'batch');
          setHierarchyBatchOpen(false);
          setHierarchyBatchSelectedKeys([]);
        }}
        okText="确认关联"
        cancelText="取消"
      >
        <div style={{ marginBottom: 10, display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <span style={{ color: '#595959', fontWeight: 700 }}>{`候选子容器 ${hierarchyBatchCandidates.length}个`}</span>
          <span style={{ color: '#8c8c8c', fontSize: 12 }}>提示：已作废容器不会显示在候选列表中。</span>
          <Input
            allowClear
            autoFocus
            placeholder="输入子容器系统编码（模糊匹配）"
            value={hierarchyBatchQuery}
            onChange={(e) => setHierarchyBatchQuery(String((e as any)?.target?.value || ''))}
            style={{ width: 320, fontFamily: 'monospace' }}
          />
        </div>
        <Table
          size="small"
          rowKey={(r: any) => String(r?.code || '')}
          dataSource={hierarchyBatchCandidates}
          rowSelection={{
            selectedRowKeys: hierarchyBatchSelectedKeys,
            onChange: (keys: React.Key[]) => setHierarchyBatchSelectedKeys(keys),
          }}
          onRow={(record: any) => {
            const key = String(record?.code || '').trim();
            const selected = (hierarchyBatchSelectedKeys || []).some((k) => String(k || '').trim() === key);
            return {
              onClick: (e: any) => {
                if (isRowToggleIgnoredTarget(e)) return;
                if (!key) return;
                setHierarchyBatchSelectedKeys((prev) => {
                  const set = new Set((Array.isArray(prev) ? prev : []).map((k) => String(k || '').trim()).filter(Boolean));
                  if (set.has(key)) set.delete(key);
                  else set.add(key);
                  return Array.from(set);
                });
              },
              style: { cursor: 'pointer', ...(selected ? {} : { background: '#fafafa' }) },
            } as any;
          }}
          pagination={{
            current: hierarchyBatchPage,
            pageSize: hierarchyBatchPageSize,
            total: hierarchyBatchCandidates.length,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'] as any,
            showTotal: (total: number, range: [number, number]) => `共${total}条记录，显示${range[0]}-${range[1]}条`,
            onChange: (p: number, ps: number) => { setHierarchyBatchPage(p); setHierarchyBatchPageSize(ps); },
          }}
          columns={[
            { title: '序号', width: 70, align: 'center' as const, render: (_: any, __: any, idx: number) => ((Math.max(1, Number(hierarchyBatchPage) || 1) - 1) * (Math.max(1, Number(hierarchyBatchPageSize) || 10)) + idx + 1) },
            { title: '子容器系统编码', dataIndex: 'code', width: 220, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
            {
              title: '状态',
              dataIndex: 'status',
              width: 120,
              render: (v: any) => {
                const st = String(v || '').trim();
                if (st === 'open') return <Tag color="blue">进行中</Tag>;
                if (st === 'cancelled') return <Tag>已作废</Tag>;
                return <Tag color="green">已结束</Tag>;
              },
            },
            {
              title: '已关联父容器',
              dataIndex: 'occupiedBy',
              width: 200,
              render: (v: any) => {
                const s = String(v || '').trim();
                return s ? <span style={{ fontFamily: 'monospace', color: '#cf1322' }}>{s}</span> : <span style={{ color: '#8c8c8c' }}>未关联</span>;
              },
            },
          ] as any}
          locale={{ emptyText: '暂无可选子容器' }}
        />
        <div style={{ margin: '24px 0 20px' }}>
          <Divider style={{ margin: 0, borderColor: '#f0f0f0' }} />
        </div>
        <div style={{ marginBottom: 10, display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <span style={{ color: '#595959', fontWeight: 700 }}>{`已关联子容器 ${hierarchyBatchLinkedChildren.length}个`}</span>
          <Input
            allowClear
            placeholder="输入子容器系统编码（模糊匹配）"
            value={hierarchyBatchLinkedQuery}
            onChange={(e) => setHierarchyBatchLinkedQuery(String((e as any)?.target?.value || ''))}
            style={{ width: 320, fontFamily: 'monospace' }}
          />
        </div>
        <Table
          size="small"
          rowKey={(r: any) => String(r?.id || r?.code || '')}
          dataSource={hierarchyBatchLinkedChildren}
          pagination={{
            current: hierarchyBatchLinkedPage,
            pageSize: hierarchyBatchLinkedPageSize,
            total: hierarchyBatchLinkedChildren.length,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'] as any,
            showTotal: (total: number, range: [number, number]) => `共${total}条记录，显示${range[0]}-${range[1]}条`,
            onChange: (p: number, ps: number) => { setHierarchyBatchLinkedPage(p); setHierarchyBatchLinkedPageSize(ps); },
          }}
          columns={[
            { title: '序号', width: 70, align: 'center' as const, render: (_: any, __: any, idx: number) => ((Math.max(1, Number(hierarchyBatchLinkedPage) || 1) - 1) * (Math.max(1, Number(hierarchyBatchLinkedPageSize) || 10)) + idx + 1) },
            { title: '子容器系统编码', dataIndex: 'code', width: 220, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
            {
              title: '状态',
              dataIndex: 'status',
              width: 120,
              render: (v: any) => {
                const st = String(v || '').trim();
                if (st === 'open') return <Tag color="blue">进行中</Tag>;
                if (st === 'cancelled') return <Tag>已作废</Tag>;
                return <Tag color="green">已结束</Tag>;
              },
            },
          ] as any}
          locale={{ emptyText: '暂无已关联子容器' }}
        />
      </Modal>
      <Modal
        title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, paddingRight: 36 }}>
            <span>贴单操作记录</span>
            <Space size={8} wrap>
              {labelingConstraintLoading ? (
                <Tag color="processing">约束加载中</Tag>
              ) : labelingConstraintReady ? (
                <Tag
                  style={{
                    background: '#fff1f0',
                    border: '1px solid #ffccc7',
                    color: '#cf1322',
                    fontSize: 14,
                    fontWeight: 700,
                    lineHeight: '20px',
                    paddingInline: 10,
                  }}
                >
                  {`已启用有效贴单约束（${labelingConstraintMatchableRowCount}条）`}
                </Tag>
              ) : (
                <Tag
                  style={{
                    background: '#f6ffed',
                    border: '1px solid #b7eb8f',
                    color: '#389e0d',
                    fontSize: 14,
                    fontWeight: 700,
                    lineHeight: '20px',
                    paddingInline: 10,
                  }}
                >
                  无贴单约束
                </Tag>
              )}
              {labelingConstraintName ? (
                <Tooltip title={labelingConstraintName}>
                  <span style={{ color: '#8c8c8c', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {labelingConstraintName}
                  </span>
                </Tooltip>
              ) : null}
            </Space>
          </div>
        )}
        open={labelingModalOpen}
        width={980}
        destroyOnClose={false}
        onCancel={closeLabelingModalDirectly}
        footer={(
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Space size={10} wrap>
              <Switch checked={labelingAutoNext} onChange={setLabelingAutoNext} />
              <span style={{ color: '#595959' }}>连续扫码（每组完成后回到“出库对象编码”）</span>
              <Switch checked={labelingWarnBeep} onChange={setLabelingWarnBeep} />
              <span style={{ color: '#595959' }}>错误提示音</span>
            </Space>
            <Space size={8}>
              <Button danger onClick={() => resetLabelingSession()}>重扫</Button>
              <Button onClick={closeLabelingModalDirectly}>直接关闭</Button>
              <Button type="primary" disabled={!canOperateExec || labelingPendingOperationRecords.length <= 0} onClick={commitLabelingSessionRecords}>记录本次贴单</Button>
            </Space>
          </div>
        )}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, alignItems: 'end' }}>
            <div>
              <div style={{ marginBottom: 6, fontWeight: 600 }}>
                {labelingStep === 'target' ? '1) 请扫描出库对象编码' : '1) 出库对象编码（已读取）'}
              </div>
              <Input
                ref={labelingTargetInputRef}
                value={labelingTargetCode}
                onChange={(e) => setLabelingTargetCode(String(e?.target?.value || ''))}
                placeholder="先扫出库对象编码（需以 IOC/LIU/BIU/PIU/NIU/AIU 开头）"
                onPressEnter={() => {
                  const parsed = parseAndValidateLabelingTargetCode(labelingTargetCode);
                  if (!parsed.ok) {
                    handleLabelingTargetInvalid(parsed.reason);
                    return;
                  }
                  setLabelingTargetCode(parsed.code);
                  setLabelingStep('label');
                  setTimeout(() => {
                    try { labelingLabelInputRef.current?.focus?.(); } catch {}
                  }, 0);
                }}
              />
            </div>
            <div>
              <div style={{ marginBottom: 6, fontWeight: 600 }}>
                {labelingStep === 'label' ? '2) 请扫描运输标签编码' : '2) 运输标签编码'}
              </div>
              <Input
                ref={labelingLabelInputRef}
                value={labelingLabelCode}
                onChange={(e) => setLabelingLabelCode(String(e?.target?.value || ''))}
                placeholder="再扫运输标签编码"
                onPressEnter={submitLabelingRecord}
              />
            </div>
            <Space size={8}>
              <Button type="primary" style={{ marginBottom: 1 }} disabled={!canOperateExec} onClick={submitLabelingRecord}>
                执行
              </Button>
              <Button
                style={{ marginBottom: 1 }}
                onClick={() => {
                  setLabelingTargetCode('');
                  setLabelingLabelCode('');
                  setLabelingStep('target');
                  setLabelingLastResult({ ok: true, text: '已清空，等待下一组扫描' });
                }}
              >
                清空
              </Button>
            </Space>
          </div>
          <div style={{ color: labelingLastResult.ok ? '#389e0d' : '#cf1322', background: labelingLastResult.ok ? '#f6ffed' : '#fff1f0', border: `1px solid ${labelingLastResult.ok ? '#b7eb8f' : '#ffa39e'}`, borderRadius: 6, padding: '8px 10px' }}>
            {labelingLastResult.text}
          </div>
          {labelingConstraintReady ? (
            <div style={{ border: '1px solid #d9f7be', background: '#fcffe6', borderRadius: 8, padding: 10 }}>
              {renderReadyBaselineInlinePreview()}
            </div>
          ) : null}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ color: '#595959', fontWeight: 600 }}>{`本次会话记录 ${labelingSessionRows.length} 条（待提交 ${labelingPendingOperationRecords.length} 条）`}</span>
            <span style={{ color: '#8c8c8c', fontSize: 12 }}>{`可匹配对象候选 ${labelingTargetCandidates.length} 个（可直接扫码，不必手工选择）`}</span>
          </div>
          <Table
            size="small"
            rowKey={(r: any) => String(r?.key || '')}
            dataSource={labelingSessionRows}
            pagination={{
              current: labelingSessionPage,
              pageSize: labelingSessionPageSize,
              total: labelingSessionRows.length,
              showSizeChanger: true,
              pageSizeOptions: ['8', '20', '50', '100'] as any,
              showTotal: (total, range) => `共${total}条，显示${range[0]}-${range[1]}条`,
              onChange: (page, pageSize) => {
                setLabelingSessionPage(page);
                if (pageSize !== labelingSessionPageSize) {
                  setLabelingSessionPageSize(pageSize);
                }
              },
            }}
            columns={[
              { title: '时间', dataIndex: 'time', width: 120 },
              { title: '出库对象编码', dataIndex: 'target', width: 220, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
              { title: '运输标签编码', dataIndex: 'label', width: 240, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
              {
                title: '结果',
                dataIndex: 'result',
                width: 90,
                render: (v: any) => (String(v || '').trim() === '成功' ? <Tag color="green">成功</Tag> : <Tag color="red">警告</Tag>),
              },
              { title: '说明', dataIndex: 'remark', ellipsis: true },
            ] as any}
            locale={{ emptyText: '暂无贴单记录，请扫码开始' }}
          />
        </div>
      </Modal>
      <Modal
        open={unableReasonModalOpen}
        title="标注无法操作原因"
        onCancel={() => setUnableReasonModalOpen(false)}
        onOk={submitUnableReasonModal}
        okText="确认标注"
        cancelText="取消"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div style={{ marginBottom: 6 }}>快速选择</div>
            <Radio.Group
              value={unableReasonQuickValue || undefined}
              onChange={(e) => {
                const v = String(e?.target?.value || '');
                setUnableReasonQuickValue(v);
                setUnableReasonInput(v);
              }}
            >
              <Radio.Button value="库存不足">库存不足</Radio.Button>
              <Radio.Button value="指令错误">指令错误</Radio.Button>
            </Radio.Group>
          </div>
          <div>
            <div style={{ marginBottom: 6 }}>原因说明（可编辑）</div>
            <Input.TextArea
              rows={3}
              value={unableReasonInput}
              onChange={(e) => {
                const v = String(e.target.value || '');
                setUnableReasonInput(v);
                if (v !== unableReasonQuickValue) setUnableReasonQuickValue('');
              }}
              placeholder="请输入无法操作原因"
            />
          </div>
        </div>
      </Modal>
      <Modal
        title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingRight: 24 }}>
            <span>{hasIocTarget ? '选择操作目标（指令产出结果）' : '选择操作目标（当前库存）'}</span>
            <Input
              allowClear
              size="small"
              value={targetPickerSearch}
              onChange={(e) => setTargetPickerSearch(String((e as any)?.target?.value || ''))}
              placeholder="筛选：系统编码 / 内容物分拣条码（模糊）"
              style={{ width: 360 }}
            />
          </div>
        )}
        open={targetPickerOpen}
        onCancel={() => {
          setTargetPickerOpen(false);
          setTargetPickerSearch('');
          setTargetPickerPage(1);
          setTargetPickerPageSize(10);
        }}
        footer={(
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center' }}>
            <div />
            <div style={{ justifySelf: 'center', color: '#595959' }}>
              <span
                style={{
                  display: 'inline-block',
                  padding: '2px 12px',
                  borderRadius: 999,
                  border: '1px solid #d9d9d9',
                  background: '#fafafa',
                  color: '#595959',
                  fontSize: 14,
                  fontWeight: 700,
                  lineHeight: '22px',
                }}
              >
                {hasIocTarget
                  ? `已选定:${targetPickerSelectedStats.items}个IOC容器`
                  : `已选定: ${targetPickerSelectedStats.items}项 ${targetPickerSelectedStats.qty}件`}
              </span>
            </div>
            <div style={{ justifySelf: 'end', display: 'inline-flex', gap: 8 }}>
              <Button onClick={() => setTargetPickerOpen(false)}>关闭</Button>
              <Button type="primary" disabled={!hasActivePackContainer || targetPickerSelectedStats.items <= 0} onClick={submitTargetPickerSelection}>加入记录并放入容器</Button>
            </div>
          </div>
        )}
        width="78vw"
        destroyOnClose
      >
        <Table
          size="small"
          rowKey={targetPickerRowKey as any}
          columns={targetPickerColumns as any}
          dataSource={targetPickerFilteredRows}
          loading={hasIocTarget ? iocDisplayLoading : false}
          rowSelection={{
            selectedRowKeys: targetPickerSelectedKeys,
            preserveSelectedRowKeys: true,
            getCheckboxProps: (record: any) => ({
              disabled: isIocRowLocked(record),
            }),
            onSelect: (record: any, selected: boolean) => {
              if (selected && isIocRowLocked(record)) {
                message.warning(IOC_LOCKED_TIP);
                return;
              }
              const key = targetPickerRowKey(record);
              setTargetPickerSelectedKeys((prev) => {
                const set = new Set<React.Key>(prev);
                if (selected) set.add(key);
                else set.delete(key);
                return Array.from(set);
              });
              if (!selected) resetTargetPickerAddQtyForRows([record]);
            },
            onSelectAll: (selected: boolean, _selectedRows: any[], changeRows: any[]) => {
              setTargetPickerSelectedKeys((prev) => {
                const set = new Set<React.Key>(prev);
                (changeRows || []).forEach((row: any) => {
                  if (isIocRowLocked(row)) return;
                  const key = targetPickerRowKey(row);
                  if (selected) set.add(key);
                  else set.delete(key);
                });
                return Array.from(set);
              });
              if (!selected) resetTargetPickerAddQtyForRows(changeRows || []);
            },
          }}
          onRow={(record: any, index?: number) => {
            const key = String(targetPickerRowKey(record, index));
            const selected = targetPickerSelectedKeySet.has(key);
            return {
              onClick: (e: any) => {
                if (isRowToggleIgnoredTarget(e)) return;
                toggleTargetPickerRowSelection(record, index);
              },
              style: selected
                ? { cursor: 'pointer' }
                : { cursor: 'pointer', background: '#fafafa', color: '#bfbfbf', opacity: 0.75 },
            } as any;
          }}
          pagination={{
            current: targetPickerPage,
            pageSize: targetPickerPageSize,
            total: targetPickerFilteredRows.length,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'] as any,
            showTotal: (total: number, range: [number, number]) => `共${total}条记录，显示${range[0]}-${range[1]}条`,
            onChange: (p: number, ps: number) => { setTargetPickerPage(p); setTargetPickerPageSize(ps); },
          }}
          scroll={{ x: hasIocTarget ? 1400 : 980, y: '60vh' as any }}
          locale={{ emptyText: hasIocTarget ? '暂无历史指令执行结果' : '暂无当前可用库存' }}
        />
      </Modal>
      <Modal
        title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, width: '100%', paddingRight: 40 }}>
            <span>容器内所有产品（共{Math.floor(Number(adjustMoveAllPreviewTotal || 0) || 0)}件）</span>
            <span style={{ color: '#8c8c8c', fontSize: 12 }}>确认后将全部移入目标容器</span>
          </div>
        )}
        open={adjustMoveAllPreviewOpen}
        onCancel={() => {
          setAdjustMoveAllPreviewOpen(false);
          setAdjustMoveAll(false);
          setAdjustMoveAllPreviewRows([]);
          setAdjustMoveAllPreviewTotal(0);
        }}
        onOk={async () => {
          setAdjustMoveAllPreviewOpen(false);
          await execAdjustByMode();
          setAdjustMoveAllPreviewRows([]);
          setAdjustMoveAllPreviewTotal(0);
        }}
        okText="确认并执行移动"
        cancelText="取消"
        okButtonProps={{ danger: true }}
        width={860}
      >
        <div style={{ marginBottom: 8, color: '#595959' }}>
          <span style={{ fontWeight: 700 }}>取出容器：</span>
          <span style={{ fontFamily: 'monospace' }}>{String(adjustFromBoxCode || '').trim() || '-'}</span>
          <span style={{ margin: '0 10px' }} />
          <span style={{ fontWeight: 700 }}>移入容器：</span>
          <span style={{ fontFamily: 'monospace' }}>{String(adjustToBoxCode || '').trim() || '-'}</span>
        </div>
        <Table
          size="small"
          rowKey={(r: any, i?: number) => String(`${r?.target_type || '-'}:${r?.target || '-'}:${i || 0}`)}
          dataSource={adjustMoveAllPreviewRows}
          pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] as any }}
          columns={[
            { title: '序号', width: 70, align: 'center' as const, render: (_: any, __: any, idx: number) => idx + 1 },
            {
              title: '操作对象类型',
              dataIndex: 'target_type',
              width: 180,
              render: (v: any) => {
                const t = String(v || '').trim();
                if (t === 'instruction_output_container') return 'IOC容器';
                if (t === 'inventory_unit') return '库存单元';
                return t || '-';
              },
            },
            {
              title: '操作对象编码',
              dataIndex: 'target',
              width: 320,
              render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span>,
            },
            { title: '数量', dataIndex: 'qty', width: 120, render: (v: any) => <span style={{ fontWeight: 800 }}>{Math.floor(Number(v || 0) || 0)}</span> },
          ] as any}
        />
      </Modal>
      <Modal
        title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, width: '100%', paddingRight: 40 }}>
            <span>{rescanFromBoxCode ? `重扫结果比对 · ${rescanFromBoxCode}` : '重扫结果比对'}</span>
            <span style={{ color: '#8c8c8c', fontSize: 12 }}>理论内容物：重扫开始时快照</span>
          </div>
        )}
        open={rescanCompareOpen}
        onCancel={() => setRescanCompareOpen(false)}
        width={920}
        footer={[
          <Button
            key="cleanup"
            danger
            disabled={!rescanStarted}
            onClick={() => {
              Modal.confirm({
                title: '确认一键清理误操作？',
                centered: true,
                okText: '确认清理',
                cancelText: '取消',
                okButtonProps: { danger: true },
                content: '将把“本次重扫产生但未加回原容器”的池内余量作废并清空（追加负记录，不删除历史）。',
                onOk: cleanupRescanPoolRemainder,
              });
            }}
          >
            一键清理误操作
          </Button>,
          <Button
            key="done"
            type="primary"
            onClick={() => {
              setRescanCompareOpen(false);
              resetRescanContext();
            }}
          >
            结束重扫
          </Button>,
          <Button key="close" onClick={() => setRescanCompareOpen(false)}>关闭</Button>,
        ]}
      >
        <div style={{ marginBottom: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#fff7e6', borderColor: '#ffd591', color: '#ad4e00', fontWeight: 800 }}>
            {`重扫前总数${rescanCompareSummary.beforeTotal}件`}
          </Tag>
          <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d', fontWeight: 800 }}>
            {`重扫后总数${rescanCompareSummary.afterTotal}件`}
          </Tag>
          <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#e6f4ff', borderColor: '#91caff', color: '#0958d9', fontWeight: 800 }}>
            {`差异${rescanCompareSummary.diff >= 0 ? `+${rescanCompareSummary.diff}` : String(rescanCompareSummary.diff)}件`}
          </Tag>
        </div>
        <Table
          size="small"
          rowKey={(r: any) => String(r?.key || '')}
          dataSource={rescanCompareRows}
          pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] as any }}
          columns={[
            { title: '序号', width: 70, align: 'center' as const, render: (_: any, __: any, idx: number) => idx + 1 },
            {
              title: '操作对象类型',
              dataIndex: 'target_type',
              width: 160,
              render: (v: any) => {
                const t = String(v || '').trim();
                if (t === 'instruction_output_container') return 'IOC容器';
                if (t === 'inventory_unit') return '库存单元';
                return t || '-';
              },
            },
            { title: '操作对象编码', dataIndex: 'target', width: 300, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
            { title: '重扫前', dataIndex: 'before', width: 110, render: (v: any) => <span style={{ fontWeight: 800 }}>{Math.floor(Number(v || 0) || 0)}</span> },
            { title: '重扫后', dataIndex: 'after', width: 110, render: (v: any) => <span style={{ fontWeight: 800 }}>{Math.floor(Number(v || 0) || 0)}</span> },
            {
              title: '差异',
              dataIndex: 'delta',
              width: 110,
              render: (v: any) => {
                const n = Math.floor(Number(v || 0) || 0);
                if (!n) return <span style={{ color: '#999' }}>0</span>;
                return <span style={{ fontWeight: 900, color: n < 0 ? '#cf1322' : '#389e0d' }}>{n > 0 ? `+${n}` : String(n)}</span>;
              },
            },
          ] as any}
          locale={{ emptyText: '暂无重扫对比数据，请先开始重扫' }}
        />
        <div style={{ marginTop: 8, color: '#8c8c8c', lineHeight: 1.4 }}>
          提示：若“重扫后总数”小于“重扫前总数”，通常表示仍有部分产品留在调整池（可继续加回或一键清理）。
        </div>
      </Modal>
      <Modal
        title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 12, flexWrap: 'wrap', paddingRight: 48 }}>
            <span>选择已有容器</span>
            <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#e6f4ff', borderColor: '#91caff', color: '#0958d9', fontWeight: 800 }}>
              {`已有容器 ${closedContainers.length}`}
            </Tag>
          </div>
        )}
        open={pickExistingOpen}
        onCancel={() => {
          setPickExistingOpen(false);
          setPickExistingCode('');
          setPickExistingQuery('');
          setPickExistingPage(1);
          setPickExistingPageSize(10);
        }}
        onOk={startAddToExistingContainer}
        okText="开始新增"
        cancelText="取消"
        width={720}
        destroyOnClose
      >
        <div style={{ marginBottom: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ color: '#595959', fontWeight: 700 }}>筛选：</span>
          <Input
            allowClear
            autoFocus
            placeholder="扫描/输入容器系统编码（模糊匹配）"
            value={pickExistingQuery}
            onChange={(e) => setPickExistingQuery(String((e as any)?.target?.value || ''))}
            style={{ width: 360, fontFamily: 'monospace' }}
          />
          {pickExistingQuery ? (
            <span style={{ color: '#8c8c8c' }}>{`匹配 ${closedContainersFiltered.length}/${closedContainers.length}`}</span>
          ) : null}
        </div>
        <Table
          size="small"
          rowKey={(r: any) => String(r?.code || '')}
          pagination={{
            current: pickExistingPage,
            pageSize: pickExistingPageSize,
            total: closedContainersFiltered.length,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50'] as any,
            onChange: (p: number, ps: number) => { setPickExistingPage(p); setPickExistingPageSize(ps); },
          }}
          dataSource={closedContainersFiltered}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: pickExistingCode ? [pickExistingCode] : [],
            onChange: (keys: React.Key[]) => {
              const k = String(keys?.[0] || '').trim();
              setPickExistingCode(k);
            },
          }}
          onRow={(record: any) => {
            const code = String(record?.code || '').trim();
            return {
              onClick: (e: any) => {
                if (isRowToggleIgnoredTarget(e)) return;
                if (!code) return;
                setPickExistingCode((prev) => (String(prev || '').trim() === code ? '' : code));
              },
              style: { cursor: 'pointer' },
            } as any;
          }}
          columns={[
            {
              title: '容器系统编码',
              dataIndex: 'code',
              render: (v: any) => {
                const s = String(v || '').trim();
                return (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontFamily: 'monospace' }}>{s || '-'}</span>
                    {s ? (
                      <Button
                        type="link"
                        size="small"
                        style={{ padding: 0, height: 'auto', lineHeight: 1 }}
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await navigator.clipboard.writeText(s);
                            message.success('已复制');
                          } catch {
                            message.error('复制失败');
                          }
                        }}
                      >
                        <CopyOutlined />
                      </Button>
                    ) : null}
                  </span>
                );
              },
            },
            {
              title: '容器类型',
              dataIndex: 'container_type',
              width: 140,
              render: (v: any) => {
                const t = String(v || '').trim().toLowerCase();
                if (t === 'box' || t === 'carton') return <Tag>纸箱</Tag>;
                if (t === 'pallet') return <Tag>托盘</Tag>;
                if (t === 'other') return <Tag>其他</Tag>;
                return <Tag>{t || '-'}</Tag>;
              },
            },
            {
              title: '状态',
              dataIndex: 'status',
              width: 120,
              render: (v: any) => {
                const st = String(v || '').trim();
                if (st === 'closed_pending_final') return <Tag color="gold">待最终核对</Tag>;
                return <Tag color="green">已结束</Tag>;
              },
            },
          ] as any}
          scroll={{ y: 420 }}
          locale={{ emptyText: '暂无可选的已结束容器' }}
        />
      </Modal>
      <Modal
        title={(
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: 12, width: '100%', paddingRight: 36 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <span>结束容器确认</span>
              {endContainerDraft?.code ? (
                <>
                  <span style={{ color: '#d9d9d9' }}>·</span>
                  <span style={{ color: '#fa541c', fontWeight: 800 }}>{String(endContainerDraft.code)}</span>
                </>
              ) : null}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap', justifySelf: 'end' }}>
              <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#fffbe6', borderColor: '#ffe58f', color: '#ad8b00' }}>
                包材类型 {(() => {
                  const t = String(endContainerDraft?.container_type || '').toLowerCase();
                  if (t === 'pallet') return '托盘';
                  if (t === 'box' || t === 'carton') return '纸箱';
                  if (t === 'other') return '其他';
                  return t || '-';
                })()}
              </Tag>
              <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#f9f0ff', borderColor: '#d3adf7', color: '#531dab' }}>
                操作对象种类 {endContainerStats.targetKinds}
              </Tag>
              <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d' }}>
                已装入容器 {endContainerStats.totalQty}件
              </Tag>
              <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#e6fffb', borderColor: '#87e8de', color: '#006d75' }}>
                预估重量 {(() => {
                  const g = Number(endContainerDraft?.weight_g);
                  if (!Number.isFinite(g) || g <= 0) return '-';
                  return `${Math.ceil(g / 1000)}kg`;
                })()}
              </Tag>
            </span>
          </div>
        )}
        open={endContainerOpen}
        onCancel={() => { if (!endContainerSubmitting) setEndContainerOpen(false); }}
        onOk={submitEndCurrentContainer}
        okText="提交并结束容器"
        cancelText="取消"
        confirmLoading={endContainerSubmitting}
        width={980}
        destroyOnClose
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Descriptions size="small" bordered column={2}>
            <Descriptions.Item label="容器系统编码">
              <span style={{ fontFamily: 'monospace' }}>{String(endContainerDraft?.code || '-')}</span>
            </Descriptions.Item>
            <Descriptions.Item label="容器类型">
              {(() => {
                const t = String(endContainerDraft?.container_type || '').toLowerCase();
                if (t === 'pallet') return '托盘';
                if (t === 'box' || t === 'carton') return '纸箱';
                if (t === 'other') return '其他';
                return t || '-';
              })()}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              {(() => {
                const cc = String(endContainerDraft?.code || '').trim();
                const st = String((Array.isArray(boxes) ? boxes : []).find((b: any) => String(b?.code || '').trim() === cc)?.status || 'open').trim();
                if (st === 'cancelled') return '已作废';
                if (st === 'closed') return '已结束';
                return '进行中';
              })()}
            </Descriptions.Item>
            <Descriptions.Item label="统计">{`操作对象种类 ${endContainerStats.targetKinds} | 已装入容器 ${endContainerStats.totalQty}件`}</Descriptions.Item>
            <Descriptions.Item label="开始时间">
              {(() => {
                const cc = String(endContainerDraft?.code || '').trim();
                let startTs = 0;
                (Array.isArray(operationRows) ? operationRows : []).forEach((row: any) => {
                  if (String(row?.container_code || '').trim() !== cc) return;
                  if (String(row?.event || '').trim() !== '开始容器') return;
                  const raw = row?.raw_time ?? row?.time;
                  const ts = Number(new Date(raw).getTime());
                  if (Number.isFinite(ts) && ts > 0) startTs = startTs > 0 ? Math.min(startTs, ts) : ts;
                });
                return startTs ? new Date(startTs).toLocaleString() : '-';
              })()}
            </Descriptions.Item>
            <Descriptions.Item label="结束时间">-</Descriptions.Item>
            <Descriptions.Item label="总耗时">-</Descriptions.Item>
            <Descriptions.Item label="预估重量">
              {(() => {
                const g = Number(endContainerDraft?.weight_g);
                if (!Number.isFinite(g) || g <= 0) return '-';
                return `${Math.ceil(g / 1000)}kg`;
              })()}
            </Descriptions.Item>
          </Descriptions>
          <Divider style={{ margin: '8px 0' }} />
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 420 }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>
                {hasIocTarget ? '容器内容物统计(历史指令产出结果)' : '容器内容物统计(当前可用库存)'}
              </div>
              <Table
                size="small"
                rowKey={(r: any) => r.key}
                dataSource={endContainerContentRows}
                pagination={{
                  current: endContainerContentPage,
                  pageSize: endContainerContentPageSize,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  pageSizeOptions: ['10', '20', '50'] as any,
                  onChange: (p: number, ps: number) => { setEndContainerContentPage(p); setEndContainerContentPageSize(ps); },
                }}
                columns={[
                  { title: '序号', width: 70, align: 'center' as const, render: (_: any, __: any, idx: number) => ((Math.max(1, Number(endContainerContentPage) || 1) - 1) * (Math.max(1, Number(endContainerContentPageSize) || 10)) + idx + 1) },
                  { title: '操作对象', dataIndex: 'target', width: 220, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                  ...(hasIocTarget ? [
                    {
                      title: '内容类型',
                      key: 'content_kind',
                      width: 130,
                      render: (_: any, row: any) => {
                        const code = String(row?.target || '').trim().toUpperCase();
                        const hit = (Array.isArray(iocDisplayRows) ? iocDisplayRows : []).find((x: any) =>
                          String(x?.output_code || x?.display_code || '').trim().toUpperCase() === code,
                        );
                        const kind = String(hit?.output_content_kind || '').toLowerCase();
                        const text = String(hit?.output_content_kind_display || '-');
                        const isException = kind === 'exception';
                        return (
                          <span style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: 4,
                            border: `1px solid ${isException ? '#ffa940' : '#73d13d'}`,
                            background: isException ? '#fff7e6' : '#f6ffed',
                            color: isException ? '#fa8c16' : '#389e0d',
                            lineHeight: 1.5,
                          }}>
                            {text}
                          </span>
                        );
                      },
                    },
                    {
                      title: '容器内容统计',
                      key: 'content_summary',
                      width: 150,
                      render: (_: any, row: any) => {
                        const code = String(row?.target || '').trim().toUpperCase();
                        const hit = (Array.isArray(iocDisplayRows) ? iocDisplayRows : []).find((x: any) =>
                          String(x?.output_code || x?.display_code || '').trim().toUpperCase() === code,
                        );
                        const kind = String(hit?.output_content_kind || '').toLowerCase();
                        const qty = Number(hit?.output_contents_total_qty || 0);
                        if (kind === 'exception') return `${Number.isFinite(qty) ? qty : 0}件`;
                        const raw = String(hit?.output_contents_summary || '').trim();
                        if (raw && raw.includes('款') && raw.includes('件')) return raw;
                        const m = String(hit?.output_contents_detail || '').split(/[；;]/).map((x: any) => String(x || '').trim()).filter(Boolean).length;
                        return `${Math.max(0, m)}款${Math.max(0, qty)}件`;
                      },
                    },
                  ] : [
                    {
                      title: '内容物',
                      key: 'content_lines',
                      width: 280,
                      render: (_: any, row: any) => {
                        const code = String(row?.target || '').trim().toUpperCase();
                        const hit = (Array.isArray(inventoryRows) ? inventoryRows : []).find((x: any) =>
                          String(x?.unit_code || '').trim().toUpperCase() === code,
                        );
                        const lines = Array.isArray(hit?.content_lines) ? hit.content_lines : [];
                        if (lines.length === 0) return '-';
                        const shown = lines.slice(0, 3);
                        const more = lines.length > 3;
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {shown.map((line: string, i: number) => <span key={`${line}-${i}`}>{line}</span>)}
                            {more ? (
                              <Button
                                type="link"
                                size="small"
                                style={{ padding: 0, height: 'auto', width: 'fit-content' }}
                                onClick={() => openContentModal(String(hit?.unit_code || '-'), lines)}
                              >
                                [...]
                              </Button>
                            ) : null}
                          </div>
                        );
                      },
                    },
                  ]),
                  { title: '数量', dataIndex: 'qty', width: 90, align: 'center' as const, render: (v: any) => <span style={{ fontWeight: 700 }}>{Math.floor(Number(v || 0) || 0)}</span> },
                ] as any}
                locale={{ emptyText: '该容器暂无可结束的内容物记录' }}
              />
              {(() => {
                const linkedRows = resolveLinkedChildrenRows(endContainerDraft?.code);
                if (!linkedRows.length) return null;
                return (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontWeight: 700, marginBottom: 8 }}>{`关联子容器统计（${linkedRows.length}）`}</div>
                    <Table
                      size="small"
                      rowKey={(r: any) => String(r?.id || r?.child_code || '')}
                      dataSource={linkedRows}
                      pagination={{
                        current: endContainerLinkedPage,
                        pageSize: endContainerLinkedPageSize,
                        total: linkedRows.length,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        pageSizeOptions: ['5', '10', '20', '50'] as any,
                        onChange: (p: number, ps: number) => {
                          setEndContainerLinkedPage(p);
                          setEndContainerLinkedPageSize(ps);
                        },
                      }}
                      columns={[
                        {
                          title: '序号',
                          width: 70,
                          align: 'center' as const,
                          render: (_: any, __: any, idx: number) => ((Math.max(1, Number(endContainerLinkedPage) || 1) - 1) * (Math.max(1, Number(endContainerLinkedPageSize) || 5)) + idx + 1),
                        },
                        { title: '子容器系统编码', dataIndex: 'child_code', width: 220, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                        {
                          title: '状态',
                          dataIndex: 'child_status',
                          width: 120,
                          render: (v: any) => {
                            const st = String(v || '').trim();
                            if (st === 'open') return <Tag color="blue">进行中</Tag>;
                            if (st === 'cancelled') return <Tag>已作废</Tag>;
                            return <Tag color="green">已结束</Tag>;
                          },
                        },
                        {
                          title: '容器实重(kg)',
                          dataIndex: 'child_weight_kg',
                          width: 120,
                          align: 'center' as const,
                          render: (v: any) => {
                            const n = Number(v);
                            return (Number.isFinite(n) && n > 0) ? <span style={{ fontWeight: 700 }}>{Math.floor(n)}</span> : '-';
                          },
                        },
                      ] as any}
                      locale={{ emptyText: '暂无关联子容器' }}
                    />
                  </div>
                );
              })()}
            </div>
            <div style={{ width: 1, alignSelf: 'stretch', background: '#f0f0f0', margin: '0 4px' }} />
            <div style={{ width: 420, flexShrink: 0 }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>最终尺寸与重量（可编辑）</div>
              <div
                style={{
                  color: '#8c8c8c',
                  marginBottom: 10,
                  background: '#fffbe6',
                  border: '1px solid #ffe58f',
                  borderRadius: 6,
                  padding: '6px 10px',
                }}
              >
                作为父容器的托盘,可先填入预估值,待关联子容器完成后再更新最终数据
              </div>
              {(() => {
                const cmToIn = (cm: number) => cm / 2.54;
                const inToCm = (inch: number) => inch * 2.54;
                const kgToLb = (kg: number) => kg * 2.2046226218;
                const lbToKg = (lb: number) => lb / 2.2046226218;
                const cmInput = (val: any, onCmChange: (cm: number | undefined) => void) => (
                  <InputNumber
                    min={1}
                    precision={0}
                    value={val as any}
                    onChange={(v) => {
                      const n = (v == null) ? undefined : Math.max(1, Math.floor(Number(v) || 0));
                      onCmChange(n);
                    }}
                    style={{ width: 86 }}
                  />
                );
                const inInput = (val: any, onInChange: (inch: number | undefined) => void) => (
                  <InputNumber
                    min={1}
                    precision={0}
                    value={val as any}
                    onChange={(v) => {
                      const n = (v == null) ? undefined : Math.max(1, Math.floor(Number(v) || 0));
                      onInChange(n);
                    }}
                    style={{ width: 86 }}
                  />
                );
                const kgValue = (() => {
                  if (Number.isFinite(Number(endContainerKgDraft)) && Number(endContainerKgDraft) > 0) {
                    return Math.floor(Number(endContainerKgDraft));
                  }
                  const g = Number(endContainerDraft?.weight_g);
                  if (!Number.isFinite(g) || g <= 0) return undefined;
                  return Math.ceil(g / 1000);
                })();
                const lbValue = (() => {
                  if (Number.isFinite(Number(endContainerLbDraft)) && Number(endContainerLbDraft) > 0) {
                    return Math.floor(Number(endContainerLbDraft));
                  }
                  const kg = Number(kgValue);
                  if (!Number.isFinite(kg) || kg <= 0) return undefined;
                  return Math.ceil(kgToLb(kg));
                })();
                const lenIn = (() => {
                  if (Number.isFinite(Number(endContainerLenInDraft)) && Number(endContainerLenInDraft) > 0) {
                    return Math.floor(Number(endContainerLenInDraft));
                  }
                  const cm = Number(endContainerDraft?.length_cm);
                  if (!Number.isFinite(cm) || cm <= 0) return undefined;
                  return Math.ceil(cmToIn(cm));
                })();
                const widIn = (() => {
                  if (Number.isFinite(Number(endContainerWidInDraft)) && Number(endContainerWidInDraft) > 0) {
                    return Math.floor(Number(endContainerWidInDraft));
                  }
                  const cm = Number(endContainerDraft?.width_cm);
                  if (!Number.isFinite(cm) || cm <= 0) return undefined;
                  return Math.ceil(cmToIn(cm));
                })();
                const heiIn = (() => {
                  if (Number.isFinite(Number(endContainerHeiInDraft)) && Number(endContainerHeiInDraft) > 0) {
                    return Math.floor(Number(endContainerHeiInDraft));
                  }
                  const cm = Number(endContainerDraft?.height_cm);
                  if (!Number.isFinite(cm) || cm <= 0) return undefined;
                  return Math.ceil(cmToIn(cm));
                })();
                const setWeightKg = (kg: number | undefined) => {
                  const g = (kg == null) ? undefined : Math.max(1, Math.floor(Number(kg) || 0) * 1000);
                  setEndContainerDraft((prev) => ({ ...(prev || { code: '', container_type: 'box' }), weight_g: g as any }));
                };
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{ color: '#595959', width: 70 }}>长度</div>
                      {cmInput(endContainerDraft?.length_cm as any, (cm) => {
                        setEndContainerDraft((prev) => ({ ...(prev || { code: '', container_type: 'box' }), length_cm: cm as any }));
                        const nextIn = (cm == null) ? undefined : Math.ceil(cmToIn(cm));
                        setEndContainerLenInDraft(nextIn);
                      })}
                      <span style={{ color: '#8c8c8c' }}>厘米</span>
                      <span style={{ color: '#d9d9d9' }}>|</span>
                      {inInput(lenIn as any, (inch) => {
                        setEndContainerLenInDraft(inch);
                        const cm = (inch == null) ? undefined : Math.ceil(inToCm(inch));
                        setEndContainerDraft((prev) => ({ ...(prev || { code: '', container_type: 'box' }), length_cm: cm as any }));
                      })}
                      <span style={{ color: '#8c8c8c' }}>英寸</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{ color: '#595959', width: 70 }}>宽度</div>
                      {cmInput(endContainerDraft?.width_cm as any, (cm) => {
                        setEndContainerDraft((prev) => ({ ...(prev || { code: '', container_type: 'box' }), width_cm: cm as any }));
                        const nextIn = (cm == null) ? undefined : Math.ceil(cmToIn(cm));
                        setEndContainerWidInDraft(nextIn);
                      })}
                      <span style={{ color: '#8c8c8c' }}>厘米</span>
                      <span style={{ color: '#d9d9d9' }}>|</span>
                      {inInput(widIn as any, (inch) => {
                        setEndContainerWidInDraft(inch);
                        const cm = (inch == null) ? undefined : Math.ceil(inToCm(inch));
                        setEndContainerDraft((prev) => ({ ...(prev || { code: '', container_type: 'box' }), width_cm: cm as any }));
                      })}
                      <span style={{ color: '#8c8c8c' }}>英寸</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{ color: '#595959', width: 70 }}>高度</div>
                      {cmInput(endContainerDraft?.height_cm as any, (cm) => {
                        setEndContainerDraft((prev) => ({ ...(prev || { code: '', container_type: 'box' }), height_cm: cm as any }));
                        const nextIn = (cm == null) ? undefined : Math.ceil(cmToIn(cm));
                        setEndContainerHeiInDraft(nextIn);
                      })}
                      <span style={{ color: '#8c8c8c' }}>厘米</span>
                      <span style={{ color: '#d9d9d9' }}>|</span>
                      {inInput(heiIn as any, (inch) => {
                        setEndContainerHeiInDraft(inch);
                        const cm = (inch == null) ? undefined : Math.ceil(inToCm(inch));
                        setEndContainerDraft((prev) => ({ ...(prev || { code: '', container_type: 'box' }), height_cm: cm as any }));
                      })}
                      <span style={{ color: '#8c8c8c' }}>英寸</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{ color: '#595959', width: 70 }}>容器实重</div>
                      <InputNumber
                        min={1}
                        precision={0}
                        value={kgValue as any}
                        onChange={(v) => {
                          const kg = (v == null) ? undefined : Math.max(1, Math.floor(Number(v) || 0));
                          setEndContainerKgDraft(kg);
                          const lb = (kg == null) ? undefined : Math.ceil(kgToLb(kg));
                          setEndContainerLbDraft(lb);
                          setWeightKg(kg);
                        }}
                        style={{ width: 86 }}
                      />
                      <span style={{ color: '#8c8c8c' }}>公斤</span>
                      <span style={{ color: '#d9d9d9' }}>|</span>
                      <InputNumber
                        min={1}
                        precision={0}
                        value={lbValue as any}
                        onChange={(v) => {
                          const lb = (v == null) ? undefined : Math.max(1, Math.floor(Number(v) || 0));
                          const kg = (lb == null) ? undefined : Math.ceil(lbToKg(lb));
                          setEndContainerLbDraft(lb);
                          setEndContainerKgDraft(kg);
                          setWeightKg(kg);
                        }}
                        style={{ width: 86 }}
                      />
                      <span style={{ color: '#8c8c8c' }}>磅</span>
                    </div>
                    <div style={{ marginTop: 10 }}>
                      <div style={{ color: '#595959' }}>
                        {endContainerHasLinkedChildren
                          ? '理论总重量(含父容器本身及关联子容器的总重)'
                          : '理论总重量（与操作页口径一致，仅用于提示）'}
                      </div>
                      <div style={{ marginTop: 6 }}>
                        {endContainerTheory.ok ? (
                          <Tag color="blue" style={{ fontWeight: 700 }}>{`理论总重 ${Number(endContainerTheory.totalKg).toFixed(3)} kg`}</Tag>
                        ) : (
                          <Tag color="default">无法估算理论总重量</Tag>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        title="开始新容器"
        open={startContainerOpen}
        onCancel={() => { if (!startContainerLoading) setStartContainerOpen(false); }}
        onOk={createNewContainerLocal}
        okText="创建新容器"
        cancelText="取消"
        confirmLoading={startContainerLoading}
        width={720}
        destroyOnClose
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ padding: '8px 10px', border: '1px solid #faad14', background: '#fff7e6', color: '#ad6800', borderRadius: 6 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>指令包装要求</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                是否需重新包装：
                {repackRequiredText === '需重新包装'
                  ? renderSoftTag(repackRequiredText, 'red')
                  : repackRequiredText === '无需重新包装'
                    ? renderSoftTag(repackRequiredText, 'green')
                    : repackRequiredText}
              </div>
              <div>
                是否可混装：
                {mixPackText === '可混装'
                  ? renderSoftTag(mixPackText, 'green')
                  : mixPackText === '不可混装'
                    ? renderSoftTag(mixPackText, 'red')
                    : mixPackText}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>包装方式：{packingModeText}</div>
              <div>装箱包材选择：{cartonSourceText}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>最大装箱重量：{cartonMaxWeightText !== '-' ? `${cartonMaxWeightText} KG` : '-'}</div>
              <div>装箱最长边：{cartonMaxLengthText !== '-' ? `${cartonMaxLengthText} CM` : '-'}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>托盘种类：{palletTypeText}</div>
              <div>托盘最大高度：{palletMaxHeightText !== '-' ? `${palletMaxHeightText} CM` : '-'}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>包装需求备注：<span style={{ color: '#cf1322', fontWeight: 400 }}>{repackRequirementNotesText}</span></div>
              <div />
            </div>
          </div>
          <div style={{ padding: '8px 10px', border: '1px solid #91caff', background: '#e6f4ff', color: '#0958d9', borderRadius: 6 }}>
            可手动填写容器信息，或直接扫描发货包材系统编码自动填充。
          </div>
          <Card size="small" title="扫描发货包材（可选）" headStyle={{ background: '#fafafa' }}>
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              <Select
                ref={shippingPkgSelectRef}
                showSearch
                allowClear
                value={shippingPkgQuery || undefined}
                placeholder="扫描/输入包材系统编码或名称关键字（模糊匹配，忽略大小写）"
                filterOption={false}
                onDropdownVisibleChange={(open) => { if (open) void loadDefaultShippingPkgOptions(); }}
                onSearch={(v) => { setShippingPkgQuery(String(v || '')); }}
                onChange={(v) => {
                  if (!v) {
                    setShippingPkgQuery('');
                    setShippingPkgErr('');
                    setShippingPkgHit(null);
                    setShippingPkgOptions([]);
                    lastShipQueryRef.current = '';
                    try { startForm.setFieldsValue({ condition: 'used' }); } catch {}
                    return;
                  }
                  const code = String(v || '').trim();
                  const hit = (shippingPkgOptions || []).find((it) => String(it?.system_code || '').trim() === code);
                  if (hit) fillFromShippingPkg(hit);
                  setShippingPkgQuery(code);
                }}
                loading={shippingPkgLoading}
                notFoundContent={shippingPkgLoading ? '搜索中...' : '无匹配包材'}
                style={{ width: '100%' }}
                options={shippingPkgGroupedOptions as any}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {shippingPkgLoading ? <span style={{ color: '#8c8c8c' }}>识别中...</span> : null}
                {shippingPkgErr ? <span style={{ color: '#cf1322' }}>{shippingPkgErr}</span> : null}
                {shippingPkgHit ? (
                  <Tag style={{ background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d', fontWeight: 600 }}>
                    已识别：{shippingPkgHit.name || '-'}（{shippingPkgHit.system_code}）
                  </Tag>
                ) : null}
                {shippingPkgHit && !['carton', 'pallet', 'other'].includes(String(shippingPkgHit.packaging_type || '')) ? (
                  <Tag color="gold">该包材类型将映射为“其他”容器</Tag>
                ) : null}
              </div>
            </Space>
          </Card>
          <Card size="small" title="容器信息" headStyle={{ background: '#fffbe6' }}>
            <Form form={startForm} layout="vertical" preserve={false}>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="容器类型" name="container_type" rules={[{ required: true, message: '请选择容器类型' }]}>
                    <Select options={[{ label: '纸箱', value: 'box' }, { label: '托盘', value: 'pallet' }, { label: '其他', value: 'other' }]} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Row gutter={12}>
                    <Col span={14}>
                      <Form.Item label="名称">
                        <Input value={String(shippingPkgHit?.name || '-')} readOnly style={{ color: '#000', background: '#fff' }} />
                      </Form.Item>
                    </Col>
                    <Col span={10}>
                      <Form.Item label="新旧" name="condition">
                        <Select options={[{ label: '全新', value: 'new' }, { label: '旧', value: 'used' }]} />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Form.Item label="尺寸(cm)">
                <Row gutter={12}>
                  <Col span={8}>
                    <Form.Item name="length_cm">
                      <InputNumber min={1} precision={0} step={1} style={{ width: '100%' }} placeholder="长" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="width_cm">
                      <InputNumber min={1} precision={0} step={1} style={{ width: '100%' }} placeholder="宽" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="height_cm">
                      <InputNumber min={1} precision={0} step={1} style={{ width: '100%' }} placeholder="高" disabled={String(startForm.getFieldValue('container_type') || '').trim() === 'pallet'} />
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="净重(g)" name="weight_g">
                    <InputNumber min={1} precision={0} step={1} style={{ width: '100%' }} placeholder="例如 850" />
                  </Form.Item>
                </Col>
                <Col span={12} />
              </Row>
            </Form>
          </Card>
        </div>
      </Modal>
      <Modal
        open={printOtherAttachmentsModalOpen}
        title="打印其他附件"
        onCancel={() => setPrintOtherAttachmentsModalOpen(false)}
        footer={null}
        width={760}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {printableAttachmentsWithPrintRequirement.length === 0 ? (
            <div style={{ color: '#8c8c8c' }}>暂无有打印需求的其他附件</div>
          ) : printableAttachmentsWithPrintRequirement.map((a: any) => (
            <div
              key={String(a?.key || '')}
              style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 500, wordBreak: 'break-all' }}>{a.name}</div>
                <div style={{ color: '#cf1322', marginTop: 4, wordBreak: 'break-all' }}>{a.printText || '未配置打印设置'}</div>
              </div>
              <Button
                type="primary"
                size="small"
                onClick={() => { void printOtherAttachmentFromList(a.raw, a.name, a.printText); }}
              >
                预览并下载
              </Button>
            </div>
          ))}
        </div>
      </Modal>
      <Modal
        open={buyWaybillModalOpen}
        title={(
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span>购买并上传运单信息</span>
            <span style={{ color: '#1677ff', fontWeight: 600 }}>{instructionCode || '-'}</span>
            <span style={{ marginLeft: 12 }}>
              偏好承运商: <span style={{ color: '#ff4d4f', fontWeight: 600 }}>{recipientAuxInfo.preferredCarrierText || '无'}</span>
            </span>
          </div>
        )}
        onCancel={() => setBuyWaybillModalOpen(false)}
        onOk={submitBuyWaybillUpload}
        okText="提交并更新指令"
        cancelText="取消"
        confirmLoading={buyWaybillSubmitting}
        width={900}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13, lineHeight: 1.6 }}>
          {buyWaybillPackagingRows.length > 0 ? (
            <div style={{ border: '1px solid #fa8c16', borderRadius: 6, padding: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ fontWeight: 600, color: '#d46b08' }}>包装数据</div>
                <Button size="small" onClick={copyBuyWaybillPackagingInfo}>复制信息</Button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {buyWaybillPackagingRows.map((r: any) => (
                  <div key={String(r?.key || '')} style={{ display: 'grid', gridTemplateColumns: '220px 90px 1fr 1fr', gap: 10 }}>
                    <div style={{ fontFamily: 'monospace' }}>{String(r?.container_code || '-')}</div>
                    <div>
                      {String(r?.hierarchyLevel || '-') === '父' ? (
                        <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#e6f4ff', borderColor: '#91caff', color: '#0958d9', fontWeight: 700 }}>
                          父
                        </Tag>
                      ) : String(r?.hierarchyLevel || '-') === '子' ? (
                        <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#fff1f0', borderColor: '#ffa39e', color: '#cf1322', fontWeight: 700 }}>
                          子
                        </Tag>
                      ) : (
                        <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d', fontWeight: 700 }}>
                          独立
                        </Tag>
                      )}
                    </div>
                    <div>尺寸：{`${String(r?.dimsMetric || '-')} | ${String(r?.dimsImperial || '-')}`}</div>
                    <div>重量：{`${String(r?.weightMetric || '-')} | ${String(r?.weightImperial || '-')}`}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          <div style={{ border: '1px solid #52c41a', borderRadius: 6, padding: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <div style={{ fontWeight: 600, color: '#389e0d' }}>收件人完整信息</div>
              <Button size="small" onClick={copyRecipientAuxInfo}>复制信息</Button>
            </div>
            <div>{recipientAuxInfo.line1}</div>
            <div>{recipientAuxInfo.line2}</div>
            <div>{recipientAuxInfo.line3}</div>
            <div>{recipientAuxInfo.line4}</div>
            <div>{recipientAuxInfo.line5}</div>
          </div>
          <div style={{ border: '1px solid #1677ff', borderRadius: 6, padding: 12 }}>
            <div style={{ fontWeight: 600, color: '#1677ff', marginBottom: 6 }}>辅助信息</div>
            <div>偏好承运商：{recipientAuxInfo.preferredCarrierText || '-'}</div>
            <div>
              保险信息：
              {recipientAuxInfo.insuranceRequired === true ? (
                <span style={{ marginLeft: 6, padding: '1px 8px', borderRadius: 4, background: '#ff4d4f', color: '#fff', fontWeight: 600 }}>
                  {recipientAuxInfo.insuranceInfo}
                </span>
              ) : (
                <span>{recipientAuxInfo.insuranceInfo}</span>
              )}
            </div>
            <div>
              签名需求：
              {recipientAuxInfo.signatureRequired === true ? (
                <span style={{ marginLeft: 6, padding: '1px 8px', borderRadius: 4, background: '#ff4d4f', color: '#fff', fontWeight: 600 }}>
                  {recipientAuxInfo.signatureInfo}
                </span>
              ) : (
                <span>{recipientAuxInfo.signatureInfo}</span>
              )}
            </div>
            <div>
              是否需要升降尾板服务：
              {recipientAuxInfo.liftgateRequired === true ? (
                <span style={{ marginLeft: 6, padding: '1px 8px', borderRadius: 4, background: '#ff4d4f', color: '#fff', fontWeight: 600 }}>
                  {recipientAuxInfo.liftgateInfo}
                </span>
              ) : (
                <span>{recipientAuxInfo.liftgateInfo}</span>
              )}
            </div>
            <div>
              送货是否需提前预约：
              {recipientAuxInfo.appointmentRequired === true ? (
                <span style={{ marginLeft: 6, padding: '1px 8px', borderRadius: 4, background: '#ff4d4f', color: '#fff', fontWeight: 600 }}>
                  {recipientAuxInfo.appointmentInfo}
                </span>
              ) : (
                <span>{recipientAuxInfo.appointmentInfo}</span>
              )}
            </div>
            <div>
              派送车辆是否受限：
              {recipientAuxInfo.limitedAccessDelivery === true ? (
                <span style={{ marginLeft: 6, padding: '1px 8px', borderRadius: 4, background: '#ff4d4f', color: '#fff', fontWeight: 600 }}>
                  {recipientAuxInfo.limitedAccessInfo}
                </span>
              ) : (
                <span>{recipientAuxInfo.limitedAccessInfo}</span>
              )}
            </div>
            <div>参考号：{recipientAuxInfo.referenceNumberText || '-'}</div>
            <div>指令备注：{recipientAuxInfo.instructionRemarksText || '-'}</div>
          </div>
          <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, padding: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 10 }}>上传运输标签信息和附件</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <div style={{ marginBottom: 6 }}>运单所属承运商</div>
                <Select
                  value={buyWaybillCarrier || undefined}
                  onChange={(v) => setBuyWaybillCarrier(String(v || ''))}
                  placeholder="请选择承运商"
                  options={buyWaybillProviderOptions}
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="label"
                  allowClear
                />
              </div>
              <div>
                <div style={{ marginBottom: 6 }}>物流跟踪号/BOL提单号码/PRO号码</div>
                <Input
                  value={buyWaybillTrackingNo}
                  onChange={(e) => setBuyWaybillTrackingNo(String(e.target.value || ''))}
                  placeholder="请输入物流跟踪号/BOL提单号码/PRO号码"
                />
              </div>
              <div>
                <div style={{ marginBottom: 6 }}>运单费用金额</div>
                <InputNumber
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  value={buyWaybillChargeAmount ?? undefined}
                  onChange={(v) => setBuyWaybillChargeAmount(v == null ? null : Number(v))}
                  placeholder="可为0，表示仓库免费提供"
                />
              </div>
              <div>
                <div style={{ marginBottom: 6 }}>币种</div>
                <Input
                  value={buyWaybillChargeCurrency}
                  onChange={(e) => setBuyWaybillChargeCurrency(String(e.target.value || '').toUpperCase())}
                  placeholder="USD"
                />
              </div>
            </div>
            <div style={{ marginTop: 10 }}>
              <div style={{ marginBottom: 6 }}>费用备注</div>
              <Input.TextArea
                rows={2}
                value={buyWaybillChargeNote}
                onChange={(e) => setBuyWaybillChargeNote(String(e.target.value || ''))}
                placeholder="可选"
              />
            </div>
            <div style={{ marginTop: 10 }}>
              <div style={{ marginBottom: 6 }}>运输标签附件</div>
              {waybillAttachments.length > 0 ? (
                <div style={{ marginBottom: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <span style={{ color: '#666' }}>当前运输标签文件:</span>
                  {waybillAttachments.map((att: any, idx: number) => {
                    const fileName = String(att?.file_name || att?.name || att?.file || att?.file_path || `运单附件${idx + 1}`);
                    return (
                      <span key={`waybill-file-${idx}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span>{fileName}</span>
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0, height: 'auto' }}
                          onClick={() => { void openAttachmentPreview(att, fileName, ''); }}
                        >
                          查看
                        </Button>
                      </span>
                    );
                  })}
                </div>
              ) : null}
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap' }}>
                <Upload
                  beforeUpload={() => false}
                  multiple
                  showUploadList={false}
                  fileList={buyWaybillFileList}
                  onChange={(info) => setBuyWaybillFileList(info.fileList || [])}
                  accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls"
                >
                  <Button>选择文件</Button>
                </Upload>
                <span style={{ fontSize: 12, color: '#fa8c16', whiteSpace: 'nowrap' }}>
                  将移除旧的附件并上传新附件,支持多选
                </span>
              </div>
              {buyWaybillFileList.length > 0 ? (
                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {buyWaybillFileList.map((file: any, idx: number) => {
                    const name = String(file?.name || `运单附件${idx + 1}`);
                    return (
                      <span key={String(file?.uid || `upload-file-${idx}`)} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ color: '#1677ff', cursor: 'pointer' }} onClick={() => { void previewBuyWaybillUploadFile(file); }}>
                          {name}
                        </span>
                        <Button type="link" size="small" style={{ padding: 0, height: 'auto' }} onClick={() => { void previewBuyWaybillUploadFile(file); }}>
                          查看
                        </Button>
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0, height: 'auto' }}
                          onClick={() => {
                            setBuyWaybillFileList((prev) => (Array.isArray(prev) ? prev.filter((x: any) => String(x?.uid || '') !== String(file?.uid || '')) : []));
                          }}
                        >
                          移除
                        </Button>
                      </span>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        title={(
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, paddingRight: 36 }}>
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{previewTitle}</span>
            <span
              style={{
                color: '#fa541c',
                fontSize: 14,
                fontWeight: 600,
                marginRight: 16,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {previewPrintText || '未配置打印需求'}
            </span>
          </div>
        )}
        open={previewOpen}
        onCancel={closePreview}
        footer={(
          <Space>
            <Button onClick={closePreview}>关闭</Button>
            <Button type="primary" onClick={() => { void downloadPreview(); }}>下载</Button>
          </Space>
        )}
        width="75vw"
        destroyOnClose
      >
        {previewLoading ? (
          <div style={{ color: '#8c8c8c', marginBottom: 8 }}>附件加载中...</div>
        ) : null}
        <div style={{ height: '70vh', border: '1px solid #f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
          {previewType === 'table' ? (
            <Table
              size="small"
              rowKey={(r: any) => String(r?.__k ?? '')}
              columns={previewTableCols as any}
              dataSource={previewTableData}
              pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] as any }}
              scroll={{ x: 'max-content', y: 'calc(70vh - 56px)' as any }}
              locale={{ emptyText: '表格无数据' }}
            />
          ) : previewUrl ? (
            previewType === 'image' ? (
              <img src={previewUrl} alt={previewTitle} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            ) : (
              <iframe ref={previewFrameRef} title="附件预览" src={previewUrl} style={{ width: '100%', height: '100%', border: 'none' }} />
            )
          ) : (
            <div style={{ padding: 12, color: '#8c8c8c' }}>附件预览地址不可用</div>
          )}
        </div>
      </Modal>
      <Modal
        open={contentModalOpen}
        title={(
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingRight: 36 }}>
            <span style={{ fontWeight: 600, flexShrink: 0 }}>库存单元内容物明细</span>
            <span style={{ color: '#1677ff', whiteSpace: 'nowrap', flex: 1, textAlign: 'center' }}>
              {contentModalUnitCode || '-'}
            </span>
            <Button size="small" onClick={() => { void copyContentModalInfo(); }} style={{ flexShrink: 0 }}>
              复制
            </Button>
          </div>
        )}
        onCancel={() => setContentModalOpen(false)}
        footer={<Button onClick={() => setContentModalOpen(false)}>关闭</Button>}
        width={760}
        destroyOnClose
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '60vh', overflowY: 'auto' }}>
          {contentModalLines.length === 0 ? (
            <div style={{ color: '#8c8c8c' }}>暂无内容物明细</div>
          ) : contentModalLines.map((line, idx) => (
            <div key={`${line}-${idx}`} style={{ display: 'flex', gap: 8 }}>
              <span style={{ width: 28, color: '#8c8c8c' }}>{idx + 1}.</span>
              <span style={{ flex: 1 }}>{line}</span>
            </div>
          ))}
        </div>
      </Modal>
      <Modal
        open={targetDetailOpen}
        title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingRight: 36 }}>
            <span>{targetDetailTitle || '记录明细'}</span>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, lineHeight: '18px', flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #91caff', background: '#e6f4ff', color: '#0958d9', borderRadius: 4, fontWeight: 700 }}>
                涉及容器 {targetDetailHeaderStats.boxCount}
              </span>
              <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #b7eb8f', background: '#f6ffed', color: '#389e0d', borderRadius: 4, fontWeight: 700 }}>
                已装入容器 {targetDetailHeaderStats.totalQty}件
              </span>
              <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #d9d9d9', background: '#fafafa', color: '#595959', borderRadius: 4, fontWeight: 700 }}>
                记录数 {targetDetailHeaderStats.recordCount}
              </span>
            </div>
          </div>
        )}
        onCancel={() => { setTargetDetailOpen(false); setTargetDetailCode(''); setTargetDetailTitle(''); }}
        footer={<Button onClick={() => { setTargetDetailOpen(false); setTargetDetailCode(''); setTargetDetailTitle(''); }}>关闭</Button>}
        width={980}
        destroyOnClose
      >
        <Table
          size="small"
          rowKey={(r: any) => String(r?.key || r?.raw_id || `${r?.index || 0}`)}
          dataSource={targetDetailRows}
          pagination={{
            current: targetDetailPage,
            pageSize: targetDetailPageSize,
            total: targetDetailRows.length,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'] as any,
            showTotal: (total: number, range: [number, number]) => `共${total}条记录，显示${range[0]}-${range[1]}条`,
            onChange: (p: number, ps: number) => {
              setTargetDetailPage(p);
              setTargetDetailPageSize(ps);
            },
          }}
          columns={[
            { title: '序号', width: 72, align: 'center' as const, render: (_: any, __: any, idx: number) => ((Math.max(1, Number(targetDetailPage) || 1) - 1) * (Math.max(1, Number(targetDetailPageSize) || 10)) + idx + 1) },
            { title: '事件', dataIndex: 'event', width: 160, render: renderOperationEventTag },
            { title: '容器系统编码', dataIndex: 'container_code', width: 220 },
            { title: '操作对象', dataIndex: 'target', width: 220 },
            { title: '数量', dataIndex: 'qty', width: 100, align: 'center' as const },
            {
              title: '时间',
              dataIndex: 'time',
              width: 180,
              render: (_: any, row: any) => {
                const raw = row?.raw_time ?? row?.time;
                const d = new Date(raw);
                if (Number.isNaN(d.getTime())) return normalizeText(row?.time);
                return d.toLocaleString();
              },
            },
            {
              title: '操作',
              key: 'ops',
              width: 120,
              render: (_: any, row: any) => {
                const canDel = canDeleteOperationRow(row);
                if (!canDel) return <span style={{ color: '#999' }}>-</span>;
                return (
                  <Button
                    size="small"
                    danger
                    style={{ background: '#fff', color: '#ff4d4f', borderColor: '#ff4d4f' }}
                    onClick={() => { deleteOperationRowById(row); }}
                  >
                    删除
                  </Button>
                );
              },
            },
          ] as any}
          scroll={{ x: 1040 }}
          locale={{ emptyText: '暂无记录' }}
        />
      </Modal>
      <Modal
        open={targetBoxOpen}
        title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingRight: 36 }}>
            <span>{targetBoxTitle || '装箱汇总'}</span>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, lineHeight: '18px', flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #91caff', background: '#e6f4ff', color: '#0958d9', borderRadius: 4, fontWeight: 700 }}>
                涉及容器 {targetBoxStats.boxCount}
              </span>
              <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #b7eb8f', background: '#f6ffed', color: '#389e0d', borderRadius: 4, fontWeight: 700 }}>
                已装入容器 {targetBoxStats.totalQty}件
              </span>
            </div>
          </div>
        )}
        onCancel={() => { setTargetBoxOpen(false); setTargetBoxRows([]); setTargetBoxTitle(''); }}
        footer={<Button onClick={() => { setTargetBoxOpen(false); setTargetBoxRows([]); setTargetBoxTitle(''); }}>关闭</Button>}
        width={900}
        destroyOnClose
      >
        <Table
          size="small"
          rowKey={(r: any, i?: number) => String(r?.container_code || i || 0)}
          dataSource={targetBoxRows}
          pagination={{
            current: targetBoxPage,
            pageSize: targetBoxPageSize,
            total: targetBoxRows.length,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'] as any,
            showTotal: (total: number, range: [number, number]) => `共${total}条记录，显示${range[0]}-${range[1]}条`,
            onChange: (p: number, ps: number) => { setTargetBoxPage(p); setTargetBoxPageSize(ps); },
          }}
          columns={[
            { title: '序号', width: 70, align: 'center' as const, render: (_: any, __: any, idx: number) => ((Math.max(1, Number(targetBoxPage) || 1) - 1) * (Math.max(1, Number(targetBoxPageSize) || 10)) + idx + 1) },
            {
              title: '容器系统编码',
              dataIndex: 'container_code',
              width: 260,
              render: (v: any) => {
                const code = String(v || '').trim();
                if (!code || code === '-') return '-';
                return (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontFamily: 'monospace' }}>{code}</span>
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0, height: 'auto', lineHeight: 1 }}
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await navigator.clipboard.writeText(code);
                          message.success('已复制');
                        } catch {
                          message.error('复制失败');
                        }
                      }}
                    >
                      <CopyOutlined />
                    </Button>
                  </span>
                );
              },
            },
            { title: '数量合计', dataIndex: 'qty', width: 120, render: (v: any) => <span style={{ fontWeight: 700 }}>{Math.floor(Number(v || 0) || 0)}</span> },
            { title: '记录条数', dataIndex: 'cnt', width: 110, render: (v: any) => <span style={{ color: '#595959' }}>{Math.floor(Number(v || 0) || 0)}</span> },
            { title: '最近时间', dataIndex: 'lastTs', width: 180, render: (v: any) => (v ? new Date(Number(v)).toLocaleString() : '-') },
            {
              title: '操作',
              key: 'ops',
              width: 120,
              render: (_: any, row: any) => (
                <Button
                  size="small"
                  onClick={() => {
                    const cc = String(row?.container_code || '').trim();
                    if (!cc) return;
                    setContainerDetailCode(cc);
                    setContainerDetailOpen(true);
                  }}
                >
                  查看明细
                </Button>
              ),
            },
          ] as any}
          scroll={{ x: 980 }}
          locale={{ emptyText: '暂无装箱汇总数据' }}
        />
      </Modal>
      <Modal
        open={containerDetailOpen}
        title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingRight: 36 }}>
            <span>容器 {containerDetailCode || '-'} 记录明细</span>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, lineHeight: '18px' }}>
              <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #91caff', background: '#e6f4ff', color: '#0958d9', borderRadius: 4, fontWeight: 700 }}>
                记录数 {containerDetailHeaderStats.recordCount}
              </span>
              {(String(containerDetailCode || '').trim() === String(adjustPoolCode || '').trim()) ? (
                <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #b7eb8f', background: '#f6ffed', color: '#389e0d', borderRadius: 4, fontWeight: 700 }}>
                  调整池内可用数量 {Math.floor(Number(adjustPoolSummary?.totalPos || 0) || 0)}件
                </span>
              ) : null}
              <span
                style={{
                  display: 'inline-block',
                  padding: '1px 6px',
                  border: '1px solid #87e8de',
                  background: '#e6fffb',
                  color: '#006d75',
                  borderRadius: 4,
                  fontWeight: 700,
                }}
              >
                直接装载 {containerDetailQtyStats.directQty}件
              </span>
              {containerDetailQtyStats.hasChildren ? (
                <span
                  style={{
                    display: 'inline-block',
                    padding: '1px 6px',
                    borderRadius: 4,
                    border: (containerDetailQtyStats.aggregatedQty > containerDetailQtyStats.directQty)
                      ? '1px solid #91caff'
                      : '1px solid #d9d9d9',
                    background: (containerDetailQtyStats.aggregatedQty > containerDetailQtyStats.directQty)
                      ? '#e6f4ff'
                      : '#fafafa',
                    color: (containerDetailQtyStats.aggregatedQty > containerDetailQtyStats.directQty)
                      ? '#0958d9'
                      : '#595959',
                    fontWeight: 700,
                  }}
                  title={`直接装载:${containerDetailQtyStats.directQty}，含子容器汇总:${containerDetailQtyStats.aggregatedQty}`}
                >
                  含子容器汇总 {containerDetailQtyStats.aggregatedQty}件
                </span>
              ) : null}
            </div>
          </div>
        )}
        onCancel={() => setContainerDetailOpen(false)}
        footer={<Button onClick={() => setContainerDetailOpen(false)}>关闭</Button>}
        width={1100}
        destroyOnClose
      >
        <Table
          size="small"
          rowKey={(r: any) => String(r?.key || r?.raw_id || `${r?.index || 0}`)}
          dataSource={containerDetailRows}
          pagination={{
            current: containerDetailPage,
            pageSize: containerDetailPageSize,
            total: containerDetailRows.length,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'] as any,
            showTotal: (total: number, range: [number, number]) => `共${total}条记录，显示${range[0]}-${range[1]}条`,
            onChange: (p: number, ps: number) => {
              setContainerDetailPage(p);
              setContainerDetailPageSize(ps);
            },
          }}
          columns={[
            {
              title: '序号',
              width: 72,
              align: 'center' as const,
              render: (_: any, __: any, idx: number) => ((Math.max(1, Number(containerDetailPage) || 1) - 1) * (Math.max(1, Number(containerDetailPageSize) || 10)) + idx + 1),
            },
            { title: '事件', dataIndex: 'event', width: 160, render: renderOperationEventTag },
            { title: '容器系统编码', dataIndex: 'container_code', width: 220 },
            { title: '操作对象', dataIndex: 'target', width: 220 },
            { title: '数量', dataIndex: 'qty', width: 100, align: 'center' as const },
            {
              title: '时间',
              dataIndex: 'time',
              width: 180,
              render: (_: any, row: any) => {
                const raw = row?.raw_time ?? row?.time;
                const d = new Date(raw);
                if (Number.isNaN(d.getTime())) return normalizeText(row?.time);
                return d.toLocaleString();
              },
            },
            {
              title: '操作',
              key: 'ops',
              width: 120,
              render: (_: any, row: any) => {
                const canDel = canDeleteOperationRow(row);
                if (!canDel) return <span style={{ color: '#999' }}>-</span>;
                return (
                  <Button
                    size="small"
                    danger
                    style={{ background: '#fff', color: '#ff4d4f', borderColor: '#ff4d4f' }}
                    onClick={() => { deleteOperationRowById(row); }}
                  >
                    删除
                  </Button>
                );
              },
            },
          ] as any}
          scroll={{ x: 1080 }}
          locale={{ emptyText: '暂无记录' }}
        />
      </Modal>
      <Modal
        open={boxFinalOpen}
        title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10, width: '100%', flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span>最终包装数据</span>
              {boxFinalData?.code ? (
                <>
                  <span style={{ color: '#d9d9d9' }}>·</span>
                  <span style={{ color: '#fa541c', fontWeight: 800 }}>{boxFinalData.code}</span>
                </>
              ) : null}
              <span style={{ color: '#8c8c8c' }}>
                指令: <span style={{ color: '#1677ff', fontWeight: 800 }}>{String(instructionCode || '-')}</span>
              </span>
            </span>
            <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#e6fffb', borderColor: '#87e8de', color: '#006d75' }}>
              <span>直接装载{Number(boxFinalData?.directQty || 0)}件</span>
            </Tag>
            {boxFinalData?.hasChildren ? (
              <Tag
                style={{
                  marginInlineEnd: 0,
                  borderRadius: 999,
                  background: Number(boxFinalData?.aggregatedQty || 0) > Number(boxFinalData?.directQty || 0) ? '#e6f4ff' : '#fafafa',
                  borderColor: Number(boxFinalData?.aggregatedQty || 0) > Number(boxFinalData?.directQty || 0) ? '#91caff' : '#d9d9d9',
                  color: Number(boxFinalData?.aggregatedQty || 0) > Number(boxFinalData?.directQty || 0) ? '#0958d9' : '#595959',
                }}
              >
                <span>含子容器汇总{Number(boxFinalData?.aggregatedQty || 0)}件</span>
              </Tag>
            ) : null}
            <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#f9f0ff', borderColor: '#d3adf7', color: '#531dab' }}>
              <span>操作对象种类{Number(boxFinalData?.objectKinds || 0)}</span>
            </Tag>
          </div>
        )}
        onCancel={closeBoxFinalModal}
        footer={(
          <Button
            onClick={closeBoxFinalModal}
          >
            {boxFinalDirty ? '保存并关闭' : '关闭'}
          </Button>
        )}
        width={980}
        destroyOnClose
      >
        {boxFinalData ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Descriptions size="small" bordered column={2}>
              <Descriptions.Item label="容器系统编码">
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontFamily: 'monospace' }}>{boxFinalData.code || '-'}</span>
                  {boxFinalData.code ? (
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0, height: 'auto', lineHeight: 1 }}
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(String(boxFinalData.code || ''));
                          message.success('已复制');
                        } catch {
                          message.error('复制失败');
                        }
                      }}
                    >
                      <CopyOutlined />
                    </Button>
                  ) : null}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="容器类型">{boxFinalData.containerTypeText || '-'}</Descriptions.Item>
              <Descriptions.Item label="状态">{boxFinalData.statusText || '-'}</Descriptions.Item>
              <Descriptions.Item label="记录数">{boxFinalData.recordCount}</Descriptions.Item>
              <Descriptions.Item label="开始时间">{boxFinalData.startTs ? new Date(Number(boxFinalData.startTs)).toLocaleString() : '-'}</Descriptions.Item>
              <Descriptions.Item label="结束时间">{boxFinalData.endTs ? new Date(Number(boxFinalData.endTs)).toLocaleString() : '-'}</Descriptions.Item>
              <Descriptions.Item label="总耗时">
                {boxFinalData.durationHours > 0 ? `${Number(boxFinalData.durationHours).toFixed(1)}小时` : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="统计">{`操作对象种类 ${boxFinalData.objectKinds} | 直接装载 ${boxFinalData.directQty}${boxFinalData.hasChildren ? ` | 含子容器汇总 ${boxFinalData.aggregatedQty}` : ''}`}</Descriptions.Item>
            </Descriptions>
            <Divider style={{ margin: '8px 0' }} />
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 420 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>容器内容物统计</div>
                <Table
                  size="small"
                  rowKey={(r: any) => String(r?.target || '')}
                  dataSource={boxFinalData.detailRows}
                  tableLayout="fixed"
                  pagination={{
                    current: boxFinalContentPage,
                    pageSize: boxFinalContentPageSize,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: ['10', '20', '50'] as any,
                    onChange: (p: number, ps: number) => {
                      setBoxFinalContentPage(p);
                      setBoxFinalContentPageSize(ps);
                    },
                  }}
                  columns={[
                    {
                      title: '序号',
                      width: 72,
                      align: 'center' as const,
                      render: (_: any, __: any, idx: number) => ((Math.max(1, Number(boxFinalContentPage) || 1) - 1) * (Math.max(1, Number(boxFinalContentPageSize) || 10)) + idx + 1),
                    },
                    { title: '操作对象', dataIndex: 'target', width: 96, ellipsis: true },
                    {
                      title: '内容物',
                      key: 'content_lines',
                      width: 280,
                      render: (_: any, row: any) => {
                        const lines = Array.isArray(row?.content_lines) ? row.content_lines : [];
                        if (lines.length === 0) return '-';
                        const shown = lines.slice(0, 3);
                        const more = lines.length > 3;
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {shown.map((line: string, i: number) => <span key={`${line}-${i}`}>{line}</span>)}
                            {more ? (
                              <Button
                                type="link"
                                size="small"
                                style={{ padding: 0, height: 'auto', width: 'fit-content' }}
                                onClick={() => openContentModal(String(row?.target || '-'), lines)}
                              >
                                [...]
                              </Button>
                            ) : null}
                          </div>
                        );
                      },
                    },
                    { title: '数量', dataIndex: 'qty', width: 48, align: 'center' as const, ellipsis: true, render: (v: any) => <span style={{ fontWeight: 700 }}>{Math.floor(Number(v || 0) || 0)}</span> },
                  ] as any}
                  scroll={{ x: 760 }}
                  locale={{ emptyText: '暂无内容明细' }}
                />
                {(() => {
                  const linkedRows = resolveLinkedChildrenRows(boxFinalData.code);
                  if (!linkedRows.length) return null;
                  return (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontWeight: 700, marginBottom: 8 }}>{`关联子容器统计（${linkedRows.length}）`}</div>
                      <Table
                        size="small"
                        rowKey={(r: any) => String(r?.id || r?.child_code || '')}
                        dataSource={linkedRows}
                        pagination={{
                          current: boxFinalLinkedPage,
                          pageSize: boxFinalLinkedPageSize,
                          total: linkedRows.length,
                          showSizeChanger: true,
                          showQuickJumper: true,
                          pageSizeOptions: ['5', '10', '20', '50'] as any,
                          onChange: (p: number, ps: number) => {
                            setBoxFinalLinkedPage(p);
                            setBoxFinalLinkedPageSize(ps);
                          },
                        }}
                        columns={[
                          {
                            title: '序号',
                            width: 70,
                            align: 'center' as const,
                            render: (_: any, __: any, idx: number) => ((Math.max(1, Number(boxFinalLinkedPage) || 1) - 1) * (Math.max(1, Number(boxFinalLinkedPageSize) || 5)) + idx + 1),
                          },
                          { title: '子容器系统编码', dataIndex: 'child_code', width: 220, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                          {
                            title: '状态',
                            dataIndex: 'child_status',
                            width: 120,
                            render: (v: any) => {
                              const st = String(v || '').trim();
                              if (st === 'open') return <Tag color="blue">进行中</Tag>;
                              if (st === 'cancelled') return <Tag>已作废</Tag>;
                              return <Tag color="green">已结束</Tag>;
                            },
                          },
                          {
                            title: '容器实重(kg)',
                            dataIndex: 'child_weight_kg',
                            width: 120,
                            align: 'center' as const,
                            render: (v: any) => {
                              const n = Number(v);
                              return (Number.isFinite(n) && n > 0) ? <span style={{ fontWeight: 700 }}>{Math.floor(n)}</span> : '-';
                            },
                          },
                        ] as any}
                        locale={{ emptyText: '暂无关联子容器' }}
                      />
                    </div>
                  );
                })()}
              </div>
              <div
                style={{
                  width: 1,
                  alignSelf: 'stretch',
                  background: '#f0f0f0',
                  margin: '0 4px',
                }}
              />
              <div style={{ width: 420, flexShrink: 0 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>{canOperateExec ? '最终尺寸与重量（可编辑）' : '最终尺寸与重量（只读）'}</div>
                {(() => {
                  const readonly = !canOperateExec;
                  const cmToIn = (cm: number) => cm / 2.54;
                  const inToCm = (inch: number) => inch * 2.54;
                  const kgToLb = (kg: number) => kg * 2.2046226218;
                  const lbToKg = (lb: number) => lb / 2.2046226218;
                  const cmInput = (val: any, onCmChange: (cm: number | undefined) => void) => (
                    <InputNumber
                      min={1}
                      precision={0}
                      value={val as any}
                      disabled={readonly}
                      onChange={(v) => {
                        const n = (v == null) ? undefined : Math.max(1, Math.floor(Number(v) || 0));
                        onCmChange(n);
                      }}
                      style={{ width: 86 }}
                    />
                  );
                  const inInput = (val: any, onInChange: (inch: number | undefined) => void) => (
                    <InputNumber
                      min={1}
                      precision={0}
                      value={val as any}
                      disabled={readonly}
                      onChange={(v) => {
                        const n = (v == null) ? undefined : Math.max(1, Math.floor(Number(v) || 0));
                        onInChange(n);
                      }}
                      style={{ width: 86 }}
                    />
                  );
                  const kgValue = (() => {
                    const g = Number((boxFinalDraft as any)?.weight_g);
                    if (!Number.isFinite(g) || g <= 0) return undefined;
                    return Math.ceil(g / 1000);
                  })();
                  const setWeightKg = (kg: number | undefined) => {
                    const g = (kg == null) ? undefined : Math.max(1000, Math.floor(Number(kg) || 0) * 1000);
                    setBoxFinalDraft((prev) => ({ ...(prev || {}), weight_g: g }));
                  };
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <div style={{ color: '#595959', width: 70 }}>长度</div>
                        {cmInput((boxFinalDraft as any)?.length_cm, (cm) => {
                          setBoxFinalDraft((prev) => ({ ...(prev || {}), length_cm: cm }));
                          const inch = (cm == null) ? undefined : Math.ceil(cmToIn(cm));
                          setBoxFinalLenIn(inch);
                          setBoxFinalDirty(true);
                        })}
                        <span style={{ color: '#8c8c8c' }}>厘米</span>
                        <span style={{ color: '#d9d9d9' }}>|</span>
                        {inInput(boxFinalLenIn, (inch) => {
                          setBoxFinalLenIn(inch);
                          const cm = (inch == null) ? undefined : Math.ceil(inToCm(inch));
                          setBoxFinalDraft((prev) => ({ ...(prev || {}), length_cm: cm }));
                          setBoxFinalDirty(true);
                        })}
                        <span style={{ color: '#8c8c8c' }}>英寸</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <div style={{ color: '#595959', width: 70 }}>宽度</div>
                        {cmInput((boxFinalDraft as any)?.width_cm, (cm) => {
                          setBoxFinalDraft((prev) => ({ ...(prev || {}), width_cm: cm }));
                          const inch = (cm == null) ? undefined : Math.ceil(cmToIn(cm));
                          setBoxFinalWidIn(inch);
                          setBoxFinalDirty(true);
                        })}
                        <span style={{ color: '#8c8c8c' }}>厘米</span>
                        <span style={{ color: '#d9d9d9' }}>|</span>
                        {inInput(boxFinalWidIn, (inch) => {
                          setBoxFinalWidIn(inch);
                          const cm = (inch == null) ? undefined : Math.ceil(inToCm(inch));
                          setBoxFinalDraft((prev) => ({ ...(prev || {}), width_cm: cm }));
                          setBoxFinalDirty(true);
                        })}
                        <span style={{ color: '#8c8c8c' }}>英寸</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <div style={{ color: '#595959', width: 70 }}>高度</div>
                        {cmInput((boxFinalDraft as any)?.height_cm, (cm) => {
                          setBoxFinalDraft((prev) => ({ ...(prev || {}), height_cm: cm }));
                          const inch = (cm == null) ? undefined : Math.ceil(cmToIn(cm));
                          setBoxFinalHeiIn(inch);
                          setBoxFinalDirty(true);
                        })}
                        <span style={{ color: '#8c8c8c' }}>厘米</span>
                        <span style={{ color: '#d9d9d9' }}>|</span>
                        {inInput(boxFinalHeiIn, (inch) => {
                          setBoxFinalHeiIn(inch);
                          const cm = (inch == null) ? undefined : Math.ceil(inToCm(inch));
                          setBoxFinalDraft((prev) => ({ ...(prev || {}), height_cm: cm }));
                          setBoxFinalDirty(true);
                        })}
                        <span style={{ color: '#8c8c8c' }}>英寸</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <div style={{ color: '#595959', width: 70 }}>容器实重</div>
                        <InputNumber
                          min={1}
                          precision={0}
                          value={kgValue as any}
                          disabled={readonly}
                          onChange={(v) => {
                            const kg = (v == null) ? undefined : Math.max(1, Math.floor(Number(v) || 0));
                            setWeightKg(kg);
                            const lb = (kg == null) ? undefined : Math.ceil(kgToLb(kg));
                            setBoxFinalLb(lb);
                            setBoxFinalDirty(true);
                          }}
                          style={{ width: 86 }}
                        />
                        <span style={{ color: '#8c8c8c' }}>公斤</span>
                        <span style={{ color: '#d9d9d9' }}>|</span>
                        <InputNumber
                          min={1}
                          precision={0}
                          value={boxFinalLb as any}
                          disabled={readonly}
                          onChange={(v) => {
                            const lb = (v == null) ? undefined : Math.max(1, Math.floor(Number(v) || 0));
                            setBoxFinalLb(lb);
                            const kg = (lb == null) ? undefined : Math.ceil(lbToKg(lb));
                            setWeightKg(kg);
                            setBoxFinalDirty(true);
                          }}
                          style={{ width: 86 }}
                        />
                        <span style={{ color: '#8c8c8c' }}>磅</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ color: '#8c8c8c' }}>暂无容器数据</div>
        )}
      </Modal>
      <Modal
        open={boxLabelOpen}
        title={`箱标预览 · ${String(boxLabelCode || '').trim() || '-'}`}
        onCancel={closeBoxLabelPreview}
        footer={(
          <Space>
            <Button
              onClick={closeBoxLabelPreview}
            >
              关闭
            </Button>
            <Button
              type="primary"
              disabled={!boxLabelUrl}
              onClick={() => {
                try {
                  if (!boxLabelUrl) return;
                  let finished = false;
                  let closedCheckTimer: any = null;
                  let fallbackTimer: any = null;
                  const listeners: Array<{ target: any; handler: any }> = [];
                  const addAfterPrintListener = (target: any) => {
                    if (!target?.addEventListener) return;
                    const handler = () => {
                      if (finished) return;
                      finished = true;
                      try {
                        listeners.forEach(({ target: t, handler: h }) => {
                          try { t?.removeEventListener?.('afterprint', h as any); } catch {}
                        });
                      } catch {}
                      try { if (closedCheckTimer) clearInterval(closedCheckTimer); } catch {}
                      try { if (fallbackTimer) clearTimeout(fallbackTimer); } catch {}
                      closeBoxLabelPreview();
                    };
                    try {
                      target.addEventListener('afterprint', handler as any);
                      listeners.push({ target, handler });
                    } catch {}
                  };
                  addAfterPrintListener(window);
                  const frameWin = boxLabelFrameRef.current?.contentWindow;
                  if (frameWin) {
                    addAfterPrintListener(frameWin);
                    fallbackTimer = setTimeout(() => {
                      if (finished) return;
                      finished = true;
                      closeBoxLabelPreview();
                    }, 15000);
                    frameWin.focus();
                    frameWin.print();
                    return;
                  }
                  const w = window.open(boxLabelUrl, '_blank');
                  if (!w) {
                    message.warning('请允许弹窗后重试打印');
                    return;
                  }
                  boxLabelPopupRef.current = w;
                  addAfterPrintListener(w);
                  fallbackTimer = setTimeout(() => {
                    if (finished) return;
                    finished = true;
                    try {
                      if (boxLabelPopupRef.current === w && !w.closed) w.close();
                    } catch {}
                    closeBoxLabelPreview();
                  }, 15000);
                  closedCheckTimer = setInterval(() => {
                    try {
                      if (w.closed && !finished) {
                        finished = true;
                        try { if (fallbackTimer) clearTimeout(fallbackTimer); } catch {}
                        try { if (closedCheckTimer) clearInterval(closedCheckTimer); } catch {}
                        closeBoxLabelPreview();
                      }
                    } catch {}
                  }, 300);
                  w.focus();
                  w.print?.();
                } catch {}
              }}
            >
              打印
            </Button>
          </Space>
        )}
        width="75vw"
        destroyOnClose
      >
        {boxLabelLoading ? (
          <div style={{ padding: 16, color: '#595959' }}>正在生成箱标预览…</div>
        ) : (
          <div style={{ width: '100%', height: '70vh', border: '1px solid #f0f0f0' }}>
            {boxLabelUrl ? (
              <iframe ref={boxLabelFrameRef} src={boxLabelUrl} style={{ width: '100%', height: '100%', border: 'none' }} title="PDF Preview" />
            ) : (
              <div style={{ padding: 12, color: '#8c8c8c' }}>暂无可预览箱标</div>
            )}
          </div>
        )}
      </Modal>
      {(printTransportLabelPdfRunning || printTransportLabelImageRunning) ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            background: 'rgba(255,255,255,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spin size="large" />
        </div>
      ) : null}
    </div>
  );
};

export default RegularOutboundPackRenderer;
